import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, Space, Button, InputNumber, Input, Table, message, Tag, Modal, Divider, Select, Tabs, Tooltip, Typography, Form, Row, Col, Dropdown, Descriptions, Switch } from 'antd';
import { SwapLeftOutlined, DownOutlined, InboxOutlined, ShoppingOutlined, TagsOutlined, CopyOutlined, WarningOutlined } from '@ant-design/icons';
import request from '../../../../utils/request';
import { getShippingPackagings } from '../../../../services/logistics';
import type { ShippingPackaging } from '../../../../services/logistics';
import { getInventoryUnitDetail } from '../../../../services/inventory';

type BoxRow = { code: string; status?: 'open'|'closed'|'cancelled'; container_type?: 'box'|'pallet'|'other'|string; volume_cc?: number; weight_g?: number };
type OpEventType = '换标' | '开始容器' | '结束容器' | string;
type OpRecordRow = {
  id: string;
  ts: number;
  event: OpEventType;
  container_code: string;
  old_code?: string;
  new_code?: string;
  qty?: number;
  // 本次操作最终落库到哪个操作对象（库存单元）
  op_iu_id?: number;
  op_iu_code?: string; // 库存单元系统编码（用于“操作记录”展示）
};
type MappingDef = {
  key: string;          // OLD->NEW
  old: string;
  newer: string;
  newText?: string;
  limitQty?: number;    // undefined 表示不限
  matchTargets?: string; // 匹配操作对象（IU1 | IU2 ...），用于执行页复用（可选）
  remark?: string;
  rowNo?: number;
};

const formatNewBarcodeText = (v: any): string => {
  const s = String(v ?? '');
  if (!s) return '-';
  if (s.length <= 20) return s;
  return `${s.slice(0, 10)}...${s.slice(-10)}`;
};

type OldCodeWeightResolveItem = { old_code: string; inventory_unit_id: number; actual_weight_g: number };
type OldCodeWeightResolveMiss = { old_code: string; reason: string; inventory_unit_id?: number; inventory_unit_ids?: number[] };

type OutputContainerDraftRow = {
  id?: number;
  instruction_id?: number;
  output_container_id?: number | null;
  code: string;
  container_type?: string;
  status?: string;
  length_cm?: number;
  width_cm?: number;
  height_cm?: number;
  weight_g?: number;
  length_in?: number;
  width_in?: number;
  height_in?: number;
  weight_lb?: number;
  version?: number;
  updated_at?: any;
  items?: Array<{ inventory_unit_id: number; quantity: number }>;
};

// TSX 解析器兼容：避免出现 `>>>` 导致的 parsing error（语义等价）
type NewAggByCode = Record<string, Record<string, number>>;

type Props = {
  taskId: number | null;
  stageNo: number;
  executionSessionId?: string | null;
  groupKey?: string;
  relabelStep?: any;
  packStep?: any;
  submitRelabel: (metrics: any) => Promise<void>;
  submitPack: (metrics: any) => Promise<void>;
  canOperate?: boolean;
};

const CombinedRelabelBoxRenderer: React.FC<Props> = ({
  taskId,
  stageNo,
  executionSessionId,
  groupKey,
  relabelStep,
  packStep,
  submitRelabel,
  submitPack,
  canOperate
}) => {
  const draftKey = useMemo(() => `COMBINED_RELABEL_PACK_${taskId}_${stageNo}_${groupKey || 'G'}`, [taskId, stageNo, groupKey]);
  const draftStepId = useMemo(() => {
    try {
      const p = Number(packStep?.id);
      if (Number.isFinite(p) && p > 0) return p;
      const r = Number(relabelStep?.id);
      if (Number.isFinite(r) && r > 0) return r;
    } catch {}
    return undefined;
  }, [packStep, relabelStep]);
  const [labelCount, setLabelCount] = useState<number>(0);
  const [boxes, setBoxes] = useState<BoxRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const saveTimer = useRef<any>(null);
  const saveToServerTimer = useRef<any>(null);
  const lastServerDraftSigRef = useRef<string>('');
  const readonlyRecoveredFromCollectedRef = useRef<boolean>(false);
  // 新增：操作态（箱与记录）
  const [currentBoxCode, setCurrentBoxCode] = useState<string>('');
  // 新增：允许“新增到已结束容器”（只在无打开容器时使用）
  const [addToClosedEnabled, setAddToClosedEnabled] = useState<boolean>(false);
  const [addToClosedCode, setAddToClosedCode] = useState<string>('');
  // 临时覆盖扫描目标容器（用于“重扫多出数量 -> 去新增到已有容器”的快捷跳转）
  // 说明：此覆盖优先级最高，避免当前存在“未结束新容器”时误加入错误容器。
  const [forcedScanBoxCode, setForcedScanBoxCode] = useState<string>('');
  const [pickClosedOpen, setPickClosedOpen] = useState<boolean>(false);
  const [pickClosedCode, setPickClosedCode] = useState<string>('');
  const [pickClosedQuery, setPickClosedQuery] = useState<string>('');
  const [pickClosedPage, setPickClosedPage] = useState<number>(1);
  const [pickClosedPageSize, setPickClosedPageSize] = useState<number>(10);
  const activeScanBoxCode = useMemo(() => {
    const forced = String(forcedScanBoxCode || '').trim();
    if (forced) return forced;
    const open = String(currentBoxCode || '').trim();
    if (open) return open;
    if (!addToClosedEnabled) return '';
    return String(addToClosedCode || '').trim();
  }, [forcedScanBoxCode, currentBoxCode, addToClosedEnabled, addToClosedCode]);

  const closedContainers = useMemo(() => {
    try {
      return (boxes || [])
        .filter((b:any) => String(b?.status || '') === 'closed')
        .map((b:any) => ({ code: String(b?.code || '').trim(), container_type: String(b?.container_type || '') }))
        .filter((x:any) => !!x.code);
    } catch { return []; }
  }, [boxes]);

  const closedContainersFiltered = useMemo(() => {
    try {
      const q = String(pickClosedQuery || '').trim().toUpperCase();
      if (!q) return closedContainers;
      return (closedContainers || []).filter((x:any) => String(x?.code || '').toUpperCase().includes(q));
    } catch { return closedContainers; }
  }, [closedContainers, pickClosedQuery]);

  // 筛选后仅剩一个结果时，自动选定（仍保留列表 radio 可手动切换）
  useEffect(() => {
    try {
      if (!pickClosedOpen) return;
      // 每次打开弹窗，重置分页到第一页（避免筛选后页码越界）
      try { setPickClosedPage(1); } catch {}
      const list: any[] = Array.isArray(closedContainersFiltered) ? closedContainersFiltered : [];
      if (list.length === 1) {
        const only = String(list?.[0]?.code || '').trim();
        if (only && only !== String(pickClosedCode || '').trim()) setPickClosedCode(only);
        return;
      }
      // 无匹配时清空选择，避免选中项不在列表里
      if (list.length === 0) {
        if (String(pickClosedCode || '').trim()) setPickClosedCode('');
      }
    } catch {}
  }, [pickClosedOpen, closedContainersFiltered, pickClosedCode]);
  const [boxMeta, setBoxMeta] = useState<Record<string, { weight_g?: number; weight_kg?: number; weight_lb?: number; length_cm?: number; width_cm?: number; height_cm?: number; length_in?: number; width_in?: number; height_in?: number }>>({});
  // 旧条码 -> 库存单元实际重量(g)。number=可用；null=无法解析/无重量/重复等（不阻断业务，只影响理论重量展示）
  const [oldCodeWeightG, setOldCodeWeightG] = useState<Record<string, number | null>>({});
  // 旧条码 -> 库存单元ID（用于结束容器落库内容项；无法解析时为 null）
  const [oldCodeIuId, setOldCodeIuId] = useState<Record<string, number | null>>({});
  // 旧条码解析原因（用于“初次打开审核”）
  const [oldCodeResolveReason, setOldCodeResolveReason] = useState<Record<string, { reason: string; inventory_unit_id?: number; inventory_unit_ids?: number[] }>>({});
  // IU 概览：用于“按条码映射进度”展示库存单元系统编码 + Tooltip 概览
  const [iuPreviewById, setIuPreviewById] = useState<Record<number, any>>({});
  const [oldCodeWeightLoading, setOldCodeWeightLoading] = useState<boolean>(false);

  // 输出容器 Draft（后端草稿表）：用于“按容器统计/最终装箱数据/最终提交覆盖”
  const [outputDraftByCode, setOutputDraftByCode] = useState<Record<string, OutputContainerDraftRow>>({});
  const [outputDraftLoading, setOutputDraftLoading] = useState<boolean>(false);
  // 新条码汇总草稿（仅用于“按容器统计/箱标/最终装箱数据”的新标签种类&总件数口径；与后端 IU item 解耦）
  const [draftNewAggByCode, setDraftNewAggByCode] = useState<NewAggByCode>({});
  // 箱标是否“需重打”（本地口径：只要 draftNewAgg 或 draft items/物理数据发生变化即置脏；生成箱标后清除）
  const [labelDirtyByCode, setLabelDirtyByCode] = useState<Record<string, boolean>>({});
  const [voidingContainerCodes, setVoidingContainerCodes] = useState<Set<string>>(new Set());

  // BARCODE_MAPPING 完整性审核（初次打开聚合UI时必须保证每条映射能明确到 IU）
  const [mappingAuditOpen, setMappingAuditOpen] = useState<boolean>(false);
  const [mappingAuditOk, setMappingAuditOk] = useState<boolean>(false);
  const [mappingAuditRows, setMappingAuditRows] = useState<any[]>([]);
  // 允许用户“暂时关闭”审核弹窗以便查看其他信息（保持 canOperateScan=false，不允许扫码）
  const [mappingAuditDismissed, setMappingAuditDismissed] = useState<boolean>(false);
  const [mappingKeyIuOverride, setMappingKeyIuOverride] = useState<Record<string, number>>({});
  // key -> iuIds[]（用于写入“操作对象匹配信息”列；同时取第一个作为 mappingKeyIuOverride 的 IU）
  const [mappingKeyIuOverrideMulti, setMappingKeyIuOverrideMulti] = useState<Record<string, number[]>>({});
  const [mappingAuditSaving, setMappingAuditSaving] = useState<boolean>(false);
  const [mappingAuditAutoMatching, setMappingAuditAutoMatching] = useState<boolean>(false);
  const mappingAuditRunningRef = useRef<boolean>(false);
  //（已移除批量应用功能）仅保留逐行指定
  // BARCODE_MAPPING：解析时保留原始表格，用于在执行页直接改写基线文件
  const [mappingFileRowCache, setMappingFileRowCache] = useState<any | null>(null);
  const [mappingAoaCache, setMappingAoaCache] = useState<any[][]>([]);
  const [mappingIsCsvCache, setMappingIsCsvCache] = useState<boolean>(false);

  const [targetIuOptions, setTargetIuOptions] = useState<Array<{ id: number; system_barcode?: string; use_all_available?: boolean; expected_qty?: number | null }>>([]);

  // IU id -> system_barcode 映射（用于记录“最终落库对象”的展示）
  const iuIdToSys = useMemo(() => {
    const m = new Map<number, string>();
    try {
      (targetIuOptions || []).forEach((x:any) => {
        const id = Math.floor(Number(x?.id || 0));
        if (!Number.isFinite(id) || id <= 0) return;
        const sys = String(x?.system_barcode || '').trim();
        if (sys) m.set(id, sys);
      });
    } catch {}
    return m;
  }, [targetIuOptions]);

  const pickFinalOpTarget = useCallback((args: { key: string; old_code?: string; new_code?: string }) => {
    try {
      const key = String(args?.key || '').trim();
      const oldc = String(args?.old_code || '').trim().toUpperCase() || '-';
      // new_code 仅用于调试/扩展，目前不参与决策
      // const newc = String(args?.new_code || '').trim().toUpperCase() || '-';

      // 1) 旧条码存在且已解析出唯一 IU：直接使用（与结束容器落库口径保持一致）
      if (oldc && oldc !== '-') {
        const iu = Math.floor(Number((oldCodeIuId as any)?.[oldc] || 0));
        if (Number.isFinite(iu) && iu > 0) {
          return { op_iu_id: iu, op_iu_code: undefined };
        }
      }

      // 2) 执行页审核覆盖：key -> iu_id（单选）
      const ov = Math.floor(Number((mappingKeyIuOverride as any)?.[key] || 0));
      if (Number.isFinite(ov) && ov > 0) {
        return { op_iu_id: ov, op_iu_code: undefined };
      }

      // 3) 执行页审核覆盖：key -> iu_ids[]（多选），取第一个作为“本次落库对象”（其余用于规则判断/审计）
      const ovs: any[] = Array.isArray((mappingKeyIuOverrideMulti as any)?.[key]) ? (mappingKeyIuOverrideMulti as any)[key] : [];
      const first = Math.floor(Number(ovs?.[0] || 0));
      if (Number.isFinite(first) && first > 0) {
        return { op_iu_id: first, op_iu_code: undefined };
      }

      // 兜底：未知
      return { op_iu_id: undefined, op_iu_code: '-' };
    } catch {
      return { op_iu_id: undefined, op_iu_code: '-' };
    }
  }, [oldCodeIuId, iuIdToSys, mappingKeyIuOverride, mappingKeyIuOverrideMulti]);
  const [targetIuLoading, setTargetIuLoading] = useState<boolean>(false);
  const [scanOld, setScanOld] = useState<string>('');
  const [scanNew, setScanNew] = useState<string>('');
  const [scanLast, setScanLast] = useState<'old' | 'new'>('old');
  const [scanQty, setScanQty] = useState<number>(1);
  // 扫码框锁定态：用于“自动回填”的那一侧输入框（显示禁用样式，但允许点 × 清空后恢复可输入）
  const [scanOldLocked, setScanOldLocked] = useState<boolean>(false);
  const [scanNewLocked, setScanNewLocked] = useState<boolean>(false);
  // 扫码提交模式：默认=打印并加入记录；A=仅打印；B=仅加入记录
  const [scanCommitMode, setScanCommitMode] = useState<'both' | 'print_only' | 'record_only'>('both');
  const [records, setRecords] = useState<OpRecordRow[]>([]);

  // 操作记录展示专用：IU id -> system_barcode（补拉缓存，避免显示 IU#123）
  const [opIuSysById, setOpIuSysById] = useState<Record<number, string>>({});
  const opIuSysCacheRef = useRef<Record<number, string>>({});

  const ensureOpIuSysCodes = useCallback(async (ids: number[]) => {
    try {
      const uniq = Array.from(new Set((ids || []).map(x => Math.floor(Number(x || 0))).filter(n => Number.isFinite(n) && n > 0)));
      if (!uniq.length) return;
      const missing = uniq.filter((id) => {
        if (iuIdToSys.has(id)) return false;
        const cached = opIuSysCacheRef.current?.[id];
        if (cached && String(cached).trim()) return false;
        const inState = (opIuSysById as any)?.[id];
        if (inState && String(inState).trim()) return false;
        return true;
      });
      if (!missing.length) return;
      // 批量并发补拉（数量不会大；仅用于 UI 展示）
      const results = await Promise.all(missing.map(async (id) => {
        try {
          const resp: any = await getInventoryUnitDetail(id);
          const data = resp?.data || {};
          const sys = String(data?.system_barcode || data?.system_code || data?.systemCode || '').trim();
          return { id, sys };
        } catch {
          return { id, sys: '' };
        }
      }));
      const patch: Record<number, string> = {};
      for (const it of results) {
        const id = Math.floor(Number((it as any)?.id || 0));
        const sys = String((it as any)?.sys || '').trim();
        if (!Number.isFinite(id) || id <= 0) continue;
        if (!sys) continue;
        patch[id] = sys;
        try { opIuSysCacheRef.current[id] = sys; } catch {}
      }
      if (Object.keys(patch).length) {
        setOpIuSysById(prev => ({ ...(prev || {}), ...patch }));
      }
    } catch {}
  }, [iuIdToSys, opIuSysById]);

  // 操作记录里出现新的 op_iu_id 时，自动补拉系统编码（不阻断）
  useEffect(() => {
    try {
      const list: any[] = Array.isArray(records) ? records : [];
      const ids: number[] = [];
      for (const r of list) {
        const ev = String(r?.event || '').trim();
        // 仅在会产生/继承操作对象的事件里补拉（换标/调整/重扫）
        if (!(ev === '换标' || ev.startsWith('调整-') || ev.startsWith('重扫-'))) continue;
        const id = Math.floor(Number((r as any)?.op_iu_id || 0));
        if (Number.isFinite(id) && id > 0) ids.push(id);
      }
      if (ids.length) {
        ensureOpIuSysCodes(ids);
      }
    } catch {}
  }, [records, ensureOpIuSysCodes]);
  // 指令系统编码 / 指令ID（用于执行锁、文件名等）
  const [instrCode, setInstrCode] = useState<string | undefined>(undefined);
  const [instrIdState, setInstrIdState] = useState<number | undefined>(undefined);
  // 指令涉及项目系统编码汇总：25121 等N个
  const [instrProjectSummary, setInstrProjectSummary] = useState<string>('-');
  const [instrCustomerId, setInstrCustomerId] = useState<number | undefined>(undefined);
  const [instrWarehouseId, setInstrWarehouseId] = useState<number | undefined>(undefined);
  // 指令详情：预计操作总数量（与指令详情页“预计操作总数量”口径一致：targets_detail.expected_qty 汇总）
  const [instrExpectedTotalQty, setInstrExpectedTotalQty] = useState<number | null>(null);
  // 指令问卷与答案（用于“开始新容器”弹窗提示 / 限重解析等）
  const [instrQuestionRows, setInstrQuestionRows] = useState<any[]>([]);
  const [instrAnswers, setInstrAnswers] = useState<Record<string, any>>({});
  const scanOldInputRef = useRef<any>(null);
  const scanNewInputRef = useRef<any>(null);
  // 方案B：未知条码软中断（冷却窗口 1.5s），避免快速连续扫码时无法确认哪一枪失败
  const [scanCooling, setScanCooling] = useState<boolean>(false);
  // 错误提示固定显示在“放入容器”按钮右侧，直到下一次扫描成功完成
  const [scanErrInline, setScanErrInline] = useState<{ title: string; desc?: string } | null>(null);
  const scanCooldownTimerRef = useRef<any>(null);
  // 扫码错误提示音（非阻塞）
  const audioCtxRef = useRef<any>(null);
  const playErrorBeep = useCallback(() => {
    try {
      const AudioCtx: any = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      void (async () => {
        try {
          let ctx = audioCtxRef.current;
          if (!ctx) {
            ctx = new AudioCtx();
            audioCtxRef.current = ctx;
          }
          // 浏览器可能会把 AudioContext 挂起（autoplay policy）；这里尽量在用户手势链路中 resume
          try { if (ctx.state === 'suspended') await ctx.resume(); } catch {}
          // 警告音：3段脉冲音（每段 1 秒），明显区别于扫码枪提示音
          const startAt = ctx.currentTime + 0.01;
          const pulses = [
            { f1: 1600, f2: 900, dur: 1.0, gap: 0.06 },
            { f1: 1600, f2: 900, dur: 1.0, gap: 0.06 },
            { f1: 1600, f2: 900, dur: 1.0, gap: 0.00 },
          ];
          let t = startAt;
          for (const p of pulses) {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'sawtooth';
            // 频率在单个脉冲内做下扫，形成更“报警”的感觉
            osc.frequency.setValueAtTime(p.f1, t);
            osc.frequency.linearRampToValueAtTime(p.f2, t + p.dur);
            // 包络：快速起音 + 衰减
            gain.gain.setValueAtTime(0.0001, t);
            gain.gain.exponentialRampToValueAtTime(0.65, t + 0.01);
            gain.gain.exponentialRampToValueAtTime(0.0001, t + p.dur);
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start(t);
            osc.stop(t + p.dur + 0.03);
            t = t + p.dur + p.gap;
          }
        } catch {}
      })();
    } catch {}
  }, []);
  // 容器内容调整专用告警音：与“常规出库-扫描添加模式”一致（两声短促）
  const playAdjustErrorBeep = useCallback(() => {
    try {
      const AudioCtx: any = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = audioCtxRef.current || new AudioCtx();
      audioCtxRef.current = ctx;
      try {
        if (ctx.state === 'suspended') {
          void ctx.resume();
        }
      } catch {}
      const beep = (at: number, freq: number) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'square';
        osc.frequency.setValueAtTime(freq, at);
        gain.gain.setValueAtTime(0.0001, at);
        gain.gain.exponentialRampToValueAtTime(0.22, at + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.2);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(at);
        osc.stop(at + 0.22);
      };
      const t0 = ctx.currentTime;
      beep(t0, 980);
      beep(t0 + 0.24, 820);
    } catch {}
  }, []);
  const adjustWarn = useCallback((text: string) => {
    try { playAdjustErrorBeep(); } catch {}
    message.warning(text);
  }, [playAdjustErrorBeep]);
  const adjustError = useCallback((text: string) => {
    try { playAdjustErrorBeep(); } catch {}
    message.error(text);
  }, [playAdjustErrorBeep]);

  // ========= 理论重量：旧条码 -> 库存单元实际重量(g)（权威数据） =========
  const ensureOldCodeWeights = useCallback(async (oldCodes: string[]) => {
    try {
      const instrId = Number(instrIdState);
      if (!Number.isFinite(instrId)) return;
      const norm = (v:any) => String(v ?? '').trim().toUpperCase();
      const req = (oldCodes || []).map(norm).filter(Boolean);
      if (!req.length) return;
      // weight 或 iuId 任一缺失都认为需要补齐（避免“只拿到重量但缺 IU id”导致结束容器无法落库内容项）
      const unknown = req.filter(c => !(c in (oldCodeWeightG || {})) || !(c in (oldCodeIuId || {})));
      if (!unknown.length) return;
      setOldCodeWeightLoading(true);
      const resp = await request.post(`/api/v1/instruction/instructions/${instrId}/relabel-pack/resolve-old-barcodes/`, {
        old_codes: unknown,
      });
      const items: OldCodeWeightResolveItem[] = Array.isArray(resp?.data?.items) ? resp.data.items : [];
      const misses: OldCodeWeightResolveMiss[] = Array.isArray(resp?.data?.misses) ? resp.data.misses : [];
      const patchW: Record<string, number | null> = {};
      const patchIU: Record<string, number | null> = {};
      const patchReason: Record<string, { reason: string; inventory_unit_id?: number; inventory_unit_ids?: number[] }> = {};
      for (const it of items) {
        const c = norm((it as any)?.old_code);
        const w = Number((it as any)?.actual_weight_g);
        const iu = Number((it as any)?.inventory_unit_id);
        if (c) {
          if (Number.isFinite(w) && w > 0) patchW[c] = Math.floor(w);
          if (Number.isFinite(iu) && iu > 0) patchIU[c] = Math.floor(iu);
          patchReason[c] = { reason: 'ok', inventory_unit_id: Number.isFinite(iu) ? Math.floor(iu) : undefined };
        }
      }
      for (const m of misses) {
        const c = norm((m as any)?.old_code);
        if (c) {
          const reason = String((m as any)?.reason || 'unknown');
          const iu1 = Number((m as any)?.inventory_unit_id);
          const iuIds = Array.isArray((m as any)?.inventory_unit_ids) ? (m as any).inventory_unit_ids : undefined;
          // no_weight：库存单元是明确的，只是缺少重量（允许继续；重量用于理论重量展示）
          if (reason === 'no_weight' && Number.isFinite(iu1) && iu1 > 0) {
            patchIU[c] = Math.floor(iu1);
            patchW[c] = null;
            patchReason[c] = { reason, inventory_unit_id: Math.floor(iu1) };
          } else {
            patchW[c] = null;
            patchIU[c] = null;
            patchReason[c] = { reason, inventory_unit_id: (Number.isFinite(iu1) && iu1 > 0) ? Math.floor(iu1) : undefined, inventory_unit_ids: iuIds };
          }
        }
      }
      // 未返回的也标记为 null（避免反复刷接口）
      for (const c of unknown) {
        if (!(c in patchW)) patchW[c] = null;
        if (!(c in patchIU)) patchIU[c] = null;
        if (!(c in patchReason)) patchReason[c] = { reason: 'unknown' };
      }
      setOldCodeWeightG(prev => ({ ...(prev || {}), ...patchW }));
      setOldCodeIuId(prev => ({ ...(prev || {}), ...patchIU }));
      setOldCodeResolveReason(prev => ({ ...(prev || {}), ...patchReason }));
    } catch {
      // 不阻断业务：失败时不写入缓存（允许后续重试）
    } finally {
      try { setOldCodeWeightLoading(false); } catch {}
    }
  }, [instrIdState, oldCodeWeightG, oldCodeIuId]);

  // 当前容器的记录变化时，批量预取旧条码重量（不阻断）
  useEffect(() => {
    try {
      const cc = String(currentBoxCode || '').trim();
      if (!cc) return;
      const codes = (records || [])
        .filter((r:any) => String(r?.event || '') === '换标' && String(r?.container_code || '').trim() === cc)
        .map((r:any) => String(r?.old_code || '').trim().toUpperCase())
        .filter(Boolean);
      if (!codes.length) return;
      ensureOldCodeWeights(codes);
    } catch {}
  }, [records, currentBoxCode, ensureOldCodeWeights]);

  // 解析“限制重量”(kg)：找不到则不显示；仅用于提示，不阻断业务
  const limitWeightKg = useMemo(() => {
    try {
      const qs = Array.isArray(instrQuestionRows) ? instrQuestionRows : [];
      const ans = (instrAnswers && typeof instrAnswers === 'object') ? instrAnswers : {};
      const norm = (s:any)=> String(s ?? '').trim().toLowerCase();
      const findQ = (cands: string[]) => {
        const candNorm = (cands || []).map(norm).filter(Boolean);
        return qs.find((q:any) => {
          const label = norm(q?.label);
          const key = norm(q?.key);
          return candNorm.some(c => (label && label.includes(c)) || (key && key.includes(c)));
        }) || null;
      };
      // 兼容：题目 label/名称（如“最大装箱重量”）与题目 key（如 max_weight）
      // 注意：key 可能包含下划线，不能只匹配 "max weight"
      const q = findQ([
        '最大装箱重量', '装箱重量',
        '限重', '重量上限', '最大重量', '最大毛重', '箱重上限',
        'max_weight', 'maxweight',
        'weight limit', 'max weight',
      ]);
      if (!q) return undefined;
      const k = String(q?.key || '').trim();
      if (!k) return undefined;
      const raw = (ans as any)[k];
      if (raw === undefined || raw === null || raw === '') return undefined;
      const s = String(raw).trim();
      const m = s.match(/-?\d+(\.\d+)?/);
      if (!m) return undefined;
      const n = Number(m[0]);
      if (!Number.isFinite(n) || n <= 0) return undefined;
      const lower = s.toLowerCase();
      if (lower.includes('kg') || lower.includes('千克')) return n;
      if (lower.includes('g') || lower.includes('克')) return n / 1000.0;
      // 无单位：默认按 kg
      return n;
    } catch { return undefined; }
  }, [instrQuestionRows, instrAnswers]);

  // 当前容器理论重量：ok=false 时统一显示“无法估算理论总重量”（不阻断业务）
  const currentBoxTheory = useMemo(() => {
    try {
      const cc = String(activeScanBoxCode || '').trim();
      if (!cc) return { ok: false as const };
      const meta: any = (boxMeta as any)?.[cc] || {};
      const tareG = Number(meta?.weight_g);
      if (!Number.isFinite(tareG) || tareG <= 0) return { ok: false as const };
      const list = (records || []).filter((r:any) => String(r?.event || '') === '换标' && String(r?.container_code || '').trim() === cc);
      if (!list.length) {
        return { ok: true as const, tareG, contentG: 0, totalKg: Number((tareG / 1000).toFixed(3)) };
      }
      let contentG = 0;
      for (const r of list) {
        const oc = String(r?.old_code || '').trim().toUpperCase();
        const qty = Math.floor(Number(r?.qty || 0) || 0);
        if (!oc || qty <= 0) return { ok: false as const };
        if (!(oc in (oldCodeWeightG || {}))) return { ok: false as const };
        const w = (oldCodeWeightG as any)[oc];
        if (w == null) return { ok: false as const };
        const ww = Number(w);
        if (!Number.isFinite(ww) || ww <= 0) return { ok: false as const };
        contentG += ww * qty;
      }
      const totalKg = Number(((tareG + contentG) / 1000).toFixed(3));
      return { ok: true as const, tareG, contentG, totalKg };
    } catch { return { ok: false as const }; }
  }, [activeScanBoxCode, boxMeta, records, oldCodeWeightG]);
  const triggerScanCooldown = useCallback((title: string, desc?: string) => {
    try { playErrorBeep(); } catch {}
    try { if (scanCooldownTimerRef.current) clearTimeout(scanCooldownTimerRef.current); } catch {}
    setScanErrInline({ title, desc });
    setScanCooling(true);
    scanCooldownTimerRef.current = setTimeout(() => {
      setScanCooling(false);
      // 冷却结束后自动把焦点回到扫码框（默认旧条码；若最后输入的是新条码则回新条码）
      setTimeout(() => {
        try {
          const target = scanLast === 'new' ? scanNewInputRef.current : scanOldInputRef.current;
          if (target && typeof target.focus === 'function') target.focus();
        } catch {}
      }, 0);
    }, 3000);
  }, [playErrorBeep, scanLast]);
  // 操作控制台：三种模式页签（先落 UI，不改业务逻辑）
  const [opConsoleTab, setOpConsoleTab] = useState<'relabel_pack' | 'adjust' | 'rescan'>('relabel_pack');
  // 够用版：指令执行租约（避免意外并发）
  const [leaseLoading, setLeaseLoading] = useState<boolean>(false);
  const [leaseOk, setLeaseOk] = useState<boolean>(true);
  const [leaseRecovering, setLeaseRecovering] = useState<boolean>(false);
  const [leaseBlocked, setLeaseBlocked] = useState<boolean>(false);
  const [leaseId, setLeaseId] = useState<string>('');
  const [leaseInfo, setLeaseInfo] = useState<any | null>(null);
  const [recordPage, setRecordPage] = useState<number>(1);
  const [recordPageSize, setRecordPageSize] = useState<number>(10);
  const [recordSearch, setRecordSearch] = useState<string>('');
  const [recordDetailPage, setRecordDetailPage] = useState<number>(1);
  const [recordDetailPageSize, setRecordDetailPageSize] = useState<number>(10);
  const makeRecId = () => `rec_${Date.now()}_${Math.random().toString(36).slice(2,8)}`;

  // 系统生成标签（归档副本）：new_code -> item（用于复用打印 + 总进度预览/下载）
  const [genLabelMap, setGenLabelMap] = useState<Record<string, any>>({});
  const [genLabelLoading, setGenLabelLoading] = useState<boolean>(false);

  // 开始新容器弹窗（UI：类型/尺寸/净重/包材扫码自动回填）
  const [startContainerOpen, setStartContainerOpen] = useState<boolean>(false);
  const [startContainerLoading, setStartContainerLoading] = useState<boolean>(false);
  const [shippingPkgQuery, setShippingPkgQuery] = useState<string>(''); // 单输入框：编码/名称模糊匹配（忽略大小写）
  const [shippingPkgLoading, setShippingPkgLoading] = useState<boolean>(false);
  const [shippingPkgErr, setShippingPkgErr] = useState<string>('');
  const [shippingPkgHit, setShippingPkgHit] = useState<ShippingPackaging | null>(null);
  const [shippingPkgOptions, setShippingPkgOptions] = useState<ShippingPackaging[]>([]);
  const shipQueryTimerRef = useRef<any>(null);
  const lastShipQueryRef = useRef<string>('');
  const shippingPkgSelectRef = useRef<any>(null);
  const [startForm] = Form.useForm();

  // 结束容器弹窗（展示统计 + 允许手动修正尺寸/重量/内容数量）
  type EndBoxContentRow = { key: string; old_code: string; new_code: string; qty: number };
  const [endBoxOpen, setEndBoxOpen] = useState<boolean>(false);
  const [endBoxSubmitting, setEndBoxSubmitting] = useState<boolean>(false);
  const [endBoxDraft, setEndBoxDraft] = useState<{ code: string; container_type: 'box'|'pallet'|'other'|string; length_cm?: number; width_cm?: number; height_cm?: number; weight_g?: number }>({ code: '', container_type: 'box' });
  // 结束容器弹窗：英制输入（用于“用户输入值”保真；另一单位按换算向上取整联动）
  const [endBoxLenIn, setEndBoxLenIn] = useState<number | undefined>(undefined);
  const [endBoxWidIn, setEndBoxWidIn] = useState<number | undefined>(undefined);
  const [endBoxHeiIn, setEndBoxHeiIn] = useState<number | undefined>(undefined);
  const [endBoxLb, setEndBoxLb] = useState<number | undefined>(undefined);
  const [endBoxContentRows, setEndBoxContentRows] = useState<EndBoxContentRow[]>([]);
  const [endBoxWarn, setEndBoxWarn] = useState<string>('');
  const [endBoxContentPage, setEndBoxContentPage] = useState<number>(1);
  const [endBoxContentPageSize, setEndBoxContentPageSize] = useState<number>(10);

  useEffect(() => {
    if (!endBoxOpen) return;
    try { setEndBoxContentPage(1); } catch {}
    try { if (!endBoxContentPageSize) setEndBoxContentPageSize(10); } catch {}
  }, [endBoxOpen]);

  // 结束容器后：箱标预览（4x6 inch）
  const [boxLabelOpen, setBoxLabelOpen] = useState<boolean>(false);
  const [boxLabelLoading, setBoxLabelLoading] = useState<boolean>(false);
  const [boxLabelCode, setBoxLabelCode] = useState<string>('');
  const [boxLabelUrl, setBoxLabelUrl] = useState<string>('');
  const [boxLabelBlob, setBoxLabelBlob] = useState<Blob | null>(null);

  const toPosInt = (v: any): number | undefined => {
    const n = Number(v);
    if (!Number.isFinite(n)) return undefined;
    const i = Math.floor(n);
    if (i <= 0) return undefined;
    return i;
  };
  const mapShippingPackagingToContainerType = (packagingType?: string): 'box' | 'pallet' | 'other' => {
    const t = String(packagingType || '').toLowerCase();
    if (t === 'carton') return 'box';
    if (t === 'pallet') return 'pallet';
    return 'other';
  };
  const fillFromShippingPkg = useCallback((hit: ShippingPackaging) => {
    try {
      setShippingPkgHit(hit);
      setShippingPkgErr('');
      // 同步写回系统编码输入框，便于用户确认/后续再次识别
      try { setShippingPkgQuery(String(hit?.system_code || '').trim()); } catch {}

      const ct = mapShippingPackagingToContainerType((hit as any)?.packaging_type);
      const l = toPosInt((hit as any)?.length_cm);
      const w = toPosInt((hit as any)?.width_cm);
      const h = toPosInt((hit as any)?.height_cm);
      const wg = toPosInt((hit as any)?.weight_g);
      const cond = String((hit as any)?.condition || '').toLowerCase();

      startForm.setFieldsValue({
        container_type: ct,
        length_cm: (ct === 'pallet' ? 122 : l),
        width_cm: (ct === 'pallet' ? 102 : w),
        height_cm: (ct === 'pallet' ? undefined : h),
        weight_g: wg,
        condition: (cond === 'new' ? 'new' : 'used'),
      });
    } catch {}
  }, [startForm]);

  // 托盘默认尺寸：长122、宽102；托盘无预设高度（禁用 + 不校验）
  useEffect(() => {
    try {
      if (!startContainerOpen) return;
      const ct = String(startForm.getFieldValue('container_type') || '').trim();
      if (ct !== 'pallet') return;
      const curL = startForm.getFieldValue('length_cm');
      const curW = startForm.getFieldValue('width_cm');
      // 仅在为空时写默认，避免覆盖用户输入（或未来其它包材配置）
      const patch: any = {};
      if (curL == null || curL === '') patch.length_cm = 122;
      if (curW == null || curW === '') patch.width_cm = 102;
      patch.height_cm = undefined; // 托盘不维护高度
      startForm.setFieldsValue(patch);
    } catch {}
  }, [startContainerOpen, startForm, shippingPkgHit]);
  const searchShippingPkg = useCallback(async (queryRaw: string) => {
    const qRaw = String(queryRaw || '');
    const q = qRaw.trim();
    if (!q) {
      setShippingPkgOptions([]);
      setShippingPkgErr('');
      setShippingPkgHit(null);
      return;
    }
    // 避免太短导致频繁请求
    if (q.length < 2) {
      setShippingPkgOptions([]);
      setShippingPkgErr('');
      setShippingPkgHit(null);
      return;
    }
    const norm = q.toLowerCase();
    if (lastShipQueryRef.current === norm) return;
    lastShipQueryRef.current = norm;
    setShippingPkgLoading(true);
    setShippingPkgErr('');
    try {
      // 需求：下拉点击时可展示“仓库可用 + 客户专属”包材；搜索时也要覆盖两类
      const reqs: any[] = [];
      // 1) 仓库可用（按指令 warehouse 过滤更准确；缺失则不传 warehouse）
      reqs.push(getShippingPackagings({
        search: q,
        page_size: 50,
        ownership_type: 'warehouse',
        warehouse: (instrWarehouseId != null ? instrWarehouseId : undefined) as any
      } as any));
      // 2) 客户专属（按指令 customer 过滤）
      if (instrCustomerId != null) {
        reqs.push(getShippingPackagings({
          search: q,
          page_size: 50,
          ownership_type: 'customer',
          customer: instrCustomerId as any
        } as any));
      }
      const resps = await Promise.all(reqs);
      const merged: any[] = [];
      for (const r of resps) {
        const part = Array.isArray((r as any)?.data?.results)
          ? (r as any).data.results
          : (Array.isArray((r as any)?.data) ? (r as any).data : []);
        merged.push(...(part || []));
      }
      // 兜底：前端再做一次 name/system_code 模糊过滤（忽略大小写）
      const filtered = (merged || []).filter((x:any) => {
        const name = String(x?.name || '').toLowerCase();
        const code = String(x?.system_code || '').toLowerCase();
        return (name && name.includes(norm)) || (code && code.includes(norm));
      });
      const uniqMap = new Map<string, ShippingPackaging>();
      (filtered || []).forEach((it:any) => {
        const code = String(it?.system_code || '').trim();
        if (!code) return;
        if (!uniqMap.has(code)) uniqMap.set(code, it);
      });
      const finalList = Array.from(uniqMap.values()).slice(0, 50);
      setShippingPkgOptions(finalList);

      // 扫码场景：若输入“看起来像 system_code”，且命中精确 code，则自动回填（无需点击）
      const looksLikeCode = /^sp-[a-z0-9]{1,6}-\d+$/i.test(q);
      if (looksLikeCode) {
        const hit = finalList.find((it) => String(it?.system_code || '').trim().toUpperCase() === q.toUpperCase());
        if (hit) {
          fillFromShippingPkg(hit);
        }
      }
    } catch (e:any) {
      setShippingPkgOptions([]);
      setShippingPkgHit(null);
      setShippingPkgErr(e?.response?.data?.detail || e?.message || '搜索失败');
    } finally {
      setShippingPkgLoading(false);
    }
  }, [fillFromShippingPkg, instrCustomerId, instrWarehouseId]);

  const loadDefaultShippingPkgOptions = useCallback(async () => {
    try {
      if (!startContainerOpen) return;
      if (shippingPkgLoading) return;
      // 若已有列表则不重复加载（仍允许用户输入/扫码触发 searchShippingPkg）
      if ((shippingPkgOptions || []).length > 0) return;
      setShippingPkgLoading(true);
      setShippingPkgErr('');
      const reqs: any[] = [];
      reqs.push(getShippingPackagings({
        page_size: 80,
        ownership_type: 'warehouse',
        warehouse: (instrWarehouseId != null ? instrWarehouseId : undefined) as any
      } as any));
      if (instrCustomerId != null) {
        reqs.push(getShippingPackagings({
          page_size: 80,
          ownership_type: 'customer',
          customer: instrCustomerId as any
        } as any));
      }
      const resps = await Promise.all(reqs);
      const merged: any[] = [];
      for (const r of resps) {
        const part = Array.isArray((r as any)?.data?.results)
          ? (r as any).data.results
          : (Array.isArray((r as any)?.data) ? (r as any).data : []);
        merged.push(...(part || []));
      }
      const uniqMap = new Map<string, ShippingPackaging>();
      (merged || []).forEach((it:any) => {
        const code = String(it?.system_code || '').trim();
        if (!code) return;
        if (!uniqMap.has(code)) uniqMap.set(code, it);
      });
      setShippingPkgOptions(Array.from(uniqMap.values()).slice(0, 120));
    } catch (e:any) {
      setShippingPkgOptions([]);
      setShippingPkgHit(null);
      setShippingPkgErr(e?.response?.data?.detail || e?.message || '加载包材列表失败');
    } finally {
      setShippingPkgLoading(false);
    }
  }, [startContainerOpen, shippingPkgLoading, shippingPkgOptions, instrWarehouseId, instrCustomerId]);

  const shippingPkgGroupedOptions = useMemo(() => {
    try {
      const list = Array.isArray(shippingPkgOptions) ? shippingPkgOptions : [];
      const sys: ShippingPackaging[] = [];
      const cus: ShippingPackaging[] = [];
      for (const it of list) {
        const own = String((it as any)?.ownership_type || '').trim();
        if (own === 'customer') cus.push(it);
        else sys.push(it); // warehouse/未知都视为系统所属
      }
      const renderItem = (it: ShippingPackaging) => ({
        value: String((it as any)?.system_code || ''),
        label: (
          <div style={{ display:'flex', justifyContent:'space-between', gap: 10 }}>
            <span style={{ color:'#000' }}>{String((it as any)?.name || '-')}</span>
            <span style={{ color:'#595959', fontFamily:'monospace' }}>{String((it as any)?.system_code || '')}</span>
          </div>
        )
      });
      const out: any[] = [];
      if (sys.length) out.push({ label: '系统所属包材', options: sys.map(renderItem) });
      if (cus.length) out.push({ label: '客户专属包材', options: cus.map(renderItem) });
      // 若两者都为空，保持空数组
      return out;
    } catch {
      return [];
    }
  }, [shippingPkgOptions]);

  useEffect(() => {
    if (!startContainerOpen) return;
    if (shipQueryTimerRef.current) clearTimeout(shipQueryTimerRef.current);
    shipQueryTimerRef.current = setTimeout(() => {
      searchShippingPkg(shippingPkgQuery);
    }, 250);
    return () => { try { if (shipQueryTimerRef.current) clearTimeout(shipQueryTimerRef.current); } catch {} };
  }, [shippingPkgQuery, startContainerOpen, searchShippingPkg]);
  useEffect(() => {
    if (!startContainerOpen) return;
    // 弹窗打开时自动聚焦到包材扫描输入框
    try {
      setTimeout(() => {
        // 初次打开：写入默认值（纸箱/旧），后续扫码/选择包材会自动覆盖
        try {
          startForm.setFieldsValue({
            container_type: 'box',
            condition: 'used',
            length_cm: undefined,
            width_cm: undefined,
            height_cm: undefined,
            weight_g: undefined,
          });
        } catch {}
        try { shippingPkgSelectRef.current?.focus?.(); } catch {}
      }, 0);
    } catch {}
  }, [startContainerOpen, startForm]);
  // 基线（聚合：换标与装箱两个步骤的 baselines 合并展示，去重按 id）
  const baselines: Array<any> = useMemo(() => {
    const acc: any[] = [];
    try {
      const r = (relabelStep?.data?.baselines || []) as any[];
      if (Array.isArray(r)) acc.push(...r);
    } catch {}
    try {
      const p = (packStep?.data?.baselines || []) as any[];
      if (Array.isArray(p)) acc.push(...p);
    } catch {}
    const map = new Map<string, any>();
    acc.forEach((b:any) => {
      const key = String(b?.id ?? b?.code ?? Math.random());
      if (!map.has(key)) map.set(key, b);
    });
    return Array.from(map.values());
  }, [relabelStep, packStep]);

  // 生成/持久化 workstation_id（同一台电脑/浏览器稳定）
  const workstationId = useMemo(() => {
    try {
      const key = 'XT_WORKSTATION_ID';
      const got = window.localStorage.getItem(key);
      if (got && String(got).trim()) return String(got).trim();
      const gen = (() => {
        try {
          const anyCrypto: any = (window as any).crypto;
          if (anyCrypto?.randomUUID) return anyCrypto.randomUUID();
        } catch {}
        return `ws_${Date.now()}_${Math.random().toString(36).slice(2,10)}`;
      })();
      window.localStorage.setItem(key, gen);
      return gen;
    } catch {
      return `ws_${Date.now()}_${Math.random().toString(36).slice(2,10)}`;
    }
  }, []);

  const canOperateExec = !!canOperate && !leaseLoading && leaseOk && !leaseRecovering && !leaseBlocked;
  const canOperateScan = canOperateExec && mappingAuditOk;
  const isRelabelStepDone = useMemo(() => {
    const s = String(relabelStep?.status || '').trim().toLowerCase();
    return s === 'done' || s === 'skipped';
  }, [relabelStep?.status]);
  const isPackStepDone = useMemo(() => {
    const s = String(packStep?.status || '').trim().toLowerCase();
    return s === 'done' || s === 'skipped';
  }, [packStep?.status]);
  // 回看态（阶段已提交）只允许预览，不允许修改“最终装箱数据”弹窗中的值
  const boxFinalReadonly = (!canOperateExec) || (isRelabelStepDone && isPackStepDone);

  const leaseRetryRef = useRef<{ timer?: any; attempt: number; inFlight: boolean; lastHintTs?: number }>({ attempt: 0, inFlight: false });
  const leaseOkRef = useRef<boolean>(leaseOk);
  const leaseBlockedRef = useRef<boolean>(leaseBlocked);
  const leaseRecoveringRef = useRef<boolean>(leaseRecovering);
  useEffect(() => { leaseOkRef.current = leaseOk; }, [leaseOk]);
  useEffect(() => { leaseBlockedRef.current = leaseBlocked; }, [leaseBlocked]);
  useEffect(() => { leaseRecoveringRef.current = leaseRecovering; }, [leaseRecovering]);
  const stopLeaseRetry = useCallback(() => {
    try { if (leaseRetryRef.current.timer) clearTimeout(leaseRetryRef.current.timer); } catch {}
    leaseRetryRef.current.timer = undefined;
    leaseRetryRef.current.attempt = 0;
    leaseRetryRef.current.inFlight = false;
  }, []);
  const doAcquireLease = useCallback(async (opts?: { reason?: string; showHint?: boolean }) => {
    if (!canOperate) return;
    if (!instrIdState) return;
    if (leaseRetryRef.current.inFlight) return;
    leaseRetryRef.current.inFlight = true;
    try {
      const showHint = opts?.showHint !== false;
      try {
        const now = Date.now();
        if (showHint && (!leaseRetryRef.current.lastHintTs || now - leaseRetryRef.current.lastHintTs > 3000)) {
          leaseRetryRef.current.lastHintTs = now;
          message.loading({ content: '正在恢复执行权限…', duration: 1.2 });
        }
      } catch {}
      const resp = await request.post(`/api/v1/instruction/instructions/${instrIdState}/lease/acquire/`, { workstation_id: workstationId });
      const d = (resp as any)?.data || {};
      setLeaseId(String(d?.lease_id || ''));
      setLeaseInfo(d);
      setLeaseOk(true);
      setLeaseBlocked(false);
      setLeaseRecovering(false);
      stopLeaseRetry();
      try { if (opts?.reason && opts.reason !== 'init') message.success({ content: '执行权限已恢复', duration: 1.2 }); } catch {}
    } catch (e:any) {
      const st = e?.response?.status;
      if (st === 409) {
        const payload = e?.response?.data || {};
        setLeaseOk(false);
        setLeaseBlocked(true);
        setLeaseRecovering(false);
        setLeaseInfo(payload?.lease || null);
        stopLeaseRetry();
        message.warning(payload?.detail || '指令正在被其他工作站执行');
      } else {
        // 网络抖动/短暂失败：继续处于 recovering，由外层退避重试
        setLeaseOk(false);
        setLeaseBlocked(false);
        setLeaseRecovering(true);
      }
    } finally {
      leaseRetryRef.current.inFlight = false;
    }
  }, [canOperate, instrIdState, workstationId, stopLeaseRetry]);

  const startLeaseRecover = useCallback((reason?: string) => {
    if (!canOperate) return;
    if (!instrIdState) return;
    if (leaseBlockedRef.current) return;
    setLeaseOk(false);
    setLeaseRecovering(true);
    try { if (leaseRetryRef.current.timer) clearTimeout(leaseRetryRef.current.timer); } catch {}
    const scheduleNext = () => {
      const attempt = Math.max(0, Number(leaseRetryRef.current.attempt || 0));
      const delay = attempt === 0 ? 0 : Math.min(10000, 800 * Math.pow(2, attempt - 1));
      leaseRetryRef.current.timer = setTimeout(() => {
        doAcquireLease({ reason: reason || 'recover', showHint: true }).finally(() => {
          // 仍未恢复且未被占用：继续退避重试
          if (leaseBlockedRef.current) return;
          if (leaseOkRef.current) return;
          if (!leaseRecoveringRef.current) return;
          leaseRetryRef.current.attempt = attempt + 1;
          scheduleNext();
        });
      }, delay);
    };
    scheduleNext();
  }, [canOperate, instrIdState, doAcquireLease]);

  // acquire + periodic renew（自动恢复版）
  useEffect(() => {
    (async () => {
      try {
        if (!canOperate) return;
        if (!instrIdState) return;
        setLeaseLoading(true);
        setLeaseBlocked(false);
        setLeaseRecovering(true);
        await doAcquireLease({ reason: 'init', showHint: false });
      } catch (e:any) {
        // doAcquireLease 内部已处理；这里兜底启动恢复重试
        startLeaseRecover('init');
      } finally {
        setLeaseLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instrIdState, canOperate, workstationId, doAcquireLease, startLeaseRecover]);

  useEffect(() => {
    if (!canOperate) return;
    if (!instrIdState) return;
    if (!leaseOk || !leaseId || leaseRecovering || leaseBlocked) return;
    const t = setInterval(() => {
      request.post(`/api/v1/instruction/instructions/${instrIdState}/lease/renew/`, { workstation_id: workstationId, lease_id: leaseId })
        .then((resp:any) => { try { setLeaseInfo(resp?.data || null); } catch {} })
        .catch((e:any) => {
          const st = e?.response?.status;
          if (st === 409) {
            try { setLeaseOk(false); } catch {}
            try { setLeaseInfo(e?.response?.data?.lease || null); } catch {}
            // 自动恢复：立即尝试重新获取；若被占用则会转入 blocked
            startLeaseRecover('renew_409');
            message.warning('执行权限已失效，正在自动恢复…');
          } else {
            // 网络抖动等：进入恢复重试
            try { setLeaseOk(false); } catch {}
            startLeaseRecover('renew_err');
          }
        });
    }, 60000);
    return () => clearInterval(t);
  }, [canOperate, instrIdState, leaseOk, leaseId, leaseRecovering, leaseBlocked, workstationId, startLeaseRecover]);

  useEffect(() => {
    return () => {
      try {
        if (!instrIdState || !leaseId) return;
        // 关闭页面时不保证一定成功，TTL 会兜底；这里尽量释放
        request.post(`/api/v1/instruction/instructions/${instrIdState}/lease/release/`, { workstation_id: workstationId, lease_id: leaseId }).catch(() => {});
      } catch {}
    };
  }, [instrIdState, leaseId, workstationId]);
  const mappingBaseline = useMemo(() => (baselines.find(b => String(b?.code || '').toUpperCase() === 'BARCODE_MAPPING') || null), [baselines]);
  const newBarcodeBaseline = useMemo(() => (baselines.find(b => String(b?.code || '').toUpperCase() === 'NEW_BARCODE') || null), [baselines]);
  // BARCODE_MAPPING（新旧条码对应表）解析结果：A旧条码 B新条码 C新条码文本 D数量分配(上限) E备注
  const [mappingDefs, setMappingDefs] = useState<MappingDef[]>([]);
  const [mappingLoading, setMappingLoading] = useState<boolean>(false);
  const [mappingErr, setMappingErr] = useState<string>('');
  const [mappingSearch, setMappingSearch] = useState<string>(''); // 映射进度搜索
  // 总进度分页（受控，保证“每页条数”可用）
  const [mappingPage, setMappingPage] = useState<number>(1);
  const [mappingPageSize, setMappingPageSize] = useState<number>(10);
  const [boxStatPage, setBoxStatPage] = useState<number>(1);
  const [boxStatPageSize, setBoxStatPageSize] = useState<number>(10);
  const [mapDetailOpen, setMapDetailOpen] = useState<boolean>(false);
  const [mapDetailTitle, setMapDetailTitle] = useState<string>('');
  const [mapDetailRows, setMapDetailRows] = useState<any[]>([]);
  const [mapDetailHighlightKey, setMapDetailHighlightKey] = useState<string>('');
  const [mapDetailFilter, setMapDetailFilter] = useState<
    | { kind: 'none' }
    | { kind: 'mapping'; key: string; onlyRelabel?: boolean }
    | { kind: 'container'; container_code: string; onlyRelabel?: boolean }
  >({ kind: 'none' });
  const mapDetailLiveRows = useMemo(() => {
    try {
      if (mapDetailFilter.kind === 'mapping') {
        const k = String(mapDetailFilter.key || '').trim();
        if (!k) return [];
        return (records || []).filter((x:any) => {
          if (mapDetailFilter.onlyRelabel && String(x?.event || '') !== '换标') return false;
          return `${String(x?.old_code || '').toUpperCase()}->${String(x?.new_code || '').toUpperCase()}` === k;
        });
      }
      if (mapDetailFilter.kind === 'container') {
        const cc = String(mapDetailFilter.container_code || '').trim();
        if (!cc) return [];
        return (records || []).filter((x:any) => {
          if (mapDetailFilter.onlyRelabel && String(x?.event || '') !== '换标') return false;
          return String(x?.container_code || '').trim() === cc;
        });
      }
      return mapDetailRows || [];
    } catch { return mapDetailRows || []; }
  }, [mapDetailFilter, mapDetailRows, records]);
  const mapDetailStats = useMemo(() => {
    try {
      const isContainerMode = mapDetailFilter.kind === 'container';
      const isContentEv = (evAny: any): boolean => {
        const ev = String(evAny || '').trim();
        return ev === '换标'
          || ev === '调整-取出'
          || ev === '调整-放入'
          || ev === '调整-入池'
          || ev === '调整-出池'
          || ev === '重扫-冲销'
          || ev === '重扫-加回'
          || ev === '重扫-入池'
          || ev === '重扫-出池';
      };
      const set = new Set<string>();
      let totalQty = 0;
      (mapDetailLiveRows || []).forEach((x:any) => {
        const cc = String(x?.container_code || '').trim();
        if (cc) set.add(cc);
        const q = Math.floor(Number(x?.qty || 0) || 0);
        if (isContainerMode) {
          // 容器维度：统计“当前容器中的产品总数”=> 按内容事件净额（含负数扣减）
          if (isContentEv(x?.event)) totalQty += q;
        } else {
          // 映射维度：默认按“操作量（正数）”统计
          if (q > 0) totalQty += q;
        }
      });
      // 兜底：容器净额不应显示负数（异常数据/误操作时也避免困惑）
      if (isContainerMode && totalQty < 0) totalQty = 0;
      return { boxCount: set.size, totalQty };
    } catch { return { boxCount: 0, totalQty: 0 }; }
  }, [mapDetailLiveRows, mapDetailFilter.kind]);
  const mapDetailNewLabelStats = useMemo(() => {
    try {
      const set = new Set<string>();
      (mapDetailLiveRows || []).forEach((x:any) => {
        if (String(x?.event || '') !== '换标') return;
        const nc = String(x?.new_code || '').trim().toUpperCase();
        if (nc) set.add(nc);
      });
      return { newLabelCount: set.size };
    } catch { return { newLabelCount: 0 }; }
  }, [mapDetailLiveRows]);
  // “查看装箱”：按映射行汇总每个容器中的数量
  const [mapBoxOpen, setMapBoxOpen] = useState<boolean>(false);
  const [mapBoxKey, setMapBoxKey] = useState<string>('');
  const [mapBoxTitle, setMapBoxTitle] = useState<string>('');
  const [mapBoxRows, setMapBoxRows] = useState<any[]>([]);
  // “最终装箱数据”预览（只读）
  const [boxFinalOpen, setBoxFinalOpen] = useState<boolean>(false);
  const [boxFinalCode, setBoxFinalCode] = useState<string>('');
  const [boxFinalTitle, setBoxFinalTitle] = useState<string>('');
  const [boxFinalDraft, setBoxFinalDraft] = useState<{ length_cm?: number; width_cm?: number; height_cm?: number; weight_g?: number }>({});
  const [boxFinalLenIn, setBoxFinalLenIn] = useState<number | undefined>(undefined);
  const [boxFinalWidIn, setBoxFinalWidIn] = useState<number | undefined>(undefined);
  const [boxFinalHeiIn, setBoxFinalHeiIn] = useState<number | undefined>(undefined);
  const [boxFinalLb, setBoxFinalLb] = useState<number | undefined>(undefined);
  const [boxFinalDirty, setBoxFinalDirty] = useState<boolean>(false);
  const [boxFinalContentPage, setBoxFinalContentPage] = useState<number>(1);
  const [boxFinalContentPageSize, setBoxFinalContentPageSize] = useState<number>(10);
  // 操作控制台：允许在“已结束容器”上执行调整/重扫，因此单独维护一个当前操作容器选择
  const [consoleBoxCode, setConsoleBoxCode] = useState<string>('');

  // 容器内容调整/重扫：仅扫新条码（需要选择容器）
  const [adjustNewCode, setAdjustNewCode] = useState<string>('');
  const [adjustQty, setAdjustQty] = useState<number>(1);
  const [adjustMode, setAdjustMode] = useState<'take_only' | 'take_put' | 'put_only'>('take_only');
  const [adjustLoading, setAdjustLoading] = useState<boolean>(false);
  const [adjustToBoxCode, setAdjustToBoxCode] = useState<string>('');
  // 容器间移动：一次性移动取出容器内所有当前产品
  const [adjustMoveAll, setAdjustMoveAll] = useState<boolean>(false);
  const [adjustMoveAllPreviewOpen, setAdjustMoveAllPreviewOpen] = useState<boolean>(false);
  const [adjustMoveAllPreviewRows, setAdjustMoveAllPreviewRows] = useState<any[]>([]);
  const [adjustMoveAllPreviewTotal, setAdjustMoveAllPreviewTotal] = useState<number>(0);
  const [rescanNewCode, setRescanNewCode] = useState<string>('');
  const [rescanQty, setRescanQty] = useState<number>(1);
  const [rescanLoading, setRescanLoading] = useState<boolean>(false);
  // 重扫：简化为“全量取出到池 → 扫描后仅加回原容器”，并提供前后对比
  const [rescanFromBoxCode, setRescanFromBoxCode] = useState<string>('');
  const [rescanBaselineRows, setRescanBaselineRows] = useState<any[]>([]);
  const [rescanCompareOpen, setRescanCompareOpen] = useState<boolean>(false);
  const [rescanStarted, setRescanStarted] = useState<boolean>(false);
  // 重扫“误操作一键清理”所需快照：
  // - rescanBaselineLotsNetByKey：本次重扫开始时，从容器搬入池子的净额明细（iu+old+new -> qty）
  // - rescanPoolNetBaselineByKey：本次重扫开始前，池内已有的净额（用于计算本次重扫产生的剩余）
  const [rescanBaselineLotsNetByKey, setRescanBaselineLotsNetByKey] = useState<Record<string, { op_iu_id: number; old_code: string; new_code: string; qty: number }>>({});
  const [rescanPoolNetBaselineByKey, setRescanPoolNetBaselineByKey] = useState<Record<string, number>>({});
  // 调整池编码（用于调整/重扫/统计）；需在依赖它的 useMemo/useCallback 之前声明
  const poolCode = useMemo(() => {
    const n = Math.floor(Number(instrIdState || 0) || 0);
    return n > 0 ? `POOL-${n}` : 'POOL-0';
  }, [instrIdState]);
  const mapBoxStats = useMemo(() => {
    try {
      const set = new Set<string>();
      let totalQty = 0;
      (mapBoxRows || []).forEach((x:any) => {
        const cc = String(x?.container_code || '').trim();
        // “调整池(POOL-xxx)”不计入“涉及容器”口径
        if (cc && cc !== '-' && cc !== String(poolCode || '').trim()) set.add(cc);
        const q = Math.floor(Number(x?.qty || 0) || 0);
        // “操作总数量”应反映当前现状：容器 + 调整池 合计（若仅剩调整池，也要能看到数量）
        if (q > 0) totalQty += q;
      });
      return { boxCount: set.size, totalQty };
    } catch { return { boxCount: 0, totalQty: 0 }; }
  }, [mapBoxRows, poolCode]);

  const boxFinalData = useMemo(() => {
    try {
      const cc = String(boxFinalCode || '').trim();
      if (!cc) return null;
      const hit: any = (boxes || []).find((b:any) => String(b?.code || '').trim() === cc) || {};
      const st = String(hit?.status || '');
      const isClosed = st && st !== 'cancelled' && st !== 'open';
      const meta: any = (boxMeta as any)?.[cc] || {};
      const relabel = (records || []).filter((r:any) => String(r?.event || '') === '换标' && String(r?.container_code || '').trim() === cc);
      const startTs = (() => {
        let min = 0;
        (records || []).forEach((r:any) => {
          if (String(r?.event || '') !== '开始容器') return;
          if (String(r?.container_code || '').trim() !== cc) return;
          const ts = Number(r?.ts || 0) || 0;
          if (ts > 0 && (!min || ts < min)) min = ts;
        });
        return min;
      })();
      const endTs = (() => {
        let max = 0;
        (records || []).forEach((r:any) => {
          if (String(r?.event || '') !== '结束容器') return;
          if (String(r?.container_code || '').trim() !== cc) return;
          const ts = Number(r?.ts || 0) || 0;
          if (ts > max) max = ts;
        });
        return max;
      })();
      // 内容物：按 records 记账本净额（支持结束后调整/重扫/新增到已结束容器）
      const contentEvOk = (ev:any) => {
        const e = String(ev || '').trim();
        return e === '换标'
          || e === '调整-取出' || e === '调整-放入'
          || e === '重扫-冲销' || e === '重扫-加回';
      };
      const mNew = new Map<string, number>();
      (records || []).forEach((r:any) => {
        if (String(r?.container_code || '').trim() !== cc) return;
        if (!contentEvOk(r?.event)) return;
        const newc = String(r?.new_code || '').trim().toUpperCase();
        if (!newc || newc === '-') return;
        const q = Math.trunc(Number(r?.qty || 0) || 0);
        if (!Number.isFinite(q) || q === 0) return;
        mNew.set(newc, (mNew.get(newc) || 0) + q);
      });
      const rows = Array.from(mNew.entries())
        .map(([new_code, qty]) => ({ key: new_code, new_code, qty: Math.floor(Number(qty || 0) || 0) }))
        .filter((x) => Number.isFinite(x.qty) && x.qty > 0)
        .sort((a,b)=> (b.qty||0)-(a.qty||0));
      const totalQty = rows.reduce((s,x)=> s + Math.floor(Number(x.qty||0)||0), 0);
      const newKinds = rows.length;
      const durHours = (() => {
        if (!startTs || !endTs || endTs <= startTs) return undefined;
        const h = (endTs - startTs) / 3600000;
        if (!Number.isFinite(h) || h <= 0) return undefined;
        return Number(h.toFixed(1));
      })();
      return {
        container_code: cc,
        container_type: String(hit?.container_type || meta?.container_type || '').trim(),
        status: st,
        isClosed,
        startTs,
        endTs,
        durHours,
        newKinds,
        totalQty,
        rows,
        meta,
      };
    } catch { return null; }
  }, [boxFinalCode, boxes, boxMeta, records]);

  // 最终装箱数据弹窗：打开时初始化编辑草稿（以 boxMeta 中的最终值为准）
  useEffect(() => {
    try {
      if (!boxFinalOpen) return;
      try { setBoxFinalContentPage(1); } catch {}
      const cc = String(boxFinalCode || '').trim();
      if (!cc) return;
      const meta: any = (boxMeta as any)?.[cc] || {};
      const l = meta?.length_cm != null ? Math.floor(Number(meta.length_cm) || 0) : undefined;
      const w = meta?.width_cm != null ? Math.floor(Number(meta.width_cm) || 0) : undefined;
      const h = meta?.height_cm != null ? Math.floor(Number(meta.height_cm) || 0) : undefined;
      const wg = meta?.weight_g != null ? Math.floor(Number(meta.weight_g) || 0) : undefined;
      setBoxFinalDraft({ length_cm: l, width_cm: w, height_cm: h, weight_g: wg });
      const cmToInCeil = (cm?: number) => {
        const n = Number(cm);
        if (!Number.isFinite(n) || n <= 0) return undefined;
        return Math.ceil(n / 2.54);
      };
      const gToKgCeil = (g?: number) => {
        const n = Number(g);
        if (!Number.isFinite(n) || n <= 0) return undefined;
        return Math.ceil(n / 1000);
      };
      const kgToLbCeil = (kg?: number) => {
        const n = Number(kg);
        if (!Number.isFinite(n) || n <= 0) return undefined;
        return Math.ceil(n * 2.2046226218);
      };
      const lin = meta?.length_in != null ? Math.floor(Number(meta.length_in) || 0) : undefined;
      const win = meta?.width_in != null ? Math.floor(Number(meta.width_in) || 0) : undefined;
      const hin = meta?.height_in != null ? Math.floor(Number(meta.height_in) || 0) : undefined;
      const wlb = meta?.weight_lb != null ? Math.floor(Number(meta.weight_lb) || 0) : undefined;
      setBoxFinalLenIn(lin && lin > 0 ? lin : cmToInCeil(l));
      setBoxFinalWidIn(win && win > 0 ? win : cmToInCeil(w));
      setBoxFinalHeiIn(hin && hin > 0 ? hin : cmToInCeil(h));
      setBoxFinalLb(wlb && wlb > 0 ? wlb : kgToLbCeil(gToKgCeil(wg)));
      setBoxFinalDirty(false);
    } catch {}
  }, [boxFinalOpen, boxFinalCode, boxMeta]);
  const [blVisible, setBlVisible] = useState(false);
  const [blLoading, setBlLoading] = useState(false);
  const [blFiles, setBlFiles] = useState<any[]>([]);
  const [blSearch, setBlSearch] = useState<string>(''); // 基线文件名搜索（模糊匹配，忽略大小写）
  const blFiltered = useMemo(() => {
    try {
      const q = String(blSearch || '').trim().toLowerCase();
      if (!q) return blFiles || [];
      return (blFiles || []).filter((row:any) => {
        const raw = String(row?.file_name || row?.file || row?.file_path || '').toLowerCase();
        return raw.includes(q);
      });
    } catch { return blFiles || []; }
  }, [blFiles, blSearch]);
  // 基线文件分页（卡片列表）
  const [blPage, setBlPage] = useState<number>(1);
  const [blPageSize, setBlPageSize] = useState<number>(10);
  useEffect(() => { setBlPage(1); }, [blFiles?.length]);
  // 基线文件选择（跨页）
  const [blSelectedIds, setBlSelectedIds] = useState<Set<number>>(new Set());
  const selectAllAcrossPagesRef = useRef<boolean>(false);
  const ignoreSelectionChangeOnceRef = useRef<boolean>(false);
  const selCount = blSelectedIds.size;
  // 基线 id -> code / code -> 显示名称 映射
  const baselineIdToCode = useMemo(() => {
    const m = new Map<number, string>();
    try {
      (baselines || []).forEach((b:any) => {
        const id = Number(b?.id);
        const code = String(b?.code || '').trim();
        if (Number.isFinite(id) && code) m.set(id, code);
      });
    } catch {}
    return m;
  }, [baselines]);

  // 批量删除选中文件
  const handleDeleteSelectedBaselineFiles = async () => {
    try {
      const selIds = Array.from(blSelectedIds);
      if (selIds.length === 0) { message.info('请先选择要删除的基线文件'); return; }
      let ok = 0, fail = 0;
      for (const id of selIds) {
        try {
          await request.delete(`/api/v1/instruction/baseline-files/${id}/`);
          ok += 1;
        } catch { fail += 1; }
      }
      message.success(`批量删除完成：成功 ${ok}${fail ? `，失败 ${fail}` : ''}`);
      setBlSelectedIds(new Set());
      await loadBaselineFiles();
    } catch {
      message.error('批量删除失败');
    }
  };
  // 指令问卷与答案（用于“开始新容器”弹窗提示）
  // 模板基线类型选项（来自 template_snapshot.baseline_requirements.items）
  const [baselineTypeOptions, setBaselineTypeOptions] = useState<Array<{ code: string; name?: string; acceptExts?: string[]; bind?: any; schema_hint?: any; countMin?: number; countMax?: number }>>([]);
  const [uploadTargetCode, setUploadTargetCode] = useState<string | undefined>(undefined);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [fileAccept, setFileAccept] = useState<string>(''); // input accept string like ".csv,.xlsx"
  const baselineCodeToName = useMemo(() => {
    const m = new Map<string, string>();
    try {
      (baselineTypeOptions || []).forEach((o:any) => {
        const code = String(o?.code || '').trim();
        const name = String(o?.name || o?.code || '').trim();
        if (code) m.set(code.toUpperCase(), name);
      });
    } catch {}
    return m;
  }, [baselineTypeOptions]);
  const getBaselineTypeText = (row: any): string => {
    try {
      const bid = Number(row?.baseline);
      const code = baselineIdToCode.get(bid) || String(row?.baseline_code || '').trim();
      if (!code) return '-';
      const label = baselineCodeToName.get(code.toUpperCase());
      return label || code;
    } catch { return '-'; }
  };
  useEffect(() => {
    (async () => {
      try {
        if (!taskId) return;
        const resp = await request.get(`/api/v1/instruction/exec/tasks/${taskId}/`);
        const d = resp?.data || {};
        let code = d?.instruction?.system_code || d?.instruction_detail?.system_code || d?.instruction?.code || d?.instruction_detail?.code;
        let instrId = d?.instruction?.id || d?.instruction_id || d?.instruction_detail?.id;
        if (instrId == null) {
          const raw = d?.instruction;
          if (typeof raw === 'number') {
            instrId = raw;
          } else if (typeof raw === 'string') {
            if (/^\d+$/.test(raw)) {
              instrId = Number(raw);
            } else {
              const m = raw.match(/(\d+)(?:\/)?$/);
              if (m && m[1]) instrId = Number(m[1]);
            }
          }
        }
        if (instrId != null && Number.isFinite(Number(instrId))) {
          setInstrIdState(Number(instrId));
          try {
            const ins = await request.get(`/api/v1/instruction/instructions/${instrId}/`);
            const data = ins?.data || {};
              try {
                const cid = Number(data?.customer_id);
                if (Number.isFinite(cid) && cid > 0) setInstrCustomerId(cid);
              } catch {}
              try {
                const wid = Number(data?.warehouse_id);
                if (Number.isFinite(wid) && wid > 0) setInstrWarehouseId(wid);
              } catch {}
            // 问卷与答案（优先 snapshot，其次 template_snapshot.questionnaire / template_detail.questionnaire）
            try {
              const qnSnap = (data?.questionnaire_snapshot || data?.template_snapshot?.questionnaire || data?.template_detail?.questionnaire || {}) as any;
              const qs = Array.isArray(qnSnap?.questions) ? qnSnap.questions : [];
              setInstrQuestionRows(qs);
            } catch { setInstrQuestionRows([]); }
            try {
              const ans = (data?.customer_answers || {}) as any;
              setInstrAnswers((ans && typeof ans === 'object') ? ans : {});
            } catch { setInstrAnswers({}); }
            // 指令涉及项目系统编码（从 targets_detail 解析；多项目显示为：<第一个> 等N个）
            try {
              const tds: any[] = Array.isArray((data as any)?.targets_detail) ? (data as any).targets_detail : [];
              const codes: string[] = [];
              for (const r of tds) {
                const cand = [
                  r?.project_system_code,
                  r?.project_code,
                  r?.project?.system_code,
                  r?.project?.code,
                  r?.project?.project_code,
                  r?.target?.project_system_code,
                  r?.target?.project_code,
                  r?.snapshot?.project_system_code,
                  r?.snapshot?.project_code,
                ];
                const hit = cand.map((x:any)=> String(x || '').trim()).find((s:string)=> !!s);
                if (hit) codes.push(hit);
              }
              const uniq = Array.from(new Set(codes.map(s => s.trim()).filter(Boolean)));
              if (!uniq.length) {
                setInstrProjectSummary('-');
              } else if (uniq.length === 1) {
                setInstrProjectSummary(uniq[0]);
              } else {
                setInstrProjectSummary(`${uniq[0]} 等${uniq.length}个`);
              }
            } catch { setInstrProjectSummary('-'); }
            // 预计操作总数量（与指令详情页一致：targets_detail.expected_qty 汇总；NaN 按 0 处理）
            try {
              const list: any[] = Array.isArray((data as any)?.targets_detail) ? (data as any).targets_detail : [];
              const totalQty = (list || []).reduce((acc:number, t:any) => {
                const v = Number(t?.expected_qty ?? 0);
                return acc + (Number.isFinite(v) ? v : 0);
              }, 0);
              setInstrExpectedTotalQty(Number.isFinite(totalQty) ? totalQty : null);
            } catch { setInstrExpectedTotalQty(null); }
            const ts = data?.template_snapshot || {};
            const td = data?.template_detail || {};
            const br = (ts?.baseline_requirements || td?.baseline_requirements || {}) as any;
            const items = Array.isArray(br?.items) ? br.items : [];
            const resolveCountRangeFromItem = (it: any): { min?: number; max?: number } => {
              const tryNum = (v: any): number | undefined => {
                const n = Number(v);
                return Number.isFinite(n) && n >= 0 ? n : undefined;
              };
              const pickPath = (root: any, path: Array<string>): any => {
                try {
                  let cur = root;
                  for (const k of path) {
                    if (cur == null) return undefined;
                    cur = cur[k];
                  }
                  return cur;
                } catch { return undefined; }
              };
              const candidateMinPaths = [
                ['schema_hint','accept_count','min'],
                ['schema_hint','count','min'],
                ['schema_hint','limits','count','min'],
                ['accept_count','min'],
                ['count','min'],
                ['limits','count','min'],
                ['min_count'],
                ['min_files'],
                ['min'],
              ];
              const candidateMaxPaths = [
                ['schema_hint','accept_count','max'],
                ['schema_hint','count','max'],
                ['schema_hint','limits','count','max'],
                ['accept_count','max'],
                ['count','max'],
                ['limits','count','max'],
                ['max_count'],
                ['max_files'],
                ['max'],
              ];
              let outMin: number | undefined = undefined;
              for (const p of candidateMinPaths) {
                const v = pickPath(it, p);
                const n = tryNum(v);
                if (n !== undefined) { outMin = n; break; }
              }
              let outMax: number | undefined = undefined;
              for (const p of candidateMaxPaths) {
                const v = pickPath(it, p);
                const n = tryNum(v);
                if (n !== undefined) { outMax = n; break; }
              }
              if (outMin !== undefined && outMax !== undefined && outMin > outMax) {
                outMin = undefined;
              }
              return { min: outMin, max: outMax };
            };
            const opts = items.map((it:any) => {
              const range = resolveCountRangeFromItem(it);
              const fmts = Array.isArray(it?.formats) ? it.formats : [];
              return {
                code: String(it?.code || '').trim(),
                name: it?.name,
                acceptExts: (
                  Array.isArray(it?.schema_hint?.accept_formats)
                    ? it.schema_hint.accept_formats
                    : fmts
                ).map((x:any)=> String(x).toLowerCase()),
                bind: it?.bind,
                schema_hint: it?.schema_hint,
                countMin: range.min,
                countMax: range.max,
              };
            }).filter((o:any)=> !!o.code);
            setBaselineTypeOptions(opts);
            try { console.log('[Baseline][TEMPLATE] parsed types', (opts || []).map(o => ({ code: o.code, min: o.countMin, max: o.countMax, accept: o.acceptExts }))); } catch {}
          } catch {}
        }
        if (!code && (instrId != null)) {
          try {
            const ins = await request.get(`/api/v1/instruction/instructions/${instrId}/`);
            code = ins?.data?.system_code || ins?.data?.code || code;
          } catch {}
        }
        if (code) {
          setInstrCode(String(code));
          try { console.debug('[Baseline][ZIP] instruction system_code resolved', { taskId, instrId, code }); } catch {}
        } else {
          try { console.debug('[Baseline][ZIP] instruction system_code missing', { taskId, instrId }); } catch {}
        }
      } catch {}
    })();
  }, [taskId]);

  // 从后端 Draft 恢复“已结束容器”的最终物理数据（boxMeta 回填）
  // 说明：这是 boxMeta 的兜底来源。执行期仍可用 step draft/本地状态；但一旦写入 Draft，就以 Draft 为准。
  useEffect(() => {
    (async () => {
      try {
        const instrId = Number(instrIdState);
        if (!Number.isFinite(instrId) || instrId <= 0) return;
        const resp = await request.get('/api/v1/storage/output-containers/drafts/', { params: { instruction_id: instrId } as any });
        const drafts: any[] = Array.isArray(resp?.data?.drafts) ? resp.data.drafts : [];
        if (!drafts.length) return;

        // 合并到 boxMeta（只覆盖 draft 有值的字段）
        setBoxMeta((prev) => {
          const next: any = { ...(prev || {}) };
          for (const d of drafts) {
            const cc = String(d?.code || '').trim();
            if (!cc) continue;
            const cur = next[cc] || {};
            const merged: any = { ...cur };
            for (const k of ['length_cm','width_cm','height_cm','weight_g','length_in','width_in','height_in','weight_lb'] as any) {
              const v = (d as any)?.[k];
              if (v !== undefined && v !== null && v !== '') merged[k] = v;
            }
            try {
              const wg = Number(merged?.weight_g);
              merged.weight_kg = (Number.isFinite(wg) && wg > 0) ? Math.ceil(wg / 1000) : merged.weight_kg;
            } catch {}
            next[cc] = merged;
          }
          return next;
        });

        // 同步 boxes 状态：若 Draft 标记为已结束/最终锁定，则将本地状态推进到 closed
        // 保护规则：已作废(cancelled)优先级高于closed，绝不回写为closed。
        try {
          const closedSet = new Set<string>();
          const cancelledSet = new Set<string>();
          for (const d of drafts) {
            const st = String(d?.status || '').trim();
            if (st === 'closed_pending_final' || st === 'final_locked') {
              const cc = String(d?.code || '').trim();
              if (cc) closedSet.add(cc);
            } else if (st === 'cancelled') {
              const cc = String(d?.code || '').trim();
              if (cc) cancelledSet.add(cc);
            }
          }
          if (closedSet.size || cancelledSet.size) {
            setBoxes((prev) => {
              const list = Array.isArray(prev) ? prev : [];
              const exists = new Set(list.map((b:any)=> String(b?.code || '').trim()));
              const appended = [...list];
              for (const cc of Array.from(closedSet)) {
                if (cancelledSet.has(cc)) continue;
                if (!exists.has(cc)) appended.push({ code: cc, status: 'closed' } as any);
              }
              for (const cc of Array.from(cancelledSet)) {
                if (!exists.has(cc)) appended.push({ code: cc, status: 'cancelled' } as any);
              }
              return appended.map((b:any) => {
                const cc = String(b?.code || '').trim();
                if (!cc) return b;
                const cur = String(b?.status || '').trim().toLowerCase();
                // 已作废优先，保持不变
                if (cur === 'cancelled' || cancelledSet.has(cc)) {
                  return cur === 'cancelled' ? b : ({ ...b, status: 'cancelled' });
                }
                if (!closedSet.has(cc)) return b;
                return cur === 'closed' ? b : ({ ...b, status: 'closed' });
              });
            });
          }
        } catch {}
      } catch {}
    })();
  }, [instrIdState]);
  // 动态加载 JSZip
  const lazyLoadJSZip = async (): Promise<any> => {
    try {
      // @ts-ignore
      const mod = await import('jszip');
      return (mod as any).default || mod;
    } catch {
      if ((window as any).JSZip) return (window as any).JSZip;
      await new Promise<void>((resolve, reject) => {
        const s = document.createElement('script');
        s.src = 'https://cdn.jsdelivr.net/npm/jszip@3.10.1/dist/jszip.min.js';
        s.onload = () => resolve();
        s.onerror = () => reject(new Error('JSZip加载失败'));
        document.body.appendChild(s);
      });
      return (window as any).JSZip;
    }
  };
  // 批量下载：将 blFiles 全部打包下载
  const handleDownloadAllBaselineFiles = async () => {
    try {
      const all = Array.isArray(blFiles) ? blFiles : [];
      const selIds = new Set(Array.from(blSelectedIds));
      const list = (selIds.size > 0
        ? all.filter((x:any) => selIds.has(Number(x?.id)))
        : []);
      try { console.debug('[Baseline][ZIP][Download] selectedCount=', selIds.size, 'finalList=', list.length); } catch {}
      if (!list || list.length === 0) { message.warning('请先选择要下载的基线文件'); return; }
      const JSZip = await lazyLoadJSZip();
      const zip = new JSZip();
      const addedNames = new Set<string>();
      for (let i = 0; i < list.length; i++) {
        const row = list[i];
        const url = resolveFileUrl(row);
        if (!url) continue;
        try {
          const resp = await fetch(url, { credentials: 'include' as any });
          if (!resp.ok) continue;
          const blob = await resp.blob();
          let name = String(row?.file_name || row?.file || row?.file_path || `基线文件_${i+1}`);
          try {
            const base = (name.split('?')[0] || name).split('#')[0];
            name = decodeURIComponent(base.split('/').pop() || base);
          } catch {}
          if (addedNames.has(name)) {
            const ext = name.includes('.') ? '.' + name.split('.').pop() : '';
            const base = ext ? name.slice(0, -ext.length) : name;
            let idx = 2; let candidate = `${base}(${idx})${ext}`;
            while (addedNames.has(candidate)) { idx++; candidate = `${base}(${idx})${ext}`; }
            name = candidate;
          }
          addedNames.add(name);
          zip.file(name, blob);
        } catch {}
      }
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const href = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = href;
      const code = String(instrCode || 'instruction');
      a.download = `${code}-baseline-files.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(href), 1500);
    } catch (e) {
      message.error('批量下载失败');
    }
  };
  // 确保某 code 的基线头存在（没有则创建），返回 baselineId
  const ensureBaselineHead = async (code: string): Promise<number> => {
    if (!instrIdState) throw new Error('缺少指令ID');
    // 先查已存在的（不限 state）
    try {
      const resp = await request.get('/api/v1/instruction/baselines/', { params: { instruction: instrIdState, code, page_size: 20 } });
      const arr = Array.isArray(resp?.data?.results) ? resp.data.results : (Array.isArray(resp?.data) ? resp.data : []);
      const hit = (arr || []).find((b:any)=> String(b?.code || '').trim().toUpperCase() === String(code).toUpperCase());
      if (hit && Number.isFinite(Number(hit.id))) return Number(hit.id);
    } catch {}
    // 创建
    const meta = baselineTypeOptions.find(o => String(o.code).toUpperCase() === String(code).toUpperCase()) || { code };
    const payload:any = {
      instruction: instrIdState,
      code: meta.code,
      name: meta.name || meta.code,
      bind: meta.bind,
      schema_hint: meta.schema_hint,
      state: 'draft',
    };
    const created = await request.post('/api/v1/instruction/baselines/', payload);
    const bid = Number(created?.data?.id);
    if (!Number.isFinite(bid)) throw new Error('创建基线失败');
    return bid;
  };
  // 上传文件到 baseline（覆盖同名：先删再传），完毕后设为 ready
  const uploadFilesToBaseline = async (baselineId: number, files: File[]) => {
    // 先拉现有文件，构建 name -> ids
    let existing:any[] = [];
    try {
      const resp = await request.get('/api/v1/instruction/baseline-files/', { params: { baseline: baselineId, page_size: 1000 } });
      existing = Array.isArray(resp?.data?.results) ? resp.data.results : (Array.isArray(resp?.data) ? resp.data : []);
    } catch {}
    const nameToIds = new Map<string, number[]>();
    (existing || []).forEach((f:any) => {
      const raw = String(f?.file_name || f?.file || '');
      const base = (raw.split('?')[0] || raw).split('#')[0];
      const last = base.split('/').pop() || base;
      let key = last;
      try { key = decodeURIComponent(last); } catch {}
      key = String(key).toLowerCase().trim();
      const fid = Number(f?.id);
      if (!nameToIds.has(key)) nameToIds.set(key, []);
      if (Number.isFinite(fid)) nameToIds.get(key)!.push(fid);
    });
    try { console.debug('[Baseline][UPLOAD] existingIndex', { baselineId, totalKeys: nameToIds.size, sample: Array.from(nameToIds.keys()).slice(0, 10) }); } catch {}
    // 删除同名
    for (const file of files) {
      const base = (file.name || '').split('/').pop() || file.name;
      let key = base;
      try { key = decodeURIComponent(base); } catch {}
      key = String(key).toLowerCase().trim();
      const ids = nameToIds.get(key) || [];
        try { console.debug('[Baseline][UPLOAD] deleteSameName', { name: file.name, key, matchCount: ids.length }); } catch {}
      for (const id of ids) {
        try { await request.delete(`/api/v1/instruction/baseline-files/${id}/`); } catch {}
      }
    }
    // 上传
    let ok = 0, fail = 0;
    try { console.debug('[Baseline][UPLOAD] uploading', files.map(f => f.name)); } catch {}
    for (const file of files) {
      const fd = new FormData();
      fd.append('baseline', String(baselineId));
      fd.append('file', file, file.name);
      try {
        await request.post('/api/v1/instruction/baseline-files/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        ok += 1;
      } catch { fail += 1; }
    }
    // 设为 ready
    try { await request.patch(`/api/v1/instruction/baselines/${baselineId}/`, { state: 'ready' }); } catch {}
    try { console.debug('[Baseline][UPLOAD] done', { baselineId, ok, fail }); } catch {}
    return { ok, fail };
  };

  // 执行页：覆盖写入 BARCODE_MAPPING 的“操作对象匹配信息”列（并上传为基线文件，确保后续进入执行页无需重复审核）
  const persistBmMatchInfoToBaseline = async (opts?: { overrideMulti?: Record<string, number[]>; overrideSingle?: Record<string, number> }) => {
    const instrId = Number(instrIdState);
    if (!Number.isFinite(instrId)) throw new Error('缺少指令ID');
    const baselineId = Number(mappingBaseline?.id);
    if (!Number.isFinite(baselineId) || baselineId <= 0) throw new Error('未找到 BARCODE_MAPPING 基线');
    const fileRow = mappingFileRowCache;
    if (!fileRow) throw new Error('未找到当前 BARCODE_MAPPING 文件记录');
    const rows = Array.isArray(mappingAoaCache) ? mappingAoaCache : [];
    if (!rows.length) throw new Error('BARCODE_MAPPING 表格为空');
    const overrideMulti = (opts && opts.overrideMulti) ? opts.overrideMulti : (mappingKeyIuOverrideMulti as any);
    const overrideSingle = (opts && opts.overrideSingle) ? opts.overrideSingle : (mappingKeyIuOverride as any);

    const isHeaderRow = (row:any[]) => {
      try {
        const s = (row || []).map(x => String(x ?? '').trim()).join('|');
        return /旧条码|新条码|数量|分配|备注/i.test(s);
      } catch { return false; }
    };
    const normalizeCode = (v:any) => String(v ?? '').trim().toUpperCase();

    // 1) 准备 header + 插入/定位“操作对象匹配信息”列
    let aoa: any[][] = rows.map(r => Array.isArray(r) ? [...r] : []);
    let startIdx = 0;
    if (aoa.length > 0 && isHeaderRow(aoa[0])) startIdx = 1;
    if (startIdx === 0) {
      // 无表头：补一个最小表头（不追求花哨，优先可用）
      aoa = [['旧条码','新条码','新条码文本','数量分配','操作对象匹配信息','备注'], ...aoa];
      startIdx = 1;
    }
    const header = aoa[0] || [];
    const matchTitle = '操作对象匹配信息';
    let matchColIdx = header.findIndex((h:any) => String(h ?? '').trim() === matchTitle);
    if (matchColIdx < 0) {
      // 插入在 D(数量分配) 后
      matchColIdx = 4;
      header.splice(matchColIdx, 0, matchTitle);
      aoa[0] = header;
      for (let i = startIdx; i < aoa.length; i++) {
        const row = aoa[i] || [];
        row.splice(matchColIdx, 0, '-');
        aoa[i] = row;
      }
    }

    // 2) 构造 JSON（复用审核页字段：iu_id/system_barcode/use_all_available/expected_qty）
    const idToMeta = new Map<number, { iu_id: number; system_barcode?: string; use_all_available: boolean; expected_qty: number | null }>();
    (targetIuOptions || []).forEach((x:any) => {
      const id = Math.floor(Number(x?.id || 0));
      if (!id) return;
      const sys = String(x?.system_barcode || '').trim();
      idToMeta.set(id, { iu_id: id, system_barcode: sys || undefined, use_all_available: !!x?.use_all_available, expected_qty: (x?.expected_qty == null ? null : Number(x.expected_qty)) });
    });
    const toJson = (ids: number[]) => {
      const metas = ids
        .map((id:number) => idToMeta.get(Math.floor(Number(id) || 0)) || null)
        .filter(Boolean) as any[];
      metas.sort((a:any, b:any) => {
        const ua = a.use_all_available ? 1 : 0;
        const ub = b.use_all_available ? 1 : 0;
        if (ua !== ub) return ua - ub; // 否在前
        const ea = (a.expected_qty == null || !Number.isFinite(Number(a.expected_qty))) ? Number.MAX_SAFE_INTEGER : Number(a.expected_qty);
        const eb = (b.expected_qty == null || !Number.isFinite(Number(b.expected_qty))) ? Number.MAX_SAFE_INTEGER : Number(b.expected_qty);
        if (ea !== eb) return ea - eb;
        return String(a.system_barcode || '').localeCompare(String(b.system_barcode || ''));
      });
      try { return JSON.stringify(metas); } catch { return '[]'; }
    };

    // 3) 写入每行（按 key=old->new 取 selection）
    for (let i = startIdx; i < aoa.length; i++) {
      const r = aoa[i] || [];
      const old = normalizeCode(r?.[0]) || '-';
      const newer = normalizeCode(r?.[1]);
      if (!newer) continue;
      const key = `${old}->${newer}`;
      const rawIds: any[] = Array.isArray((overrideMulti as any)?.[key])
        ? (((overrideMulti as any)[key]) as any[])
        : (Number((overrideSingle as any)?.[key] || 0) > 0 ? [Number((overrideSingle as any)[key])] : []);
      const clean = Array.from(new Set<number>((rawIds || []).map((x:any)=> Math.floor(Number(x)||0)).filter((n:number)=> Number.isFinite(n) && n > 0)));
      // 仅在“有选择结果”时写入；未指定的不覆盖原单元格（避免把原有匹配信息冲掉）
      if (clean.length) {
        r[matchColIdx] = toJson(clean as number[]);
      }
      aoa[i] = r;
    }

    // 3.1) 关键修复：裁剪尾部“纯空白列”，避免写回时把 Excel 的 used range（!ref）扩大到 Z 等无意义列
    // 现象：若原始文件曾被扩展过有效区域（例如到 Z 列），sheet_to_json(defval:'') 会把空单元格读成 ''，
    // 导致 aoa 每行都变成“很宽的矩形”；aoa_to_sheet 会将 !ref 固化到最右列，从而预览出现 C7..C26 等空列。
    const isBlankCell = (v:any) => {
      if (v === null || v === undefined) return true;
      if (typeof v === 'string') return String(v).trim() === '';
      return false;
    };
    const trimRowRight = (row:any[], minLen:number) => {
      try {
        const arr = Array.isArray(row) ? row : [];
        let last = -1;
        for (let j = arr.length - 1; j >= 0; j--) {
          if (!isBlankCell(arr[j])) { last = j; break; }
        }
        const needLast = Math.max(minLen - 1, last);
        const out = arr.slice(0, needLast + 1);
        // 兜底：保证至少有 minLen 列，避免缺列导致列头对不上
        while (out.length < minLen) out.push('');
        return out;
      } catch {
        const out:any[] = [];
        while (out.length < minLen) out.push('');
        return out;
      }
    };
    try {
      const hdr = aoa[0] || [];
      const remarkIdx = (hdr as any[]).findIndex((h:any) => String(h ?? '').trim() === '备注');
      const minLen = Math.max(
        (Number.isFinite(matchColIdx) ? (matchColIdx + 1) : 1),
        (remarkIdx >= 0 ? (remarkIdx + 1) : 1),
      );
      // 先裁剪表头（会直接决定 sheet 的末列）
      aoa[0] = trimRowRight(hdr, minLen);
      const finalLen = (aoa[0] || []).length;
      // 再裁剪数据行（保留真实末列；去掉纯空白延伸列）
      for (let i = startIdx; i < aoa.length; i++) {
        aoa[i] = trimRowRight(aoa[i] || [], finalLen);
      }
    } catch {}

    // 4) 上传覆盖：先删除该 baseline 下所有文件，再上传一份（避免执行页选错文件）
    let existing:any[] = [];
    try {
      const resp = await request.get('/api/v1/instruction/baseline-files/', { params: { baseline: baselineId, page_size: 1000 } });
      existing = Array.isArray(resp?.data?.results) ? resp.data.results : (Array.isArray(resp?.data) ? resp.data : []);
    } catch {}
    for (const f of (existing || [])) {
      const id = Number(f?.id);
      if (!Number.isFinite(id) || id <= 0) continue;
      try { await request.delete(`/api/v1/instruction/baseline-files/${id}/`); } catch {}
    }

    const rawName = String(fileRow?.file_name || fileRow?.file || fileRow?.file_path || 'BARCODE_MAPPING.xlsx');
    const base = (rawName.split('?')[0] || rawName).split('#')[0];
    const name = (() => {
      try { return decodeURIComponent(base.split('/').pop() || base); } catch { return (base.split('/').pop() || base); }
    })();
    const isCsv = !!mappingIsCsvCache || String(name).toLowerCase().endsWith('.csv');

    let outBlob: Blob;
    let outName = name;
    if (isCsv) {
      let Papa: any = null;
      try { Papa = (await import('papaparse')).default || (await import('papaparse')); } catch {}
      if (!Papa) throw new Error('缺少 CSV 解析库（papaparse）');
      const text = Papa.unparse(aoa);
      outBlob = new Blob([text], { type: 'text/csv;charset=utf-8' });
      if (!String(outName).toLowerCase().endsWith('.csv')) outName = 'BARCODE_MAPPING.csv';
    } else {
      let XLSX: any = null;
      try { XLSX = await import('xlsx'); } catch {}
      if (!XLSX) throw new Error('缺少 Excel 解析库（xlsx）');
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
      const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
      outBlob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      if (!String(outName).toLowerCase().endsWith('.xlsx')) outName = 'BARCODE_MAPPING.xlsx';
    }

    const file = new File([outBlob], outName, { type: outBlob.type });
    await uploadFilesToBaseline(baselineId, [file]);
    await loadBaselineFiles();
  };
  // 选择 code 后触发文件选择
  const onPickUploadCode = (val: string) => {
    setUploadTargetCode(val);
    // 计算 accept：仅使用模板 schema_hint.accept_formats（不做任何兜底猜测）
    const meta = baselineTypeOptions.find(o => String(o.code).toUpperCase() === String(val).toUpperCase());
    const exts = (meta?.acceptExts || []).map(x => x.startsWith('.') ? x.slice(1) : x);
    const acceptStr = exts && exts.length > 0 ? exts.map(e => `.${e}`).join(',') : '';
    setFileAccept(acceptStr);
    setTimeout(() => { try { fileInputRef.current?.click(); } catch {} }, 0);
  };
  // 处理文件选择
  const onFilesChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const code = String(uploadTargetCode || '').trim();
      if (!code) { message.warning('请选择上传目标类型'); return; }
      const files = Array.from(e.target.files || []).filter(Boolean) as File[];
      e.target.value = '';
      if (files.length === 0) return;
      const meta = baselineTypeOptions.find(o => String(o.code).toUpperCase() === code.toUpperCase());
      const acceptExts = (meta?.acceptExts || []).map(x => String(x).toLowerCase());
      const codeUpper = code.toUpperCase();
      try { console.log('[Baseline][LIMIT] meta', { code, codeUpper, acceptExts, countMin: meta?.countMin, countMax: meta?.countMax, schema_hint: meta?.schema_hint }); } catch {}
      const filtered: File[] = [];
      const rejected: { name: string; ext: string }[] = [];
      files.forEach(f => {
        const lower = String(f.name || '').toLowerCase();
        const ext = lower.includes('.') ? lower.split('.').pop() || '' : '';
        if (acceptExts.length === 0 || acceptExts.includes(ext)) {
          filtered.push(f);
        } else {
          rejected.push({ name: f.name, ext });
        }
      });
      if (filtered.length === 0) {
        message.warning('所选文件均不符合该类型的格式要求');
        // 关键：清空“上传到…”选择，允许用户重复选择同一类型再次尝试
        setUploadTargetCode(undefined);
        setFileAccept('');
        return;
      }
      if (rejected.length > 0) {
        try { console.log('[Baseline][Upload][SkipByExt]', { code, accepted: filtered.length, rejected }); } catch {}
        message.info(`已跳过 ${rejected.length} 个不匹配的文件`);
      }
      const bid = await ensureBaselineHead(code);
      try { console.log('[Baseline][LIMIT] ensureBaselineHead', { code, bid }); } catch {}
      // 数量限制检查
      let existing:any[] = [];
      try {
        const resp = await request.get('/api/v1/instruction/baseline-files/', { params: { baseline: bid, page_size: 1000 } });
        existing = Array.isArray(resp?.data?.results) ? resp.data.results : (Array.isArray(resp?.data) ? resp.data : []);
      } catch {}
      const existingCount = (existing || []).length;
      // 有效上限：仅来源于模板定义（不做任何兜底）
      const maxAllowed = Number.isFinite(Number(meta?.countMax)) ? Number(meta?.countMax) : undefined;
      try { console.log('[Baseline][LIMIT] check', { codeUpper, maxAllowed, existingCount, selectedCount: files.length, filteredCount: filtered.length }); } catch {}
      if (maxAllowed !== undefined) {
        if (existingCount >= maxAllowed) {
          message.warning(`该类型基线文件已达到上限（${maxAllowed}），请先删除已存在的文件后再上传`);
          setUploadTargetCode(undefined);
          setFileAccept('');
          return;
        }
        const slots = Math.max(0, maxAllowed - existingCount);
        if (filtered.length > slots) {
          message.info(`超过数量上限，仅上传前 ${slots} 个文件，其余将跳过`);
          filtered.splice(slots); // 截断
        }
      }
      const { ok, fail } = await uploadFilesToBaseline(bid, filtered);
      message.success(`上传完成：成功 ${ok}${fail ? `，失败 ${fail}` : ''}`);
      setUploadTargetCode(undefined);
      setFileAccept('');
      await loadBaselineFiles();
    } catch (err:any) {
      message.error(err?.message || '上传失败');
    }
  };
  // 预览态（CSV/XLSX/PDF）
  const [pvVisible, setPvVisible] = useState(false);
  const [pvLoading, setPvLoading] = useState(false);
  const [pvKind, setPvKind] = useState<'csv'|'xlsx'|'pdf'|'image'|'unknown'>('unknown');
  const [pvColumns, setPvColumns] = useState<any[]>([]);
  const [pvRows, setPvRows] = useState<any[]>([]);
  const [pvUrl, setPvUrl] = useState<string | undefined>(undefined);
  const [pvSheetName, setPvSheetName] = useState<string | undefined>(undefined);
  const [pvFileName, setPvFileName] = useState<string | undefined>(undefined);
  const [pvSearch, setPvSearch] = useState<string>(''); // 预览表搜索（CSV/XLSX）
  const [pvPage, setPvPage] = useState<number>(1);
  const [pvPageSize, setPvPageSize] = useState<number>(20);
  const pvFilteredRows = useMemo(() => {
    try {
      if (!(pvKind === 'csv' || pvKind === 'xlsx')) return pvRows;
      const q = String(pvSearch || '').trim().toLowerCase();
      if (!q) return pvRows;
      // 模糊匹配（忽略大小写）：任何单元格包含查询即命中
      return (pvRows || []).filter((r:any) => {
        const keys = Object.keys(r || {}).filter(k => k !== '__idx');
        for (const k of keys) {
          const v = r?.[k];
          if (v === null || v === undefined) continue;
          const s = String(v).toLowerCase();
          if (s.includes(q)) return true;
        }
        return false;
      });
    } catch { return pvRows; }
  }, [pvRows, pvKind, pvSearch]);
  useEffect(() => { try { setPvPage(1); } catch {} }, [pvKind, pvFileName, pvSearch]);
  useEffect(() => {
    try { console.debug('[RelabelPack][BASELINE] baselines=', baselines, 'mappingBaseline=', mappingBaseline); } catch {}
  }, [baselines, mappingBaseline]);
  const resolveFileUrl = useCallback((att: any): string | undefined => {
    try {
      const raw = att?.file || att?.file_path || '';
      if (!raw) return undefined;
      if (/^https?:\/\//i.test(String(raw))) return String(raw);
      let backendOrigin = window.location.origin;
      // 开发环境端口修正：3000 → 8000（后端）
      try {
        const u = new URL(backendOrigin);
        if (u.port === '3000') { u.port = '8000'; backendOrigin = u.toString().replace(/\/$/, ''); }
      } catch {}
      try {
        const anyReq: any = request as any;
        const baseURL = anyReq?.defaults?.baseURL || anyReq?.getUri?.();
        if (baseURL && /^https?:\/\//i.test(baseURL)) {
          const b = new URL(baseURL);
          backendOrigin = `${b.protocol}//${b.host}`;
        }
      } catch {}
      let path = String(raw);
      if (!path.startsWith('/')) path = '/' + path;
      if (!/^\/media\//i.test(path)) path = '/media' + path;
      const finalUrl = `${backendOrigin}${path}`;
      try { console.debug('[Baseline][URL]', { raw, backendOrigin, finalUrl }); } catch {}
      return finalUrl;
    } catch { return undefined; }
  }, []);

  const normalizeCodeUpper = (v:any) => {
    try { return String(v ?? '').trim().toUpperCase(); } catch { return ''; }
  };

  const loadGeneratedLabels = useCallback(async () => {
    const instrId = Number(instrIdState);
    if (!Number.isFinite(instrId) || instrId <= 0) return;
    setGenLabelLoading(true);
    try {
      const resp = await request.get(`/api/v1/instruction/instructions/${instrId}/generated-labels/`);
      const list = Array.isArray((resp as any)?.data?.results) ? (resp as any).data.results : [];
      const map: Record<string, any> = {};
      (list || []).forEach((it:any) => {
        const k = normalizeCodeUpper(it?.new_code);
        if (k) map[k] = it;
      });
      setGenLabelMap(map);
    } catch {
      // 静默：不阻断执行
    } finally {
      setGenLabelLoading(false);
    }
  }, [instrIdState]);

  useEffect(() => { loadGeneratedLabels(); }, [loadGeneratedLabels]);

  const ensureGeneratedLabel = useCallback(async (
    newCodeUpper: string,
    newText?: string,
    opts?: { force?: boolean }
  ): Promise<any | null> => {
    const instrId = Number(instrIdState);
    if (!Number.isFinite(instrId) || instrId <= 0) return null;
    const code = normalizeCodeUpper(newCodeUpper);
    if (!code) return null;
    // 已有缓存：直接返回
    const existed = genLabelMap?.[code];
    if (existed && !opts?.force) return existed;
    try {
      const resp = await request.post(`/api/v1/instruction/instructions/${instrId}/generated-labels/get-or-create/`, {
        new_code: code,
        new_text: String(newText ?? '').trim() || 'New Item',
      });
      const item = (resp as any)?.data?.item;
      if (item && normalizeCodeUpper(item?.new_code) === code) {
        setGenLabelMap(prev => ({ ...(prev || {}), [code]: item }));
        return item;
      }
      return null;
    } catch {
      return null;
    }
  }, [instrIdState, genLabelMap]);

  const previewGeneratedLabelPdf = useCallback(async (fileUrl: string, fileName: string) => {
    const url = String(fileUrl || '').trim();
    if (!url) { message.warning('无可用文件链接'); return; }
    try { setPvFileName(fileName || '系统生成标签.pdf'); } catch { setPvFileName(undefined); }
    setPvKind('pdf');
    setPvVisible(true);
    setPvLoading(true);
    try {
      const resp = await request.get(url, { responseType: 'blob' as any });
      const blob = (resp as any)?.data as Blob;
      const o = URL.createObjectURL(blob);
      setPvUrl(o);
    } catch (e:any) {
      message.error(e?.response?.data?.detail || e?.message || '预览失败');
    } finally {
      setPvLoading(false);
    }
  }, []);

  const downloadGeneratedLabelPdf = useCallback(async (fileUrl: string, fileName: string) => {
    const url = String(fileUrl || '').trim();
    if (!url) { message.warning('无可用文件链接'); return; }
    try {
      const resp = await request.get(url, { responseType: 'blob' as any });
      const blob = (resp as any)?.data as Blob;
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = fileName || '系统生成标签.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(href);
    } catch { message.error('下载失败'); }
  }, []);

  const deleteGeneratedLabel = useCallback(async (newCodeUpper: string) => {
    const instrId = Number(instrIdState);
    if (!Number.isFinite(instrId) || instrId <= 0) { message.error('指令ID无效'); return; }
    const code = normalizeCodeUpper(newCodeUpper);
    if (!code) { message.warning('新条码为空'); return; }
    Modal.confirm({
      title: '删除系统生成标签',
      content: `确认删除系统自动生成的新标签副本？删除后，下次遇到该新条码会重新生成标签。(${code})`,
      okText: '删除标签',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await request.delete(`/api/v1/instruction/instructions/${instrId}/generated-labels/by-code/${encodeURIComponent(code)}/`);
          setGenLabelMap(prev => {
            const next = { ...(prev || {}) } as any;
            delete next[code];
            return next;
          });
          message.success(`已删除标签：${code}`);
        } catch (e:any) {
          message.error(e?.response?.data?.detail || e?.message || '删除失败');
        }
      }
    });
  }, [instrIdState]);

  // 前端 FIFO 打印队列：确保连续扫码时按顺序发送到打印代理（避免并发导致乱序）
  const printQueueRef = useRef<Promise<any>>(Promise.resolve());
  const enqueuePrint = useCallback(<T,>(fn: () => Promise<T>): Promise<T> => {
    const run = printQueueRef.current.then(fn, fn);
    // 队列不传播 reject，避免后续任务被短路
    printQueueRef.current = run.then(() => undefined, () => undefined);
    return run;
  }, []);

  const getPrintAgentUrl = (): string => {
    try {
      const v = String(window.localStorage.getItem('XT_PRINT_AGENT_URL') || '').trim();
      if (v) return v;
    } catch {}
    try {
      const env = String((process as any)?.env?.REACT_APP_PRINT_AGENT_URL || '').trim();
      if (env) return env;
    } catch {}
    return 'http://127.0.0.1:9135';
  };

  const blobToBase64 = async (blob: Blob): Promise<string> => {
    return await new Promise((resolve, reject) => {
      try {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('read blob failed'));
        reader.onload = () => {
          const res = String(reader.result || '');
          // res: data:xxx;base64,....
          const idx = res.indexOf(',');
          resolve(idx >= 0 ? res.slice(idx + 1) : res);
        };
        reader.readAsDataURL(blob);
      } catch (e) {
        reject(e);
      }
    });
  };

  const tryPrintViaAgent = useCallback(async (args: {
    jobType: string;
    labelSize: { wIn: number; hIn: number };
    fileName?: string;
    code?: string;
    blob: Blob;
    mime?: string;
    copies?: number;
  }): Promise<{ ok: boolean; jobId?: string; err?: string }> => {
    const base = getPrintAgentUrl();
    const endpoint = `${String(base).replace(/\/$/, '')}/api/print`;
    try {
      const b64 = await blobToBase64(args.blob);
      const ctrl = new AbortController();
      const t = setTimeout(() => { try { ctrl.abort(); } catch {} }, 4000);
      const resp = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          job_type: args.jobType,
          label_size_in: { w: args.labelSize.wIn, h: args.labelSize.hIn },
          copies: (() => {
            try {
              const n = Number(args.copies);
              if (!Number.isFinite(n)) return 1;
              const i = Math.floor(n);
              if (i <= 0) return 1;
              return Math.min(50, i);
            } catch { return 1; }
          })(),
          file_name: args.fileName || '',
          code: args.code || '',
          content_base64: b64,
          content_type: args.mime || args.blob.type || 'application/octet-stream',
          // 让代理端按 job_type/label_size 自动路由到对应打印机（避免前端写死打印机名）
          hint: { source: 'xt01b-web', ts: Date.now() }
        }),
        signal: ctrl.signal
      });
      clearTimeout(t);
      if (!resp.ok) {
        const txt = await resp.text().catch(() => '');
        return { ok: false, err: `HTTP ${resp.status} ${txt || ''}`.trim() };
      }
      const data = await resp.json().catch(() => ({}));
      const jobId = String((data as any)?.job_id || (data as any)?.id || '');
      return { ok: true, jobId };
    } catch (e:any) {
      return { ok: false, err: e?.message || 'print agent unreachable' };
    }
  }, []);

  // 直接打印新条码标签文件：优先在就绪的基线文件中按“文件名(去后缀)=新条码”匹配并打印（不依赖后缀/类型）
  // 优先使用“本地打印代理”实现无预览自动打印；若代理不可用，则回退浏览器打印（可能被弹窗拦截）。
  const printNewBarcodePdfByCode = useCallback(async (
    newCodeUpper: string,
    opts?: { preferAgent?: boolean; printWin?: Window | null; copies?: number; newText?: string }
  ): Promise<boolean> => {
    try {
      const code = String(newCodeUpper || '').trim().toUpperCase();
      if (!code) return false;
      const preferAgent = opts?.preferAgent !== false;

      const stripExt = (name: string) => {
        const s = String(name || '').trim();
        if (!s) return '';
        const dot = s.lastIndexOf('.');
        if (dot <= 0) return s;
        return s.slice(0, dot);
      };
      const pickLabelFileRow = () => {
        const all = Array.isArray(blFiles) ? blFiles : [];
        const bid = Number(newBarcodeBaseline?.id);
        const byName = all.filter((f:any) => {
          const n = String(f?.file_name || '').trim();
          if (!n) return false;
          const base = stripExt(n).trim().toUpperCase();
          return base === code;
        });
        if (!byName.length) return null;
        // 优先：NEW_BARCODE 基线内
        if (Number.isFinite(bid)) {
          const inNew = byName.filter((f:any)=> Number(f?.baseline) === bid);
          if (inNew.length) return inNew[0];
        }
        // 次优：PDF 先（虽然“不必考虑后缀/类型”，但打印体验更稳定）
        const pdf = byName.find((f:any)=> String(f?.file_name || '').toLowerCase().endsWith('.pdf'));
        if (pdf) return pdf;
        return byName[0];
      };

      const row = pickLabelFileRow();
      let url = '';
      let fileName = '';
      const isGenerated = !row;
      if (row) {
        const u = resolveFileUrl(row);
        if (!u) { message.warning('新条码标签文件链接无效'); return false; }
        url = u;
        fileName = String(row?.file_name || '');
      } else {
        // 兜底：系统生成标签（按指令归档，只生成一次，不覆盖）
        const item = await ensureGeneratedLabel(code, opts?.newText);
        const u = String(item?.file_url || item?.pdf_file || '').trim();
        if (!u) { message.warning(`未找到新条码标签文件：${code}（系统生成失败/不可用）`); return false; }
        url = u;
        fileName = `GEN_${code}.pdf`;
      }
      const withCacheBust = (u: string) => {
        try {
          const t = Date.now();
          return u.includes('?') ? `${u}&t=${t}` : `${u}?t=${t}`;
        } catch { return u; }
      };
      // 生成标签的 PDF：避免浏览器缓存导致“删除后重新生成仍拿到旧PDF”
      const fetchUrl = isGenerated ? withCacheBust(url) : url;

      let blob: Blob | null = null;
      try {
        const resp = await request.get(fetchUrl, { responseType: 'blob' as any });
        blob = (resp as any)?.data as Blob;
      } catch (e:any) {
        // 若是“生成标签”且取文件失败：强制 get-or-create 一次并重试（避免前端/浏览器缓存）
        if (isGenerated) {
          try {
            const item2 = await ensureGeneratedLabel(code, opts?.newText, { force: true });
            const u2 = String(item2?.file_url || item2?.pdf_file || '').trim();
            if (u2) {
              const resp2 = await request.get(withCacheBust(u2), { responseType: 'blob' as any });
              blob = (resp2 as any)?.data as Blob;
            }
          } catch {}
        }
        if (!blob) {
          message.error(e?.response?.data?.detail || e?.message || '获取标签文件失败');
          return false;
        }
      }
      const mime = String(blob?.type || '') || 'application/pdf';

      // 1) 打印代理（无预览）
      if (preferAgent) {
        const r = await tryPrintViaAgent({
          jobType: 'RELABEL_NEW_LABEL',
          labelSize: { wIn: 2.25, hIn: 1.25 },
          fileName: fileName || `${code}.pdf`,
          code,
          blob,
          mime,
          copies: (() => {
            try {
              const n = Number(opts?.copies);
              if (!Number.isFinite(n)) return 1;
              const i = Math.floor(n);
              if (i <= 0) return 1;
              return Math.min(50, i);
            } catch { return 1; }
          })(),
        });
        if (r.ok) {
          if (r.jobId) message.success(`已发送打印任务：${r.jobId}`);
          else message.success('已发送打印任务');
          return true;
        }
        // 代理不可用：回退浏览器打印（尽量不阻断）
        try { console.warn('[PrintAgent] fallback to browser print:', r.err); } catch {}
      }

      const objectUrl = URL.createObjectURL(new Blob([blob], { type: mime }));

      // 采用“占位窗口填充打印”方案：更不容易被浏览器拦截
      const w = opts?.printWin || window.open('about:blank', '_blank');
      if (!w) { message.error('浏览器阻止了弹窗/打印窗口，请允许弹窗后重试'); try { URL.revokeObjectURL(objectUrl); } catch {} return false; }

      const cleanup = () => { try { URL.revokeObjectURL(objectUrl); } catch {} };
      try {
        // 尽量减少“预览感”：直接写入内容并立即触发打印
        const isPdf = /pdf/i.test(mime) || String(row?.file_name || '').toLowerCase().endsWith('.pdf');
        const isImg = /^image\//i.test(mime) || /\.(png|jpg|jpeg|webp|gif|bmp|svg|ico)$/i.test(String(row?.file_name || ''));
        w.document.open();
        w.document.write(`<!doctype html><html><head><meta charset="utf-8" /><title>${code}</title>
<style>
@page { size: 2.25in 1.25in; margin: 0; }
html, body { margin:0; padding:0; width:100%; height:100%; overflow:hidden; }
</style>
</head><body>
${isPdf ? `<iframe src="${objectUrl}" style="border:0;width:100vw;height:100vh"></iframe>`
        : isImg ? `<img src="${objectUrl}" style="width:100%;height:100%;object-fit:contain" />`
        : `<iframe src="${objectUrl}" style="border:0;width:100vw;height:100vh"></iframe>`}
</body></html>`);
        w.document.close();

        const doPrint = () => {
          try { w.focus(); } catch {}
          try { (w as any).onafterprint = () => { try { w.close(); } catch {} cleanup(); }; } catch {}
          try { w.print(); } catch {}
          // 兜底：若 onafterprint 不触发，延迟关闭（避免“闪退过快导致没出打印对话框”）
          setTimeout(() => { try { w.close(); } catch {} cleanup(); }, 20000);
        };
        try { (w as any).onload = () => setTimeout(doPrint, 80); } catch {}
        setTimeout(doPrint, 500);
      } catch {
        try { w.close(); } catch {}
        cleanup();
        message.error('打开打印窗口失败');
        return false;
      }

      return true;
    } catch (e:any) {
      message.error(e?.response?.data?.detail || e?.message || '打印失败');
      return false;
    }
  }, [blFiles, newBarcodeBaseline?.id, resolveFileUrl, ensureGeneratedLabel]);

  // 解析 BARCODE_MAPPING（新旧条码对应表）
  const getMappingFileRow = useCallback((): any | null => {
    try {
      const bid = Number(mappingBaseline?.id);
      if (!Number.isFinite(bid)) return null;
      const arr = (blFiles || []).filter((f:any) => Number(f?.baseline) === bid);
      if (!arr || arr.length === 0) return null;
      const getName = (x:any) => String(x?.file_name || x?.file || x?.file_path || '').toLowerCase();
      const prefer = (x:any) => {
        const n = getName(x);
        if (n.endsWith('.xlsx')) return 5;
        if (n.endsWith('.xls')) return 4;
        if (n.endsWith('.csv')) return 3;
        return 0;
      };
      const sorted = [...arr].sort((a:any,b:any) => prefer(b) - prefer(a));
      return sorted[0] || null;
    } catch { return null; }
  }, [mappingBaseline, blFiles]);

  useEffect(() => {
    (async () => {
      try {
        const fileRow = getMappingFileRow();
        if (!fileRow) { setMappingDefs([]); setMappingErr('未找到BARCODE_MAPPING基线文件，请先上传并刷新'); return; }
        const url = resolveFileUrl(fileRow);
        if (!url) { setMappingDefs([]); setMappingErr('BARCODE_MAPPING文件链接无效'); return; }
        setMappingLoading(true);
        setMappingErr('');
        setMappingFileRowCache(fileRow);
        const nameLower = String(fileRow?.file_name || fileRow?.file || fileRow?.file_path || '').toLowerCase();
        const isHeaderRow = (row:any[]) => {
          try {
            const s = (row || []).map(x => String(x ?? '').trim()).join('|');
            return /旧条码|新条码|数量|分配|备注/i.test(s);
          } catch { return false; }
        };
        const normalizeCode = (v:any) => String(v ?? '').trim().toUpperCase();
        const parseLimit = (v:any): number | undefined => {
          const s = String(v ?? '').trim();
          if (!s) return undefined; // 空=不限
          const n = Number(s);
          if (!Number.isFinite(n) || n < 0) return undefined;
          return n;
        };
        let aoa: any[][] = [];
        if (nameLower.endsWith('.csv')) {
          let Papa: any = null;
          try { Papa = (await import('papaparse')).default || (await import('papaparse')); } catch {}
          if (!Papa) throw new Error('缺少 CSV 解析库（papaparse）');
          const resp = await fetch(url, { credentials: 'include' as any });
          const text = await resp.text();
          const parsed = Papa.parse(text, { skipEmptyLines: true });
          aoa = Array.isArray(parsed?.data) ? parsed.data : [];
          setMappingIsCsvCache(true);
        } else {
          let XLSX: any = null;
          try { XLSX = await import('xlsx'); } catch {}
          if (!XLSX) throw new Error('缺少 Excel 解析库（xlsx）');
          const resp = await fetch(url, { credentials: 'include' as any });
          const buf = await resp.arrayBuffer();
          const wb = XLSX.read(buf, { type: 'array' });
          const sheetName = (wb.SheetNames && wb.SheetNames[0]) || undefined;
          const ws = sheetName ? wb.Sheets[sheetName] : undefined;
          aoa = ws ? (XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' }) as any[][]) : [];
          setMappingIsCsvCache(false);
        }
        const rows = Array.isArray(aoa) ? aoa.filter(r => Array.isArray(r) && r.some(x => String(x ?? '').trim() !== '')) : [];
        setMappingAoaCache(rows);
        const startIdx = (rows.length > 0 && isHeaderRow(rows[0])) ? 1 : 0;
        const hasMatchTargetsCol = (() => {
          try {
            if (!(rows.length > 0 && isHeaderRow(rows[0]))) return false;
            const header = rows[0] || [];
            // 仅以“操作对象匹配信息”为准（由审核页写入基线文件）
            return header.some((x:any) => /操作对象匹配信息/i.test(String(x ?? '').trim()));
          } catch { return false; }
        })();
        const parseMatchTargetsIuIds = (v:any): number[] => {
          try {
            const s = String(v ?? '').trim();
            if (!s || s === '-' || s === '[]') return [];
            const js = JSON.parse(s);
            const arr = Array.isArray(js) ? js : [];
            const ids = arr.map((x:any) => Math.floor(Number(x?.iu_id || 0))).filter((n:number) => Number.isFinite(n) && n > 0);
            return Array.from(new Set(ids));
          } catch { return []; }
        };
        const parseMatchTargetsFirstIuId = (v:any): number => {
          try {
            const s = String(v ?? '').trim();
            if (!s || s === '-' || s === '[]') return 0;
            const js = JSON.parse(s);
            const arr = Array.isArray(js) ? js : [];
            if (!arr.length) return 0;
            const id = Math.floor(Number(arr?.[0]?.iu_id || 0));
            return Number.isFinite(id) && id > 0 ? id : 0;
          } catch { return 0; }
        };
        const defs: MappingDef[] = [];
        const preMulti: Record<string, number[]> = {};
        const preSingle: Record<string, number> = {};
        for (let i = startIdx; i < rows.length; i++) {
          const r = rows[i] || [];
          let old = normalizeCode(r?.[0]);
          const newer = normalizeCode(r?.[1]);
          const newText = String(r?.[2] ?? '').trim();
          const limitQty = parseLimit(r?.[3]);
          const matchTargets = hasMatchTargetsCol ? String(r?.[4] ?? '').trim() : '';
          const remark = hasMatchTargetsCol ? String(r?.[5] ?? '').trim() : String(r?.[4] ?? '').trim();
          // 约定：
          // - 仅“新条码缺失”视为无效（仍丢弃）
          // - “旧条码缺失但新条码存在”视为有效：旧条码展示为“-”，用于人工视觉分拣场景
          if (!newer) continue;
          if (!old) old = '-';
          const keyStr = `${old}->${newer}`;
          if (hasMatchTargetsCol) {
            const ids = parseMatchTargetsIuIds(matchTargets);
            if (ids.length) {
              preMulti[keyStr] = ids;
              // 方案A：多对象时不再默认“选第一个”写入 single override（避免把后续分配策略提前钉死）
              // 仅当确实只有 1 个对象时，才预填 single（便利性 + 与未来策略不冲突）
              if (ids.length === 1) preSingle[keyStr] = ids[0];
            }
          }
          defs.push({
            key: keyStr,
            old, newer,
            newText: newText || undefined,
            limitQty,
            matchTargets: matchTargets ? matchTargets : undefined,
            remark: remark || undefined,
            rowNo: i + 1,
          });
        }
        setMappingDefs(defs);
        // 若基线文件已提供“操作对象匹配信息/匹配操作对象”，则预填 multi 覆盖（key -> iu_ids[]）
        try {
          if (Object.keys(preMulti).length > 0) {
            setMappingKeyIuOverrideMulti(prev => ({ ...(prev || {}), ...preMulti }));
          }
          if (Object.keys(preSingle).length > 0) {
            setMappingKeyIuOverride(prev => ({ ...(prev || {}), ...preSingle }));
          }
        } catch {}
        if (defs.length === 0) setMappingErr('BARCODE_MAPPING为空或无法解析（需至少包含A旧条码、B新条码）');
      } catch (e:any) {
        setMappingDefs([]);
        setMappingErr(e?.message || '解析BARCODE_MAPPING失败');
      } finally {
        setMappingLoading(false);
      }
    })();
  }, [getMappingFileRow, resolveFileUrl]);

  const loadBaselineFiles = async () => {
    const ids: number[] = (baselines || []).map((b:any) => Number(b?.id)).filter((v:number) => Number.isFinite(v));
    if (!ids || ids.length === 0) { setBlFiles([]); return; }
    setBlLoading(true);
    try {
      // 汇总拉取所有相关基线的文件
      const all:any[] = [];
      for (const bid of ids) {
        try {
          const resp = await request.get('/api/v1/instruction/baseline-files/', { params: { baseline: bid, page_size: 1000 } });
          const data = resp?.data;
          const results = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
          all.push(...(results || []));
        } catch {}
      }
      // 去重：按文件 id
      const map = new Map<string, any>();
      (all || []).forEach((it:any) => {
        const key = String(it?.id || `${it?.file}|${it?.file_name}`);
        if (!map.has(key)) map.set(key, it);
      });
      const merged = Array.from(map.values());
      setBlFiles(merged);
      try { console.debug('[RelabelPack][FILES] baseline_ids=', ids, 'files_total=', merged.length); } catch {}
    } catch {
      setBlFiles([]);
      message.error('加载基线文件失败');
      try { console.debug('[RelabelPack][FILES][ERROR] baseline_id=', mappingBaseline?.id); } catch {}
    } finally { setBlLoading(false); }
  };
  // 若存在任一基线且未加载文件，进入时自动加载一次
  useEffect(() => {
    try {
      if ((baselines && baselines.length > 0) && (!blFiles || blFiles.length === 0) && !blLoading) {
        loadBaselineFiles();
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baselines && baselines.length]);

  const guessExt = (row:any): string => {
    try {
      const fileName = String(row?.file_name || row?.file || '').toLowerCase();
      const mt = String(row?.mime_type || '').toLowerCase();
      let kind = 'unknown';
      if (fileName.endsWith('.csv')) kind = 'csv';
      else if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) kind = 'xlsx';
      else if (fileName.endsWith('.pdf')) kind = 'pdf';
      else if (/\.(png|jpg|jpeg|webp|gif|apng|svg|bmp|ico)$/.test(fileName)) kind = 'image';
      else if (mt.includes('csv')) kind = 'csv';
      else if (mt.includes('excel') || mt.includes('spreadsheet')) kind = 'xlsx';
      else if (mt.includes('pdf')) kind = 'pdf';
      else if (/image\//.test(mt) || /(svg|ico)/.test(mt)) kind = 'image';
      try { console.debug('[Baseline][GuessExt]', { fileName, mime: row?.mime_type, kind }); } catch {}
      return kind;
    } catch { return 'unknown'; }
  };

  const previewFile = async (row:any) => {
    const url = resolveFileUrl(row);
    if (!url) { message.warning('无可用文件链接'); return; }
    const kind = guessExt(row) as any;
    const finalUrl = `${url}${url.includes('?') ? '&' : '?'}t=${Date.now()}`;
    try { console.debug('[Baseline][Preview] start', { kind, finalUrl, id: row?.id }); } catch {}
    try {
      const rawName = String(row?.file_name || row?.file || '').trim();
      const base = rawName ? rawName.split('/').pop() as string : '';
      const disp = base ? decodeURIComponent(base) : base;
      setPvFileName(disp || undefined);
    } catch { setPvFileName(undefined); }
    setPvKind(kind);
    setPvUrl(finalUrl);
    setPvVisible(true);
    setPvLoading(true);
    setPvColumns([]);
    setPvRows([]);
    setPvSheetName(undefined);
    const matchTitle = '操作对象匹配信息';
    const isBmPreview = (() => {
      try {
        const bid = Number(row?.baseline);
        const code = (Number.isFinite(bid) ? (baselineIdToCode.get(bid) || '') : '') || String(row?.baseline_code || '').trim();
        return String(code || '').trim().toUpperCase() === 'BARCODE_MAPPING';
      } catch { return false; }
    })();
    const renderBmMatchInfo = (v:any) => {
      const txt = String(v ?? '').trim();
      const isMiss = (!txt || txt === '-' || txt === '[]');
      if (isMiss) return <span style={{ color:'#cf1322', fontWeight: 700 }}>-</span>;
      let arr: any[] = [];
      try { arr = JSON.parse(txt); } catch { arr = []; }
      const list = Array.isArray(arr) ? arr : [];
      if (!list.length) return <span style={{ color:'#cf1322', fontWeight: 700 }}>-</span>;
      // 仅改变展示方式（胶囊Tag + 分行），不改变取值方式：
      // - system_barcode 优先；否则展示 IU#id
      // - use_all_available / expected_qty 按原值展示
      const targets = (list || [])
        .map((x:any) => ({
          iu_id: Math.floor(Number(x?.iu_id || 0)),
          system_barcode: String(x?.system_barcode || '').trim(),
          use_all_available: !!x?.use_all_available,
          expected_qty: x?.expected_qty,
        }))
        .filter((x:any) => Number.isFinite(x.iu_id) && x.iu_id > 0);
      try {
        targets.sort((a:any, b:any) => {
          const ua = a?.use_all_available ? 1 : 0;
          const ub = b?.use_all_available ? 1 : 0;
          if (ua !== ub) return ua - ub; // 否在前
          const ea = (a?.expected_qty == null || !Number.isFinite(Number(a.expected_qty))) ? Number.MAX_SAFE_INTEGER : Number(a.expected_qty);
          const eb = (b?.expected_qty == null || !Number.isFinite(Number(b.expected_qty))) ? Number.MAX_SAFE_INTEGER : Number(b.expected_qty);
          if (ea !== eb) return ea - eb;
          return String(a?.system_barcode || '').localeCompare(String(b?.system_barcode || ''));
        });
      } catch {}

      const renderOne = (x:any, i:number) => {
        const iuId = Math.floor(Number(x?.iu_id || 0));
        const sys = String(x?.system_barcode || '').trim() || (iuId ? `IU#${iuId}` : '-');
        const useAll = !!x?.use_all_available;
        const eq = (x?.expected_qty == null || x?.expected_qty === '') ? null : Number(x.expected_qty);
        const eqTxt = (eq == null || !Number.isFinite(eq)) ? '-' : String(eq);
        // 颜色：是=蓝色系；否=橘红色背景（与总进度一致）
        const bg = useAll ? '#e6f4ff' : '#ff7a45';
        const bd = useAll ? '#91caff' : '#ff7a45';
        const fg = useAll ? '#0958d9' : '#ffffff';
        return (
          <Tag
            key={`bm_it_${i}`}
            style={{
              marginInlineEnd: 0,
              marginBottom: 0,
              background: bg,
              borderColor: bd,
              color: fg,
              borderRadius: 999,
              fontWeight: 700,
              padding: '0 8px',
              lineHeight: '20px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              maxWidth: 860,
            }}
          >
            <span style={{ fontFamily:'monospace', maxWidth: 220, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{sys}</span>
            <span style={{ opacity: 0.9 }}>{useAll ? '是' : '否'}</span>
            <span style={{ opacity: 0.9 }}>{`预${eqTxt}`}</span>
          </Tag>
        );
      };

      return (
        <div style={{ display:'flex', flexDirection:'column', gap: 6 }}>
          {targets.map((x:any, i:number) => renderOne(x, i))}
        </div>
      );
    };
    const decorateCols = (cols:any[]) => {
      try {
        if (!isBmPreview) return cols;
        const tipQty = '默认为空，仅用于一个旧条码对应多个新条码时按顺序指定数量限制，请参考指令详情手动修改。';
        const tipMatch = '数据格式：操作对象-是否操作所有库存-预计数量，用于判断是否执行换标及操作记录的落库操作对象。';
        return (cols || []).map((c:any) => {
          const t = String(c?.title || '').trim();
          if (!t) return c;
          // 数量分配：列头 hover 提示
          if (t === '数量分配' || t.includes('数量分配')) {
            return {
              ...c,
              title: (
                <Tooltip title={tipQty}>
                  <span style={{ cursor: 'help' }}>{t}</span>
                </Tooltip>
              ),
            };
          }
          if (t === matchTitle || t.includes(matchTitle)) {
            return {
              ...c,
              title: (
                <Tooltip title={tipMatch}>
                  <span style={{ cursor: 'help' }}>{matchTitle}</span>
                </Tooltip>
              ),
              render: (v:any) => renderBmMatchInfo(v),
            };
          }
          return c;
        });
      } catch { return cols; }
    };
    try {
      // 轻量后端日志（便于 runserver 观察“点击预览”是否触发）
      try { await request.post(`/api/v1/instruction/baseline-files/${row?.id}/preview-log/`, { action: 'preview', url: finalUrl, file_name: row?.file_name }); } catch {}
      if (kind === 'csv') {
        let Papa: any = null;
        try { Papa = (await import('papaparse')).default || (await import('papaparse')); } catch {}
        if (!Papa) throw new Error('缺少 CSV 解析库（papaparse）');
        const resp = await fetch(finalUrl, { credentials: 'include' as any });
        const text = await resp.text();
        const parsed = Papa.parse(text, { header: true });
        const data: any[] = Array.isArray(parsed?.data) ? parsed.data.slice(0, 500) : [];
        const cols = decorateCols(Object.keys(data[0] || {}).map((k) => ({ title: k, dataIndex: k })));
        setPvColumns(cols);
        setPvRows(data.map((r, i) => ({ __idx: i, ...r })));
      } else if (kind === 'xlsx') {
        let XLSX: any = null;
        try { XLSX = await import('xlsx'); } catch {}
        if (!XLSX) throw new Error('缺少 Excel 解析库（xlsx）');
        const resp = await fetch(finalUrl, { credentials: 'include' as any });
        const buf = await resp.arrayBuffer();
        // 按 SheetJS 官方建议，type: 'array' 直接传 ArrayBuffer
        const wb = XLSX.read(buf, { type: 'array' });
        const sheetName = (wb.SheetNames && wb.SheetNames[0]) || undefined;
        const ws = sheetName ? wb.Sheets[sheetName] : undefined;
        const aoa: any[][] = ws ? XLSX.utils.sheet_to_json(ws, { header: 1 }) : [];
        const header = (aoa[0] || []).map((h:any, idx:number) => String(h || `C${idx+1}`));
        const cols = decorateCols(header.map((h) => ({ title: h, dataIndex: h })));
        const body = aoa.slice(1, 501).map((row:any[], i:number) => {
          const obj: any = { __idx: i };
          header.forEach((h, j) => { obj[h] = row?.[j]; });
          return obj;
        });
        setPvSheetName(sheetName);
        setPvColumns(cols);
        setPvRows(body);
        try { console.debug('[Baseline][Preview] xlsx parsed rows=', body.length, 'cols=', header.length, 'sheet=', sheetName); } catch {}
      } else if (kind === 'pdf') {
        // 拉取为 blob 并以 objectURL 内嵌，避免 dev 下端口不一致导致拒绝连接
        try { console.debug('[Baseline][Preview] pdf show via blob iframe'); } catch {}
        setPvKind('pdf');
        const resp = await fetch(finalUrl, { credentials: 'include' as any });
        if (!resp.ok) throw new Error('PDF 加载失败');
        const blob = await resp.blob();
        const o = URL.createObjectURL(blob);
        setPvUrl(o);
      } else {
        // 图片：转为 objectURL 在弹窗中展示
        if (kind === 'image') {
          try { console.debug('[Baseline][Preview][IMG] fetch.start', { finalUrl, withCreds: true }); } catch {}
          const resp = await fetch(finalUrl, { credentials: 'include' as any });
          try { console.debug('[Baseline][Preview][IMG] fetch.done', { ok: resp.ok, status: resp.status, ct: resp.headers.get('Content-Type'), len: resp.headers.get('Content-Length') }); } catch {}
          if (!resp.ok) throw new Error('图片加载失败');
          const blob = await resp.blob();
          const rawName = String(row?.file_name || row?.file || '').toLowerCase();
          const guessedMime = rawName.endsWith('.jpg') || rawName.endsWith('.jpeg') ? 'image/jpeg'
            : rawName.endsWith('.png') ? 'image/png'
            : rawName.endsWith('.webp') ? 'image/webp'
            : rawName.endsWith('.gif') ? 'image/gif'
            : rawName.endsWith('.apng') ? 'image/apng'
            : rawName.endsWith('.svg') ? 'image/svg+xml'
            : rawName.endsWith('.bmp') ? 'image/bmp'
            : rawName.endsWith('.ico') ? 'image/x-icon'
            : (blob.type && blob.type.startsWith('image/') ? blob.type : 'image/*');
          const typedBlob = (blob && (!blob.type || blob.type === 'application/octet-stream'))
            ? new Blob([await blob.arrayBuffer()], { type: guessedMime })
            : blob;
          try { console.debug('[Baseline][Preview][IMG] blob', { size: typedBlob.size, type: typedBlob.type, guessedMime }); } catch {}
          const o = URL.createObjectURL(typedBlob);
          setPvKind('image' as any);
          setPvUrl(o);
          try { console.debug('[Baseline][Preview][IMG] url', { pvUrl: (o || '').slice(0, 48) + '...' }); } catch {}
        } else {
          message.info('不支持的预览类型');
          try { console.debug('[Baseline][Preview] unsupported kind, keep modal for manual download'); } catch {}
        }
      }
    } catch (e:any) {
      message.error(e?.message || '预览失败');
      try { console.debug('[Baseline][Preview] error=', e); } catch {}
    } finally {
      setPvLoading(false);
    }
  };

  useEffect(() => {
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const d = JSON.parse(raw);
      if (typeof d?.label_count === 'number') setLabelCount(Number(d.label_count));
      // v3: 仅使用 IOC code（container_code）作为容器主键；兼容 v2(box_no/box_code) 草稿
      const boxNoToCode = new Map<number, string>();
      if (Array.isArray(d?.boxes)) {
        const nextBoxes = (d.boxes as any[]).map((x, i) => {
          const code = String(x?.code || x?.container_code || x?.box_code || '').trim();
          const no = Number(x?.box_no ?? (i + 1));
          if (code && Number.isFinite(no)) boxNoToCode.set(no, code);
          return {
            code,
            status: (x.status === 'open' || x.status === 'closed' || x.status === 'cancelled') ? x.status : undefined,
            volume_cc: x.volume_cc != null ? Number(x.volume_cc) : undefined,
            weight_g: x.weight_g != null ? Number(x.weight_g) : undefined
          } as any;
        }).filter((x:any) => String(x?.code || '').trim());
        setBoxes(nextBoxes);
      }
      // 当前打开容器（IOC code）
      if (d?.current_box_code != null) {
        setCurrentBoxCode(String(d.current_box_code || ''));
      } else if (d?.current_box_no != null) {
        const n = Number(d.current_box_no);
        const c = Number.isFinite(n) ? (boxNoToCode.get(n) || '') : '';
        if (c) setCurrentBoxCode(String(c));
      }
      // 容器 meta：旧草稿可能以 box_no 为 key，这里尝试映射为 IOC code key
      if (d?.box_meta && typeof d.box_meta === 'object') {
        try {
          const rawMeta: any = d.box_meta;
          const nextMeta: any = {};
          Object.keys(rawMeta || {}).forEach((k) => {
            const v = rawMeta[k];
            const nk = (() => {
              const n = Number(k);
              if (Number.isFinite(n)) return boxNoToCode.get(n) || '';
              return String(k || '').trim();
            })();
            if (nk) nextMeta[nk] = v;
          });
          setBoxMeta(nextMeta as any);
        } catch {}
      }
      // 操作记录：优先 container_code；回退 box_code；再回退 boxes(box_no->code)
      if (Array.isArray(d?.records)) {
        try {
          const list = (d.records as any[]).map((r:any) => {
            const bn = Number(r?.box_no);
            const cc = String(r?.container_code || r?.box_code || (Number.isFinite(bn) ? (boxNoToCode.get(bn) || '') : '') || '').trim();
            const opIuId = (() => {
              try {
                const n = Math.floor(Number(r?.op_iu_id || 0));
                return (Number.isFinite(n) && n > 0) ? n : undefined;
              } catch { return undefined; }
            })();
            const opIuCode = (() => {
              try {
                const s = String(r?.op_iu_code || '').trim();
                return s ? s : undefined;
              } catch { return undefined; }
            })();
            return {
              id: String(r?.id || makeRecId()),
              ts: Number(r?.ts || Date.now()),
              container_code: cc,
              event: String(r?.event || '换标'),
              old_code: r?.old_code != null ? String(r.old_code) : '',
              new_code: r?.new_code != null ? String(r.new_code) : '',
              qty: r?.qty != null ? Number(r.qty) : 0,
              op_iu_id: opIuId,
              op_iu_code: opIuCode,
            } as any;
          }).filter((r:any)=> Number.isFinite(r.ts));
          setRecords(list);
        } catch {}
      }
      // 新条码汇总草稿：用于“按容器统计/箱标”
      if (d?.draft_new_agg_by_code && typeof d.draft_new_agg_by_code === 'object') {
        try { setDraftNewAggByCode(d.draft_new_agg_by_code || {}); } catch {}
      }
      if (d?.label_dirty_by_code && typeof d.label_dirty_by_code === 'object') {
        try { setLabelDirtyByCode(d.label_dirty_by_code || {}); } catch {}
      }
      if (d?.console_box_code != null) {
        try { setConsoleBoxCode(String(d.console_box_code || '')); } catch {}
      }
    } catch {}
  }, [draftKey]);

  const applyDraftState = useCallback((state: any) => {
    try {
      if (!state || typeof state !== 'object') return;
      if (typeof state.label_count === 'number') setLabelCount(Number(state.label_count));
      const boxNoToCode = new Map<number, string>();
      if (Array.isArray(state.boxes)) {
        const nextBoxes = (state.boxes as any[]).map((x, i) => {
          const code = String(x?.code || x?.container_code || x?.box_code || '').trim();
          const no = Number(x?.box_no ?? (i + 1));
          if (code && Number.isFinite(no)) boxNoToCode.set(no, code);
          return {
            code,
            status: (x.status === 'open' || x.status === 'closed' || x.status === 'cancelled') ? x.status : undefined,
            volume_cc: x.volume_cc != null ? Number(x.volume_cc) : undefined,
            weight_g: x.weight_g != null ? Number(x.weight_g) : undefined
          } as any;
        }).filter((x:any) => String(x?.code || '').trim());
        setBoxes(nextBoxes);
      }
      if (state.current_box_code != null) {
        setCurrentBoxCode(String(state.current_box_code || ''));
      } else if (state.current_box_no != null) {
        const n = Number(state.current_box_no);
        const c = Number.isFinite(n) ? (boxNoToCode.get(n) || '') : '';
        if (c) setCurrentBoxCode(String(c));
      }
      if (state.box_meta && typeof state.box_meta === 'object') {
        try {
          const rawMeta: any = state.box_meta;
          const nextMeta: any = {};
          Object.keys(rawMeta || {}).forEach((k) => {
            const v = rawMeta[k];
            const nk = (() => {
              const n = Number(k);
              if (Number.isFinite(n)) return boxNoToCode.get(n) || '';
              return String(k || '').trim();
            })();
            if (nk) nextMeta[nk] = v;
          });
          setBoxMeta(nextMeta as any);
        } catch {}
      }
      if (Array.isArray(state.records)) {
        try {
          const list = (state.records as any[]).map((r:any) => {
            const bn = Number(r?.box_no);
            const cc = String(r?.container_code || r?.box_code || (Number.isFinite(bn) ? (boxNoToCode.get(bn) || '') : '') || '').trim();
            const opIuId = (() => {
              try {
                const n = Math.floor(Number(r?.op_iu_id || 0));
                return (Number.isFinite(n) && n > 0) ? n : undefined;
              } catch { return undefined; }
            })();
            const opIuCode = (() => {
              try {
                const s = String(r?.op_iu_code || '').trim();
                return s ? s : undefined;
              } catch { return undefined; }
            })();
            return {
              id: String(r?.id || makeRecId()),
              ts: Number(r?.ts || Date.now()),
              container_code: cc,
              event: String(r?.event || '换标'),
              old_code: r?.old_code != null ? String(r.old_code) : '',
              new_code: r?.new_code != null ? String(r.new_code) : '',
              qty: r?.qty != null ? Number(r.qty) : 0,
              op_iu_id: opIuId,
              op_iu_code: opIuCode,
            } as any;
          }).filter((r:any)=> Number.isFinite(r.ts));
          setRecords(list);
        } catch {}
      }
      if (state?.draft_new_agg_by_code && typeof state.draft_new_agg_by_code === 'object') {
        try { setDraftNewAggByCode(state.draft_new_agg_by_code || {}); } catch {}
      }
      if (state?.label_dirty_by_code && typeof state.label_dirty_by_code === 'object') {
        try { setLabelDirtyByCode(state.label_dirty_by_code || {}); } catch {}
      }
      if (state?.console_box_code != null) {
        try { setConsoleBoxCode(String(state.console_box_code || '')); } catch {}
      }
    } catch {}
  }, []);

  // 方案B：优先从后端步骤草稿恢复（跨设备）
  useEffect(() => {
    (async () => {
      try {
        if (!draftStepId) return;
        const resp = await request.get(`/api/v1/instruction/exec/steps/${draftStepId}/draft/`);
        const d = resp?.data?.draft;
        if (!d) return;
        // 约定：draft = { kind, version, state, updated_at }
        const kind = String(d?.kind || '');
        if (kind && kind !== 'combined_relabel_pack') return;
        const state = d?.state || {};
        applyDraftState(state);
        // 同步写一份到本地兜底（不覆盖用户当前更改：仅在恢复时写）
        try {
          const local = { version: 2, updated_at: Date.now(), ...state };
          localStorage.setItem(draftKey, JSON.stringify(local));
        } catch {}
      } catch {}
    })();
  }, [draftStepId, applyDraftState, draftKey]);

  // 只读回看兜底：当阶段已完成（canOperate=false）且草稿为空时，从 collected_metrics 恢复展示数据
  // 目标：提交后页面仍可浏览“操作目标与总进度/操作记录”，但不允许修改。
  useEffect(() => {
    try {
      if (!!canOperate) {
        readonlyRecoveredFromCollectedRef.current = false;
        return;
      }
      if (readonlyRecoveredFromCollectedRef.current) return;
      const hasLocalState = (
        (Array.isArray(boxes) && boxes.length > 0)
        || (Array.isArray(records) && records.length > 0)
        || (boxMeta && Object.keys(boxMeta).length > 0)
        || (draftNewAggByCode && Object.keys(draftNewAggByCode).length > 0)
      );
      if (hasLocalState) return;

      const relabelCollected = (relabelStep?.collected_metrics && typeof relabelStep.collected_metrics === 'object')
        ? relabelStep.collected_metrics
        : {};
      const packCollected = (packStep?.collected_metrics && typeof packStep.collected_metrics === 'object')
        ? packStep.collected_metrics
        : {};
      const merged = { ...(relabelCollected || {}), ...(packCollected || {}) } as any;
      const hasCollected = (
        (Array.isArray(merged?.boxes) && merged.boxes.length > 0)
        || (Array.isArray(merged?.records) && merged.records.length > 0)
        || (merged?.box_meta && typeof merged.box_meta === 'object' && Object.keys(merged.box_meta).length > 0)
        || (merged?.draft_new_agg_by_code && typeof merged.draft_new_agg_by_code === 'object' && Object.keys(merged.draft_new_agg_by_code).length > 0)
      );
      if (!hasCollected) return;
      applyDraftState(merged);
      readonlyRecoveredFromCollectedRef.current = true;
    } catch {}
  }, [canOperate, boxes, records, boxMeta, draftNewAggByCode, relabelStep?.collected_metrics, packStep?.collected_metrics, applyDraftState]);

  useEffect(() => {
    if (!canOperateExec) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      try {
        const safeRecords = Array.isArray(records) ? records.slice(0, 3000) : []; // 防止 localStorage 过大
        const d = {
          version: 2,
          updated_at: Date.now(),
          label_count: labelCount,
          boxes,
          current_box_code: currentBoxCode,
          console_box_code: consoleBoxCode,
          box_meta: boxMeta,
          records: safeRecords,
          draft_new_agg_by_code: draftNewAggByCode,
          label_dirty_by_code: labelDirtyByCode,
        };
        localStorage.setItem(draftKey, JSON.stringify(d));
      } catch {}
    }, 800);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [labelCount, boxes, records, currentBoxCode, consoleBoxCode, boxMeta, draftNewAggByCode, labelDirtyByCode, draftKey, canOperate]);

  // 方案B：自动保存到后端（debounce），保证跨设备恢复
  useEffect(() => {
    if (!canOperateExec) return;
    if (!draftStepId) return;
    if (saveToServerTimer.current) clearTimeout(saveToServerTimer.current);
    saveToServerTimer.current = setTimeout(() => {
      (async () => {
        try {
          const state = {
            label_count: labelCount,
            boxes,
            current_box_code: currentBoxCode,
            console_box_code: consoleBoxCode,
            box_meta: boxMeta,
            records: Array.isArray(records) ? records.slice(0, 3000) : [],
            draft_new_agg_by_code: draftNewAggByCode,
            label_dirty_by_code: labelDirtyByCode,
          };
          const draft = { kind: 'combined_relabel_pack', version: 1, updated_at: Date.now(), state };
          const sig = (() => { try { return JSON.stringify(draft); } catch { return String(Date.now()); } })();
          if (sig === lastServerDraftSigRef.current) return;
          lastServerDraftSigRef.current = sig;
          await request.post(`/api/v1/instruction/exec/steps/${draftStepId}/draft/`, { draft });
        } catch {}
      })();
    }, 1000);
    return () => { try { if (saveToServerTimer.current) clearTimeout(saveToServerTimer.current); } catch {} };
  }, [draftStepId, labelCount, boxes, currentBoxCode, consoleBoxCode, boxMeta, records, draftNewAggByCode, labelDirtyByCode, canOperate]);

  const doSaveDraft = useCallback(async () => {
    try {
      setSaving(true);
      const safeRecords = Array.isArray(records) ? records.slice(0, 3000) : [];
      const d = {
        version: 2,
        updated_at: Date.now(),
        label_count: labelCount,
        boxes,
        current_box_code: currentBoxCode,
        console_box_code: consoleBoxCode,
        box_meta: boxMeta,
        records: safeRecords,
        draft_new_agg_by_code: draftNewAggByCode,
        label_dirty_by_code: labelDirtyByCode,
      };
      localStorage.setItem(draftKey, JSON.stringify(d));
      // 同步保存到后端（跨设备）
      try {
        if (draftStepId) {
          const state = {
            label_count: labelCount,
            boxes,
            current_box_code: currentBoxCode,
            console_box_code: consoleBoxCode,
            box_meta: boxMeta,
            records: safeRecords,
            draft_new_agg_by_code: draftNewAggByCode,
            label_dirty_by_code: labelDirtyByCode,
          };
          const draft = { kind: 'combined_relabel_pack', version: 1, updated_at: Date.now(), state };
          await request.post(`/api/v1/instruction/exec/steps/${draftStepId}/draft/`, { draft });
        }
      } catch {}
      message.success('已保存草稿');
    } catch { message.error('保存草稿失败'); } finally { setSaving(false); }
  }, [labelCount, boxes, records, currentBoxCode, consoleBoxCode, boxMeta, draftNewAggByCode, labelDirtyByCode, draftKey, draftStepId]);

  const submitBoth = useCallback(async () => {
    if (!canOperateExec) return;
    try {
      setSubmitting(true);
      // 提交前校验：禁止存在“未作废空容器”（与常规出库对齐）
      const qtyByContainer = new Map<string, number>();
      const contentEvOk = (ev: any) => {
        const e = String(ev || '').trim();
        return e === '换标'
          || e === '调整-取出' || e === '调整-放入'
          || e === '重扫-冲销' || e === '重扫-加回';
      };
      const aggByBox = new Map<string, Map<string, number>>();
      (Array.isArray(records) ? records : []).forEach((r: any) => {
        const cc = String(r?.container_code || '').trim();
        if (!cc) return;
        const ev = String(r?.event || '').trim();
        if (!contentEvOk(ev)) return;
        const newc = String(r?.new_code || '').trim().toUpperCase();
        if (!newc || newc === '-') return;
        const q = Math.trunc(Number(r?.qty || 0) || 0);
        if (!Number.isFinite(q) || q === 0) return;
        const mm = aggByBox.get(cc) || new Map<string, number>();
        mm.set(newc, (mm.get(newc) || 0) + q);
        aggByBox.set(cc, mm);
      });
      aggByBox.forEach((mm: Map<string, number>, cc: string) => {
        let qty = 0;
        mm.forEach((v: number) => {
          const n = Math.floor(Number(v || 0) || 0);
          if (Number.isFinite(n) && n > 0) qty += n;
        });
        qtyByContainer.set(cc, qty);
      });
      const collectInvalidEmptyCodes = (boxRows: any[]) => (
        (Array.isArray(boxRows) ? boxRows : [])
          .map((b: any) => ({
            code: String(b?.code || '').trim(),
            status: String(b?.status || '').trim().toLowerCase(),
          }))
          .filter((x: any) => !!x.code && x.status !== 'cancelled' && x.status !== 'open')
          .filter((x: any) => Math.floor(Number(qtyByContainer.get(x.code) || 0) || 0) <= 0)
          .map((x: any) => x.code)
      );
      let workingBoxes = Array.isArray(boxes) ? boxes.map((b: any) => ({ ...(b || {}) })) : [];
      let invalidEmptyCodes = collectInvalidEmptyCodes(workingBoxes);
      while (invalidEmptyCodes.length > 0) {
        const action = await new Promise<'void' | 'cancel'>((resolve) => {
          Modal.confirm({
            title: '提交前检测到空容器',
            width: 820,
            okText: '作废空容器并继续提交',
            cancelText: '取消提交',
            content: (
              <div style={{ lineHeight: 1.8 }}>
                <div style={{ marginBottom: 8, color: '#595959' }}>
                  以下容器当前总件数为0，且尚未作废。可批量作废后继续提交。
                </div>
                <div style={{ maxHeight: 260, overflow: 'auto', border: '1px solid #f0f0f0', borderRadius: 6, padding: '6px 8px', background: '#fff' }}>
                  {invalidEmptyCodes.map((code: string, idx: number) => (
                    <div key={`${code}-${idx}`} style={{ fontFamily: 'monospace' }}>
                      {idx + 1}. {code}
                    </div>
                  ))}
                </div>
              </div>
            ),
            onOk: () => resolve('void'),
            onCancel: () => resolve('cancel'),
          });
        });
        if (action !== 'void') {
          setSubmitting(false);
          return;
        }
        try {
          const instrId = Number(instrIdState || 0);
          if (!Number.isFinite(instrId) || instrId <= 0) {
            message.error('缺少指令ID，无法作废空容器');
            setSubmitting(false);
            return;
          }
          const resp = await request.post('/api/v1/storage/output-containers/void/', {
            instruction_id: instrId,
            codes: invalidEmptyCodes,
            defer_inventory_settlement: true,
          });
          const voidedCodes = Array.isArray(resp?.data?.voided_codes) ? resp.data.voided_codes : [];
          const blockedRows = Array.isArray(resp?.data?.blocked_codes) ? resp.data.blocked_codes : [];
          const voidedSet = new Set<string>(
            (voidedCodes || [])
              .map((x: any) => String(x || '').trim())
              .filter((x: string) => !!x),
          );
          if (voidedSet.size <= 0) {
            message.warning('未作废任何空容器，已中断提交');
            setSubmitting(false);
            return;
          }
          workingBoxes = (workingBoxes || []).map((b: any) => {
            const cc = String(b?.code || '').trim();
            return voidedSet.has(cc) ? ({ ...b, status: 'cancelled' }) : b;
          });
          setBoxes(workingBoxes as any);
          setOutputDraftByCode((prev) => {
            const base: any = (prev && typeof prev === 'object') ? prev : {};
            const next: any = { ...base };
            voidedSet.forEach((cc: string) => {
              if (!next?.[cc]) return;
              next[cc] = {
                ...(next[cc] || {}),
                status: 'cancelled',
                updated_at: new Date().toISOString(),
              };
            });
            return next;
          });
          if (blockedRows.length > 0) {
            const sample = blockedRows.slice(0, 3).map((x: any) => `${String(x?.code || '-')}:${String(x?.reason || '-')}`).join('；');
            message.warning(`部分容器未作废：${sample}${blockedRows.length > 3 ? ` 等${blockedRows.length}条` : ''}`);
          }
        } catch (e: any) {
          message.error(e?.response?.data?.detail || e?.message || '批量作废空容器失败，已中断提交');
          setSubmitting(false);
          return;
        }
        invalidEmptyCodes = collectInvalidEmptyCodes(workingBoxes);
      }
      // 1) 贴换标
      await submitRelabel({ label_count: Number(labelCount || 0) });

      // 1.5) 最终确认：从 Draft 覆盖写入 final content（并锁定 Draft）
      // 说明：这一步失败时不继续提交“装箱”步骤，避免出现“步骤已提交但 final content 未生成”的半成功状态。
      try {
        const instrId = Number(instrIdState);
        if (!Number.isFinite(instrId) || instrId <= 0) throw new Error('缺少指令ID，无法最终确认容器内容物');
        const closedCodes = (workingBoxes || [])
          .map((b:any) => ({ code: String(b?.code || '').trim(), status: String(b?.status || '') }))
          .filter((x:any) => x.code && x.status && x.status !== 'open' && x.status !== 'cancelled')
          .map((x:any) => x.code);
        const poolCodeLocal = `POOL-${instrId}`;
        await request.post('/api/v1/storage/output-containers/finalize-from-records/', {
          instruction_id: instrId,
          codes: closedCodes,
          pool_code: poolCodeLocal,
          records: Array.isArray(records) ? records : [],
          execution_session_id: String(executionSessionId || '').trim() || undefined,
          defer_inventory_settlement: true,
        });
        // 成功即提示：若有调整/重扫/新增到已结束容器，通常需要重打箱标
        try {
          const dirty = closedCodes.filter((c:string) => !!(labelDirtyByCode as any)?.[c]);
          if (dirty.length) {
            message.info(`存在 ${dirty.length} 个容器需要重打箱标：${dirty.slice(0, 6).join('、')}${dirty.length > 6 ? ` 等${dirty.length}个` : ''}`);
          }
        } catch {}
      } catch (e:any) {
        message.error(e?.response?.data?.detail || e?.message || '最终确认容器内容物失败');
        throw e;
      }

      // 2) 装箱
      const payloadBoxes = (workingBoxes || []).map(b => ({
        container_code: String((b as any)?.code || '').trim(),
        volume_cc: b.volume_cc != null ? Number(b.volume_cc) : undefined,
        weight_g: b.weight_g != null ? Number(b.weight_g) : undefined,
        content: [], // 预留，后续可扩展逐件入箱
      }));
      const progressSnapshot = (() => {
        try {
          const k = `COMBINED_RELABEL_PACK_${taskId}_${stageNo}_${groupKey || 'G'}`;
          const all = (window as any).__RELABEL_PACK_PROGRESS__ || {};
          return all?.[k] || null;
        } catch { return null; }
      })();
      await submitPack({
        boxes: payloadBoxes,
        records: Array.isArray(records) ? records.slice(0, 3000) : [],
        box_meta: boxMeta || {},
        draft_new_agg_by_code: draftNewAggByCode || {},
        label_dirty_by_code: labelDirtyByCode || {},
        output_draft_by_code: outputDraftByCode || {},
        label_count: Number(labelCount || 0) || 0,
        current_box_code: String(currentBoxCode || ''),
        console_box_code: String(consoleBoxCode || ''),
        progress_snapshot: progressSnapshot,
      });
      try { localStorage.removeItem(draftKey); } catch {}
      try { if (draftStepId) await request.post(`/api/v1/instruction/exec/steps/${draftStepId}/draft/`, { draft: null }); } catch {}
      message.success('已提交（贴换标 + 装箱）');
    } catch (e:any) {
      message.error(e?.response?.data?.detail || e?.message || '提交失败');
    } finally {
      setSubmitting(false);
    }
  }, [
    canOperateExec, submitRelabel, submitPack, labelCount, boxes, records, labelDirtyByCode, draftKey, canOperate, draftStepId, instrIdState,
    boxMeta, draftNewAggByCode, outputDraftByCode, currentBoxCode, consoleBoxCode, taskId, stageNo, groupKey,
  ]);

  const cols = [
    { title: '容器系统编码', dataIndex: 'code', width: 240, render: (_:any, r:BoxRow) => {
      const c = String((r as any)?.code || '').trim();
      if (!c) return <span style={{ color:'#999' }}>-</span>;
      return (
        <Typography.Text style={{ fontFamily:'monospace' }} copyable={{ text: c }}>
          {c}
        </Typography.Text>
      );
    } },
    { title: '体积(cc)', dataIndex: 'volume_cc', width: 160, render: (_:any, r:BoxRow, idx:number) => (
      <InputNumber disabled={!canOperateExec} min={0} value={r.volume_cc as any} onChange={(nv)=> setBoxes(prev => prev.map((x,i)=> i===idx ? ({ ...x, volume_cc: (nv==null? undefined : Number(nv)) }) : x))} />
    ) },
    { title: '重量(g)', dataIndex: 'weight_g', width: 160, render: (_:any, r:BoxRow, idx:number) => (
      <InputNumber disabled={!canOperateExec} min={0} value={r.weight_g as any} onChange={(nv)=> setBoxes(prev => prev.map((x,i)=> i===idx ? ({ ...x, weight_g: (nv==null? undefined : Number(nv)) }) : x))} />
    ) },
    { title: '操作', key: 'ops', width: 100, render: (_:any, __:BoxRow, idx:number) => (
      <Button size="small" danger disabled={!canOperateExec} onClick={()=> setBoxes(prev => prev.filter((_, i)=> i!==idx))}>删除</Button>
    ) },
  ];

  // 操作记录数量编辑校验：仅在“换标”记录上校验；减少放行，增加需同时满足
  // - 审核A：映射行数量分配上限（limitQty）
  // - 审核B：操作对象(IU)预计数量（全局 doneByIuId 口径）
  const getRecordQtyEditAllowance = (row: any, oldQty: number) => {
    try {
      const ev = String(row?.event || '');
      if (ev !== '换标') return { ok: true as const, maxIncrease: Number.POSITIVE_INFINITY };
      const old = String(row?.old_code || '').trim().toUpperCase();
      const newer = String(row?.new_code || '').trim().toUpperCase();
      const key = `${old}->${newer}`;

      // 1) 审核A：映射行上限
      const def = (mappingDefs || []).find((d:any) => String(d?.key || '') === key) || null;
      if (!def) {
        return { ok: false as const, reason: `未找到映射行（${key}），无法校验上限` };
      }
      const usedNow = Math.floor(Number(usedAgg.get(key)?.used || 0) || 0); // 含 oldQty
      const limit = (def.limitQty === undefined || def.limitQty === null) ? Number.POSITIVE_INFINITY : Math.floor(Number(def.limitQty) || 0);
      const maxIncA = (!Number.isFinite(limit) || limit < 0 || limit === Number.POSITIVE_INFINITY)
        ? Number.POSITIVE_INFINITY
        : Math.max(0, limit - usedNow);

      // 2) 审核B：IU 上限（全局 doneByIuId 口径）
      const iuId = (() => {
        const n = Math.floor(Number(row?.op_iu_id || 0));
        if (Number.isFinite(n) && n > 0) return n;
        // 兼容旧草稿：尝试从旧条码解析
        const fromOld = Math.floor(Number((oldCodeIuId as any)?.[old] || 0));
        return (Number.isFinite(fromOld) && fromOld > 0) ? fromOld : 0;
      })();
      if (!iuId) {
        return { ok: false as const, reason: '该记录缺少操作对象(IU)，无法校验预计数量' };
      }
      const doneNow = Math.floor(Number(doneByIuId.get(iuId) || 0) || 0); // 含 oldQty（若 op_iu_id 存在）

      const mt = parseMatchTargetsForAudit(def.matchTargets);
      const idx = mt.findIndex((x:any) => Math.floor(Number(x?.iu_id || 0)) === iuId);
      if (idx < 0) {
        return { ok: false as const, reason: '该记录的操作对象(IU)不在映射行的匹配对象列表中，无法校验预计数量' };
      }
      const isLast = (idx >= 0) ? (idx === mt.length - 1) : false;
      const meta = (idx >= 0) ? mt[idx] : null;
      const useAll = !!meta?.use_all_available;
      const expectedMissing = meta ? isExpectedQtyMissing(meta?.expected_qty) : true;
      const expected = expectedMissing ? Number.POSITIVE_INFINITY : Math.floor(Number(meta?.expected_qty || 0));

      const maxIncB = (isLast && useAll)
        ? Number.POSITIVE_INFINITY
        : (expected === Number.POSITIVE_INFINITY || !Number.isFinite(expected))
          ? Number.POSITIVE_INFINITY
          : Math.max(0, Number(expected) - doneNow);

      const maxIncrease = Math.min(maxIncA, maxIncB);
      return { ok: true as const, maxIncrease };
    } catch {
      return { ok: false as const, reason: '校验失败（请重试）' };
    }
  };

  // 操作记录表列：新增“序号”“事件”，并将“时间”移动到“操作”列左侧
  const buildRecordCols = (page: number, pageSize: number) => ([
    { title: '序号', width: 70, align: 'center', render: (_:any, __:any, idx:number) => ((Math.max(1, Number(page) || 1) - 1) * (Math.max(1, Number(pageSize) || 10)) + idx + 1) },
    { title: '事件', dataIndex: 'event', width: 120, render: (v:any)=> {
      const ev = String(v || '').trim() || '-';
      if (ev === '开始容器') {
        return (
          <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background:'#1677ff', borderColor:'#1677ff', color:'#fff' }}>
            开始新容器
          </Tag>
        );
      }
      if (ev === '结束容器') {
        return (
          <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background:'#fa541c', borderColor:'#fa541c', color:'#fff' }}>
            结束新容器
          </Tag>
        );
      }
      if (ev === '新增到已有容器') {
        return (
          <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background:'#13c2c2', borderColor:'#13c2c2', color:'#fff' }}>
            新增到已有容器
          </Tag>
        );
      }
      if (ev === '结束新增') {
        return (
          <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background:'#fa541c', borderColor:'#fa541c', color:'#fff' }}>
            结束新增
          </Tag>
        );
      }
      return <Tag style={{ marginInlineEnd: 0, borderRadius: 999 }}>{ev}</Tag>;
    } },
    { title: '容器系统编码', dataIndex: 'container_code', width: 240, render: (v:any)=> {
      const s = String(v || '').trim();
      if (!s) return <span style={{ color:'#999' }}>-</span>;
      return (
        <Typography.Text style={{ fontFamily:'monospace' }} copyable={{ text: s }}>
          {s}
        </Typography.Text>
      );
    } },
    { title: '旧条码', dataIndex: 'old_code', width: 240, render: (v:any) => {
      const s = String(v || '').trim();
      if (!s) return <span style={{ color:'#999' }}>-</span>;
      return (
        <Space size={6}>
          <span style={{ fontFamily:'monospace' }}>{s}</span>
          <Tooltip title="复制到操作控制台：旧条码">
            <Button
              size="small"
              type="text"
              icon={<SwapLeftOutlined style={{ color: '#1677ff' }} />}
              onClick={() => {
                setScanOld(String(s).trim().toUpperCase());
                setScanOldLocked(false);
                // 复制到旧条码时，清空新条码（若有）
                setScanNew('');
                setScanNewLocked(false);
                setScanLast('old');
                try { setTimeout(() => { try { scanOldInputRef.current?.focus?.(); } catch {} }, 0); } catch {}
              }}
            />
          </Tooltip>
        </Space>
      );
    } },
    { title: '新条码', dataIndex: 'new_code', width: 240, render: (v:any) => {
      const s = String(v || '').trim();
      if (!s) return <span style={{ color:'#999' }}>-</span>;
      return (
        <Space size={6}>
          <span style={{ fontFamily:'monospace', color: s && s !== '-' ? '#389e0d' : '#999' }}>{s}</span>
          <Tooltip title="复制到操作控制台：新条码">
            <Button
              size="small"
              type="text"
              icon={<SwapLeftOutlined style={{ color: '#1677ff' }} />}
              onClick={() => {
                setScanNew(String(s).trim().toUpperCase());
                setScanNewLocked(false);
                // 复制到新条码时，清空旧条码（若有）
                setScanOld('');
                setScanOldLocked(false);
                setScanLast('new');
                try { setTimeout(() => { try { scanNewInputRef.current?.focus?.(); } catch {} }, 0); } catch {}
              }}
            />
          </Tooltip>
        </Space>
      );
    } },
    { title: '操作对象', dataIndex: 'op_iu_code', width: 220, render: (v:any, row:any) => {
      const ev = String(row?.event || '');
      // 调整/重扫同样需要展示操作对象（用于最终内容落库到具体 IU）
      if (!(ev === '换标' || ev.startsWith('调整-') || ev.startsWith('重扫-'))) return <span style={{ color:'#999' }}>-</span>;
      const iuId = Math.floor(Number(row?.op_iu_id || 0));
      const s = String(v || '').trim()
        || (Number.isFinite(iuId) && iuId > 0 ? (iuIdToSys.get(iuId) || String((opIuSysById as any)?.[iuId] || '').trim()) : '');
      if (!s) return <span style={{ color:'#cf1322', fontWeight: 700 }}>-</span>;
      return (
        <Typography.Text style={{ fontFamily:'monospace', color:'#1677ff' }} copyable={{ text: s }}>
          {s}
        </Typography.Text>
      );
    } },
    { title: '数量', dataIndex: 'qty', width: 70, render: (v:any, row:any) => {
      const ev = String(row?.event || '');
      if (ev === '扫码失败' || ev === '开始容器' || ev === '结束容器' || ev === '新增到已有容器' || ev === '结束新增') return '-';
      // 调整/重扫/记录池：允许负数显示，但不允许在表格里直接编辑（避免破坏记账本守恒）
      if (ev.startsWith('调整-') || ev.startsWith('重扫-')) {
        const n = Math.trunc(Number(v || 0) || 0);
        if (!Number.isFinite(n) || n === 0) return <span style={{ color:'#999' }}>0</span>;
        return (
          <span style={{ fontWeight: 800, color: n < 0 ? '#cf1322' : '#389e0d' }}>
            {n > 0 ? `+${n}` : String(n)}
          </span>
        );
      }
      // 默认仅允许编辑“换标”记录数量（正整数/含0）
      if (ev !== '换标') {
        const n = Math.trunc(Number(v || 0) || 0);
        return <span style={{ fontWeight: 700 }}>{Number.isFinite(n) ? n : 0}</span>;
      }
      // 换标：仅“未结束(open)”容器内允许编辑/删除；已结束容器内换标记录只读
      const canEditRelabelRow = (() => {
        if (!canOperateExec) return false;
        const cc = String(row?.container_code || '').trim();
        if (!cc) return false;
        // 优先使用 boxes 状态；兜底：当前进行中容器也视为 open
        const hit = (boxes || []).find((b:any) => String(b?.code || '').trim() === cc) as any;
        const st = String((hit as any)?.status || '').trim();
        if (st === 'open') return true;
        const cur = String(currentBoxCode || '').trim();
        return !!cur && cur === cc;
      })();
      const cur = (() => {
        const n = Number(v);
        if (!Number.isFinite(n) || n < 0) return 0;
        return Math.floor(n);
      })();
      return (
        <InputNumber
          size="small"
          min={0}
          precision={0}
          step={1}
          value={cur as any}
          disabled={!canEditRelabelRow}
          onChange={(nv) => {
            if (!canEditRelabelRow) return;
            const next = (() => {
              const n = Number(nv);
              if (!Number.isFinite(n) || n < 0) return 0;
              return Math.floor(n);
            })();
            // 增加数量时校验映射行与操作对象的可允许数量；减少放行
            const delta = next - cur;
            if (delta > 0) {
              const allow = getRecordQtyEditAllowance(row, cur);
              if (!allow.ok) {
                message.error(String((allow as any).reason || '数量修改不允许'));
                return;
              }
              const maxInc = (allow as any).maxIncrease;
              if (Number.isFinite(maxInc) && delta > Number(maxInc)) {
                message.error(`数量超出限制：本记录最多还可增加 ${Math.max(0, Math.floor(Number(maxInc) || 0))} 件`);
                return;
              }
            }
            setRecords(prev => (prev || []).map((r:any) => (r?.id === row?.id ? ({ ...r, qty: next }) : r)));
          }}
          style={{ width: 56 }}
        />
      );
    } },
    { title: '时间', dataIndex: 'ts', width: 170, render: (v:any) => {
      const n = Number(v);
      return Number.isFinite(n) ? new Date(n).toLocaleString() : '-';
    } },
    { title: '操作', key: 'ops', width: 100, render: (_:any, row:any) => {
      const ev = String(row?.event || '');
      // 仅允许删除：换标 & 容器未结束(open)
      const canDel = (() => {
        if (!canOperateExec) return false;
        if (ev !== '换标') return false;
        const cc = String(row?.container_code || '').trim();
        if (!cc) return false;
        const hit = (boxes || []).find((b:any) => String(b?.code || '').trim() === cc) as any;
        const st = String((hit as any)?.status || '').trim();
        if (st === 'open') return true;
        const cur = String(currentBoxCode || '').trim();
        return !!cur && cur === cc;
      })();
      if (!canDel) return <span style={{ color:'#999' }}>-</span>;
      return (
        <Button
          size="small"
          danger
          onClick={() => setRecords(prev => prev.filter(r => r.id !== row.id))}
        >
          删除
        </Button>
      );
    } },
  ]);

  const clearAllRecords = useCallback(() => {
    try {
      Modal.confirm({
        title: '确认清空操作记录？',
        content: '将清空所有分页中的操作记录，此操作不可恢复。',
        okText: '清空',
        okButtonProps: { danger: true },
        cancelText: '取消',
        onOk: () => {
          setRecords([]);
          setRecordPage(1);
          setRecordDetailPage(1);
          try { setMapDetailOpen(false); setMapDetailRows([]); setMapDetailFilter({ kind: 'none' }); setMapDetailHighlightKey(''); } catch {}
          message.success('已清空操作记录');
        }
      });
    } catch {}
  }, []);

  const recordHeaderStats = useMemo(() => {
    try {
      const list = Array.isArray(records) ? records : [];
      const boxSet = new Set<string>();
      let productQty = 0;
      for (const r of list) {
        const ev = String((r as any)?.event || '');
        if (ev === '扫码失败') continue;
        const cc = String((r as any)?.container_code || '').trim();
        if (cc) boxSet.add(cc);
        if (ev === '换标') {
          const q = Math.floor(Number((r as any)?.qty || 0) || 0);
          if (Number.isFinite(q) && q > 0) productQty += q;
        }
      }
      return { recordCount: list.length, boxCount: boxSet.size, productQty };
    } catch {
      return { recordCount: (records || []).length || 0, boxCount: 0, productQty: 0 };
    }
  }, [records]);

  const recordShownRows = useMemo(() => {
    try {
      const q = String(recordSearch || '').trim().toLowerCase();
      const list: any[] = Array.isArray(records) ? records : [];
      if (!q) return list;
      const norm = (v:any) => String(v || '').trim().toLowerCase();
      return list.filter((x:any) => {
        const a = norm(x?.new_code);
        const b = norm(x?.old_code);
        const c = norm(x?.container_code);
        const iuId = Math.floor(Number(x?.op_iu_id || 0));
        const d = norm(
          x?.op_iu_code
          || (Number.isFinite(iuId) && iuId > 0 ? (iuIdToSys.get(iuId) || String((opIuSysById as any)?.[iuId] || '').trim() || `IU#${iuId}`) : '')
        );
        return a.includes(q) || b.includes(q) || c.includes(q) || d.includes(q);
      });
    } catch { return Array.isArray(records) ? records : []; }
  }, [records, recordSearch]);

  const recordRowBgById = useMemo(() => {
    const m = new Map<string, string>();
    try {
      // 交替底色（按“容器号变更”分段）：同一容器段内所有记录同色；遇到下一个容器段则切换颜色
      const colors = ['#ffffff', '#f0f5ff']; // 白 / 浅蓝（可按需调整）
      let seg = 0;
      let lastCC: string | null = null;
      const list: any[] = Array.isArray(records) ? records : [];
      for (let i = 0; i < list.length; i++) {
        const r = list[i] || {};
        const id = String(r?.id || '');
        if (!id) continue;
        const cc = String(r?.container_code || '').trim();
        if (lastCC === null) {
          lastCC = cc;
        } else if (cc !== lastCC) {
          seg += 1;
          lastCC = cc;
        }
        m.set(id, colors[seg % colors.length]);
      }
    } catch {}
    return m;
  }, [records]);

  const downloadAllRecordsExcel = useCallback(async () => {
    try {
      // 按“当前筛选结果”导出所有分页数据（不受当前页影响）
      const list: any[] = Array.isArray(recordShownRows) ? recordShownRows : [];
      if (!list.length) { message.info('暂无可下载的记录'); return; }

      let XLSX: any = null;
      try { XLSX = await import('xlsx'); } catch {}
      if (!XLSX) { message.error('缺少 Excel 导出库（xlsx）'); return; }

      const aoa: any[][] = [];
      aoa.push(['序号', '事件', '容器系统编码', '旧条码', '新条码', '数量', '时间']);
      for (let i = 0; i < list.length; i++) {
        const r = list[i] || {};
        const ev = String(r?.event || '');
        const boxText = String(r?.container_code || '').trim();
        const oldCode = String(r?.old_code || '').trim();
        const newCode = String(r?.new_code || '').trim();
        const qty = (() => {
          if (ev === '扫码失败' || ev === '开始容器' || ev === '结束容器') return '-';
          const n = Math.trunc(Number(r?.qty || 0) || 0);
          if (!Number.isFinite(n)) return '-';
          return n;
        })();
        const ts = Number(r?.ts);
        const tsText = Number.isFinite(ts) ? new Date(ts).toLocaleString() : '';
        aoa.push([i + 1, ev, boxText, oldCode, newCode, qty, tsText]);
      }

      const ws = XLSX.utils.aoa_to_sheet(aoa);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '操作记录');

      const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      const pad2 = (n: number) => String(n).padStart(2, '0');
      const d = new Date();
      const stamp = `${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}-${pad2(d.getHours())}${pad2(d.getMinutes())}${pad2(d.getSeconds())}`;
      const tag = (instrCode || (instrIdState != null ? `INSTR${instrIdState}` : '') || (taskId != null ? `TASK${taskId}` : '') || '').trim();
      const name = `操作记录${tag ? '-' + tag : ''}-${stamp}.xlsx`;

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => { try { URL.revokeObjectURL(url); } catch {} }, 3000);
    } catch (e:any) {
      message.error(e?.message || '下载失败');
    }
  }, [recordShownRows, instrCode, instrIdState, taskId]);

  // 统计信息（目标与进度）
  const stats = useMemo(() => {
    const totalBoxes = boxes.length;
    const openBox = !!String(currentBoxCode || '').trim();
    const totalOps = (records || []).reduce((s:any, r:any) => {
      if (String(r?.event || '') !== '换标') return s;
      const q = Math.floor(Number(r?.qty || 0) || 0);
      if (!Number.isFinite(q) || q <= 0) return s;
      return s + q;
    }, 0);
    return { totalBoxes, openBox, totalOps };
  }, [boxes, currentBoxCode, records]);

  const usedAgg = useMemo(() => {
    const m = new Map<string, { used: number; lastTs?: number; lastContainer?: string }>();
    try {
      (records || []).forEach((r:any) => {
        if (String(r?.event || '') !== '换标') return;
        const old = String(r?.old_code || '').trim().toUpperCase();
        const newer = String(r?.new_code || '').trim().toUpperCase();
        if (!old || !newer || newer === '-') return;
        const key = `${old}->${newer}`;
        const cur = m.get(key) || { used: 0 };
        cur.used += Number(r?.qty || 0) || 0;
        if (!cur.lastTs || (Number(r?.ts || 0) > Number(cur.lastTs))) {
          cur.lastTs = Number(r?.ts || 0);
          cur.lastContainer = String(r?.container_code || '').trim() || cur.lastContainer;
        }
        m.set(key, cur);
      });
    } catch {}
    return m;
  }, [records]);

  // 映射维度下，按“匹配操作对象(IU)”拆分：已完成数量（qty 求和）
  // key = `${old}->${new}`；value = Map<iu_id, done_qty>
  const mappingDoneByKeyIu = useMemo(() => {
    const m = new Map<string, Map<number, number>>();
    try {
      const list: any[] = Array.isArray(records) ? records : [];
      for (const r of list) {
        if (String(r?.event || '') !== '换标') continue;
        const old = String(r?.old_code || '').trim().toUpperCase();
        const newer = String(r?.new_code || '').trim().toUpperCase();
        if (!old || !newer || newer === '-') continue;
        const key = `${old}->${newer}`;
        const qty = Math.floor(Number(r?.qty || 0) || 0);
        if (!Number.isFinite(qty) || qty <= 0) continue;
        // 1) 优先使用记录里写入的最终落库对象
        let iu = Math.floor(Number((r as any)?.op_iu_id || 0));
        // 2) 兼容旧草稿：尝试从旧条码解析
        if (!iu) {
          if (old && old !== '-') {
            iu = Math.floor(Number((oldCodeIuId as any)?.[old] || 0));
          } else {
            const ov = Math.floor(Number((mappingKeyIuOverride as any)?.[key] || 0));
            if (ov > 0) iu = ov;
            else {
              const ovs: any[] = Array.isArray((mappingKeyIuOverrideMulti as any)?.[key]) ? (mappingKeyIuOverrideMulti as any)[key] : [];
              const first = Math.floor(Number(ovs?.[0] || 0));
              if (first > 0) iu = first;
            }
          }
        }
        if (!Number.isFinite(iu) || iu <= 0) continue;
        const inner = m.get(key) || new Map<number, number>();
        inner.set(iu, (inner.get(iu) || 0) + qty);
        m.set(key, inner);
      }
    } catch {}
    return m;
  }, [records, oldCodeIuId, mappingKeyIuOverride, mappingKeyIuOverrideMulti]);

  // 操作对象(IU)维度：已完成数量（qty 求和，全局口径）
  // 说明：当同一 IU 被多个“新旧条码映射记录”共享时，IU 的已完成数量必须在所有映射行同步更新。
  const doneByIuId = useMemo(() => {
    const m = new Map<number, number>();
    try {
      const list: any[] = Array.isArray(records) ? records : [];
      for (const r of list) {
        if (String(r?.event || '') !== '换标') continue;
        const qty = Math.floor(Number(r?.qty || 0) || 0);
        if (!Number.isFinite(qty) || qty <= 0) continue;
        // 1) 优先使用记录里写入的最终落库对象
        let iu = Math.floor(Number((r as any)?.op_iu_id || 0));
        // 2) 兼容旧草稿：若记录未写入 op_iu_id，尝试从旧条码解析
        if (!iu) {
          const old = String(r?.old_code || '').trim().toUpperCase();
          if (old && old !== '-') {
            iu = Math.floor(Number((oldCodeIuId as any)?.[old] || 0));
          }
        }
        if (!Number.isFinite(iu) || iu <= 0) continue;
        m.set(iu, (m.get(iu) || 0) + qty);
      }
    } catch {}
    return m;
  }, [records, oldCodeIuId]);
  const mapByOld = useMemo(() => {
    const m = new Map<string, MappingDef[]>();
    try {
      (mappingDefs || []).forEach((d) => {
        const k = String(d.old || '').toUpperCase();
        if (!k) return;
        const arr = m.get(k) || [];
        arr.push(d);
        m.set(k, arr);
      });
    } catch {}
    return m;
  }, [mappingDefs]);
  const mapByNew = useMemo(() => {
    const m = new Map<string, MappingDef[]>();
    try {
      (mappingDefs || []).forEach((d) => {
        const k = String(d.newer || '').toUpperCase();
        if (!k) return;
        const arr = m.get(k) || [];
        arr.push(d);
        m.set(k, arr);
      });
    } catch {}
    return m;
  }, [mappingDefs]);
  const mappingProgressRows = useMemo(() => {
    try {
      const q = String(mappingSearch || '').trim().toLowerCase();
      const base = (mappingDefs || []).map((d) => {
        const agg = usedAgg.get(d.key) || { used: 0 };
        const used = Number(agg.used || 0) || 0;
        const limit = (d.limitQty === undefined ? Infinity : Number(d.limitQty));
        const remaining = (limit === Infinity) ? Infinity : Math.max(0, limit - used);
        const status = limit === Infinity
          ? '不限'
          : used >= limit ? (used > limit ? '超额' : '已达上限')
          : used > 0 ? '进行中' : '未开始';
        return {
          __key: d.key,
          old: d.old,
          newer: d.newer,
          newText: d.newText,
          limitQty: d.limitQty,
          matchTargets: d.matchTargets,
          remark: d.remark,
          used,
          remaining,
          status,
          lastContainer: agg.lastContainer,
          lastTs: agg.lastTs,
          rowNo: d.rowNo,
        };
      });
      const filtered = !q ? base : base.filter((r:any) => {
        const s = `${r.old}|${r.newer}|${r.newText || ''}|${r.remark || ''}`.toLowerCase();
        return s.includes(q);
      });
      // 固定顺序：始终按 BARCODE_MAPPING 的原始行号升序（保持基线文件顺序不变）
      // 搜索过滤不改变顺序，仅做子集筛选。
      return filtered.sort((a:any, b:any) => {
        const ra = (a?.rowNo == null) ? Number.MAX_SAFE_INTEGER : Number(a.rowNo);
        const rb = (b?.rowNo == null) ? Number.MAX_SAFE_INTEGER : Number(b.rowNo);
        if (ra !== rb) return ra - rb;
        return String(a?.__key || '').localeCompare(String(b?.__key || ''));
      });
    } catch { return []; }
  }, [mappingDefs, usedAgg, mappingSearch]);
  useEffect(() => { try { setMappingPage(1); } catch {} }, [mappingSearch]);
  const mappingSummary = useMemo(() => {
    try {
      const total = (mappingDefs || []).length;
      let unlimited = 0, limited = 0, reached = 0, over = 0;
      (mappingDefs || []).forEach((d) => {
        const limit = d.limitQty === undefined ? Infinity : Number(d.limitQty);
        if (limit === Infinity) { unlimited += 1; return; }
        limited += 1;
        const used = Number((usedAgg.get(d.key)?.used || 0));
        if (used > limit) over += 1;
        else if (used >= limit) reached += 1;
      });
      const cur = String(currentBoxCode || '').trim();
      const containersDone = Array.from(new Set((records || []).map((r:any)=> String(r?.container_code || '').trim()).filter(Boolean)))
        .filter((c) => !c.startsWith('POOL-'))
        .filter((c) => !cur || c !== cur);
      const boxesDoing = cur ? 1 : 0;
      return { total, unlimited, limited, reached, over, boxesDone: containersDone.length, boxesDoing };
    } catch { return { total: 0, unlimited: 0, limited: 0, reached: 0, over: 0, boxesDone: 0, boxesDoing: 0 }; }
  }, [mappingDefs, usedAgg, records, currentBoxCode]);

  // “按容器统计”表格数据源（总进度表的一部分）：抽出为 useMemo 以便做阶段快照
  const boxStatRows = useMemo(() => {
    try {
      // records 仍用于“记录数/最近时间/查看记录”；新标签种类&总件数以 records 记账本净额为准（换标+调整+重扫）
      const byCode = new Map<string, any>();
      // 1) 先按 boxes 建立行（保证无 records 也可显示）
      (boxes || []).forEach((b:any) => {
        const cc = String(b?.code || '').trim();
        if (!cc) return;
        byCode.set(cc, { container_code: cc, ops: 0, lastTs: 0 });
      });
      // 2) records 聚合（ops/lastTs）
      (records || []).forEach((r:any) => {
        const cc = String(r?.container_code || '').trim();
        if (!cc) return;
        if (cc === String(poolCode || '').trim()) return; // 调整池不作为容器行（单独汇总）
        const cur = byCode.get(cc) || { container_code: cc, ops: 0, lastTs: 0 };
        cur.ops += 1;
        cur.lastTs = Math.max(cur.lastTs || 0, Number(r?.ts || 0) || 0);
        byCode.set(cc, cur);
      });
      // 2.5) draft updated_at 也计入“最近时间/排序”（支持结束后调整/重扫不写 records 的场景）
      try {
        Object.keys(outputDraftByCode || {}).forEach((k) => {
          const cc = String(k || '').trim();
          if (!cc) return;
          if (cc === String(poolCode || '').trim()) return;
          const cur = byCode.get(cc) || { container_code: cc, ops: 0, lastTs: 0 };
          const u = (outputDraftByCode as any)?.[cc]?.updated_at;
          const ts = u ? Number(new Date(u as any).getTime()) : 0;
          if (Number.isFinite(ts) && ts > 0) cur.lastTs = Math.max(cur.lastTs || 0, ts);
          byCode.set(cc, cur);
        });
      } catch {}
      // 3) 新条码汇总（records 净额）
      const contentEvOk = (ev:any) => {
        const e = String(ev || '').trim();
        return e === '换标'
          || e === '调整-取出' || e === '调整-放入'
          || e === '重扫-冲销' || e === '重扫-加回';
      };
      const aggByBox = new Map<string, Map<string, number>>();
      (records || []).forEach((r:any) => {
        const cc = String(r?.container_code || '').trim();
        if (!cc) return;
        if (cc === String(poolCode || '').trim()) return;
        if (!contentEvOk(r?.event)) return;
        const newc = String(r?.new_code || '').trim().toUpperCase();
        if (!newc || newc === '-') return;
        const q = Math.trunc(Number(r?.qty || 0) || 0);
        if (!Number.isFinite(q) || q === 0) return;
        const mm = aggByBox.get(cc) || new Map<string, number>();
        mm.set(newc, (mm.get(newc) || 0) + q);
        aggByBox.set(cc, mm);
      });
      const rows = Array.from(byCode.values()).map((it:any) => {
        const cc = String(it?.container_code || '').trim();
        const hit = (boxes || []).find((b:any) => String(b?.code || '').trim() === cc) as any;
        const st = String(hit?.status || '').trim().toLowerCase();
        const status = (st === 'open' || st === 'closed' || st === 'cancelled') ? st : '';
        const statusZh = status === 'open' ? '进行中' : (status === 'cancelled' ? '已作废' : '已结束');
        const mm = aggByBox.get(cc) || new Map<string, number>();
        let newKinds = 0;
        let qty = 0;
        mm.forEach((v:number) => {
          const n = Math.floor(Number(v || 0) || 0);
          if (Number.isFinite(n) && n > 0) { qty += n; newKinds += 1; }
        });
        // 补充容器重量/尺寸（用于阶段快照展示；若缺失则为 undefined/-）
        const meta: any = (boxMeta as any)?.[cc] || {};
        const wg = Number(meta?.weight_g);
        const wkg = (() => {
          const v = Number(meta?.weight_kg);
          if (Number.isFinite(v) && v > 0) return Math.ceil(v);
          if (Number.isFinite(wg) && wg > 0) return Math.ceil(wg / 1000);
          return undefined;
        })();
        const l = Math.floor(Number(meta?.length_cm || 0) || 0);
        const w = Math.floor(Number(meta?.width_cm || 0) || 0);
        const h = Math.floor(Number(meta?.height_cm || 0) || 0);
        const dims_cm_text = (l > 0 && w > 0 && h > 0) ? `${l}×${w}×${h}` : undefined;
        return { ...it, status, status_zh: statusZh, newKinds, qty, weight_kg: wkg, dims_cm_text };
      }).sort((a:any,b:any)=> Number(b.lastTs || 0) - Number(a.lastTs || 0));
      return rows;
    } catch {
      return [];
    }
  }, [boxes, records, outputDraftByCode, poolCode, boxMeta]);
  const boxStatDirtyCount = useMemo(() => {
    try {
      const set = new Set<string>();
      (Array.isArray(boxStatRows) ? boxStatRows : []).forEach((r: any) => {
        const cc = String(r?.container_code || '').trim();
        if (!cc) return;
        const hit = (boxes || []).find((b: any) => String(b?.code || '').trim() === cc) as any;
        const st = String((hit as any)?.status || '').trim().toLowerCase();
        if (st === 'cancelled') return;
        if ((labelDirtyByCode as any)?.[cc]) set.add(cc);
        const qty = Math.floor(Number(r?.qty || 0) || 0);
        if (qty <= 0) set.add(cc);
      });
      return set.size;
    } catch {
      return 0;
    }
  }, [boxStatRows, labelDirtyByCode, boxes]);
  const hasBoxStatDirty = boxStatDirtyCount > 0;
  const voidEmptyContainer = useCallback(async (codeAny: any) => {
    const code = String(codeAny || '').trim();
    if (!code) return;
    const instrId = Number(instrIdState || 0);
    if (!Number.isFinite(instrId) || instrId <= 0) {
      message.error('缺少指令ID，无法作废容器');
      return;
    }
    const hit = (Array.isArray(boxes) ? boxes : []).find((b: any) => String(b?.code || '').trim() === code);
    const st = String(hit?.status || '').trim().toLowerCase();
    if (st === 'cancelled') return;
    if (st === 'open') {
      message.warning('进行中的容器不能作废');
      return;
    }
    const row = (Array.isArray(boxStatRows) ? boxStatRows : []).find((r: any) => String(r?.container_code || '').trim() === code);
    const qty = Math.floor(Number(row?.qty || 0) || 0);
    if (qty > 0) {
      message.warning('仅空容器可作废');
      return;
    }
    const confirmed = await new Promise<boolean>((resolve) => {
      Modal.confirm({
        title: '确认作废空容器',
        content: (
          <div style={{ lineHeight: 1.8 }}>
            <div>将作废以下空容器：</div>
            <div style={{ marginTop: 4, fontFamily: 'monospace' }}>{code}</div>
          </div>
        ),
        okText: '确认作废',
        cancelText: '取消',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
    if (!confirmed) return;
    setVoidingContainerCodes((prev) => {
      const next = new Set(prev || []);
      next.add(code);
      return next;
    });
    try {
      const resp = await request.post('/api/v1/storage/output-containers/void/', {
        instruction_id: instrId,
        codes: [code],
        defer_inventory_settlement: true,
      });
      const voided = Array.isArray(resp?.data?.voided_codes) ? resp.data.voided_codes : [];
      const blocked = Array.isArray(resp?.data?.blocked_codes) ? resp.data.blocked_codes : [];
      const voidedSet = new Set((voided || []).map((x: any) => String(x || '').trim()).filter(Boolean));
      if (!voidedSet.has(code)) {
        const reason = blocked.find((x: any) => String(x?.code || '').trim() === code)?.reason;
        message.warning(reason ? `作废失败：${reason}` : '作废失败');
        return;
      }
      setBoxes((prev) => (Array.isArray(prev) ? prev.map((b: any) => {
        const cc = String(b?.code || '').trim();
        return voidedSet.has(cc) ? ({ ...b, status: 'cancelled' }) : b;
      }) : prev));
      setOutputDraftByCode((prev) => {
        const base = (prev && typeof prev === 'object') ? prev : {};
        const cur = (base as any)?.[code];
        if (!cur) return prev as any;
        return {
          ...(base as any),
          [code]: {
            ...(cur as any),
            status: 'cancelled',
            updated_at: new Date().toISOString(),
          },
        } as any;
      });
      message.success(`已作废空容器：${code}`);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || e?.message || '作废容器失败');
    } finally {
      setVoidingContainerCodes((prev) => {
        const next = new Set(prev || []);
        next.delete(code);
        return next;
      });
    }
  }, [instrIdState, boxes, boxStatRows]);

  // 输出容器 Draft：按 instruction 拉取（含 items），用于“按容器统计/最终提交覆盖”
  const loadOutputDrafts = useCallback(async () => {
    try {
      const instrId = Number(instrIdState);
      if (!Number.isFinite(instrId) || instrId <= 0) return;
      setOutputDraftLoading(true);
      const resp = await request.get('/api/v1/storage/output-containers/drafts/', {
        params: { instruction_id: instrId, include_items: 1 }
      } as any);
      const drafts: any[] = Array.isArray(resp?.data?.drafts) ? resp.data.drafts : [];
      const next: Record<string, OutputContainerDraftRow> = {};
      for (const d of drafts) {
        const code = String(d?.code || '').trim();
        if (!code) continue;
        next[code] = {
          ...(d as any),
          code,
          items: Array.isArray((d as any)?.items) ? (d as any).items : undefined,
        } as any;
      }
      setOutputDraftByCode(next);
    } catch (e:any) {
      message.error(e?.response?.data?.detail || e?.message || '加载容器草稿失败');
    } finally {
      setOutputDraftLoading(false);
    }
  }, [instrIdState]);

  const loadOneOutputDraft = useCallback(async (codeAny: string) => {
    const code = String(codeAny || '').trim();
    if (!code) return;
    try {
      const instrId = Number(instrIdState);
      if (!Number.isFinite(instrId) || instrId <= 0) return;
      const resp = await request.get(`/api/v1/storage/output-containers/drafts/by-code/${encodeURIComponent(code)}/`, {
        params: { instruction_id: instrId }
      } as any);
      const found = !!resp?.data?.found;
      if (!found) return;
      const d = resp?.data?.draft || {};
      const items = Array.isArray(resp?.data?.items) ? resp.data.items : [];
      const cc = String(d?.code || code).trim();
      if (!cc) return;
      setOutputDraftByCode(prev => ({ ...(prev || {}), [cc]: { ...(d as any), code: cc, items } as any }));
    } catch {}
  }, [instrIdState]);

  useEffect(() => {
    if (!canOperateExec) return;
    if (!instrIdState) return;
    loadOutputDrafts();
  }, [canOperateExec, instrIdState, loadOutputDrafts]);

  // 操作控制台默认选中的容器：优先当前打开容器，其次最近一个已结束容器
  useEffect(() => {
    try {
      const cur = String(currentBoxCode || '').trim();
      const picked = String(consoleBoxCode || '').trim();
      if (picked) return;
      if (cur) { setConsoleBoxCode(cur); return; }
      const closed = (boxes || []).find((b:any) => String(b?.status || '') === 'closed');
      const cc = String(closed?.code || '').trim();
      if (cc) setConsoleBoxCode(cc);
    } catch {}
  }, [currentBoxCode, consoleBoxCode, boxes]);

  // “操作目标与总进度”标题行汇总（已完成容器 + 最终体积/重量 + 已完成数量/目标数量/完成率）
  const overallHeaderStats = useMemo(() => {
    try {
      const closedCodes = (boxes || [])
        .filter((b:any) => String(b?.status || '').trim() === 'closed')
        .map((b:any) => String(b?.code || '').trim())
        .filter(Boolean);

      // 仅统计“内容不为空”的已结束容器：按 records 记账本净额聚合（支持调整/重扫/合并导致的空容器）
      const contentEvOk = (ev:any) => {
        const e = String(ev || '').trim();
        return e === '换标'
          || e === '调整-取出' || e === '调整-放入'
          || e === '重扫-冲销' || e === '重扫-加回';
      };
      const aggByBox = new Map<string, Map<string, number>>();
      (records || []).forEach((r:any) => {
        const cc = String(r?.container_code || '').trim();
        if (!cc) return;
        if (!contentEvOk(r?.event)) return;
        const newc = String(r?.new_code || '').trim().toUpperCase();
        if (!newc || newc === '-') return;
        const q = Math.trunc(Number(r?.qty || 0) || 0);
        if (!Number.isFinite(q) || q === 0) return;
        const mm = aggByBox.get(cc) || new Map<string, number>();
        mm.set(newc, (mm.get(newc) || 0) + q);
        aggByBox.set(cc, mm);
      });
      const nonEmptyClosedCodes = (closedCodes || []).filter((cc:string) => {
        const mm = aggByBox.get(cc) || new Map<string, number>();
        let qty = 0;
        mm.forEach((v:number) => {
          const n = Math.floor(Number(v || 0) || 0);
          if (Number.isFinite(n) && n > 0) qty += n;
        });
        return qty > 0;
      });

      let totalVolM3 = 0;
      let totalKg = 0;
      for (const code of nonEmptyClosedCodes) {
        const meta: any = (boxMeta as any)?.[code] || {};
        const l = Math.floor(Number(meta?.length_cm || 0));
        const w = Math.floor(Number(meta?.width_cm || 0));
        const h = Math.floor(Number(meta?.height_cm || 0));
        if (l > 0 && w > 0 && h > 0) totalVolM3 += (l * w * h) / 1_000_000; // cm^3 -> m^3

        const kg = (() => {
          const v = Number(meta?.weight_kg);
          if (Number.isFinite(v) && v > 0) return v;
          const g = Number(meta?.weight_g);
          if (Number.isFinite(g) && g > 0) return g / 1000;
          const lb = Number(meta?.weight_lb);
          if (Number.isFinite(lb) && lb > 0) return lb / 2.2046226218;
          return 0;
        })();
        if (kg > 0) totalKg += kg;
      }

      // 目标总数量：使用指令详情“预计操作总数量”（targets_detail.expected_qty 汇总）
      const targetQty = (instrExpectedTotalQty != null && Number.isFinite(Number(instrExpectedTotalQty)))
        ? Number(instrExpectedTotalQty)
        : 0;

      const doneQty = Math.floor(Number(stats?.totalOps || 0) || 0);
      const rate = (targetQty > 0) ? (doneQty / targetQty) * 100 : null;

      const fmt3 = (n:number) => {
        if (!Number.isFinite(n) || n <= 0) return '0';
        return String(Number(n.toFixed(3)));
      };
      const fmt2 = (n:number) => {
        if (!Number.isFinite(n) || n <= 0) return '0';
        return String(Number(n.toFixed(2)));
      };
      const fmt1 = (n:number) => {
        if (!Number.isFinite(n)) return '-';
        return String(Number(n.toFixed(1)));
      };

      return {
        closedCount: nonEmptyClosedCodes.length,
        volM3Text: fmt3(totalVolM3),
        kgText: fmt2(totalKg),
        doneQty,
        targetQtyText: (targetQty > 0) ? String(Math.floor(targetQty)) : '-',
        rateValue: (rate == null || !Number.isFinite(rate)) ? null : Number(rate),
        rateText: (rate == null || !Number.isFinite(rate)) ? '-' : `${fmt1(rate)}%`,
      };
    } catch {
      return { closedCount: 0, volM3Text: '0', kgText: '0', doneQty: 0, targetQtyText: '-', rateValue: null, rateText: '-' };
    }
  }, [boxes, boxMeta, records, stats?.totalOps, instrExpectedTotalQty]);

  // 阶段快照：暴露“总进度表”核心数据，供执行页在“提交记录并完成”时固化快照
  // 注意：必须放在 poolSummary 声明之后，避免 TDZ（Block-scoped variable used before declaration）

  // ========= “按条码映射进度”：预取旧条码->库存单元ID，以及 IU 概览（仅当前页，避免过多请求） =========
  const ensureIuPreviews = useCallback(async (iuIds: number[]) => {
    try {
      const uniq = Array.from(new Set((iuIds || []).map((x:any) => Math.floor(Number(x) || 0)).filter((n:number) => Number.isFinite(n) && n > 0)));
      if (!uniq.length) return;
      const missing = uniq.filter((id:number) => !(id in (iuPreviewById || {})));
      if (!missing.length) return;
      // 限制每次最多拉 20 个，避免页面卡顿
      const batch = missing.slice(0, 20);
      for (const id of batch) {
        try {
          const resp = await getInventoryUnitDetail(id);
          const data = (resp as any)?.data;
          if (data && Number(data?.id || 0) > 0) {
            setIuPreviewById(prev => ({ ...(prev || {}), [Number(data.id)]: data }));
          }
        } catch {}
      }
    } catch {}
  }, [iuPreviewById]);

  useEffect(() => {
    try {
      const rows: any[] = Array.isArray(mappingProgressRows) ? mappingProgressRows : [];
      const start = Math.max(0, (Number(mappingPage || 1) - 1) * Number(mappingPageSize || 10));
      const end = Math.min(rows.length, start + Number(mappingPageSize || 10));
      const pageRows = rows.slice(start, end);
      const olds = pageRows
        .map((r:any) => String(r?.old || '').trim().toUpperCase())
        .filter((c:string) => !!c && c !== '-');
      if (olds.length) ensureOldCodeWeights(olds);
    } catch {}
  }, [mappingProgressRows, mappingPage, mappingPageSize, ensureOldCodeWeights]);

  useEffect(() => {
    try {
      const rows: any[] = Array.isArray(mappingProgressRows) ? mappingProgressRows : [];
      const start = Math.max(0, (Number(mappingPage || 1) - 1) * Number(mappingPageSize || 10));
      const end = Math.min(rows.length, start + Number(mappingPageSize || 10));
      const pageRows = rows.slice(start, end);
      const olds = pageRows
        .map((r:any) => String(r?.old || '').trim().toUpperCase())
        .filter((c:string) => !!c && c !== '-');
      const iuIds = olds
        .map((oc:string) => Number((oldCodeIuId as any)?.[oc] || 0))
        .filter((n:number) => Number.isFinite(n) && n > 0);
      if (iuIds.length) ensureIuPreviews(iuIds);
    } catch {}
  }, [mappingProgressRows, mappingPage, mappingPageSize, oldCodeIuId, ensureIuPreviews]);

  // ========= 初次打开聚合UI：审核 BARCODE_MAPPING 是否“每条映射都能明确到操作目标库存单元” =========
  const loadTargetInventoryUnits = useCallback(async () => {
    try {
      const instrId = Number(instrIdState);
      if (!Number.isFinite(instrId)) return;
      setTargetIuLoading(true);
      const resp = await request.get(`/api/v1/instruction/instructions/${instrId}/relabel-pack/target-inventory-units/`);
      const items: any[] = Array.isArray(resp?.data?.items) ? resp.data.items : [];
      // 兜底：若接口未返回 expected_qty/use_all_available，则从指令详情 targets_detail 补齐
      const metaById = new Map<number, { use_all_available?: boolean; expected_qty?: number | null; system_barcode?: string }>();
      try {
        const ins = await request.get(`/api/v1/instruction/instructions/${instrId}/`);
        const td = (ins as any)?.data || {};
        const tds: any[] = Array.isArray(td?.targets_detail) ? td.targets_detail : [];
        const getAnyUnitId = (r:any) => { try { return r?.inventory_unit_id ?? r?.target?.inventory_unit_id ?? r?.snapshot?.inventory_unit_id; } catch { return undefined; } };
        const getAnyUseAll = (r:any) => {
          try {
            const v = r?.use_all_available ?? r?.target?.use_all_available ?? r?.snapshot?.use_all_available;
            return (v === undefined || v === null) ? undefined : !!v;
          } catch { return undefined; }
        };
        const getAnyExpectedQty = (r:any): number | null | undefined => {
          try {
            const v = r?.expected_qty ?? r?.target?.expected_qty ?? r?.snapshot?.expected_qty;
            if (v === undefined) return undefined;
            if (v === null || v === '') return null;
            const n = Number(v);
            return Number.isFinite(n) ? n : null;
          } catch { return undefined; }
        };
        const getAnySys = (r:any) => {
          try {
            const sys = r?.inventory_unit_system_barcode ?? r?.target?.inventory_unit_system_barcode ?? r?.snapshot?.inventory_unit_system_barcode;
            return String(sys || '').trim();
          } catch { return ''; }
        };
        tds.forEach((t:any) => {
          const uid = Math.floor(Number(getAnyUnitId(t) || 0));
          if (!uid) return;
          const useAll = getAnyUseAll(t);
          const eqVal = getAnyExpectedQty(t);
          const sys = getAnySys(t);
          metaById.set(uid, { use_all_available: useAll, expected_qty: (eqVal === undefined ? undefined : eqVal), system_barcode: sys || undefined });
        });
      } catch {}
      const opts = items
        .map((x:any) => ({
          id: Math.floor(Number(x?.id || 0)),
          system_barcode: (() => {
            const sys = String(x?.system_barcode || '').trim();
            if (sys) return sys;
            const m = metaById.get(Math.floor(Number(x?.id || 0)));
            return String(m?.system_barcode || '').trim();
          })(),
          use_all_available: (() => {
            if (x?.use_all_available === undefined || x?.use_all_available === null) {
              const m = metaById.get(Math.floor(Number(x?.id || 0)));
              return !!m?.use_all_available;
            }
            return !!x?.use_all_available;
          })(),
          expected_qty: (() => {
            const raw = x?.expected_qty;
            const n = (raw == null || raw === '') ? NaN : Number(raw);
            if (Number.isFinite(n)) return n;
            const m = metaById.get(Math.floor(Number(x?.id || 0)));
            return (m?.expected_qty == null) ? null : Number(m.expected_qty);
          })(),
        }))
        .filter((x:any) => Number.isFinite(x.id) && x.id > 0);
      setTargetIuOptions(opts);
    } catch (e:any) {
      message.error(e?.response?.data?.detail || e?.message || '获取操作目标库存单元列表失败');
      setTargetIuOptions([]);
    } finally {
      setTargetIuLoading(false);
    }
  }, [instrIdState]);

  useEffect(() => {
    (async () => {
      try {
        const instrId = Number(instrIdState);
        if (!Number.isFinite(instrId)) return;
        if (!mappingDefs || mappingDefs.length === 0) return;
        if (mappingAuditOk) return;
        if (mappingAuditDismissed) return;
        // 弹窗已打开时，不重复跑完整性审核（避免重入导致循环请求/闪烁）
        if (mappingAuditOpen) return;
        // 防止 async useEffect 并发重入：state 更新会触发再次执行
        if (mappingAuditRunningRef.current) return;
        mappingAuditRunningRef.current = true;

        // 1) 批量解析旧条码 -> IU（分批 200，避免一次性过大请求）
        const oldsAll = Array.from(new Set(
          (mappingDefs || [])
            .map((d:any) => String(d?.old || '').trim().toUpperCase())
            .filter((c:string) => !!c && c !== '-')
        ));
        const chunkSize = 200;
        for (let i = 0; i < oldsAll.length; i += chunkSize) {
          const chunk = oldsAll.slice(i, i + chunkSize);
          if (chunk.length) {
            try { await ensureOldCodeWeights(chunk); } catch {}
          }
          // 给 UI 一个喘息机会，避免卡顿
          if (i + chunkSize < oldsAll.length) {
            await new Promise(res => setTimeout(res, 50));
          }
        }

        // 2) 生成待人工选择列表：旧条码缺失(-) 或 旧条码无法唯一匹配到 IU
        const pending: any[] = [];
        for (const d of (mappingDefs || [])) {
          const key = String((d as any)?.key || `${d?.old}->${d?.newer}`);
          const oldc = String((d as any)?.old || '').trim().toUpperCase() || '-';
          const newer = String((d as any)?.newer || '').trim().toUpperCase() || '-';
          const rowNo = (d as any)?.rowNo;
          // 方案A：只要该映射行“存在>=1个匹配操作对象”，就视为可执行（不要求人工选择单一IU）
          // 仅当不存在任何匹配对象时（0个），才进入人工处理列表。
          const mtRaw = String((d as any)?.matchTargets || '').trim();
          const hasAnyTarget = (() => {
            try {
              if (!mtRaw || mtRaw === '-' || mtRaw === '[]') return false;
              const js = JSON.parse(mtRaw);
              const arr = Array.isArray(js) ? js : [];
              const ids = arr.map((x:any)=> Math.floor(Number(x?.iu_id || 0))).filter((n:number)=> Number.isFinite(n) && n > 0);
              return ids.length > 0;
            } catch { return false; }
          })();
          if (hasAnyTarget) continue;

          // 若用户已人工指定 override（用于“0对象兜底”），则视为已明确
          const override = Number((mappingKeyIuOverride as any)?.[key] || 0);
          if (Number.isFinite(override) && override > 0) continue;
          if (oldc === '-' || !oldc) {
            pending.push({ key, rowNo, old: '-', newer, reason: '缺少旧条码（人工分拣需手动选择库存单元）' });
            continue;
          }
          const iuId = (oldCodeIuId as any)?.[oldc];
          const iu = Number(iuId);
          if (Number.isFinite(iu) && iu > 0) continue; // IU 已明确（包括 no_weight 情况）
          const rr = (oldCodeResolveReason as any)?.[oldc];
          const rs = String(rr?.reason || 'not_found');
          const reasonText = rs === 'duplicate_match'
            ? '未匹配到任何操作对象（旧条码不唯一：匹配到多个库存单元，需手动选择）'
            : rs === 'not_found'
              ? '未匹配到任何操作对象（旧条码未匹配到本指令操作目标，需手动选择）'
              : rs === 'no_targets'
                ? '未匹配到任何操作对象（本指令未配置操作目标，需先配置或手动选择）'
                : `未匹配到任何操作对象（解析失败：${rs}，需手动选择）`;
          pending.push({ key, rowNo, old: oldc, newer, reason: reasonText, candidates: rr?.inventory_unit_ids });
        }

        if (pending.length > 0) {
          // 加载可选 IU 列表，并打开审核弹窗
          await loadTargetInventoryUnits();
          // 为下拉框展示“内容项”预取 IU 概览（限制数量，避免过多请求）
          try {
            const ids = (targetIuOptions || []).map((x:any) => Math.floor(Number(x?.id || 0))).filter((n:number) => Number.isFinite(n) && n > 0);
            if (ids.length) ensureIuPreviews(ids.slice(0, 60));
          } catch {}
          setMappingAuditRows(pending);
          setMappingAuditOpen(true);
          // 一旦需要弹窗，则认为“未被用户关闭”
          try { setMappingAuditDismissed(false); } catch {}
          // 默认把已知候选(duplicate_match)里第一个作为预填（若在可选列表里）
          try {
            setMappingKeyIuOverride(prev => {
              const next = { ...(prev || {}) };
              for (const r of pending) {
                const k = String(r?.key || '');
                if (!k || next[k]) continue;
                const cand = Array.isArray(r?.candidates) ? r.candidates : [];
                const c0 = cand.length ? Math.floor(Number(cand[0]) || 0) : 0;
                if (c0 > 0) next[k] = c0;
              }
              return next;
            });
          } catch {}
        } else {
          // 全部可解析：直接标记通过
          setMappingAuditOk(true);
          setMappingAuditOpen(false);
        }
      } catch {}
      finally {
        mappingAuditRunningRef.current = false;
      }
    })();
  }, [instrIdState, mappingDefs, mappingAuditOk, mappingAuditDismissed, mappingAuditOpen, ensureOldCodeWeights, oldCodeIuId, oldCodeResolveReason, loadTargetInventoryUnits, mappingKeyIuOverride]);

  // 结束容器弹窗：统计信息（仅用于展示，不阻断）
  const endBoxStats = useMemo(() => {
    try {
      const cc = String(endBoxDraft?.code || '').trim();
      if (!cc) return null;
      const list: any[] = Array.isArray(records) ? records : [];
      const inBox = list.filter((r:any) => String(r?.container_code || '').trim() === cc);
      const startRec = inBox.find((r:any) => String(r?.event || '') === '开始容器') || null;
      const startTs = startRec ? Number(startRec?.ts || 0) : 0;
      const startText = startTs > 0 ? new Date(startTs).toLocaleString() : '-';
      const swap = inBox.filter((r:any) => String(r?.event || '') === '换标');
      const fail = inBox.filter((r:any) => String(r?.event || '') === '扫码失败');
      let totalQty = 0;
      const oldSet = new Set<string>();
      const newSet = new Set<string>();
      for (const r of swap) {
        const q = Math.floor(Number(r?.qty || 0) || 0);
        if (Number.isFinite(q) && q > 0) totalQty += q;
        const oc = String(r?.old_code || '').trim().toUpperCase() || '-';
        const nc = String(r?.new_code || '').trim().toUpperCase() || '-';
        if (oc) oldSet.add(oc);
        if (nc) newSet.add(nc);
      }
      return {
        code: cc,
        containerType: String(endBoxDraft?.container_type || 'box'),
        startText,
        swapCount: swap.length,
        failCount: fail.length,
        totalQty,
        oldKinds: oldSet.size,
        newKinds: newSet.size,
      };
    } catch {
      return null;
    }
  }, [endBoxDraft, records]);

  // 结束容器弹窗：理论总重量（以弹窗中可编辑的净重为准；若任一内容项缺少旧条码或缺少重量，则显示无法估算）
  const endBoxTheory = useMemo(() => {
    try {
      const tareG = Number(endBoxDraft?.weight_g);
      if (!Number.isFinite(tareG) || tareG <= 0) return { ok: false as const };
      const rows = Array.isArray(endBoxContentRows) ? endBoxContentRows : [];
      let contentG = 0;
      for (const r of rows) {
        const oc = String(r?.old_code || '').trim().toUpperCase();
        const q = Math.floor(Number(r?.qty || 0) || 0);
        if (!Number.isFinite(q) || q <= 0) continue;
        if (!oc || oc === '-') return { ok: false as const };
        const w = (oldCodeWeightG as any)?.[oc];
        if (w == null) return { ok: false as const };
        const ww = Number(w);
        if (!Number.isFinite(ww) || ww <= 0) return { ok: false as const };
        contentG += ww * q;
      }
      const totalKg = Number(((tareG + contentG) / 1000).toFixed(3));
      return { ok: true as const, tareG, contentG, totalKg };
    } catch {
      return { ok: false as const };
    }
  }, [endBoxDraft, endBoxContentRows, oldCodeWeightG]);

  const openStartNewContainerModal = () => {
    if (!canOperateExec) return;
    setStartContainerOpen(true);
    setShippingPkgQuery('');
    setShippingPkgErr('');
    setShippingPkgHit(null);
    setShippingPkgOptions([]);
    setShippingPkgLoading(false);
    lastShipQueryRef.current = '';
    // 先清空表单；默认值在弹窗真正打开后统一写入（避免首次打开时未渲染导致不显示）
    try { startForm.resetFields(); } catch {}
  };
  const createNewContainerLocal = async () => {
    if (!canOperateExec) return;
    setStartContainerLoading(true);
    try {
      const vals = await startForm.validateFields();
      const ct = String(vals?.container_type || '').trim() as any;
      const hasAnyDim = (vals?.length_cm != null && vals?.length_cm !== '') || (vals?.width_cm != null && vals?.width_cm !== '') || (vals?.height_cm != null && vals?.height_cm !== '');
      const hasWeight = (vals?.weight_g != null && vals?.weight_g !== '');
      const l = (vals?.length_cm == null || vals?.length_cm === '') ? undefined : toPosInt(vals?.length_cm);
      const w = (vals?.width_cm == null || vals?.width_cm === '') ? undefined : toPosInt(vals?.width_cm);
      const h = (vals?.height_cm == null || vals?.height_cm === '') ? undefined : toPosInt(vals?.height_cm);
      const wg = (vals?.weight_g == null || vals?.weight_g === '') ? undefined : toPosInt(vals?.weight_g);
      const condVal = String(vals?.condition || '').trim(); // 'new' | 'used' | ''
      if (!ct) { message.warning('请选择容器类型'); return; }
      // 尺寸/净重不再必填；但如果填了，则必须为正整数；尺寸如果填了任意一个，则要求三者齐全
      if (hasAnyDim) {
        if (ct === 'pallet') {
          // 托盘：不要求高度；仅校验长/宽（若填写了任意尺寸）
          if (!l || !w) { message.warning('托盘尺寸需填写完整（长/宽均为正整数，高度无需填写）'); return; }
        } else {
          if (!l || !w || !h) { message.warning('尺寸需填写完整（长/宽/高均为正整数）'); return; }
        }
      }
      if (hasWeight && !wg) { message.warning('净重需为正整数'); return; }

      // ===== 指令装箱要求校验（新旧/最长边；不校验重量） =====
      try {
        const qs = Array.isArray(instrQuestionRows) ? instrQuestionRows : [];
        const ans = (instrAnswers && typeof instrAnswers === 'object') ? instrAnswers : {};
        const norm = (s:any)=> String(s ?? '').trim().toLowerCase();
        const findQ = (cands: string[]) => {
          const candNorm = (cands || []).map(norm).filter(Boolean);
          return qs.find((q:any) => {
            const label = norm(q?.label);
            const key = norm(q?.key);
            if (!label && !key) return false;
            return candNorm.some(c => (label && label.includes(c)) || (key && key.includes(c)));
          }) || null;
        };
        const getRawAns = (q:any) => {
          try {
            if (!q) return undefined;
            const k = String(q?.key || '').trim();
            if (!k) return undefined;
            return (ans as any)[k];
          } catch { return undefined; }
        };
        const mapSelectLabel = (q:any, v:any): string => {
          try {
            const opts = Array.isArray(q?.options) ? q.options : [];
            const hit = opts.find((o:any)=> String(o?.value) === String(v));
            return String(hit?.label || v || '').trim();
          } catch { return String(v ?? '').trim(); }
        };
        const parseNum = (v:any): number | undefined => {
          if (v === undefined || v === null || v === '') return undefined;
          if (typeof v === 'number') return Number.isFinite(v) ? v : undefined;
          const s = String(v).trim();
          if (!s) return undefined;
          const m = s.match(/-?\d+(\.\d+)?/);
          if (!m) return undefined;
          const n = Number(m[0]);
          return Number.isFinite(n) ? n : undefined;
        };
        const inferCondFromText = (txt: any): 'new' | 'used' | undefined => {
          const s = String(txt ?? '').trim().toLowerCase();
          if (!s) return undefined;
          // 注意：包含“旧纸箱/旧托盘”等应判定为 used
          if (s === 'used' || s.includes('旧') || s.includes('二手') || s.includes('used')) return 'used';
          if (s === 'new' || s.includes('全新') || s.includes('new')) return 'new';
          return undefined;
        };
        const parseWantedCondition = (): 'new' | 'used' | undefined => {
          // 1) 优先：从“装箱包材选择/物流包材选择”答案中推断（例如“旧纸箱/全新纸箱”）
          const qPkg = findQ(['装箱包材选择', '物流包材选择', '包材选择']);
          const rawPkg = getRawAns(qPkg);
          if (rawPkg !== undefined && rawPkg !== null && rawPkg !== '') {
            const t = String(qPkg?.type || '');
            const label = (t === 'select') ? mapSelectLabel(qPkg, rawPkg) : String(rawPkg);
            const inferred = inferCondFromText(label);
            if (inferred) return inferred;
          }
          // 2) 回退：从“新旧/包材新旧”类问题取值
          const q = findQ(['包材新旧', '新旧要求', '新旧', '包材新旧要求', '容器新旧', '包装新旧']);
          const raw = getRawAns(q);
          if (raw === undefined || raw === null || raw === '') return undefined;
          const t = String(q?.type || '');
          const label = (t === 'select') ? mapSelectLabel(q, raw) : String(raw);
          return inferCondFromText(label);
        };
        const validationWarnings: string[] = [];
        const wantedCond = parseWantedCondition();
        if (!condVal) {
          validationWarnings.push('未选择容器“新旧”，无法校验是否符合指令装箱要求。');
        } else if (wantedCond && condVal !== wantedCond) {
          validationWarnings.push(`容器“新旧”不符合指令装箱要求：指令要求 ${wantedCond === 'new' ? '全新' : '旧'}，当前选择 ${condVal === 'new' ? '全新' : '旧'}。`);
        }

        // 最长边：仅纸箱校验；尺寸不完整时仅提醒，不阻断
        if (ct === 'box') {
          const qMaxSide = findQ(['最长边', '最大边']);
          const maxSideLimit = parseNum(getRawAns(qMaxSide));
          if (maxSideLimit !== undefined) {
            if (!(l && w && h)) {
              validationWarnings.push('纸箱未填写完整尺寸（长/宽/高），无法校验装箱最长边。');
            } else {
              const maxSide = Math.max(l, w, h);
              if (maxSide > maxSideLimit) {
                validationWarnings.push(`纸箱最长边超出指令要求：容器最长边 ${maxSide}，指令最长边上限 ${maxSideLimit}。`);
              }
            }
          }
        }

        if (validationWarnings.length > 0) {
          const proceed = await new Promise<boolean>((resolve) => {
            Modal.confirm({
              title: '校验提醒',
              content: (
                <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                  {validationWarnings.map((wmsg, i) => `${i + 1}. ${wmsg}`).join('\n')}
                </div>
              ),
              okText: '继续提交',
              cancelText: '返回修改',
              onOk: () => resolve(true),
              onCancel: () => resolve(false),
            });
          });
          if (!proceed) return;
        }
      } catch {}

      // 预生成指令产出容器系统编码（IOC.code）
      let outputCode = '';
      try {
        if (!instrIdState) throw new Error('缺少指令ID');
        const resp = await request.post('/api/v1/storage/output-containers/reserve-code/', {
          instruction_id: instrIdState,
          container_type: ct,
        });
        outputCode = String(resp?.data?.code || '').trim();
      } catch (e:any) {
        message.error(e?.response?.data?.detail || e?.message || '预生成容器系统编码失败');
        return;
      }
      if (!outputCode) { message.error('预生成容器系统编码失败'); return; }

      // v3：仅使用 IOC code 作为容器主键（不再使用 #1/#2 序号）
      const cc = String(outputCode || '').trim();
      if (!cc) { message.error('预生成容器系统编码为空'); return; }
      const exists = (boxes || []).some((b:any) => String(b?.code || '').trim() === cc);
      if (!exists) {
        setBoxes(prev => [...prev, { code: cc, status: 'open', container_type: ct } as any]);
      } else {
        // 极端情况：同 code 重复预留时，尽量把状态写回 open
        setBoxes(prev => prev.map((b:any) => (String(b?.code || '').trim() === cc ? ({ ...b, status: 'open' }) : b)));
      }
      setCurrentBoxCode(cc);
      setBoxMeta(prev => ({
        ...(prev || {}),
        [cc]: {
          length_cm: l,
          width_cm: w,
          height_cm: (ct === 'pallet' ? undefined : h),
          // 保留 g 用于“理论重量”精确计算；kg 继续用于“按容器统计”展示
          weight_g: wg != null ? Number(wg) : undefined,
          weight_kg: wg != null ? Number((wg / 1000).toFixed(3)) : undefined,
        }
      }));

      message.success(`已开始新容器：${cc}`);
      try {
        const rec: OpRecordRow = { id: makeRecId(), ts: Date.now(), event: '开始容器', container_code: cc, qty: 0, old_code: '', new_code: '' };
        setRecords(prev => [rec, ...prev]);
      } catch {}
      setStartContainerOpen(false);
    } catch (e: any) {
      if (e?.errorFields) return; // 表单校验错误
      message.error(e?.message || '创建失败');
    } finally {
      setStartContainerLoading(false);
    }
  };
  const endCurrentBox = () => {
    // 结束容器：改为“确认弹窗”——展示统计、允许修正数据，提交后才落库并关闭
    if (!canOperateExec) { startLeaseRecover('user_action'); return; }
    const cc = String(currentBoxCode || '').trim();
    if (!cc) { message.warning('当前无打开中的容器'); return; }
    try { setEndBoxWarn(''); } catch {}

    const meta: any = (boxMeta as any)?.[cc] || {};
    const hit: any = (boxes || []).find((b:any) => String(b?.code || '').trim() === cc) || {};
    const ct = String(hit?.container_type || meta?.container_type || 'box') as any;
    // “容器实重”默认值：优先使用“理论总重”（kg -> g），否则回退到容器元数据中的 weight_g
    const defaultWeightG = (() => {
      try {
        // 注意：这里必须使用当前容器的理论重量（currentBoxTheory），不能用 endBoxTheory（它依赖 endBoxDraft/endBoxContentRows，首次打开时可能尚未同步）
        if (currentBoxTheory && (currentBoxTheory as any).ok) {
          const kg = Number((currentBoxTheory as any).totalKg);
          if (Number.isFinite(kg) && kg > 0) return Math.ceil(kg) * 1000;
        }
      } catch {}
      const wg = Number(meta?.weight_g);
      if (Number.isFinite(wg) && wg > 0) return Math.round(wg);
      return undefined;
    })();
    // 同步初始化英制输入（显示值向上取整）
    try {
      const cmToInCeil = (cm?: number) => {
        const n = Number(cm);
        if (!Number.isFinite(n) || n <= 0) return undefined;
        return Math.ceil(n / 2.54);
      };
      const gToKgCeil = (g?: number) => {
        const n = Number(g);
        if (!Number.isFinite(n) || n <= 0) return undefined;
        return Math.ceil(n / 1000);
      };
      const kgToLbCeil = (kg?: number) => {
        const n = Number(kg);
        if (!Number.isFinite(n) || n <= 0) return undefined;
        return Math.ceil(n * 2.2046226218);
      };
      setEndBoxLenIn(cmToInCeil(meta?.length_cm));
      setEndBoxWidIn(cmToInCeil(meta?.width_cm));
      setEndBoxHeiIn(cmToInCeil(meta?.height_cm));
      setEndBoxLb(kgToLbCeil(gToKgCeil(defaultWeightG)));
    } catch {}
    setEndBoxDraft({
      code: cc,
      container_type: ct,
      length_cm: meta?.length_cm,
      width_cm: meta?.width_cm,
      height_cm: meta?.height_cm,
      weight_g: defaultWeightG,
    });

    // 内容统计：按（旧条码, 新条码）聚合净额（换标+调整+重扫）
    try {
      const contentEvOk = (ev:any) => {
        const e = String(ev || '').trim();
        return e === '换标'
          || e === '调整-取出' || e === '调整-放入'
          || e === '重扫-冲销' || e === '重扫-加回';
      };
      const list = (records || []).filter((r:any) => contentEvOk(r?.event) && String(r?.container_code || '').trim() === cc);
      const agg = new Map<string, { old_code: string; new_code: string; qty: number }>();
      for (const r of list) {
        const oldc = String(r?.old_code || '').trim().toUpperCase() || '-';
        const newc = String(r?.new_code || '').trim().toUpperCase() || '-';
        const q = Math.trunc(Number(r?.qty || 0) || 0);
        if (!Number.isFinite(q) || q === 0) continue;
        const k = `${oldc}__${newc}`;
        const cur = agg.get(k) || { old_code: oldc, new_code: newc, qty: 0 };
        cur.qty += q;
        agg.set(k, cur);
      }
      const rows: EndBoxContentRow[] = Array.from(agg.values()).map((v) => ({
        key: `${v.old_code}->${v.new_code}`,
        old_code: v.old_code,
        new_code: v.new_code,
        qty: Math.floor(Number(v.qty || 0) || 0),
      }));
      const filtered = rows.filter((x) => Number.isFinite(Number(x.qty)) && Number(x.qty) > 0).sort((a,b)=> (b.qty || 0) - (a.qty || 0));
      setEndBoxContentRows(filtered);
    } catch {
      setEndBoxContentRows([]);
    }
    setEndBoxOpen(true);
  };

  const openOutputBoxLabelPreview = useCallback(async (cc: string) => {
    const code = String(cc || '').trim();
    if (!code) return;
    try {
      // 清理旧 objectURL
      if (boxLabelUrl) { try { URL.revokeObjectURL(boxLabelUrl); } catch {} }
      setBoxLabelLoading(true);
      setBoxLabelCode(code);

      const buildProjectSummary = () => {
        const s = String(instrProjectSummary || '').trim();
        return s || '-';
      };

      const buildContentDetails = () => {
        // records 记账本净额：换标 + 调整/重扫（仅容器维度；不含记录池）
        try {
          const contentEvOk = (ev:any) => {
            const e = String(ev || '').trim();
            return e === '换标'
              || e === '调整-取出' || e === '调整-放入'
              || e === '重扫-冲销' || e === '重扫-加回';
          };
          const list = (records || []).filter((r:any) =>
            String(r?.container_code || '').trim() === code
            && contentEvOk(r?.event)
          );
          const agg = new Map<string, number>();
          for (const r of list) {
            const newc = String(r?.new_code || '').trim().toUpperCase();
            if (!newc || newc === '-') continue;
            const q = Math.trunc(Number(r?.qty || 0) || 0);
            if (!Number.isFinite(q) || q === 0) continue;
            agg.set(newc, (agg.get(newc) || 0) + q);
          }
          const rows = Array.from(agg.entries())
            .map(([new_code, qty]) => ({ new_code, qty: Math.floor(Number(qty || 0) || 0) }))
            .filter((x:any) => Number.isFinite(x.qty) && x.qty > 0)
            .sort((a,b)=> (b.qty||0)-(a.qty||0));
          const totalQty = rows.reduce((s,x)=> s + Math.floor(Number(x.qty||0)||0), 0);
          return { rows, totalQty, newKinds: rows.length };
        } catch { return { rows: [], totalQty: 0, newKinds: 0 }; }
      };

      // 注意：箱标要反映“最新事实（draft）”。优先顺序：
      // 1) 当前正处于“结束容器确认弹窗”的容器：endBoxDraft/endBox*In/Lb
      // 2) 后端容器草稿：outputDraftByCode（支持结束后调整/重扫 + 最终装箱数据编辑写回）
      // 3) 本地 boxMeta（兜底）
      const meta: any = (boxMeta as any)?.[code] || {};
      const isCurEndBox = (String(endBoxDraft?.code || '').trim() === code);
      const dft: any = (outputDraftByCode as any)?.[code] || {};
      const lcm = isCurEndBox ? endBoxDraft?.length_cm : (dft?.length_cm ?? meta?.length_cm);
      const wcm = isCurEndBox ? endBoxDraft?.width_cm : (dft?.width_cm ?? meta?.width_cm);
      const hcm = isCurEndBox ? endBoxDraft?.height_cm : (dft?.height_cm ?? meta?.height_cm);
      const wg = isCurEndBox ? endBoxDraft?.weight_g : (dft?.weight_g ?? meta?.weight_g);
      const kg = (wg != null && Number.isFinite(Number(wg)) && Number(wg) > 0) ? Math.ceil(Number(wg) / 1000) : undefined;

      // 英制兜底：缺失时自动由公制换算，并向上取整
      const cmToInCeil = (cmAny: any): number | undefined => {
        const cm = Number(cmAny);
        if (!Number.isFinite(cm) || cm <= 0) return undefined;
        return Math.ceil(cm / 2.54);
      };
      const kgToLbCeil = (kgAny: any): number | undefined => {
        const k = Number(kgAny);
        if (!Number.isFinite(k) || k <= 0) return undefined;
        return Math.ceil(k * 2.2046226218);
      };

      const linRaw = isCurEndBox ? endBoxLenIn : (dft?.length_in ?? meta?.length_in);
      const winRaw = isCurEndBox ? endBoxWidIn : (dft?.width_in ?? meta?.width_in);
      const hinRaw = isCurEndBox ? endBoxHeiIn : (dft?.height_in ?? meta?.height_in);
      const lbRaw = isCurEndBox ? endBoxLb : (dft?.weight_lb ?? meta?.weight_lb);
      const lin = (linRaw != null && Number.isFinite(Number(linRaw)) && Number(linRaw) > 0) ? Math.floor(Number(linRaw)) : cmToInCeil(lcm);
      const win = (winRaw != null && Number.isFinite(Number(winRaw)) && Number(winRaw) > 0) ? Math.floor(Number(winRaw)) : cmToInCeil(wcm);
      const hin = (hinRaw != null && Number.isFinite(Number(hinRaw)) && Number(hinRaw) > 0) ? Math.floor(Number(hinRaw)) : cmToInCeil(hcm);
      const lb = (lbRaw != null && Number.isFinite(Number(lbRaw)) && Number(lbRaw) > 0) ? Math.floor(Number(lbRaw)) : kgToLbCeil(kg);

      const { rows, totalQty, newKinds } = buildContentDetails();

      const payload: any = {
        instruction_id: instrIdState,
        container_code: code,
        instruction_code: String(instrCode || '').trim(),
        project_summary: buildProjectSummary(),
        container_type_display: (() => {
          const seg = code.split('-');
          const mid = String(seg?.[1] || '').trim().toUpperCase();
          if (mid === 'B') return '纸箱';
          if (mid === 'P') return '托盘';
          if (mid === 'O') return '其他';
          return mid || '-';
        })(),
        created_at: new Date().toLocaleString(),
        dims_cm: { l: (lcm ?? '-') , w: (wcm ?? '-'), h: (hcm ?? '-') },
        dims_in: { l: (lin ?? '-') , w: (win ?? '-'), h: (hin ?? '-') },
        weight: { kg: (kg ?? '-'), lb: (lb ?? '-') },
        new_kinds: newKinds,
        total_qty: totalQty,
        details: rows,
      };

      const resp = await request.post('/api/v1/storage/output-containers/labels/preview/', payload, { responseType: 'blob' as any });
      const blob = (resp as any)?.data as Blob;
      const url = URL.createObjectURL(blob);
      setBoxLabelBlob(blob);
      setBoxLabelUrl(url);
      setBoxLabelOpen(true);

      // 生成/预览即视为“本次箱标基于最新事实”
      try { setLabelDirtyByCode(prev => ({ ...(prev || {}), [code]: false })); } catch {}
    } catch (e:any) {
      message.error(e?.response?.data?.error || e?.response?.data?.detail || e?.message || '生成箱标预览失败');
    } finally {
      setBoxLabelLoading(false);
    }
  }, [boxLabelUrl, boxMeta, outputDraftByCode, instrCode, instrIdState, instrProjectSummary, records, endBoxDraft, endBoxLenIn, endBoxWidIn, endBoxHeiIn, endBoxLb]);

  const submitEndCurrentBox = async () => {
    if (!canOperateExec) { startLeaseRecover('user_action'); return; }
    const cc = String(endBoxDraft?.code || '').trim();
    if (!cc) { message.error('容器系统编码无效'); return; }
    // 物理数据完整性校验：长宽高/实重均必须为正整数（托盘结束容器时同样必须补填高度）
    try {
      const l = Number(endBoxDraft?.length_cm);
      const w = Number(endBoxDraft?.width_cm);
      const h = Number(endBoxDraft?.height_cm);
      const wg = Number(endBoxDraft?.weight_g);
      const isPosInt = (n:number) => Number.isFinite(n) && n > 0 && Math.floor(n) === n;
      const miss: string[] = [];
      if (!isPosInt(l)) miss.push('长度');
      if (!isPosInt(w)) miss.push('宽度');
      if (!isPosInt(h)) miss.push('高度');
      // weight_g 存储为 g，但 UI 是 kg 整数：要求 weight_g 为 kg*1000
      const kg = (Number.isFinite(wg) && wg > 0) ? Math.floor(wg / 1000) : NaN;
      if (!Number.isFinite(kg) || kg <= 0 || wg !== kg * 1000) miss.push('容器实重');
      if (miss.length) {
        message.error(`请先填写完整的物理数据：${miss.join('、')}（均为正整数）`);
        return;
      }
    } catch {}
    // 超重确认：用户可取消提交或继续提交
    try {
      const limit = Number(limitWeightKg);
      const wg = Number(endBoxDraft?.weight_g);
      const actualKg = (Number.isFinite(wg) && wg > 0) ? Math.ceil(wg / 1000) : undefined;
      if (Number.isFinite(limit) && limit > 0 && Number.isFinite(actualKg as any) && (actualKg as any) > limit) {
        const ok = await new Promise<boolean>((resolve) => {
          Modal.confirm({
            title: '超重提醒',
            content: `容器 ${cc} 实重 ${actualKg}kg 已超过限重 ${Number(limit).toFixed(3)}kg，是否仍要提交？`,
            okText: '仍然提交',
            cancelText: '取消提交',
            okButtonProps: { danger: true },
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });
        if (!ok) return;
      }
    } catch {}
    setEndBoxSubmitting(true);
    try {
      try { setEndBoxWarn(''); } catch {}

      // 1) 先写入 Draft（仅物理数据；内容物最终由 records 记账本净额生成）
      try {
        const draftPayload: any = {
          instruction_id: instrIdState,
          code: cc,
          container_type: String(endBoxDraft?.container_type || 'box'),
          status: 'closed_pending_final',
          length_cm: endBoxDraft?.length_cm,
          width_cm: endBoxDraft?.width_cm,
          height_cm: endBoxDraft?.height_cm,
          weight_g: endBoxDraft?.weight_g,
          // 保存英制“用户输入保真”字段，避免二次向上取整
          length_in: (endBoxLenIn != null && Number.isFinite(Number(endBoxLenIn)) && Number(endBoxLenIn) > 0) ? Math.floor(Number(endBoxLenIn)) : undefined,
          width_in: (endBoxWidIn != null && Number.isFinite(Number(endBoxWidIn)) && Number(endBoxWidIn) > 0) ? Math.floor(Number(endBoxWidIn)) : undefined,
          height_in: (endBoxHeiIn != null && Number.isFinite(Number(endBoxHeiIn)) && Number(endBoxHeiIn) > 0) ? Math.floor(Number(endBoxHeiIn)) : undefined,
          weight_lb: (endBoxLb != null && Number.isFinite(Number(endBoxLb)) && Number(endBoxLb) > 0) ? Math.floor(Number(endBoxLb)) : undefined,
          replace_items: false,
        };
        await request.post('/api/v1/storage/output-containers/drafts/upsert/', draftPayload);
      } catch (e:any) {
        message.error(e?.response?.data?.detail || e?.message || '保存容器草稿失败（请重试）');
        return;
      }

      // 2) 再落库 IOC 本体（不再写入 final content items）
      const payload: any = {
        instruction_id: instrIdState,
        code: cc,
        container_type: String(endBoxDraft?.container_type || 'box'),
        status: 'closed',
        length_cm: endBoxDraft?.length_cm,
        width_cm: endBoxDraft?.width_cm,
        height_cm: endBoxDraft?.height_cm,
        weight_g: endBoxDraft?.weight_g,
        execution_session_id: String(executionSessionId || '').trim() || undefined,
        defer_inventory_settlement: true,
      };
      await request.post('/api/v1/storage/output-containers/finalize/', payload);

      message.success(`已结束容器：${cc}`);
      // 写回最终装箱数据（用于“按容器统计”展示最终重量/尺寸；进行中容器显示 '-'）
      try {
        const l = endBoxDraft?.length_cm;
        const w = endBoxDraft?.width_cm;
        const h = endBoxDraft?.height_cm;
        const wg = endBoxDraft?.weight_g;
        setBoxMeta(prev => ({
          ...(prev || {}),
          [cc]: {
            ...(prev as any)?.[cc],
            length_cm: l != null ? Number(l) : undefined,
            width_cm: w != null ? Number(w) : undefined,
            height_cm: h != null ? Number(h) : undefined,
            weight_g: wg != null ? Number(wg) : undefined,
            weight_kg: (wg != null && Number.isFinite(Number(wg)) && Number(wg) > 0) ? Math.ceil(Number(wg) / 1000) : undefined,
            // 同步保存英制值（避免“英寸输入→保存后又+1”的二次向上取整问题）
            length_in: (endBoxLenIn != null && Number.isFinite(Number(endBoxLenIn)) && Number(endBoxLenIn) > 0) ? Math.floor(Number(endBoxLenIn)) : undefined,
            width_in: (endBoxWidIn != null && Number.isFinite(Number(endBoxWidIn)) && Number(endBoxWidIn) > 0) ? Math.floor(Number(endBoxWidIn)) : undefined,
            height_in: (endBoxHeiIn != null && Number.isFinite(Number(endBoxHeiIn)) && Number(endBoxHeiIn) > 0) ? Math.floor(Number(endBoxHeiIn)) : undefined,
            weight_lb: (endBoxLb != null && Number.isFinite(Number(endBoxLb)) && Number(endBoxLb) > 0) ? Math.floor(Number(endBoxLb)) : undefined,
          }
        }));
      } catch {}
      try {
        const rec: OpRecordRow = { id: makeRecId(), ts: Date.now(), event: '结束容器', container_code: cc, qty: 0, old_code: '', new_code: '' };
        setRecords(prev => [rec, ...prev]);
      } catch {}
      try {
        setBoxes(prev => prev.map((b:any) => (String(b?.code || '').trim() === cc ? ({ ...b, status: 'closed' }) : b)));
      } catch {}
      // 回到默认状态：结束新容器后不允许残留“新增到已有容器”模式
      setAddToClosedEnabled(false);
      setAddToClosedCode('');
      setPickClosedCode('');
      setPickClosedOpen(false);
      setCurrentBoxCode('');
      setEndBoxOpen(false);
      // 自动弹出箱标预览（可打印/下载）
      try { setTimeout(() => { openOutputBoxLabelPreview(cc); }, 0); } catch {}
      // 结束容器后默认标记“箱标需重打”（直到用户生成/预览一次箱标）
      try { setLabelDirtyByCode(prev => ({ ...(prev || {}), [cc]: true })); } catch {}
    } catch (e:any) {
      message.error(e?.response?.data?.detail || e?.message || '结束容器失败（请重试）');
    } finally {
      setEndBoxSubmitting(false);
    }
  };
  type ScanCommitMode = 'both' | 'print_only' | 'record_only';
  // ========= 执行页扫码：审核A/B（仅影响“能否创建记录 + 落库到哪个IU”，不改打印链路） =========
  const parseMatchTargetsForAudit = (txt: any): any[] => {
    try {
      const s = String(txt ?? '').trim();
      if (!s || s === '-' || s === '[]') return [];
      const js = JSON.parse(s);
      const arr = Array.isArray(js) ? js : [];
      const list = (arr || [])
        .map((x:any) => ({
          iu_id: Math.floor(Number(x?.iu_id || 0)),
          system_barcode: String(x?.system_barcode || '').trim(),
          use_all_available: !!x?.use_all_available,
          expected_qty: (x?.expected_qty === '' || x?.expected_qty === undefined) ? null : x?.expected_qty,
        }))
        .filter((x:any) => Number.isFinite(x.iu_id) && x.iu_id > 0);
      // 与“操作对象匹配信息”列展示一致：否在前，其次 expected_qty 由小到大，其次系统编码
      try {
        list.sort((a:any, b:any) => {
          const ua = a?.use_all_available ? 1 : 0;
          const ub = b?.use_all_available ? 1 : 0;
          if (ua !== ub) return ua - ub; // 否在前
          const ea = (a?.expected_qty == null || !Number.isFinite(Number(a.expected_qty))) ? Number.MAX_SAFE_INTEGER : Number(a.expected_qty);
          const eb = (b?.expected_qty == null || !Number.isFinite(Number(b.expected_qty))) ? Number.MAX_SAFE_INTEGER : Number(b.expected_qty);
          if (ea !== eb) return ea - eb;
          return String(a?.system_barcode || '').localeCompare(String(b?.system_barcode || ''));
        });
      } catch {}
      return list;
    } catch { return []; }
  };

  const isExpectedQtyMissing = (v:any): boolean => {
    try {
      if (v === undefined || v === null || v === '') return true;
      const n = Number(v);
      return !Number.isFinite(n);
    } catch { return true; }
  };

  const showAuditALimitModal = (args: { old: string; newer: string; qty: number; used: number; limit: number; rowNo?: number }) => {
    try {
      const remain = Math.max(0, Number(args.limit) - Number(args.used));
      Modal.warning({
        title: '数量超出限制，已阻断操作',
        centered: true,
        icon: null,
        content: (
          <div style={{ whiteSpace:'pre-line', lineHeight: 1.7 }}>
            <div>{`映射：${args.old} → ${args.newer}${args.rowNo ? `（行${args.rowNo}）` : ''}`}</div>
            <div>{`数量分配上限：${args.limit}`}</div>
            <div>{`已完成：${args.used}`}</div>
            <div style={{ color:'#cf1322', fontWeight: 700 }}>{`本次数量：${args.qty}（剩余可用：${remain}）`}</div>
            <div style={{ marginTop: 8, color:'#595959' }}>请修改“数量”后再执行。</div>
          </div>
        )
      });
    } catch {}
  };

  const showAuditBFailModal = (title: string, desc: string, details?: any[]) => {
    try {
      Modal.warning({
        title,
        centered: true,
        icon: null,
        width: 760,
        content: (
          <div style={{ whiteSpace:'pre-line', lineHeight: 1.7 }}>
            <div style={{ marginBottom: 8 }}>{desc}</div>
            <div
              style={{
                background: '#fa541c',
                color: '#fff566',
                borderRadius: 6,
                padding: '8px 10px',
                fontWeight: 700,
                marginBottom: 10
              }}
            >
              请将超过数量限制的产品单独放置,后续会放回仓位,不要和无法操作的破损LPN等产品混放在一起
            </div>
            {Array.isArray(details) && details.length ? (
              <div style={{ background:'#fafafa', border:'1px solid #f0f0f0', borderRadius: 6, padding: 10, maxHeight: 280, overflow:'auto' }}>
                {details.slice(0, 50).map((x:any, i:number) => (
                  <div key={`b_${i}`} style={{ marginBottom: 6 }}>
                    <span style={{ fontFamily:'monospace' }}>{String(x?.sys || '-')}</span>
                    <span style={{ marginLeft: 8, color:'#595959' }}>{`use_all=${x?.useAll ? '是' : '否'}`}</span>
                    <span style={{ marginLeft: 8, color:'#595959' }}>{`预${x?.expected ?? '-'}`}</span>
                    <span style={{ marginLeft: 8, color:'#389e0d', fontWeight: 700 }}>{`已${x?.done ?? 0}`}</span>
                  </div>
                ))}
                {details.length > 50 ? <div style={{ color:'#8c8c8c' }}>{`... 其余 ${details.length - 50} 条已省略`}</div> : null}
              </div>
            ) : null}
          </div>
        )
      });
    } catch {}
  };

  const promptPickMappingForNewCode = (newCodeUpper: string, cands: MappingDef[]): Promise<MappingDef | null> => new Promise((resolve) => {
    try {
      const list = Array.isArray(cands) ? cands : [];
      if (!list.length) { resolve(null); return; }
      let pickedKey: string = String(list[0]?.key || '');
      const options = list.map((d:any) => {
        const old = String(d?.old || '-').trim().toUpperCase() || '-';
        const newer = String(d?.newer || '').trim().toUpperCase();
        const rn = (d?.rowNo != null) ? Number(d.rowNo) : undefined;
        const label = `${old} → ${newer}${Number.isFinite(rn as any) ? `（行${rn}）` : ''}${d?.remark ? ` · ${String(d.remark).trim()}` : ''}`;
        return { value: String(d.key), label };
      });
      Modal.confirm({
        title: `新条码 ${String(newCodeUpper || '').trim().toUpperCase()} 存在多条对应旧条码，请选择`,
        centered: true,
        icon: null,
        okText: '确定',
        cancelText: '取消',
        width: 760,
        content: (
          <div style={{ marginTop: 10 }}>
            <div style={{ color:'#595959', marginBottom: 8 }}>请选择本次要计入记录/落库的映射行：</div>
            <Select
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="label"
              defaultValue={pickedKey}
              options={options as any}
              onChange={(v:any) => { pickedKey = String(v || ''); }}
            />
          </div>
        ),
        onOk: () => {
          const found = list.find((d:any) => String(d?.key || '') === String(pickedKey));
          resolve(found || list[0] || null);
        },
        onCancel: () => resolve(null),
      });
    } catch { resolve(null); }
  });

  // ========= 调整记录池（仅 records 记账本） =========
  const isPool = useCallback((ccAny: any) => {
    const s = String(ccAny || '').trim();
    return !!s && s === String(poolCode || '').trim();
  }, [poolCode]);

  const isContentEvent = (evAny: any): boolean => {
    const ev = String(evAny || '').trim();
    // 注意：调整/重扫的事件不参与“按条码映射进度”，但参与容器内容净额
    return ev === '换标'
      || ev === '调整-取出'
      || ev === '调整-放入'
      || ev === '调整-入池'
      || ev === '调整-出池'
      || ev === '重扫-冲销'
      || ev === '重扫-加回'
      || ev === '重扫-入池'
      || ev === '重扫-出池';
  };

  type Lot = { op_iu_id: number; old_code: string; new_code: string; qty: number };
  const buildStacks = useCallback((containerCode: string) => {
    const cc = String(containerCode || '').trim();
    const byNew = new Map<string, Lot[]>();
    try {
      const list: any[] = Array.isArray(records) ? records.slice().reverse() : []; // oldest -> newest
      const norm = (v:any) => String(v || '').trim().toUpperCase();
      for (const r of list) {
        if (String(r?.container_code || '').trim() !== cc) continue;
        const ev = String(r?.event || '').trim();
        if (!isContentEvent(ev)) continue;
        const newc = norm(r?.new_code);
        const oldc = norm(r?.old_code) || '-';
        const iu = Math.floor(Number(r?.op_iu_id || 0));
        const qRaw = Number(r?.qty || 0);
        const q = Math.trunc(qRaw);
        if (!newc || newc === '-') continue;
        if (!Number.isFinite(iu) || iu <= 0) continue;
        if (!Number.isFinite(q) || q === 0) continue;
        const stack = byNew.get(newc) || [];
        if (q > 0) {
          stack.push({ op_iu_id: iu, old_code: oldc || '-', new_code: newc, qty: q });
        } else {
          // 消耗（从末尾开始扣减）
          let need = Math.abs(q);
          while (need > 0 && stack.length) {
            const last = stack[stack.length - 1];
            if (last.qty > need) { last.qty -= need; need = 0; break; }
            need -= last.qty;
            stack.pop();
          }
          // 若出现 need > 0，说明历史记录不守恒；这里不抛错，留给 UI 提示/最终提交校验
        }
        byNew.set(newc, stack);
      }
    } catch {}
    return byNew;
  }, [records]);

  const consumeFromStacks = (stacks: Map<string, Lot[]>, newCodeUpper: string, qty: number): { ok: boolean; lots: Lot[]; reason?: string } => {
    try {
      const newc = String(newCodeUpper || '').trim().toUpperCase();
      const need0 = Math.floor(Number(qty || 0) || 0);
      if (!newc || newc === '-' || !Number.isFinite(need0) || need0 <= 0) return { ok: false, lots: [], reason: '参数无效' };
      const stack = stacks.get(newc) || [];
      let avail = 0;
      stack.forEach((x) => { avail += Math.floor(Number(x.qty || 0) || 0); });
      if (avail < need0) return { ok: false, lots: [], reason: `可用数量不足：可用${avail}，需要${need0}` };
      const out: Lot[] = [];
      let need = need0;
      while (need > 0 && stack.length) {
        const last = stack[stack.length - 1];
        const take = Math.min(need, last.qty);
        out.push({ ...last, qty: take });
        last.qty -= take;
        need -= take;
        if (last.qty <= 0) stack.pop();
      }
      stacks.set(newc, stack);
      return { ok: true, lots: out };
    } catch (e:any) {
      return { ok: false, lots: [], reason: e?.message || '消费失败' };
    }
  };

  const computeNewAggFromRecords = useCallback((containerCode: string) => {
    const cc = String(containerCode || '').trim();
    const m = new Map<string, number>();
    try {
      (records || []).forEach((r:any) => {
        if (String(r?.container_code || '').trim() !== cc) return;
        const ev = String(r?.event || '').trim();
        if (!isContentEvent(ev)) return;
        const newc = String(r?.new_code || '').trim().toUpperCase();
        if (!newc || newc === '-') return;
        const q = Math.trunc(Number(r?.qty || 0) || 0);
        if (!Number.isFinite(q) || q === 0) return;
        m.set(newc, (m.get(newc) || 0) + q);
      });
    } catch {}
    // 归一化：仅保留 >0
    const rows = Array.from(m.entries())
      .map(([new_code, qty]) => ({ new_code, qty: Math.floor(Number(qty || 0) || 0) }))
      .filter((x) => Number.isFinite(x.qty) && x.qty > 0)
      .sort((a,b) => (b.qty - a.qty) || a.new_code.localeCompare(b.new_code));
    const totalQty = rows.reduce((s,x)=> s + x.qty, 0);
    return { rows, totalQty, newKinds: rows.length };
  }, [records]);

  const pushRecords = (recs: OpRecordRow[]) => {
    const list = (recs || []).filter(Boolean);
    if (!list.length) return;
    setRecords(prev => [...list, ...(prev || [])]);
  };

  const takeOutToPool = useCallback(async () => {
    const from = String(consoleBoxCode || '').trim();
    if (!from) { adjustWarn('请先选择源容器'); return; }
    const newc = normalizeCodeUpper(adjustNewCode);
    const qty = Math.floor(Number(adjustQty || 0) || 0);
    if (!newc) { adjustWarn('请输入新条码'); return; }
    if (!Number.isFinite(qty) || qty <= 0) { adjustWarn('数量需为正整数'); return; }
    setAdjustLoading(true);
    try {
      const stacks = buildStacks(from);
      const got = consumeFromStacks(stacks, newc, qty);
      if (!got.ok) { adjustError(got.reason || '取出失败'); return; }
      const now = Date.now();
      const recs: OpRecordRow[] = [];
      for (const lot of got.lots) {
        recs.push({ id: makeRecId(), ts: now, event: '调整-取出', container_code: from, old_code: lot.old_code, new_code: lot.new_code, qty: -lot.qty, op_iu_id: lot.op_iu_id });
        recs.push({ id: makeRecId(), ts: now, event: '调整-入池', container_code: poolCode, old_code: lot.old_code, new_code: lot.new_code, qty: lot.qty, op_iu_id: lot.op_iu_id });
      }
      pushRecords(recs);
      setLabelDirtyByCode(prev => ({ ...(prev || {}), [from]: true }));
      message.success('已取出到调整记录池');
      setAdjustNewCode('');
    } finally {
      setAdjustLoading(false);
    }
  }, [consoleBoxCode, adjustNewCode, adjustQty, poolCode, buildStacks, adjustWarn, adjustError]);

  const putInFromPool = useCallback(async (toContainerCode: string) => {
    const to = String(toContainerCode || '').trim();
    if (!to) { adjustWarn('请选择目标容器'); return; }
    const newc = normalizeCodeUpper(adjustNewCode);
    const qty = Math.floor(Number(adjustQty || 0) || 0);
    if (!newc) { adjustWarn('请输入新条码'); return; }
    if (!Number.isFinite(qty) || qty <= 0) { adjustWarn('数量需为正整数'); return; }
    setAdjustLoading(true);
    try {
      const stacks = buildStacks(poolCode);
      const got = consumeFromStacks(stacks, newc, qty);
      if (!got.ok) { adjustError(got.reason || '记录池数量不足'); return; }
      const now = Date.now();
      const recs: OpRecordRow[] = [];
      for (const lot of got.lots) {
        recs.push({ id: makeRecId(), ts: now, event: '调整-出池', container_code: poolCode, old_code: lot.old_code, new_code: lot.new_code, qty: -lot.qty, op_iu_id: lot.op_iu_id });
        recs.push({ id: makeRecId(), ts: now, event: '调整-放入', container_code: to, old_code: lot.old_code, new_code: lot.new_code, qty: lot.qty, op_iu_id: lot.op_iu_id });
      }
      pushRecords(recs);
      setLabelDirtyByCode(prev => ({ ...(prev || {}), [to]: true }));
      message.success('已从记录池放入目标容器');
      setAdjustNewCode('');
    } finally {
      setAdjustLoading(false);
    }
  }, [adjustNewCode, adjustQty, poolCode, buildStacks, adjustWarn, adjustError]);

  // UI：容器内容调整“执行”按钮（三种模式）
  const execAdjustByMode = useCallback(async () => {
    const mode = adjustMode;
    const from = String(consoleBoxCode || '').trim();
    const to = String(adjustToBoxCode || '').trim();
    const newc = normalizeCodeUpper(adjustNewCode);
    const qty = Math.floor(Number(adjustQty || 0) || 0);

    // 容器间“全量移动”不依赖新条码/数量输入
    const isMoveAll = mode === 'take_put' && !!adjustMoveAll;
    if (!isMoveAll) {
      if (!newc) { adjustWarn('请输入新条码'); return; }
      if (!Number.isFinite(qty) || qty <= 0) { adjustWarn('数量需为正整数'); return; }
    }
    if (mode !== 'put_only' && !from) { adjustWarn('请选择取出容器'); return; }
    if (mode !== 'take_only' && !to) { adjustWarn('请选择放入容器'); return; }
    if (mode === 'take_put' && from && to && from === to) { adjustWarn('取出容器与放入容器不能相同'); return; }

    if (mode === 'take_only') {
      await takeOutToPool();
      return;
    }
    if (mode === 'put_only') {
      // 严格限制：仅放入只能来自记录池（putInFromPool 本身就是从 pool 出）
      await putInFromPool(to);
      return;
    }
    if (isMoveAll) {
      setAdjustLoading(true);
      try {
        const stacks = buildStacks(from);
        const recs: OpRecordRow[] = [];
        const now = Date.now();
        let moved = 0;
        for (const [newCode, lots] of Array.from(stacks.entries())) {
          const nc = String(newCode || '').trim();
          for (const lot of (lots || [])) {
            const q = Math.floor(Number(lot?.qty || 0) || 0);
            if (!(q > 0)) continue;
            moved += q;
            recs.push({ id: makeRecId(), ts: now, event: '调整-取出', container_code: from, old_code: lot.old_code, new_code: nc, qty: -q, op_iu_id: lot.op_iu_id });
            recs.push({ id: makeRecId(), ts: now, event: '调整-入池', container_code: poolCode, old_code: lot.old_code, new_code: nc, qty: q, op_iu_id: lot.op_iu_id });
            recs.push({ id: makeRecId(), ts: now, event: '调整-出池', container_code: poolCode, old_code: lot.old_code, new_code: nc, qty: -q, op_iu_id: lot.op_iu_id });
            recs.push({ id: makeRecId(), ts: now, event: '调整-放入', container_code: to, old_code: lot.old_code, new_code: nc, qty: q, op_iu_id: lot.op_iu_id });
          }
        }
        if (!recs.length || moved <= 0) { message.info('该容器当前无可移动的产品'); return; }
        pushRecords(recs);
        setLabelDirtyByCode(prev => ({ ...(prev || {}), [from]: true, [to]: true }));
        message.success(`已移动容器内全部产品（合计${moved}件）`);
      } finally {
        setAdjustLoading(false);
      }
      return;
    }
    // take_put：原实现为“先入池再出池”，但 records state 更新异步会导致第二步读不到刚入池的数据。
    // 这里改为一次性按“取出/入池/出池/放入”生成完整记录，保证原子性与一致性。
    setAdjustLoading(true);
    try {
      const stacks = buildStacks(from);
      const got = consumeFromStacks(stacks, newc, qty);
      if (!got.ok) { adjustError(got.reason || '取出失败：数量不足'); return; }
      const now = Date.now();
      const recs: OpRecordRow[] = [];
      for (const lot of got.lots) {
        // 1) 从源容器取出（负）
        recs.push({ id: makeRecId(), ts: now, event: '调整-取出', container_code: from, old_code: lot.old_code, new_code: lot.new_code, qty: -lot.qty, op_iu_id: lot.op_iu_id });
        // 2) 入池（正）
        recs.push({ id: makeRecId(), ts: now, event: '调整-入池', container_code: poolCode, old_code: lot.old_code, new_code: lot.new_code, qty: lot.qty, op_iu_id: lot.op_iu_id });
        // 3) 立即出池（负）
        recs.push({ id: makeRecId(), ts: now, event: '调整-出池', container_code: poolCode, old_code: lot.old_code, new_code: lot.new_code, qty: -lot.qty, op_iu_id: lot.op_iu_id });
        // 4) 放入目标容器（正）
        recs.push({ id: makeRecId(), ts: now, event: '调整-放入', container_code: to, old_code: lot.old_code, new_code: lot.new_code, qty: lot.qty, op_iu_id: lot.op_iu_id });
      }
      pushRecords(recs);
      setLabelDirtyByCode(prev => ({ ...(prev || {}), [from]: true, [to]: true }));
      message.success('已取出并放入目标容器（经调整记录池记账）');
      setAdjustNewCode('');
    } finally {
      setAdjustLoading(false);
    }
  }, [adjustMode, adjustMoveAll, consoleBoxCode, adjustToBoxCode, adjustNewCode, adjustQty, poolCode, buildStacks, putInFromPool, takeOutToPool, adjustWarn, adjustError]);

  const rescanMoveAllToPool = useCallback(async () => {
    const from = String(consoleBoxCode || '').trim();
    if (!from) { message.warning('请先选择要重扫的容器'); return; }
    // 若调整池当前不为空，先提示：重扫会把该容器全量取出到池，容易与既有暂存数据混淆。
    // 注意：这里不强制阻断，但要求用户明确确认；并提供一键跳转查看调整池现状。
    try {
      // 避免依赖后置声明的 poolSummary：这里直接按 records+poolCode 计算“池可用件数”（净额>0 的合计）
      const pool = String(poolCode || '').trim();
      const byNew = new Map<string, number>();
      (records || []).forEach((r:any) => {
        if (String(r?.container_code || '').trim() !== pool) return;
        const ev = String(r?.event || '').trim();
        if (!(ev.startsWith('调整-') || ev.startsWith('重扫-'))) return;
        const newc = String(r?.new_code || '').trim().toUpperCase() || '-';
        const q = Math.trunc(Number(r?.qty || 0) || 0);
        if (!newc || newc === '-' || !Number.isFinite(q) || q === 0) return;
        byNew.set(newc, Math.trunc(Number(byNew.get(newc) || 0) || 0) + q);
      });
      const poolPos = Array.from(byNew.values()).filter((n) => n > 0).reduce((s, n) => s + n, 0);
      if (poolPos > 0) {
        const okPool = await new Promise<boolean>((resolve) => {
          const inst = Modal.confirm({
            title: '调整池当前不为空',
            centered: true,
            icon: null as any,
            okText: '仍然开始重扫',
            cancelText: '取消',
            okButtonProps: { danger: true },
            content: (
              <div style={{ color:'#595959', lineHeight: 1.6 }}>
                <div>检测到调整池当前仍有 <strong>{poolPos}</strong> 件可用数量未处理。</div>
                <div style={{ marginTop: 6, color:'#8c8c8c' }}>
                  建议先处理/清空调整池再开始重扫，以避免后续“比对/清理”时混淆不同来源的数据。
                </div>
                <div style={{ marginTop: 10 }}>
                  <Button
                    onClick={() => {
                      try { setOpConsoleTab('adjust'); } catch {}
                      try { inst.destroy(); } catch {}
                      resolve(false);
                    }}
                  >
                    去查看调整池现状
                  </Button>
                </div>
              </div>
            ),
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });
        if (!okPool) return;
      }
    } catch {}
    const ok = await new Promise<boolean>((resolve) => {
      Modal.confirm({
        title: '确认开始重扫？',
        content: `将把容器 ${from} 的现有装箱记录全量取出到“调整记录池”，并标记核对尺寸-重量-箱标。`,
        okText: '开始重扫（全量取出）',
        cancelText: '取消',
        okButtonProps: { danger: true },
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
    if (!ok) return;
    setRescanLoading(true);
    try {
      const stacks = buildStacks(from);
      // 1) 记录“本次重扫搬入池子的明细”（用于后续一键清理误操作）
      try {
        const byKey: Record<string, { op_iu_id: number; old_code: string; new_code: string; qty: number }> = {};
        for (const [newc, lots] of Array.from(stacks.entries())) {
          const nc = String(newc || '').trim().toUpperCase();
          for (const lot of (lots || [])) {
            const q = Math.floor(Number(lot?.qty || 0) || 0);
            if (!(q > 0)) continue;
            const iu = Math.floor(Number(lot?.op_iu_id || 0) || 0);
            const oldc = String(lot?.old_code || '').trim().toUpperCase() || '-';
            if (!(iu > 0) || !nc) continue;
            const k = `${iu}__${oldc}__${nc}`;
            const cur = byKey[k] || { op_iu_id: iu, old_code: oldc, new_code: nc, qty: 0 };
            cur.qty += q;
            byKey[k] = cur;
          }
        }
        setRescanBaselineLotsNetByKey(byKey);
      } catch {
        setRescanBaselineLotsNetByKey({});
      }

      // 2) 记录“本次重扫开始前池内净额”（用于计算本次重扫产生的剩余）
      try {
        const pool = String(poolCode || '').trim();
        const net: Record<string, number> = {};
        (records || []).forEach((r:any) => {
          if (String(r?.container_code || '').trim() !== pool) return;
          const ev = String(r?.event || '').trim();
          // 池的净额只关心与调整/重扫相关的内容事件
          if (!(ev.startsWith('调整-') || ev.startsWith('重扫-'))) return;
          const iu = Math.floor(Number(r?.op_iu_id || 0) || 0);
          const oldc = String(r?.old_code || '').trim().toUpperCase() || '-';
          const nc = String(r?.new_code || '').trim().toUpperCase() || '-';
          const q = Math.trunc(Number(r?.qty || 0) || 0);
          if (!(iu > 0) || !Number.isFinite(q) || q === 0 || !nc || nc === '-') return;
          const k = `${iu}__${oldc}__${nc}`;
          net[k] = Math.trunc(Number(net[k] || 0) || 0) + q;
        });
        setRescanPoolNetBaselineByKey(net);
      } catch {
        setRescanPoolNetBaselineByKey({});
      }
      // 重扫前快照：保存“理论内容物”（按当前 records 净额）
      try {
        const rows: any[] = [];
        for (const [newc, lots] of Array.from(stacks.entries())) {
          const nc = String(newc || '').trim();
          let s = 0;
          const ids: number[] = [];
          for (const lot of (lots || [])) {
            const q = Math.floor(Number(lot?.qty || 0) || 0);
            if (!(q > 0)) continue;
            s += q;
            const id = Math.floor(Number(lot?.op_iu_id || 0) || 0);
            if (id > 0) ids.push(id);
          }
          if (s > 0) rows.push({ new_code: nc, qty: s, iuIds: Array.from(new Set(ids)) });
        }
        rows.sort((a, b) => (b.qty || 0) - (a.qty || 0));
        setRescanFromBoxCode(from);
        setRescanBaselineRows(rows);
      } catch {
        setRescanFromBoxCode(from);
        setRescanBaselineRows([]);
      }
      const now = Date.now();
      const recs: OpRecordRow[] = [];
      for (const [newc, lots] of Array.from(stacks.entries())) {
        // lots 当前就是“剩余数量”的堆栈；把它们全部取出到池
        for (const lot of lots) {
          if (!lot.qty) continue;
          recs.push({ id: makeRecId(), ts: now, event: '重扫-冲销', container_code: from, old_code: lot.old_code, new_code: newc, qty: -Math.abs(lot.qty), op_iu_id: lot.op_iu_id });
          recs.push({ id: makeRecId(), ts: now, event: '重扫-入池', container_code: poolCode, old_code: lot.old_code, new_code: newc, qty: Math.abs(lot.qty), op_iu_id: lot.op_iu_id });
        }
      }
      if (!recs.length) { message.info('该容器当前无可重扫的装箱记录'); return; }
      pushRecords(recs);
      setLabelDirtyByCode(prev => ({ ...(prev || {}), [from]: true }));
      message.success('已全量取出到记录池，请开始扫描新条码加回原容器');
      setRescanNewCode('');
      setRescanQty(1);
      setRescanStarted(true);
    } finally {
      setRescanLoading(false);
    }
  }, [consoleBoxCode, poolCode, buildStacks, records, setOpConsoleTab]);

  const rescanAddFromPool = useCallback(async () => {
    const to = String(rescanFromBoxCode || consoleBoxCode || '').trim();
    if (!to) { message.warning('请先开始重扫并选择操作容器'); return; }
    const newc = normalizeCodeUpper(rescanNewCode);
    const qty = Math.floor(Number(rescanQty || 0) || 0);
    if (!newc) { message.warning('请输入新条码'); return; }
    if (!Number.isFinite(qty) || qty <= 0) { message.warning('数量需为正整数'); return; }
    setRescanLoading(true);
    try {
      const stacks = buildStacks(poolCode);
      const lots = stacks.get(newc) || [];
      const available = (lots || []).reduce((s:number, it:any) => {
        const q = Math.floor(Number(it?.qty || 0) || 0);
        return s + (q > 0 ? q : 0);
      }, 0);

      const takeQty = Math.min(Math.max(0, available), qty);
      const remainQty = Math.max(0, qty - takeQty);

      // 1) 先把“池里有的”加回（可能为 0）
      if (takeQty > 0) {
        const got = consumeFromStacks(stacks, newc, takeQty);
        if (!got.ok) { message.error(got.reason || '记录池数量不足'); return; }
        const now = Date.now();
        const recs: OpRecordRow[] = [];
        for (const lot of got.lots) {
          recs.push({ id: makeRecId(), ts: now, event: '重扫-出池', container_code: poolCode, old_code: lot.old_code, new_code: lot.new_code, qty: -lot.qty, op_iu_id: lot.op_iu_id });
          recs.push({ id: makeRecId(), ts: now, event: '重扫-加回', container_code: to, old_code: lot.old_code, new_code: lot.new_code, qty: lot.qty, op_iu_id: lot.op_iu_id });
        }
        pushRecords(recs);
        setLabelDirtyByCode(prev => ({ ...(prev || {}), [to]: true }));
      }

      // 2) 若本次扫描“多于理论数量”，剩余部分需要走“新增到已有容器”（复用换标扫描逻辑）
      if (remainQty > 0) {
        const cc = String(to || '').trim();
        const showQty = remainQty;
        const showCode = String(newc || '').trim();
        Modal.confirm({
          title: '重扫数量超过理论数量',
          centered: true,
          okText: '去新增到已有容器',
          cancelText: '稍后处理',
          okButtonProps: { danger: true },
          content: (
            <div style={{ color:'#595959', lineHeight: 1.6 }}>
              <div>记录池可用数量不足，无法直接把多出来的数量“加回”。</div>
              <div style={{ marginTop: 6 }}>
                建议将多出的部分作为<strong>新增扫描</strong>处理（复用“换标+装入容器”的扫描逻辑，确保 IU/预计数量校验与审计一致）。
              </div>
              <div style={{ marginTop: 10 }}>
                <Tag style={{ marginInlineEnd: 6, borderRadius: 999 }}>{`容器 ${cc || '-'}`}</Tag>
                <Tag style={{ marginInlineEnd: 6, borderRadius: 999, background:'#e6f4ff', borderColor:'#91caff', color:'#0958d9' }}>{`新条码 ${showCode}`}</Tag>
                <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background:'#fff7e6', borderColor:'#ffd591', color:'#ad4e00' }}>{`剩余 ${showQty} 件`}</Tag>
              </div>
            </div>
          ),
          onOk: () => {
            try {
              // 1) 切到“换标+装入容器”
              setOpConsoleTab('relabel_pack');
              // 跳转回“换标+装入容器”时默认选择“仅加入容器不打印”
              try { setScanCommitMode('record_only'); } catch {}
              // 2) 锁定扫描目标容器：优先使用当前重扫容器，避免误加入“未结束新容器”
              try { setForcedScanBoxCode(cc); } catch {}
              // 3) 若当前没有“未结束新容器”，则进入“新增到已有容器”模式并记录事件（容器需为 closed）
              const hit = (boxes || []).find((b:any) => String(b?.code || '').trim() === cc) as any;
              const st = String((hit as any)?.status || '').trim();
              const hasOpen = !!String(currentBoxCode || '').trim();
              if (!hasOpen && st && st !== 'open' && st !== 'cancelled') {
                const already = !!addToClosedEnabled && String(addToClosedCode || '').trim() === cc;
                setAddToClosedEnabled(true);
                setAddToClosedCode(cc);
                setPickClosedCode(cc);
                setPickClosedQuery('');
                setPickClosedOpen(false);
                if (!already) {
                  const rec: OpRecordRow = { id: makeRecId(), ts: Date.now(), event: '新增到已有容器', container_code: cc, qty: 0, old_code: '', new_code: '' };
                  setRecords(prev => [rec, ...prev]);
                }
              } else {
                // 有进行中新容器时不强行切模式（避免破坏用户现场流程），但依然锁定扫描目标到 cc
                if (!st || st === 'open' || st === 'cancelled') {
                  message.warning('该容器不是已结束容器：请在“换标+装入容器”中选择合适容器后再新增');
                }
              }
              // 4) 预填数量/新条码，方便直接扫描落账
              setScanQty(showQty);
              setScanNew(showCode);
              setScanNewLocked(false);
              setScanOld('');
              setScanOldLocked(false);
              try { setTimeout(() => { try { scanNewInputRef.current?.focus?.(); } catch {} }, 0); } catch {}
            } catch {}
          }
        });
        message.info(`已加回 ${takeQty} 件；剩余 ${remainQty} 件请走新增流程`);
      } else {
        message.success(`已从记录池加回到原容器（${takeQty}件）`);
      }
      setRescanNewCode('');
    } finally {
      setRescanLoading(false);
    }
  }, [rescanFromBoxCode, consoleBoxCode, rescanNewCode, rescanQty, poolCode, buildStacks]);

  // ========= 记录池可视化汇总 =========
  const poolSummary = useMemo(() => {
    try {
      const pool = String(poolCode || '').trim();
      const byNew = new Map<string, number>();
      const byIu = new Map<number, { iu_id: number; total: number; items: Map<string, number> }>();
      const byIuOldNew = new Map<string, { iu_id: number; old_code: string; new_code: string; qty: number }>();
      (records || []).forEach((r:any) => {
        if (String(r?.container_code || '').trim() !== pool) return;
        const ev = String(r?.event || '');
        if (!(ev.startsWith('调整-') || ev.startsWith('重扫-'))) return;
        const iu = Math.floor(Number(r?.op_iu_id || 0));
        const oldc = String(r?.old_code || '').trim().toUpperCase() || '-';
        const newc = String(r?.new_code || '').trim().toUpperCase() || '-';
        const q = Math.trunc(Number(r?.qty || 0) || 0);
        if (!Number.isFinite(iu) || iu <= 0) return;
        if (!Number.isFinite(q) || q === 0) return;
        if (!newc || newc === '-') return;
        byNew.set(newc, (byNew.get(newc) || 0) + q);
        const one = byIu.get(iu) || { iu_id: iu, total: 0, items: new Map<string, number>() };
        one.total += q;
        one.items.set(newc, (one.items.get(newc) || 0) + q);
        byIu.set(iu, one);
        const key = `${iu}__${oldc}__${newc}`;
        const cur = byIuOldNew.get(key) || { iu_id: iu, old_code: oldc, new_code: newc, qty: 0 };
        cur.qty += q;
        byIuOldNew.set(key, cur);
      });

      const rowsByNew = Array.from(byNew.entries())
        .map(([new_code, qty]) => ({ new_code, qty: Math.floor(Number(qty || 0) || 0) }))
        .filter((x) => Number.isFinite(x.qty) && x.qty !== 0)
        .sort((a,b) => (b.qty - a.qty) || a.new_code.localeCompare(b.new_code));
      const rowsByNewPos = rowsByNew.filter((x) => x.qty > 0);
      const totalPos = rowsByNewPos.reduce((s,x)=> s + x.qty, 0);

      const rowsByIu = Array.from(byIu.values()).map((x) => {
        const sys = iuIdToSys.get(x.iu_id) || String((opIuSysById as any)?.[x.iu_id] || '').trim() || `IU#${x.iu_id}`;
        const pos = Array.from(x.items.entries())
          .map(([new_code, qty]) => ({ new_code, qty: Math.floor(Number(qty || 0) || 0) }))
          .filter((it) => it.qty > 0)
          .sort((a,b)=> (b.qty-a.qty));
        return { iu_id: x.iu_id, iu_sys: sys, total: Math.floor(Number(x.total || 0) || 0), posItems: pos };
      }).sort((a,b)=> (b.total - a.total) || String(a.iu_sys).localeCompare(String(b.iu_sys)));

      const rowsDetail = Array.from(byIuOldNew.values())
        .map((x) => ({ ...x, qty: Math.floor(Number(x.qty || 0) || 0) }))
        .filter((x) => x.qty !== 0)
        .sort((a,b)=> (b.qty - a.qty));

      return {
        pool,
        totalPos,
        newKinds: rowsByNewPos.length,
        iuKinds: rowsByIu.filter((x:any)=> x.total > 0).length,
        rowsByNew: rowsByNewPos,
        rowsByIu: rowsByIu.filter((x:any)=> x.total > 0),
        rowsDetail: rowsDetail.filter((x:any)=> x.qty > 0),
      };
    } catch {
      return { pool: String(poolCode || ''), totalPos: 0, newKinds: 0, iuKinds: 0, rowsByNew: [], rowsByIu: [], rowsDetail: [] };
    }
  }, [records, poolCode, iuIdToSys, opIuSysById]);

  // 阶段快照：暴露“总进度表”核心数据，供执行页在“提交记录并完成”时固化快照
  useEffect(() => {
    try {
      const key = `COMBINED_RELABEL_PACK_${taskId}_${stageNo}_${groupKey || 'G'}`;
      const payload = {
        kind: 'combined_relabel_pack_progress',
        version: 1,
        task_id: taskId,
        stage_no: stageNo,
        group_key: groupKey || 'G',
        updated_at: Date.now(),
        overall: overallHeaderStats,
        mapping_summary: mappingSummary,
        mapping_rows: Array.isArray(mappingProgressRows) ? mappingProgressRows : [],
        boxes_rows: Array.isArray(boxStatRows) ? boxStatRows : [],
        pool_summary: poolSummary,
      };
      (window as any).__RELABEL_PACK_PROGRESS__ = (window as any).__RELABEL_PACK_PROGRESS__ || {};
      (window as any).__RELABEL_PACK_PROGRESS__[key] = payload;
    } catch {}
  }, [taskId, stageNo, groupKey, overallHeaderStats, mappingSummary, mappingProgressRows, boxStatRows, poolSummary]);

  const [poolTab, setPoolTab] = useState<'by_new' | 'by_iu'>('by_new');

  const addScanOld = async (mode: ScanCommitMode = 'both') => {
    if (!canOperateExec) { startLeaseRecover('user_action'); return; }
    const cc = String(activeScanBoxCode || '').trim();
    if (!cc) { message.warning('请先开始新容器或选择已结束容器'); return; }
    const oldCode = String(scanOld || '').trim().toUpperCase();
    const newCode = String(scanNew || '').trim().toUpperCase();
    // 规则：以“最后一次用户输入”的输入框为准（程序回填不改变 scanLast）
    if (scanLast === 'new') {
      if (!newCode) { message.warning('请输入新条码'); return; }
    } else {
      if (!oldCode) { message.warning('请输入旧条码'); return; }
    }
    const qty = (() => {
      const n = Number(scanQty);
      if (!Number.isFinite(n)) return NaN;
      return Math.floor(n);
    })();
    if (!Number.isFinite(qty) || qty <= 0) { message.warning('请输入有效数量（正整数）'); return; }
    // 仅打印不加入记录（补打）：仅允许输入新条码；不做映射选择/不走审核A/B；直接走打印链路
    if (mode === 'print_only') {
      if (scanLast !== 'new') {
        Modal.info({
          title: '仅打印模式仅支持新条码',
          centered: true,
          icon: null,
          content: '该模式用于补打新标签：请在“新条码”输入框扫码/输入新条码后再提交。',
          onOk: () => {
            try { setScanLast('new'); } catch {}
            try { setTimeout(() => { try { scanNewInputRef.current?.focus?.(); } catch {} }, 0); } catch {}
          }
        });
        return;
      }
      if (!newCode) { message.warning('请输入新条码'); return; }
      const defForText = (() => {
        try {
          const cand = mapByNew.get(newCode) || [];
          return cand && cand.length ? cand[0] : null;
        } catch { return null; }
      })();
      try {
        enqueuePrint(() => printNewBarcodePdfByCode(String(newCode || '').trim().toUpperCase(), { preferAgent: true, copies: qty, newText: defForText?.newText }))
          .then((printed) => { if (printed) setLabelCount(prev => Number(prev || 0) + 1); })
          .catch(() => {});
      } catch {}
      // 连续补打：清空新条码与数量，焦点留在新条码
      setScanNew('');
      setScanQty(1);
      try { setTimeout(() => { try { scanNewInputRef.current?.focus?.(); } catch {} }, 0); } catch {}
      return;
    }

    // 以 BARCODE_MAPPING 为唯一来源解析映射与数量上限（仅对“需要落库记录”的模式生效）
    if (mappingLoading) { message.info('正在加载新旧条码对应表，请稍后'); return; }
    if (!mappingDefs || mappingDefs.length === 0) { message.warning(mappingErr || '未加载新旧条码对应表（BARCODE_MAPPING）'); return; }

    // 分支A：输入旧条码 → 回填新条码 + 自动打印新条码
    if (scanLast !== 'new') {
      const cand = (mapByOld.get(oldCode) || []).slice().sort((a:any, b:any) => {
        const ra = (a?.rowNo == null) ? Number.MAX_SAFE_INTEGER : Number(a.rowNo);
        const rb = (b?.rowNo == null) ? Number.MAX_SAFE_INTEGER : Number(b.rowNo);
        if (ra !== rb) return ra - rb;
        return String(a?.key || '').localeCompare(String(b?.key || ''));
      });
      if (!cand || cand.length === 0) {
        // 记录一条失败记录，便于回溯“哪一枪未知条码”
        try {
          const recFail: OpRecordRow = { id: makeRecId(), ts: Date.now(), event: '扫码失败', container_code: cc, old_code: oldCode, new_code: '' };
          setRecords(prev => [recFail, ...prev]);
        } catch {}
        triggerScanCooldown('未找到旧条码映射', `旧条码：${oldCode}（不在BARCODE_MAPPING中）`);
        // 不阻塞连续扫码：清空旧条码输入，保留其它状态
        setScanOld('');
        setScanQty(1);
        return;
      }

      // 1旧多新：按顺序“拆分分摊 qty”（先映射行，再匹配对象）
      // - 打印：按新条码汇总打印（不按对象拆分）；打印份数=实际成功生成记录的总数量（不是输入数量）
      const inputQty = qty;
      let rem = Math.floor(Number(inputQty) || 0);
      const allocs: Array<{ def: any; iu_id: number; qty: number }> = [];
      const tempUsedByKey = new Map<string, number>();
      const tempDoneByIu = new Map<number, number>();
      // 保持打印顺序稳定：按分配发生顺序累计（同一 new_code 累加 copies）
      const printOrder: string[] = [];
      const printMap = new Map<string, { copies: number; newText?: string }>();

      const getCapA = (def: any): number => {
        try {
          if (def?.limitQty === undefined || def?.limitQty === null || def?.limitQty === '') return Number.POSITIVE_INFINITY;
          const lim = Math.floor(Number(def.limitQty) || 0);
          if (!Number.isFinite(lim) || lim < 0) return Number.POSITIVE_INFINITY;
          const baseUsed = Math.floor(Number(usedAgg.get(String(def.key))?.used || 0) || 0);
          const extra = Math.floor(Number(tempUsedByKey.get(String(def.key)) || 0) || 0);
          return Math.max(0, lim - (baseUsed + extra));
        } catch { return 0; }
      };
      const getCapB = (target: any, isLast: boolean): number => {
        try {
          const iuId = Math.floor(Number(target?.iu_id || 0));
          if (!Number.isFinite(iuId) || iuId <= 0) return 0;
          const useAll = !!target?.use_all_available;
          if (isLast && useAll) return Number.POSITIVE_INFINITY;
          const expectedMissing = isExpectedQtyMissing(target?.expected_qty);
          if (expectedMissing) return Number.POSITIVE_INFINITY;
          const expected = Math.floor(Number(target?.expected_qty || 0));
          if (!Number.isFinite(expected)) return Number.POSITIVE_INFINITY;
          const baseDone = Math.floor(Number(doneByIuId.get(iuId) || 0) || 0);
          const extra = Math.floor(Number(tempDoneByIu.get(iuId) || 0) || 0);
          return Math.max(0, expected - (baseDone + extra));
        } catch { return 0; }
      };

      for (const def of cand) {
        if (rem <= 0) break;
        const capA = getCapA(def);
        if (capA <= 0) continue;
        const takeA = Math.min(rem, capA);
        if (!Number.isFinite(takeA) || takeA <= 0) continue;
        let remA = takeA;
        const targets = parseMatchTargetsForAudit(def.matchTargets);
        if (!targets.length) continue;
        for (let i = 0; i < targets.length; i++) {
          if (remA <= 0) break;
          const t = targets[i];
          const iuId = Math.floor(Number(t?.iu_id || 0));
          if (!Number.isFinite(iuId) || iuId <= 0) continue;
          const isLast = (i === targets.length - 1);
          const capB = getCapB(t, isLast);
          if (capB <= 0) continue;
          const take = Math.min(remA, capB);
          const q2 = Math.floor(Number(take) || 0);
          if (!Number.isFinite(q2) || q2 <= 0) continue;
          allocs.push({ def, iu_id: iuId, qty: q2 });
          // 更新临时占用：确保后续映射/对象看见“本次已分配”的数量
          tempUsedByKey.set(String(def.key), Math.floor(Number(tempUsedByKey.get(String(def.key)) || 0) || 0) + q2);
          tempDoneByIu.set(iuId, Math.floor(Number(tempDoneByIu.get(iuId) || 0) || 0) + q2);
          remA -= q2;
          rem -= q2;
          // 生成打印计划（按新条码聚合，不按对象拆分）
          const newCodeUpper = String(def?.newer || '').trim().toUpperCase();
          if (newCodeUpper && newCodeUpper !== '-') {
            if (!printMap.has(newCodeUpper)) { printOrder.push(newCodeUpper); printMap.set(newCodeUpper, { copies: 0, newText: def?.newText }); }
            const cur = printMap.get(newCodeUpper)!;
            cur.copies += q2;
            if (!cur.newText && def?.newText) cur.newText = def.newText;
          }
        }
      }

      const allocatedTotal = inputQty - rem;
      if (!Number.isFinite(allocatedTotal) || allocatedTotal <= 0) {
        showAuditBFailModal(
          '无法创建操作记录，已阻断操作',
          `旧条码 ${oldCode} 在当前数量限制下无法生成任何操作记录，请调整数量后重试。`
        );
        return;
      }

      // 回填/锁定新条码：若本次只落在 1 个新条码，则展示并锁定；否则清空并不锁定（避免误导）
      try {
        const uniq = Array.from(new Set(allocs.map(a => String(a?.def?.newer || '').trim().toUpperCase()).filter(Boolean)));
        if (uniq.length === 1) {
          setScanNew(uniq[0]);
          setScanNewLocked(true);
        } else {
          setScanNew('');
          setScanNewLocked(false);
        }
        setScanOldLocked(false);
      } catch {}

      // 写入记录（可能多条）
      try {
        const now = Date.now();
        const recs: OpRecordRow[] = allocs.map((a) => ({
          id: makeRecId(),
          ts: now,
          event: '换标',
          container_code: cc,
          old_code: oldCode,
          new_code: String(a?.def?.newer || '').trim().toUpperCase(),
          qty: Math.floor(Number(a?.qty || 0) || 0),
          op_iu_id: Math.floor(Number(a?.iu_id || 0)),
          op_iu_code: undefined,
        }));
        setRecords(prev => [...recs, ...prev]);
        // 已结束容器新增：标记需重打箱标
        try {
          if (addToClosedEnabled && String(addToClosedCode || '').trim() === cc) {
            setLabelDirtyByCode(prev => ({ ...(prev || {}), [cc]: true }));
          }
        } catch {}
      } catch {}
      try { setScanErrInline(null); } catch {}
      try { ensureOldCodeWeights([oldCode]); } catch {}

      // 打印：按新条码聚合打印，份数=allocatedTotal（不是输入数量）
      if (mode !== 'record_only') {
        for (const code of printOrder) {
          const it = printMap.get(code);
          const copies = Math.floor(Number(it?.copies || 0) || 0);
          if (!copies) continue;
          try {
            enqueuePrint(() => printNewBarcodePdfByCode(code, { preferAgent: true, copies, newText: it?.newText }))
              .then((printed) => { if (printed) setLabelCount(prev => Number(prev || 0) + 1); })
              .catch(() => {});
          } catch {}
        }
      }

      // 若有剩余无法生成：提示剩余 + 实际打印份数（打印份数=allocatedTotal or 0 if record_only）
      if (rem > 0) {
        const printedQty = (mode === 'record_only') ? 0 : allocatedTotal;
        const splitRows = (() => {
          try {
            const rows = (printOrder || []).map((code) => {
              const it = printMap.get(code);
              const c = Math.floor(Number(it?.copies || 0) || 0);
              return { code, copies: c };
            }).filter((x) => !!x.code && x.copies > 0);
            return rows;
          } catch { return [] as any[]; }
        })();
        Modal.warning({
          title: '数量超过可允许总和，部分无法生成记录',
          centered: true,
          icon: null,
          width: 760,
          content: (
            <div style={{ whiteSpace:'pre-line', lineHeight: 1.7 }}>
              {/* 标题下方提示（橘红背景 + 黄色字体） */}
              <div
                style={{
                  background: '#fa541c',
                  color: '#fff566',
                  borderRadius: 6,
                  padding: '8px 10px',
                  fontWeight: 700,
                  marginBottom: 10
                }}
              >
                请将超过数量限制的产品单独放置,后续会放回仓位,不要和无法操作的破损LPN等产品混放在一起
              </div>

              {/* 统计信息：表格样式，每行两项 */}
              <div style={{ background:'#ffffff', border:'1px solid #f0f0f0', borderRadius: 6, padding: 10 }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <tbody>
                    <tr>
                      <td style={{ padding: '4px 8px', border: '1px solid #f0f0f0' }}>{`旧条码：${oldCode}`}</td>
                      <td style={{ padding: '4px 8px', border: '1px solid #f0f0f0' }}>{`输入数量：${inputQty}`}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '4px 8px', border: '1px solid #f0f0f0', color:'#389e0d', fontWeight: 700 }}>{`成功生成记录数量：${allocatedTotal}`}</td>
                      <td style={{ padding: '4px 8px', border: '1px solid #f0f0f0', color:'#cf1322', fontWeight: 700 }}>{`剩余无法生成记录数量：${rem}`}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '4px 8px', border: '1px solid #f0f0f0', color:'#595959' }}>{`实际打印份数：${printedQty}`}</td>
                      <td style={{ padding: '4px 8px', border: '1px solid #f0f0f0' }} />
                    </tr>
                  </tbody>
                </table>
              </div>
              {splitRows.length ? (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontWeight: 700, color:'#595959', marginBottom: 6 }}>本次拆分明细（按新条码汇总）</div>
                  <div style={{ background:'#fafafa', border:'1px solid #f0f0f0', borderRadius: 6, padding: 10 }}>
                    {splitRows.map((r:any) => (
                      <div key={`sp_${r.code}`} style={{ display:'flex', justifyContent:'space-between', gap: 10 }}>
                        <span style={{ fontFamily:'monospace' }}>{String(r.code)}</span>
                        <span style={{ fontWeight: 700 }}>{String(r.copies)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          )
        });
      }

      // 连续扫码：清空用户输入侧旧条码，焦点回旧条码
      setScanOld('');
      setScanQty(1);
      try { setTimeout(() => { try { scanOldInputRef.current?.focus?.(); } catch {} }, 0); } catch {}
      return;
    }

    // 分支B：输入新条码 → 回填旧条码 + 可选打印/计入记录（与分支A同一路径）
    const cand2 = mapByNew.get(newCode) || [];
    if (!cand2 || cand2.length === 0) {
      // 记录一条失败记录，便于回溯“哪一枪未知条码”
      try {
        const recFail: OpRecordRow = { id: makeRecId(), ts: Date.now(), event: '扫码失败', container_code: cc, old_code: '', new_code: newCode };
        setRecords(prev => [recFail, ...prev]);
      } catch {}
      triggerScanCooldown('未找到新条码映射', `新条码：${newCode}（不在BARCODE_MAPPING中）`);
      setScanNew('');
      setScanQty(1);
      return;
    }
    let def2: any = null;
    if (cand2.length > 1) {
      def2 = await promptPickMappingForNewCode(newCode, cand2);
      if (!def2) return;
    } else {
      def2 = cand2[0];
    }
    // 扫新条码：映射已确定（可能来自弹窗选择），但“数量”仍需在该映射内按对象顺序拆分分摊
    const inputQty2 = qty;
    let rem2 = Math.floor(Number(inputQty2) || 0);
    const key2 = String(def2.key || '');
    const tempDoneByIu2 = new Map<number, number>();

    // 映射维度 capA
    const capA2 = (() => {
      try {
        if (def2?.limitQty === undefined || def2?.limitQty === null || def2?.limitQty === '') return Number.POSITIVE_INFINITY;
        const lim = Math.floor(Number(def2.limitQty) || 0);
        if (!Number.isFinite(lim) || lim < 0) return Number.POSITIVE_INFINITY;
        const baseUsed = Math.floor(Number(usedAgg.get(key2)?.used || 0) || 0);
        return Math.max(0, lim - baseUsed);
      } catch { return 0; }
    })();
    let remA2 = Math.min(rem2, capA2);
    const allocs2: Array<{ iu_id: number; qty: number }> = [];
    const targets2 = parseMatchTargetsForAudit(def2.matchTargets);
    if (!targets2.length || capA2 <= 0 || remA2 <= 0) {
      showAuditBFailModal('无法创建操作记录，已阻断操作', `映射 ${String(def2?.old || '-')?.trim().toUpperCase()} → ${newCode} 在当前数量限制下无法生成任何操作记录，请调整数量后重试。`);
      return;
    }
    for (let i = 0; i < targets2.length; i++) {
      if (remA2 <= 0) break;
      const t = targets2[i];
      const iuId = Math.floor(Number(t?.iu_id || 0));
      if (!Number.isFinite(iuId) || iuId <= 0) continue;
      const isLast = (i === targets2.length - 1);
      const useAll = !!t?.use_all_available;
      const capB = (() => {
        try {
          if (isLast && useAll) return Number.POSITIVE_INFINITY;
          const expectedMissing = isExpectedQtyMissing(t?.expected_qty);
          if (expectedMissing) return Number.POSITIVE_INFINITY;
          const expected = Math.floor(Number(t?.expected_qty || 0));
          if (!Number.isFinite(expected)) return Number.POSITIVE_INFINITY;
          const baseDone = Math.floor(Number(doneByIuId.get(iuId) || 0) || 0);
          const extra = Math.floor(Number(tempDoneByIu2.get(iuId) || 0) || 0);
          return Math.max(0, expected - (baseDone + extra));
        } catch { return 0; }
      })();
      if (capB <= 0) continue;
      const take = Math.min(remA2, capB);
      const q2 = Math.floor(Number(take) || 0);
      if (!Number.isFinite(q2) || q2 <= 0) continue;
      allocs2.push({ iu_id: iuId, qty: q2 });
      tempDoneByIu2.set(iuId, Math.floor(Number(tempDoneByIu2.get(iuId) || 0) || 0) + q2);
      remA2 -= q2;
      rem2 -= q2;
    }

    const allocatedTotal2 = inputQty2 - rem2;
    if (!Number.isFinite(allocatedTotal2) || allocatedTotal2 <= 0) {
      showAuditBFailModal('无法创建操作记录，已阻断操作', `映射 ${String(def2?.old || '-')?.trim().toUpperCase()} → ${newCode} 在当前数量限制下无法生成任何操作记录，请调整数量后重试。`);
      return;
    }

    try { setScanOld(String(def2.old || '').trim().toUpperCase()); } catch {}
    try { setScanOldLocked(true); setScanNewLocked(false); } catch {}

    // 写入多条记录（同一 newCode，不按对象拆分打印）
    try {
      const now = Date.now();
      const recs2: OpRecordRow[] = allocs2.map((a) => ({
        id: makeRecId(),
        ts: now,
        event: '换标',
        container_code: cc,
        old_code: String(def2.old || '').trim().toUpperCase(),
        new_code: String(newCode || '').trim().toUpperCase(),
        qty: Math.floor(Number(a?.qty || 0) || 0),
        op_iu_id: Math.floor(Number(a?.iu_id || 0)),
        op_iu_code: undefined,
      }));
      setRecords(prev => [...recs2, ...prev]);
      // 已结束容器新增：标记需重打箱标
      try {
        if (addToClosedEnabled && String(addToClosedCode || '').trim() === cc) {
          setLabelDirtyByCode(prev => ({ ...(prev || {}), [cc]: true }));
        }
      } catch {}
    } catch {}
    try { setScanErrInline(null); } catch {}

    // 打印：只打印“成功生成记录数量”份（不是输入数量）
    if (mode !== 'record_only') {
      try {
        enqueuePrint(() => printNewBarcodePdfByCode(String(newCode || '').trim().toUpperCase(), { preferAgent: true, copies: allocatedTotal2, newText: def2.newText }))
          .then((printed) => { if (printed) setLabelCount(prev => Number(prev || 0) + 1); })
          .catch(() => {});
      } catch {}
    }
    try { ensureOldCodeWeights([String(def2.old || '').trim().toUpperCase()]); } catch {}

    // 若有剩余无法生成：提示剩余 + 实际打印份数
    if (rem2 > 0 || (capA2 !== Number.POSITIVE_INFINITY && capA2 < inputQty2)) {
      const printedQty2 = (mode === 'record_only') ? 0 : allocatedTotal2;
      Modal.warning({
        title: '数量超过可允许总和，部分无法生成记录',
        centered: true,
        icon: null,
        width: 760,
        content: (
          <div style={{ whiteSpace:'pre-line', lineHeight: 1.7 }}>
            {/* 标题下方提示（橘红背景 + 黄色字体） */}
            <div
              style={{
                background: '#fa541c',
                color: '#fff566',
                borderRadius: 6,
                padding: '8px 10px',
                fontWeight: 700,
                marginBottom: 10
              }}
            >
              请将超过数量限制的产品单独放置,后续会放回仓位,不要和无法操作的破损LPN等产品混放在一起
            </div>

            {/* 统计信息：表格样式，每行两项 */}
            <div style={{ background:'#ffffff', border:'1px solid #f0f0f0', borderRadius: 6, padding: 10 }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <tbody>
                  <tr>
                    <td style={{ padding: '4px 8px', border: '1px solid #f0f0f0' }}>{`新条码：${newCode}`}</td>
                    <td style={{ padding: '4px 8px', border: '1px solid #f0f0f0' }}>{`输入数量：${inputQty2}`}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '4px 8px', border: '1px solid #f0f0f0', color:'#389e0d', fontWeight: 700 }}>{`成功生成记录数量：${allocatedTotal2}`}</td>
                    <td style={{ padding: '4px 8px', border: '1px solid #f0f0f0', color:'#cf1322', fontWeight: 700 }}>{`剩余无法生成记录数量：${Math.max(0, rem2)}`}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '4px 8px', border: '1px solid #f0f0f0', color:'#595959' }}>{`实际打印份数：${printedQty2}`}</td>
                    <td style={{ padding: '4px 8px', border: '1px solid #f0f0f0' }} />
                  </tr>
                </tbody>
              </table>
            </div>
            <div style={{ marginTop: 10 }}>
              <div style={{ fontWeight: 700, color:'#595959', marginBottom: 6 }}>本次拆分明细（按新条码汇总）</div>
              <div style={{ background:'#fafafa', border:'1px solid #f0f0f0', borderRadius: 6, padding: 10 }}>
                <div style={{ display:'flex', justifyContent:'space-between', gap: 10 }}>
                  <span style={{ fontFamily:'monospace' }}>{String(newCode || '').trim().toUpperCase()}</span>
                  <span style={{ fontWeight: 700 }}>{String(Math.max(0, Math.floor(Number(allocatedTotal2) || 0)))}</span>
                </div>
              </div>
            </div>
          </div>
        )
      });
    }

    // 为连续输入：清空“用户输入侧”的新条码，焦点保持在新条码输入框；数量归1
    setScanNew('');
    setScanQty(1);
    try { setTimeout(() => { try { scanNewInputRef.current?.focus?.(); } catch {} }, 0); } catch {}
  };

  // 注意：这里不要用依赖不完整的 useCallback，避免闭包捕获到旧的 currentBoxCode 导致“已开始容器但仍提示未开始”
  const commitScanByMode = () => {
    const mode = scanCommitMode;
    void addScanOld(mode);
    // A/B 为低频例外：执行后自动回到默认路径
    if (mode !== 'both') {
      try { setScanCommitMode('both'); } catch {}
    }
  };

  const scanModeLabel = (m: 'both' | 'print_only' | 'record_only'): string => {
    if (m === 'print_only') return '仅打印不加入记录(适用于补打新标签)';
    if (m === 'record_only') return '仅加入记录不打印(适用于已贴新标签)';
    return '打印并加入记录(默认)';
  };
  // 扫码模式切换：仅打印时禁用旧条码、清空旧条码并聚焦新条码
  useEffect(() => {
    try {
      if (scanCommitMode !== 'print_only') return;
      // 旧条码不参与补打：清空并解除锁定（避免退出该模式后仍无法输入）
      try { setScanOld(''); setScanOldLocked(false); } catch {}
      // 新条码允许输入：解除锁定并聚焦
      try { setScanNewLocked(false); } catch {}
      try { setScanLast('new'); } catch {}
      try { setTimeout(() => { try { scanNewInputRef.current?.focus?.(); } catch {} }, 0); } catch {}
    } catch {}
  }, [scanCommitMode]);
  // 备注：容器重量/尺寸与箱标打印已从操作控制台移除（仍可在其它流程中补充）

  return (
    <div style={{ width: '100%' }}>
      <Space direction="vertical" style={{ width: '100%' }} size={12}>
        {/* 顶部：左右二列（左：基线文件；右：目标与进度） */}
        <div style={{ display:'flex', gap:12, maxWidth: '100%', overflowX: 'hidden' }}>
          {/* 左侧：基线文件列表（略窄一些），避免挤占总进度区域 */}
          <div style={{ flex: '0 0 44%', minWidth: 420, maxWidth: '44%', background:'#fafafa', border:'1px solid #f0f0f0', borderRadius: 8, padding: 8, overflowX: 'hidden' }}>
            <Card
              size="small"
              title={(<div style={{ display:'flex', alignItems:'center', gap: 12 }}>
                <span>基线文件列表</span>
                <Input
                  size="small"
                  allowClear
                  placeholder="搜索文件名 模糊匹配"
                  value={blSearch}
                  onChange={(e)=> setBlSearch(e.target.value)}
                  style={{ width: 200, marginRight: 8 }}
                />
              </div>)}
              headStyle={{ background: '#e6f4ff' }}
              extra={(
                <Space>
                  <Select
                    size="small"
                    style={{ width: 220 }}
                    placeholder="上传基线文件到..."
                    value={uploadTargetCode}
                    onChange={onPickUploadCode}
                    options={(baselineTypeOptions || []).map(o => ({ label: `${o.name || o.code}（${o.code}）`, value: o.code }))}
                  />
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    accept={fileAccept}
                    onChange={onFilesChosen}
                  />
                  <Button size="small" danger onClick={handleDeleteSelectedBaselineFiles} disabled={!(baselines && baselines.length > 0) || (selCount === 0)}>批量删除{selCount ? `${selCount}条` : ''}</Button>
                  <Button size="small" onClick={handleDownloadAllBaselineFiles} disabled={!(baselines && baselines.length > 0) || (selCount === 0)}>批量下载{selCount ? `${selCount}条` : ''}</Button>
                  <Button size="small" onClick={loadBaselineFiles} disabled={!(baselines && baselines.length > 0)} loading={blLoading}>刷新</Button>
                </Space>
              )}
            >
              {/* 内联文件列表（简化/只读），提供预览/下载 + 分页/序号 */}
              {mappingBaseline ? (
                <div style={{ marginTop: 8 }}>
                <Table
                    size="small"
                    rowKey={(r:any)=> Number(r?.id ?? 0)}
                    loading={blLoading}
                    dataSource={(() => {
                      const start = (blPage - 1) * blPageSize;
                      const end = start + blPageSize;
                      return (blFiltered || []).slice(start, end);
                    })()}
                    pagination={{
                      current: blPage,
                      pageSize: blPageSize,
                      total: (blFiltered || []).length,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      pageSizeOptions: ['10','20','50','100'] as any,
                      showTotal: (total:number, range:[number, number]) => `共${total}条记录，显示${range[0]}-${range[1]}条`,
                      onChange: (p:number, ps:number) => { setBlPage(p); setBlPageSize(ps); },
                    }}
                    rowSelection={{
                      preserveSelectedRowKeys: true,
                      selectedRowKeys: Array.from(blSelectedIds),
                      onChange: (keys: React.Key[]) => {
                        // 忽略一次 onChange（用于 onSelectAll 之后立刻触发的 onChange）
                        if (ignoreSelectionChangeOnceRef.current) { ignoreSelectionChangeOnceRef.current = false; return; }
                        // 当处于“跨页全选”模式时，忽略 Table 触发的 onChange（否则会被当前页 keys 覆盖）
                        if (selectAllAcrossPagesRef.current) return;
                        const next = new Set<number>(keys.map(k => Number(k)).filter(n => Number.isFinite(n)));
                        setBlSelectedIds(next);
                      },
                      onSelect: (record: any, selected: boolean) => {
                        const id = Number(record?.id);
                        if (!Number.isFinite(id)) return;
                        selectAllAcrossPagesRef.current = false;
                        setBlSelectedIds(prev => {
                          const next = new Set(prev);
                          if (selected) next.add(id); else next.delete(id);
                          return next;
                        });
                      },
                      onSelectAll: (selected: boolean) => {
                        if (selected) {
                          const allIds = (blFiltered || []).map((x:any)=> Number(x?.id)).filter((n:number)=>Number.isFinite(n));
                          selectAllAcrossPagesRef.current = true;
                          setBlSelectedIds(new Set(allIds));
                          ignoreSelectionChangeOnceRef.current = true;
                        } else {
                          selectAllAcrossPagesRef.current = false;
                          setBlSelectedIds(new Set());
                          ignoreSelectionChangeOnceRef.current = true;
                        }
                      },
                    }}
                    columns={[
                      { title: '序号', dataIndex: '__idx', width: 80, align: 'center', render: (_:any, __:any, idx:number) => ((blPage - 1) * blPageSize + idx + 1) },
                      { title: '文件名', dataIndex: 'file_name', render: (v:any) => v || '-' },
                      { title: '基线文件类型', dataIndex: 'baseline', width: 180, render: (_:any, row:any) => getBaselineTypeText(row) },
                      {
                        title: '操作',
                        key: 'ops',
                        width: 220,
                        render: (_:any, row:any) => {
                          const url = resolveFileUrl(row);
                          return (
                            <Space>
                              <Button size="small" type="link" onClick={() => previewFile(row)} disabled={!url}>预览</Button>
                              <Button size="small" onClick={async () => {
                                if (!url) { message.warning('无可用链接'); return; }
                                try {
                                  const resp = await fetch(url, { credentials: 'include' as any });
                                  if (!resp.ok) throw new Error('下载失败');
                                  const blob = await resp.blob();
                                  const href = URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = href;
                                  a.download = String(row?.file_name || 'mapping');
                                  document.body.appendChild(a);
                                  a.click();
                                  document.body.removeChild(a);
                                  setTimeout(() => URL.revokeObjectURL(href), 1500);
                                } catch {
                                  message.error('下载失败');
                                }
                              }} disabled={!url}>下载</Button>
                              <Button size="small" danger onClick={async () => {
                                try {
                                  const id = Number(row?.id);
                                  if (!Number.isFinite(id)) { message.warning('记录ID无效'); return; }
                                  await request.delete(`/api/v1/instruction/baseline-files/${id}/`);
                                  message.success('已删除');
                                  setBlSelectedIds(prev => { const next = new Set(prev); next.delete(id); return next; });
                                  await loadBaselineFiles();
                                } catch { message.error('删除失败'); }
                              }}>删除</Button>
                            </Space>
                          );
                        }
                      }
                  ] as any}
                  style={{ width: '100%' }}
                  scroll={{ x: 'max-content' as any }}
                  />
                  {(!blFiles || blFiles.length === 0) && !blLoading ? (
                    <div style={{ color:'#999', marginTop: 6 }}>暂无文件，请点击上方“刷新”或在审核页上传后设为就绪</div>
                  ) : null}
                </div>
              ) : null}
            </Card>
          </div>
          <div style={{ width: 1, background: '#d9d9d9', alignSelf:'stretch' }} />
          {/* 右侧：总进度（更宽）；关键：minWidth:0 允许内部横向滚动而不是撑开页面 */}
          <div style={{ flex: '1 1 56%', minWidth: 0, background:'#fafafa', border:'1px solid #f0f0f0', borderRadius: 8, padding: 8 }}>
            <Card
              size="small"
              title={(
                <div style={{ display:'inline-flex', alignItems:'center', gap: 8 }}>
                  <span>操作目标与总进度</span>
                  <span
                    style={{
                      display:'inline-block',
                      padding:'1px 6px',
                      border: (overallHeaderStats.rateValue != null && overallHeaderStats.rateValue >= 100) ? '1px solid #b7eb8f' : '1px solid #ffadd2',
                      background: (overallHeaderStats.rateValue != null && overallHeaderStats.rateValue >= 100) ? '#f6ffed' : '#fff0f6',
                      color: (overallHeaderStats.rateValue != null && overallHeaderStats.rateValue >= 100) ? '#389e0d' : '#c41d7f',
                      borderRadius: 4,
                      fontWeight: 700,
                      fontSize: 12,
                      lineHeight: '18px',
                    }}
                  >
                    完成率 {overallHeaderStats.rateText}
                  </span>
                  {(overallHeaderStats.rateValue != null && overallHeaderStats.rateValue >= 100) ? (
                    <span
                      style={{
                        color: '#389e0d',
                        fontWeight: 900,
                        fontSize: 18,
                        lineHeight: '18px',
                      }}
                    >
                      √
                    </span>
                  ) : null}
                  <span style={{ color:'#d9d9d9', fontWeight: 400 }}>|</span>
                </div>
              )}
              extra={(
                <Tooltip title={`已完成容器 ${overallHeaderStats.closedCount} · 总体积 ${overallHeaderStats.volM3Text}m³ · 总重量 ${overallHeaderStats.kgText}kg · 已完成数量 ${overallHeaderStats.doneQty}件 · 目标 ${overallHeaderStats.targetQtyText}件 · 完成率 ${overallHeaderStats.rateText}`}>
                  <div
                    style={{
                      display:'inline-flex',
                      alignItems:'center',
                      gap: 6,
                      flexWrap:'nowrap',
                      maxWidth: 760,
                      whiteSpace:'nowrap',
                      overflow:'hidden',
                      textOverflow:'ellipsis',
                      fontSize: 12,
                      lineHeight: '18px',
                      cursor:'help'
                    }}
                  >
                    <span style={{ display:'inline-block', padding:'1px 6px', border:'1px solid #91caff', background:'#e6f4ff', color:'#0958d9', borderRadius:4, fontWeight:700 }}>
                      已完成容器 {overallHeaderStats.closedCount}
                    </span>
                    <span style={{ display:'inline-block', padding:'1px 6px', border:'1px solid #87e8de', background:'#e6fffb', color:'#006d75', borderRadius:4, fontWeight:700 }}>
                      总体积 {overallHeaderStats.volM3Text}m³
                    </span>
                    <span style={{ display:'inline-block', padding:'1px 6px', border:'1px solid #b7eb8f', background:'#f6ffed', color:'#389e0d', borderRadius:4, fontWeight:700 }}>
                      总重量 {overallHeaderStats.kgText}kg
                    </span>
                    <span style={{ display:'inline-block', padding:'1px 6px', border:'1px solid #d3adf7', background:'#f9f0ff', color:'#531dab', borderRadius:4, fontWeight:700 }}>
                      已完成 {overallHeaderStats.doneQty}件
                    </span>
                    <span style={{ display:'inline-block', padding:'1px 6px', border:'1px solid #ffe58f', background:'#fffbe6', color:'#ad8b00', borderRadius:4, fontWeight:700 }}>
                      目标 {overallHeaderStats.targetQtyText}件
                    </span>
                  </div>
                </Tooltip>
              )}
              headStyle={{ background: '#f6ffed' }}
            >
              {/* 横向滚动条仅出现在总进度卡片内部，不允许撑开页面 */}
              <div style={{ maxWidth: '100%', overflowX: 'auto', overflowY: 'hidden' }}>
                <Tabs
                  size="small"
                  style={{ minWidth: 760 }}
                  items={[
                  {
                    key: 'mapping',
                    label: '按条码映射进度(含调整池)',
                    children: (
                      <div>
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap: 12, marginBottom: 8 }}>
                          <Input
                            size="small"
                            allowClear
                            value={mappingSearch}
                            onChange={(e)=> setMappingSearch(e.target.value)}
                            placeholder="搜索旧/新条码/文本/备注（模糊匹配，不区分大小写）"
                            style={{ width: 360 }}
                          />
                          <span style={{ color:'#8c8c8c' }}>
                            {mappingLoading ? '加载中...' : `共 ${mappingProgressRows.length} 条`}
                          </span>
                        </div>
                        <Table
                          size="small"
                          rowKey={(r:any)=> String(r?.__key || '')}
                          loading={mappingLoading}
                          dataSource={mappingProgressRows}
                          pagination={{
                            current: mappingPage,
                            pageSize: mappingPageSize,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            pageSizeOptions: ['10','20','50','100'] as any,
                            showTotal: (total:number, range:[number, number]) => `共${total}条记录，显示${range[0]}-${range[1]}条`,
                            onChange: (p:number, ps:number) => { setMappingPage(p); setMappingPageSize(ps); },
                          }}
                          onRow={(r:any) => {
                            try {
                              const txt = String((r as any)?.matchTargets ?? '').trim();
                              if (!txt || txt === '-' || txt === '[]') return {};
                              let arr: any[] = [];
                              try { arr = JSON.parse(txt); } catch { arr = []; }
                              const list = Array.isArray(arr) ? arr : [];
                              if (!list.length) return {};
                              const reached = list.some((x:any) => {
                                const eq = Number(x?.expected_qty);
                                if (!Number.isFinite(eq)) return false;
                                const iuId = Math.floor(Number(x?.iu_id || 0));
                                if (!Number.isFinite(iuId) || iuId <= 0) return false;
                                const done = Math.floor(Number(doneByIuId.get(iuId) || 0) || 0);
                                return done >= eq;
                              });
                              return reached ? { style: { background: '#d9f7be' } } : {};
                            } catch { return {}; }
                          }}
                          columns={[
                            { title: '序号', width: 70, align:'center', render: (_:any, __:any, idx:number) => ((mappingPage - 1) * mappingPageSize + idx + 1) },
                            {
                              title: (
                                <Tooltip title="数据格式：操作对象-是否操作所有库存-预计数量，用于判断是否执行换标及操作记录的落库操作对象。">
                                  <span style={{ cursor:'help' }}>操作对象匹配信息</span>
                                </Tooltip>
                              ),
                              key: 'matchTargets',
                              width: 260,
                              render: (_:any, r:any) => {
                                const txt = String((r as any)?.matchTargets ?? '').trim();
                                const isMiss = (!txt || txt === '-' || txt === '[]');
                                if (isMiss) return <span style={{ color:'#cf1322', fontWeight: 700 }}>-</span>;
                                let arr: any[] = [];
                                try { arr = JSON.parse(txt); } catch { arr = []; }
                                const list = Array.isArray(arr) ? arr : [];
                                if (!list.length) return <span style={{ color:'#cf1322', fontWeight: 700 }}>-</span>;
                                try {
                                  list.sort((a:any, b:any) => {
                                    const ua = a?.use_all_available ? 1 : 0;
                                    const ub = b?.use_all_available ? 1 : 0;
                                    if (ua !== ub) return ua - ub; // 否在前
                                    const ea = (a?.expected_qty == null || !Number.isFinite(Number(a.expected_qty))) ? Number.MAX_SAFE_INTEGER : Number(a.expected_qty);
                                    const eb = (b?.expected_qty == null || !Number.isFinite(Number(b.expected_qty))) ? Number.MAX_SAFE_INTEGER : Number(b.expected_qty);
                                    if (ea !== eb) return ea - eb;
                                    return String(a?.system_barcode || '').localeCompare(String(b?.system_barcode || ''));
                                  });
                                } catch {}
                                const renderOne = (x:any, i:number) => {
                                  const iuId = Math.floor(Number(x?.iu_id || 0));
                                  const sys = String(x?.system_barcode || '').trim()
                                    || (Number.isFinite(iuId) && iuId > 0 ? (iuIdToSys.get(iuId) || String((opIuSysById as any)?.[iuId] || '').trim()) : '')
                                    || (iuId ? `IU#${iuId}` : '-');
                                  const useAll = !!x?.use_all_available;
                                  const eq = (x?.expected_qty == null || x?.expected_qty === '') ? null : Number(x.expected_qty);
                                  const eqTxt = (eq == null || !Number.isFinite(eq)) ? '-' : String(eq);
                                  const done = (() => {
                                    try {
                                      if (!Number.isFinite(iuId) || iuId <= 0) return 0;
                                      return Math.floor(Number(doneByIuId.get(iuId) || 0) || 0);
                                    } catch { return 0; }
                                  })();
                                  // 颜色：是=蓝色系；否=橘红色背景（按需求）
                                  const bg = useAll ? '#e6f4ff' : '#ff7a45';
                                  const bd = useAll ? '#91caff' : '#ff7a45';
                                  const fg = useAll ? '#0958d9' : '#ffffff';
                                  return (
                                    <Tag
                                      key={`it_${i}`}
                                      style={{
                                        marginInlineEnd: 0,
                                        marginBottom: 4,
                                        background: bg,
                                        borderColor: bd,
                                        color: fg,
                                        borderRadius: 999,
                                        fontWeight: 700,
                                        padding: '0 8px',
                                        lineHeight: '20px',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        maxWidth: 320,
                                      }}
                                    >
                                      <span style={{ fontFamily:'monospace', maxWidth: 140, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{sys}</span>
                                      <span style={{ opacity: 0.9 }}>{useAll ? '是' : '否'}</span>
                                      <span style={{ opacity: 0.9 }}>{`预${eqTxt}`}</span>
                                      <span style={{ color: useAll ? '#0958d9' : '#ffffff', background: useAll ? 'rgba(9,88,217,0.10)' : 'rgba(255,255,255,0.18)', borderRadius: 999, padding: '0 6px', lineHeight: '18px' }}>
                                        {`已${done}`}
                                      </span>
                                    </Tag>
                                  );
                                };

                                const maxShow = 3;
                                const short = list.slice(0, maxShow);
                                const more = list.length > maxShow ? list.slice(maxShow) : [];

                                const nodes: any[] = [];
                                short.forEach((x:any, i:number) => { nodes.push(renderOne(x, i)); });
                                if (more.length > 0) {
                                  const full = (
                                    <div style={{ maxWidth: 560 }}>
                                      <div style={{ display:'flex', flexWrap:'wrap', gap: 6 }}>
                                        {list.map((x:any, idx:number) => renderOne(x, idx))}
                                      </div>
                                    </div>
                                  );
                                  nodes.push(
                                    <Tooltip title={full}>
                                      <Tag
                                        style={{
                                          marginInlineEnd: 0,
                                          marginBottom: 4,
                                          borderRadius: 999,
                                          cursor: 'pointer',
                                          color: '#0958d9',
                                          borderColor: '#91caff',
                                          background: '#e6f4ff',
                                          fontWeight: 700,
                                        }}
                                      >
                                        {`+${more.length}`}
                                      </Tag>
                                    </Tooltip>
                                  );
                                }

                                return <span style={{ display:'inline-flex', flexWrap:'wrap', gap: 6, alignItems:'center' }}>{nodes}</span>;
                              }
                            },
                            { title: '旧条码', dataIndex: 'old', width: 200, render: (v:any)=> {
                              const val = String(v ?? '').trim();
                              if (!val) return '-';
                              return (
                                <Typography.Text style={{ fontFamily:'monospace' }} copyable={{ text: val }}>
                                  {val}
                                </Typography.Text>
                              );
                            }},
                            { title: '新条码', dataIndex: 'newer', width: 200, render: (v:any)=> {
                              const val = String(v ?? '').trim();
                              if (!val) return '-';
                              return (
                                <Typography.Text style={{ fontFamily:'monospace' }} copyable={{ text: val }}>
                                  {val}
                                </Typography.Text>
                              );
                            }},
                            { title: '新条码文本', dataIndex: 'newText', width: 180, render: (v:any)=> {
                              const raw = String(v ?? '');
                              const show = formatNewBarcodeText(raw);
                              if (!raw) return '-';
                              return (
                                <Tooltip title={raw}>
                                  <span>{show}</span>
                                </Tooltip>
                              );
                            }},
                            { title: (
                              <Tooltip title="默认为空，仅用于一个旧条码对应多个新条码时按顺序指定数量限制">
                                <span style={{ cursor:'help' }}>数量分配</span>
                              </Tooltip>
                            ), dataIndex: 'limitQty', width: 120, render: (v:any)=> {
                              if (v === undefined || v === null || String(v).trim() === '') return <span style={{ color:'#8c8c8c' }}>-</span>;
                              return <span style={{ color:'#1677ff', fontWeight: 700 }}>{String(v)}</span>;
                            }},
                            { title: '已完成', dataIndex: 'used', width: 100, render: (v:any)=> <span style={{ color:'#389e0d', fontWeight: 700 }}>{Number(v || 0) || 0}</span> },
                            { title: '状态', dataIndex: 'status', width: 110, render: (v:any)=> {
                              const s = String(v || '');
                              if (s === '进行中') return <Tag color="blue">进行中</Tag>;
                              if (s === '已达上限') return <Tag color="red">已达上限</Tag>;
                              if (s === '超额') return <Tag color="red">超额</Tag>;
                              if (s === '不限') return <Tag style={{ background:'#f6ffed', borderColor:'#b7eb8f', color:'#135200', fontWeight: 700 }}>不限数量</Tag>;
                              return <Tag>{s || '-'}</Tag>;
                            }},
                            { title: '最近容器', dataIndex: 'lastContainer', width: 160, render: (v:any)=> {
                              const s = String(v || '').trim();
                              if (!s) return '-';
                              return (
                                <Typography.Text style={{ fontFamily:'monospace' }} copyable={{ text: s }}>
                                  {s}
                                </Typography.Text>
                              );
                            } },
                            { title: '最近时间', dataIndex: 'lastTs', width: 160, render: (v:any)=> (v ? new Date(Number(v)).toLocaleString() : '-') },
                            { title: '操作', key: 'ops', width: 260, render: (_:any, r:any) => {
                              const newer = normalizeCodeUpper(r?.newer);
                              const gen = newer ? genLabelMap?.[newer] : null;
                              const hasAnyRec = (() => {
                                try {
                                  const k = String(r?.__key || '');
                                  if (!k) return false;
                                  return (records || []).some((x:any) =>
                                    String(x?.event || '') === '换标'
                                    && `${String(x?.old_code||'').toUpperCase()}->${String(x?.new_code||'').toUpperCase()}` === k
                                  );
                                } catch { return false; }
                              })();
                              return (
                                <Space size={8}>
                                  <Button
                                    size="small"
                                    disabled={!hasAnyRec}
                                    style={hasAnyRec ? { background:'#e6f4ff', borderColor:'#91caff', color:'#0958d9' } : undefined}
                                    onClick={() => {
                                      const k = String(r?.__key || '');
                                      setMapDetailTitle(`${r.old} → ${r.newer}`);
                                      setMapDetailRows([]);
                                      setMapDetailFilter({ kind: 'mapping', key: k, onlyRelabel: true });
                                      setMapDetailHighlightKey('');
                                      setMapDetailOpen(true);
                                  }}
                                  >
                                    查看记录
                                  </Button>
                                  <Button
                                    size="small"
                                    disabled={!hasAnyRec}
                                    style={hasAnyRec ? { background:'#ff7a45', borderColor:'#ff7a45', color:'#fff' } : undefined}
                                    onClick={() => {
                                      try {
                                        const k = String(r?.__key || '');
                                        // “查看装箱”应反映实时调整后的装箱现状：按记账本净额统计（换标+调整+重扫），并把调整池单独列为一行
                                        const contentEvOk = (ev:any) => {
                                          const e = String(ev || '').trim();
                                          return e === '换标'
                                            || e === '调整-取出' || e === '调整-放入' || e === '调整-入池' || e === '调整-出池'
                                            || e === '重扫-冲销' || e === '重扫-加回' || e === '重扫-入池' || e === '重扫-出池';
                                        };
                                        const list = (records || []).filter((x:any) => {
                                          if (!contentEvOk(x?.event)) return false;
                                          return `${String(x?.old_code||'').toUpperCase()}->${String(x?.new_code||'').toUpperCase()}` === k;
                                        });
                                        const byBox = new Map<string, { container_code: string; qty: number; cnt: number; lastTs: number; iuIds: Set<number>; isPool?: boolean }>();
                                        list.forEach((x:any) => {
                                          const cc = String(x?.container_code || '').trim() || '-';
                                          const q = Math.floor(Number(x?.qty || 0) || 0);
                                          const ts = Number(x?.ts || 0) || 0;
                                          const iu = Math.floor(Number(x?.op_iu_id || 0));
                                          const isPool = cc === String(poolCode || '').trim();
                                          const cur = byBox.get(cc) || { container_code: cc, qty: 0, cnt: 0, lastTs: 0, iuIds: new Set<number>(), isPool };
                                          // 净额：允许负数（取出/出池），最终仅展示 >0 的现状；调整池也一样
                                          cur.qty += q;
                                          cur.cnt += 1;
                                          if (ts > cur.lastTs) cur.lastTs = ts;
                                          if (Number.isFinite(iu) && iu > 0) cur.iuIds.add(iu);
                                          byBox.set(cc, cur);
                                        });
                                        const pool = String(poolCode || '').trim();
                                        const rows = Array.from(byBox.values())
                                          .filter((it:any) => {
                                            const n = Math.floor(Number(it?.qty || 0) || 0);
                                            if (it?.container_code === pool) return n > 0; // 调整池：仅展示仍在池中的
                                            return n > 0; // 容器：仅展示当前仍在容器中的
                                          })
                                          .sort((a:any,b:any) => {
                                            const ap = String(a?.container_code || '') === pool ? 1 : 0;
                                            const bp = String(b?.container_code || '') === pool ? 1 : 0;
                                            if (ap !== bp) return ap - bp; // 调整池最后
                                            return (Number(b.lastTs || 0) - Number(a.lastTs || 0)) || String(a.container_code).localeCompare(String(b.container_code));
                                          });
                                        const mapped = rows.map((it, idx) => ({
                                          __idx: idx,
                                          container_code: it.container_code,
                                          qty: it.qty,
                                          cnt: it.cnt,
                                          lastTs: it.lastTs,
                                          iuIds: Array.from(it.iuIds),
                                          __isPool: !!it.isPool,
                                        }));
                                        setMapBoxKey(k);
                                        setMapBoxTitle(`${r.old} → ${r.newer} · 装箱汇总`);
                                        setMapBoxRows(mapped);
                                        setMapBoxOpen(true);
                                      } catch {
                                        message.error('查看装箱失败');
                                      }
                                    }}
                                  >
                                    查看装箱
                                  </Button>
                                  {gen ? (
                                    <>
                                      <Button
                                        size="small"
                                        onClick={() => previewGeneratedLabelPdf(String(gen?.file_url || gen?.pdf_file || ''), `系统生成标签 · ${newer}.pdf`)}
                                      >
                                        预览标签
                                      </Button>
                                      <Button
                                        size="small"
                                        onClick={() => downloadGeneratedLabelPdf(String(gen?.file_url || gen?.pdf_file || ''), `系统生成标签-${newer}.pdf`)}
                                      >
                                        下载标签
                                      </Button>
                                      <Button
                                        size="small"
                                        danger
                                        onClick={() => deleteGeneratedLabel(newer)}
                                      >
                                        删除标签
                                      </Button>
                                    </>
                                  ) : (
                                    <Tooltip title={genLabelLoading ? '加载系统生成标签中...' : '该条码尚未生成系统标签'}>
                                      <span style={{ color:'#8c8c8c' }}>-</span>
                                    </Tooltip>
                                  )}
                                </Space>
                              );
                            }},
                          ] as any}
                          scroll={{ x: 'max-content' as any }}
                          style={{ width: '100%' }}
                        />
                      </div>
                    )
                  },
                  {
                    key: 'boxes',
                    label: (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <span>按容器统计(不含调整池)</span>
                        {hasBoxStatDirty ? (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              padding: '0 8px',
                              borderRadius: 999,
                              border: '1px solid #ffa39e',
                              background: '#fff1f0',
                              color: '#cf1322',
                              fontSize: 12,
                              fontWeight: 400,
                              lineHeight: '20px',
                            }}
                          >
                            <WarningOutlined />
                            {`待核对 ${boxStatDirtyCount}`}
                          </span>
                        ) : null}
                      </span>
                    ),
                    children: (
                      <div>
                        <style>{`
                          .combined-relabel-box-stat .combined-relabel-cancelled-row > td {
                            background: #d9d9d9 !important;
                            color: #8c8c8c !important;
                          }
                          .combined-relabel-box-stat .combined-relabel-cancelled-row:hover > td {
                            background: #d9d9d9 !important;
                          }
                        `}</style>
                        <Table
                          className="combined-relabel-box-stat"
                          size="small"
                          rowKey={(r:any)=> String(r?.container_code || '')}
                          dataSource={boxStatRows as any}
                          rowClassName={(r: any) => {
                            const cc = String(r?.container_code || '').trim();
                            const hit = (boxes || []).find((b:any) => String(b?.code || '').trim() === cc) as any;
                            const st = String((hit as any)?.status || '').trim().toLowerCase();
                            return st === 'cancelled' ? 'combined-relabel-cancelled-row' : '';
                          }}
                          pagination={{
                            current: boxStatPage,
                            pageSize: boxStatPageSize,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            pageSizeOptions: ['10','20','50','100'] as any,
                            showTotal: (total:number, range:[number, number]) => `共${total}条记录，显示${range[0]}-${range[1]}条`,
                            onChange: (p:number, ps:number) => { setBoxStatPage(p); setBoxStatPageSize(ps); },
                          }}
                          columns={[
                            { title: '序号', width: 70, align: 'center', render: (_:any, __:any, idx:number) => ((Math.max(1, Number(boxStatPage) || 1) - 1) * (Math.max(1, Number(boxStatPageSize) || 10)) + idx + 1) },
                            { title: '容器系统编码', dataIndex: 'container_code', width: 180, render: (v:any, r:any)=> {
                              const s = String(v || '').trim();
                              const cur = String(currentBoxCode || '').trim();
                              const color = (cur && s && s === cur) ? 'blue' : 'default';
                              if (!s) return <span style={{ color:'#999' }}>-</span>;
                              const hit = (boxes || []).find((b:any) => String(b?.code || '').trim() === s) as any;
                              const stRaw = String((hit as any)?.status || '').trim().toLowerCase();
                              const dirty = !!(labelDirtyByCode as any)?.[s];
                              const qty = Math.floor(Number(r?.qty || 0) || 0);
                              const contentDirty = stRaw !== 'cancelled' && qty <= 0;
                              const showWarnTags = stRaw !== 'cancelled' && (dirty || contentDirty);
                              return (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                                  <span style={{ display:'inline-flex', alignItems:'center', gap: 6, flexWrap:'wrap' }}>
                                    <Tag color={color} style={{ marginInlineEnd: 0 }}>
                                      <span style={{ fontFamily:'monospace' }}>{s}</span>
                                    </Tag>
                                    <Button
                                      type="link"
                                      size="small"
                                      style={{ padding: 0, height: 'auto', lineHeight: 1 }}
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        try {
                                          await navigator.clipboard.writeText(s);
                                          message.success('已复制');
                                        } catch {
                                          message.error('复制失败');
                                        }
                                      }}
                                    >
                                      <CopyOutlined />
                                    </Button>
                                  </span>
                                  {showWarnTags ? (
                                    <span style={{ display:'inline-flex', alignItems:'center', gap: 6, flexWrap:'wrap' }}>
                                      {dirty ? (
                                        <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background:'#fff2f0', borderColor:'#ffccc7', color:'#cf1322', fontWeight: 700 }}>
                                          核对尺寸-重量-箱标
                                        </Tag>
                                      ) : null}
                                      {contentDirty ? (
                                        <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background:'#fff2f0', borderColor:'#ff4d4f', color:'#cf1322', fontWeight: 700 }}>
                                          核对容器内容
                                        </Tag>
                                      ) : null}
                                    </span>
                                  ) : null}
                                </div>
                              );
                            } },
                            { title: '容器类型', key: 'ct', width: 110, render: (_:any, r:any)=> {
                              const cc = String(r?.container_code || '').trim();
                              if (!cc) return '-';
                              // 从容器编码中解析：IOC-{X}-...，X=B纸箱 P托盘 O其他
                              const seg = cc.split('-');
                              const mid = String(seg?.[1] || '').trim().toUpperCase();
                              if (mid === 'B') return <Tag>纸箱</Tag>;
                              if (mid === 'P') return <Tag>托盘</Tag>;
                              if (mid === 'O') return <Tag>其他</Tag>;
                              return <Tag>{mid || '-'}</Tag>;
                            }},
                            { title: '状态', key: 'st', width: 120, render: (_:any, r:any)=> {
                              const s = String(r?.container_code || '').trim();
                              const cur = String(currentBoxCode || '').trim();
                              if (cur && s && s === cur) return <Tag color="blue">进行中</Tag>;
                              const hit = (boxes || []).find((b:any) => String(b?.code || '').trim() === s);
                              const st = String(hit?.status || '').trim().toLowerCase();
                              if (st === 'cancelled') return <Tag>已作废</Tag>;
                              return <Tag color="green">已结束</Tag>;
                            } },
                            { title: '记录数', dataIndex: 'ops', width: 90 },
                            { title: '新标签种类', dataIndex: 'newKinds', width: 140, render: (v:any, r:any)=> {
                              return (
                                <span style={{ display:'inline-flex', alignItems:'center', gap: 6 }}>
                                  <span style={{ color:'#722ed1', fontWeight: 700 }}>{Math.floor(Number(v||0)||0)}</span>
                                </span>
                              );
                            } },
                            { title: '总件数', dataIndex: 'qty', width: 90, render: (v:any)=> <span style={{ fontWeight: 700 }}>{Math.floor(Number(v||0)||0)}</span> },
                            { title: '重量(kg | Lb)', key: 'w', width: 170, render: (_:any, r:any)=> {
                              const cc = String(r?.container_code || '').trim();
                              const cur = String(currentBoxCode || '').trim();
                              if (cur && cc && cc === cur) return '-';
                              const hit = (boxes || []).find((b:any) => String(b?.code || '').trim() === cc) as any;
                              const st = String((hit as any)?.status || '');
                              if (!st || st === 'open' || st === 'cancelled') return '-';
                              const meta: any = (boxMeta as any)?.[cc] || {};
                              const wg = Number(meta?.weight_g);
                              if (!Number.isFinite(wg) || wg <= 0) return '-';
                              const kg = Math.ceil(wg / 1000);
                              const lb = (() => {
                                const v = Number(meta?.weight_lb);
                                if (Number.isFinite(v) && v > 0) return Math.floor(v);
                                return Math.ceil(kg * 2.2046226218);
                              })();
                              return <span style={{ fontWeight: 700 }}>{`${kg} | ${lb}`}</span>;
                            }},
                            { title: '尺寸(cm | inch)', key: 'd', width: 250, render: (_:any, r:any)=> {
                              const cc = String(r?.container_code || '').trim();
                              const cur = String(currentBoxCode || '').trim();
                              if (cur && cc && cc === cur) return '-';
                              const hit = (boxes || []).find((b:any) => String(b?.code || '').trim() === cc) as any;
                              const st = String((hit as any)?.status || '');
                              if (!st || st === 'open' || st === 'cancelled') return '-';
                              const meta: any = (boxMeta as any)?.[cc] || {};
                              const l = Math.floor(Number(meta?.length_cm || 0) || 0);
                              const w = Math.floor(Number(meta?.width_cm || 0) || 0);
                              const h = Math.floor(Number(meta?.height_cm || 0) || 0);
                              if (!(l > 0 && w > 0 && h > 0)) return '-';
                              const li = (() => {
                                const v = Number(meta?.length_in);
                                if (Number.isFinite(v) && v > 0) return Math.floor(v);
                                return Math.ceil(l / 2.54);
                              })();
                              const wi = (() => {
                                const v = Number(meta?.width_in);
                                if (Number.isFinite(v) && v > 0) return Math.floor(v);
                                return Math.ceil(w / 2.54);
                              })();
                              const hi = (() => {
                                const v = Number(meta?.height_in);
                                if (Number.isFinite(v) && v > 0) return Math.floor(v);
                                return Math.ceil(h / 2.54);
                              })();
                              return <span style={{ fontWeight: 700 }}>{`${l}×${w}×${h} | ${li}×${wi}×${hi}`}</span>;
                            }},
                            { title: '最近时间', dataIndex: 'lastTs', width: 160, render: (v:any)=> (v ? new Date(Number(v)).toLocaleString() : '-') },
                            { title: '操作', key: 'ops2', width: 300, render: (_:any, r:any)=> {
                              const cc = String(r?.container_code || '').trim();
                              const cur = String(currentBoxCode || '').trim();
                              const hit = (boxes || []).find((b:any) => String(b?.code || '').trim() === cc) as any;
                              const st = String((hit as any)?.status || '').trim().toLowerCase();
                              const canFinal = !!cc && (!cur || cc !== cur) && st && st !== 'open' && st !== 'cancelled';
                              const qtyNum = Math.floor(Number(r?.qty || 0) || 0);
                              const canVoid = !!cc && canOperateExec && st !== 'open' && st !== 'cancelled' && qtyNum <= 0;
                              const isCancelled = st === 'cancelled';
                              const voiding = voidingContainerCodes.has(cc);
                              return (
                                <Space size={8}>
                                  <Button
                                    size="small"
                                    style={{ background:'#e6f4ff', borderColor:'#91caff', color:'#0958d9' }}
                                    onClick={() => {
                                      setMapDetailTitle(`容器 ${cc} 记录明细`);
                                      setMapDetailRows([]);
                                      setMapDetailFilter({ kind: 'container', container_code: cc, onlyRelabel: false });
                                      setMapDetailHighlightKey('');
                                      setMapDetailOpen(true);
                                    }}
                                  >
                                    查看记录
                                  </Button>
                                  <Button
                                    size="small"
                                    disabled={!canFinal}
                                    style={canFinal ? { background:'#f6ffed', borderColor:'#b7eb8f', color:'#389e0d' } : undefined}
                                    onClick={() => {
                                      if (!canFinal) return;
                                      setBoxFinalCode(cc);
                                      setBoxFinalTitle(`最终装箱数据 · ${cc}`);
                                      setBoxFinalOpen(true);
                                    }}
                                  >
                                    最终装箱数据
                                  </Button>
                                  <Tooltip title="按照当前记录生成指令产出容器箱标，可先修改操作记录或容器物理数据后再生成此箱标">
                                    <Button
                                      size="small"
                                      disabled={!canFinal}
                                      style={canFinal ? { background:'#ff7a45', borderColor:'#ff7a45', color:'#fff' } : undefined}
                                      onClick={() => {
                                        if (!canFinal) return;
                                        openOutputBoxLabelPreview(cc);
                                      }}
                                    >
                                      生成箱标
                                    </Button>
                                  </Tooltip>
                                  {isCancelled ? (
                                    <Button size="small" disabled>
                                      已作废
                                    </Button>
                                  ) : null}
                                  {canVoid ? (
                                    <Button
                                      size="small"
                                      danger
                                      loading={voiding}
                                      disabled={voiding}
                                      onClick={() => { void voidEmptyContainer(cc); }}
                                    >
                                      作废
                                    </Button>
                                  ) : null}
                                </Space>
                              );
                            }},
                          ] as any}
                          scroll={{ x: 'max-content' as any }}
                          style={{ width: '100%' }}
                        />
                      </div>
                    )
                  }
                  ]}
                />
              </div>
            </Card>
          </div>
        </div>

        <Divider style={{ borderColor:'#d9d9d9' }} />

        {/* 底部：左右二列（左：操作控制台；右：操作记录） */}
        <div style={{ display:'flex', gap:12 }}>
          <div style={{ flex:1, background:'#ffffff', border:'1px solid #f0f0f0', borderRadius: 8, padding: 8 }}>
            <Card
              size="small"
              title={(
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap: 12 }}>
                  <span>操作控制台</span>
                  <span style={{ display:'inline-flex', alignItems:'center', gap: 12, flexWrap:'wrap' }}>
                    <span style={{ color:'#fa541c', fontWeight: 700 }}>
                      当前操作：{opConsoleTab === 'relabel_pack' ? '换标+装入容器' : opConsoleTab === 'adjust' ? '容器内容调整' : '容器内容重新扫描'}
                    </span>
                  </span>
                </div>
              )}
              headStyle={{ background: '#fffbe6' }}
            >
              <Tabs
                size="small"
                activeKey={opConsoleTab}
                onChange={(k) => setOpConsoleTab(k as any)}
                items={[
                  {
                    key: 'relabel_pack',
                    label: '换标+装入容器',
                    children: (
                      <Space direction="vertical" style={{ width: '100%' }} size={8}>
                        {(() => {
                          const disStyle = { background:'#f5f5f5', borderColor:'#d9d9d9', color:'#8c8c8c' } as any;
                          const startDis = !canOperateExec || !!String(currentBoxCode || '').trim() || !!addToClosedEnabled;
                          const endNewDis = !canOperateExec || !String(currentBoxCode || '').trim();
                          const addExistingDis = !canOperateExec || !!String(currentBoxCode || '').trim() || !!addToClosedEnabled;
                          const endAddDis = !canOperateExec || !addToClosedEnabled || !!String(currentBoxCode || '').trim();
                          return (
                            <div style={{ display:'flex', alignItems:'center', gap: 16, flexWrap:'wrap' }}>
                          <Button
                            type="primary"
                            onClick={openStartNewContainerModal}
                            disabled={startDis}
                            style={startDis ? disStyle : { color:'#fff' }}
                          >
                            开始新容器
                          </Button>
                          <Button
                            type="primary"
                            onClick={endCurrentBox}
                            disabled={endNewDis}
                            style={endNewDis ? disStyle : { background: '#fa541c', borderColor: '#fa541c', color:'#fff' }}
                          >
                            结束新容器
                          </Button>

                          <span style={{ color:'#d9d9d9', margin: '0 22px' }}>|</span>

                          <Button
                            disabled={addExistingDis}
                            style={addExistingDis ? disStyle : { background:'#13c2c2', borderColor:'#13c2c2', color:'#fff', fontWeight: 800 }}
                            onClick={() => {
                              // 进入“新增到已有容器”模式：弹窗选择一个已结束容器
                              try { setPickClosedCode(''); } catch {}
                              try { setPickClosedQuery(''); } catch {}
                              setPickClosedOpen(true);
                            }}
                          >
                            新增到已有容器
                          </Button>
                          <Button
                            type="primary"
                            disabled={endAddDis}
                            style={endAddDis ? disStyle : { background: '#fa541c', borderColor: '#fa541c', color:'#fff' }}
                            onClick={() => {
                              const cc = String(addToClosedCode || '').trim();
                              setAddToClosedEnabled(false);
                              setAddToClosedCode('');
                              setPickClosedCode('');
                              setPickClosedQuery('');
                              try {
                                if (cc) {
                                  const rec: OpRecordRow = { id: makeRecId(), ts: Date.now(), event: '结束新增', container_code: cc, qty: 0, old_code: '', new_code: '' };
                                  setRecords(prev => [rec, ...prev]);
                                }
                              } catch {}
                              // 结束新增后：弹出核对弹窗（复用“最终装箱数据”弹窗结构，允许修改尺寸/重量）
                              try {
                                if (cc) {
                                  setBoxFinalCode(cc);
                                  setBoxFinalTitle(`核对尺寸-重量-箱标 · ${cc}`);
                                  setBoxFinalOpen(true);
                                }
                              } catch {}
                            }}
                          >
                            结束新增
                          </Button>

                          <span style={{ display:'inline-flex', alignItems:'center', gap: 10, flexWrap:'nowrap', color:'#595959', fontWeight: 700, marginLeft: 6, maxWidth: '100%', overflowX: 'auto' }}>
                            <span>
                              容器号：<span style={{ color:'#1677ff' }}>{activeScanBoxCode || '-'}</span>
                            </span>
                            {!String(currentBoxCode || '').trim() && addToClosedEnabled && activeScanBoxCode ? (
                              <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background:'#fff7e6', borderColor:'#ffd591', color:'#ad4e00', fontWeight: 700 }}>
                                已有容器新增
                              </Tag>
                            ) : null}
                            <span style={{ display:'inline-flex', alignItems:'center', gap: 8, flexWrap:'nowrap', whiteSpace:'nowrap' }}>
                              {currentBoxTheory.ok ? (
                                <>
                                  {typeof limitWeightKg === 'number' && Number.isFinite(limitWeightKg) && limitWeightKg > 0 ? (
                                    <>
                                      <span style={{ color:'#595959' }}>
                                        理论总重 <span style={{ color:'#1677ff' }}>{currentBoxTheory.totalKg.toFixed(3)} kg</span>
                                      </span>
                                      <span style={{ color:'#d9d9d9' }}>|</span>
                                      <span style={{ color:'#595959' }}>
                                        限重 <span style={{ color:'#1677ff' }}>{Number(limitWeightKg).toFixed(3)} kg</span>
                                      </span>
                                      {currentBoxTheory.totalKg > limitWeightKg ? (
                                        <Tag style={{ background: '#fffb8f', borderColor: '#fffb8f', color: '#cf1322', fontWeight: 800, fontSize: 14, padding: '2px 10px', lineHeight: '20px', borderRadius: 999 }}>超重</Tag>
                                      ) : currentBoxTheory.totalKg >= limitWeightKg * 0.9 ? (
                                        <Tag style={{ background: '#fa541c', borderColor: '#fa541c', color: '#fff', fontWeight: 800, fontSize: 14, padding: '2px 10px', lineHeight: '20px', borderRadius: 999 }}>接近</Tag>
                                      ) : (
                                        <Tag style={{ background: '#237804', borderColor: '#237804', color: '#fff', fontWeight: 800, fontSize: 14, padding: '2px 10px', lineHeight: '20px', borderRadius: 999 }}>正常</Tag>
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      <span style={{ color:'#595959' }}>
                                        理论总重 <span style={{ color:'#1677ff' }}>{currentBoxTheory.totalKg.toFixed(3)} kg</span>
                                      </span>
                                      {oldCodeWeightLoading ? <Tag>计算中</Tag> : null}
                                    </>
                                  )}
                                </>
                              ) : (
                                <span style={{ color:'#8c8c8c' }}>无法估算理论总重量</span>
                              )}
                            </span>
                          </span>
                        </div>
                          );
                        })()}
                        <div style={{ display:'flex', alignItems:'center', gap: 8, flexWrap:'wrap' }}>
                          <span>数量：</span>
                          <InputNumber
                            min={1}
                            precision={0}
                            step={1}
                            value={scanQty as any}
                            disabled={!canOperateScan || scanCooling}
                            onChange={(v)=> setScanQty(v == null ? 1 : Number(v))}
                            style={{ width: 88 }}
                          />
                          <span>旧条码:</span>
                          <Input
                            allowClear={{ clearIcon: <span title="删除" style={{ fontSize: 16, lineHeight: 1 }}>×</span> } as any}
                            style={{
                              width: '26ch',
                              fontFamily: 'monospace',
                              ...(scanOldLocked ? { background: '#f5f5f5', color: '#8c8c8c', cursor: 'not-allowed' } : {})
                            }}
                            placeholder="输入旧条码"
                            value={scanOld}
                            disabled={!canOperateScan || scanCooling || scanCommitMode === 'print_only'}
                            status={scanCooling ? 'error' : undefined}
                            ref={scanOldInputRef}
                            onChange={(e)=> {
                              const v = String((e as any)?.target?.value ?? '');
                              // 锁定态仅允许通过“×”清空；清空后恢复可输入
                              if (scanOldLocked) {
                                if (v === '') { setScanOld(''); setScanOldLocked(false); }
                                return;
                              }
                              setScanOld(v);
                              setScanLast('old');
                            }}
                            onPressEnter={commitScanByMode}
                          />
                          <span>新条码：</span>
                          <Input
                            allowClear={{ clearIcon: <span title="删除" style={{ fontSize: 16, lineHeight: 1 }}>×</span> } as any}
                            style={{
                              width: '26ch',
                              fontFamily: 'monospace',
                              ...(scanNewLocked ? { background: '#f5f5f5', color: '#8c8c8c', cursor: 'not-allowed' } : {})
                            }}
                            placeholder="输入新条码"
                            value={scanNew}
                            disabled={!canOperateScan || scanCooling}
                            status={scanCooling ? 'error' : undefined}
                            ref={scanNewInputRef}
                            onChange={(e)=> {
                              const v = String((e as any)?.target?.value ?? '');
                              // 锁定态仅允许通过“×”清空；清空后恢复可输入
                              if (scanNewLocked) {
                                if (v === '') { setScanNew(''); setScanNewLocked(false); }
                                return;
                              }
                              setScanNew(v);
                              setScanLast('new');
                            }}
                            onPressEnter={commitScanByMode}
                          />
                          <Button
                            type="primary"
                            onClick={commitScanByMode}
                            disabled={!canOperateScan || scanCooling}
                            style={
                              (!canOperateScan || scanCooling)
                                ? { background: '#f5f5f5', borderColor: '#d9d9d9', color: '#8c8c8c' }
                                : (scanCommitMode === 'print_only'
                                  ? { background: '#ffadd2', borderColor: '#ffadd2', color: '#531dab' }
                                  : scanCommitMode === 'record_only'
                                    ? { background: '#fffb8f', borderColor: '#fffb8f', color: '#cf1322' }
                                    : { background: '#52c41a', borderColor: '#52c41a', color: '#fff' })
                            }
                          >
                            {scanModeLabel(scanCommitMode)}
                          </Button>
                          <Dropdown
                            trigger={['click']}
                            menu={{
                              items: [
                                { key: 'both', label: scanModeLabel('both') },
                                { key: 'print_only', label: scanModeLabel('print_only') },
                                { key: 'record_only', label: scanModeLabel('record_only') },
                              ],
                              selectable: true,
                              selectedKeys: [scanCommitMode],
                              onClick: ({ key }) => {
                                if (key === 'both' || key === 'print_only' || key === 'record_only') {
                                  setScanCommitMode(key as any);
                                }
                              }
                            }}
                          >
                            <Button disabled={!canOperateScan || scanCooling}>
                              更多 <DownOutlined />
                            </Button>
                          </Dropdown>
                          {scanErrInline && (
                            <div style={{ marginLeft: 8, maxWidth: 520 }}>
                              <div style={{ color: '#cf1322', fontWeight: 700, lineHeight: 1.2 }}>
                                {scanErrInline.title}
                              </div>
                              {scanErrInline.desc ? (
                                <div style={{ color: '#a8071a', fontSize: 12, marginTop: 2, lineHeight: 1.2 }}>
                                  {scanErrInline.desc}
                                </div>
                              ) : null}
                            </div>
                          )}
                        </div>
                      </Space>
                    ),
                  },
                  {
                    key: 'adjust',
                    label: '容器内容调整',
                    children: (
                      <Space direction="vertical" style={{ width: '100%' }} size={8}>
                        <div style={{ display:'flex', alignItems:'center', gap: 8, flexWrap:'nowrap', overflowX:'auto', overflowY:'hidden' }}>
                          <span style={{ color:'#595959', fontWeight: 700 }}>操作模式：</span>
                          <Select
                            value={adjustMode}
                            style={{ width: 140 }}
                            disabled={!canOperateExec}
                            options={[
                              { value: 'take_only', label: '仅取出' },
                              { value: 'take_put', label: '容器间移动' },
                              { value: 'put_only', label: '仅放入' },
                            ] as any}
                            onChange={(v) => {
                              const m = v as any;
                              setAdjustMode(m);
                              // 离开“容器间移动”时隐藏/重置全量移动开关与弹窗状态
                              if (m !== 'take_put') {
                                setAdjustMoveAll(false);
                                setAdjustMoveAllPreviewOpen(false);
                                setAdjustMoveAllPreviewRows([]);
                                setAdjustMoveAllPreviewTotal(0);
                              }
                            }}
                          />

                          {adjustMode !== 'put_only' ? (
                            <>
                              <span style={{ color:'#595959', fontWeight: 700, marginLeft: 6 }}>取出:</span>
                              <Select
                                value={consoleBoxCode || undefined}
                                style={{ width: 220 }}
                                placeholder="选择取出容器（可选已结束容器）"
                                allowClear
                                options={(() => {
                                  const other = String(adjustToBoxCode || '').trim();
                                  const set = new Map<string, { code: string; st?: string }>();
                                  (boxes || []).forEach((b:any) => {
                                    const cc = String(b?.code || '').trim();
                                    if (!cc) return;
                                    set.set(cc, { code: cc, st: String(b?.status || '') });
                                  });
                                  Object.keys(outputDraftByCode || {}).forEach((k) => {
                                    const cc = String(k || '').trim();
                                    if (!cc) return;
                                    if (!set.has(cc)) set.set(cc, { code: cc, st: 'closed' });
                                  });
                                  return Array.from(set.values()).map((x) => {
                                    const st = String(x.st || '');
                                    const stTxt = st ? (st === 'closed' ? '已结束' : st === 'open' ? '进行中' : st === 'cancelled' ? '已取消' : st) : '';
                                    const disabled = adjustMode === 'take_put' && !!other && x.code === other;
                                    return { value: x.code, label: `${x.code}${stTxt ? `（${stTxt}）` : ''}`, disabled };
                                  });
                                })()}
                                showSearch
                                optionFilterProp="label"
                                onChange={(v) => {
                                  const cc = String(v || '').trim();
                                  if (adjustMode === 'take_put') {
                                    const other = String(adjustToBoxCode || '').trim();
                                    if (cc && other && cc === other) {
                                      // 防止同值：优先保留本次选择，清空另一侧
                                      setAdjustToBoxCode('');
                                      adjustWarn('容器间移动：取出容器与移入容器不能相同，已自动清空移入容器');
                                    }
                                  }
                                  setConsoleBoxCode(cc);
                                }}
                                onClear={() => setConsoleBoxCode('')}
                              />
                            </>
                          ) : null}

                          {adjustMode !== 'take_only' ? (
                            <>
                              <span style={{ color:'#595959', fontWeight: 700, marginLeft: 6 }}>放入:</span>
                              <Select
                                value={adjustToBoxCode || undefined}
                                style={{ width: 220 }}
                                placeholder="选择放入容器（可选已结束容器）"
                                allowClear
                                options={(() => {
                                  const other = String(consoleBoxCode || '').trim();
                                  const set = new Map<string, { code: string; st?: string }>();
                                  (boxes || []).forEach((b:any) => {
                                    const cc = String(b?.code || '').trim();
                                    if (!cc) return;
                                    set.set(cc, { code: cc, st: String(b?.status || '') });
                                  });
                                  Object.keys(outputDraftByCode || {}).forEach((k) => {
                                    const cc = String(k || '').trim();
                                    if (!cc) return;
                                    if (!set.has(cc)) set.set(cc, { code: cc, st: 'closed' });
                                  });
                                  return Array.from(set.values()).map((x) => {
                                    const st = String(x.st || '');
                                    const stTxt = st ? (st === 'closed' ? '已结束' : st === 'open' ? '进行中' : st === 'cancelled' ? '已取消' : st) : '';
                                    const disabled = adjustMode === 'take_put' && !!other && x.code === other;
                                    return { value: x.code, label: `${x.code}${stTxt ? `（${stTxt}）` : ''}`, disabled };
                                  });
                                })()}
                                showSearch
                                optionFilterProp="label"
                                onChange={(v) => {
                                  const cc = String(v || '').trim();
                                  if (adjustMode === 'take_put') {
                                    const other = String(consoleBoxCode || '').trim();
                                    if (cc && other && cc === other) {
                                      setConsoleBoxCode('');
                                      adjustWarn('容器间移动：取出容器与移入容器不能相同，已自动清空取出容器');
                                    }
                                  }
                                  setAdjustToBoxCode(cc);
                                }}
                                onClear={() => setAdjustToBoxCode('')}
                              />
                            </>
                          ) : null}

                          {outputDraftLoading ? <Tag style={{ marginInlineEnd: 0 }}>草稿加载中</Tag> : null}
                        </div>

                        <div style={{ display:'flex', alignItems:'center', gap: 8, flexWrap:'wrap' }}>
                          <span style={{ color:'#595959' }}>数量</span>
                          <InputNumber
                            min={1}
                            precision={0}
                            step={1}
                            value={adjustQty as any}
                            disabled={!canOperateExec || (adjustMode === 'take_put' && adjustMoveAll)}
                            onChange={(v)=> setAdjustQty(Math.max(1, Math.floor(Number(v||1))))}
                            style={{ width: 90 }}
                          />
                          <span style={{ color:'#595959' }}>新条码</span>
                          <Input
                            value={adjustNewCode}
                            onChange={(e)=> setAdjustNewCode(String((e as any)?.target?.value ?? ''))}
                            onPressEnter={execAdjustByMode}
                            placeholder="扫码/输入新条码"
                            style={{ width: 260, fontFamily:'monospace' }}
                            allowClear
                            disabled={!canOperateExec || (adjustMode === 'take_put' && adjustMoveAll)}
                          />
                          {adjustMode === 'take_put' ? (
                            <>
                              <span style={{ color:'#595959', marginLeft: 4 }}>容器内所有产品</span>
                              <Switch
                                checked={adjustMoveAll}
                                disabled={!canOperateExec}
                                onChange={(checked) => {
                                  if (!checked) {
                                    setAdjustMoveAll(false);
                                    return;
                                  }
                                  // 打开：要求已选择取出/放入容器
                                  const from = String(consoleBoxCode || '').trim();
                                  const to = String(adjustToBoxCode || '').trim();
                                  if (!from) { adjustWarn('请先选择取出容器'); return; }
                                  if (!to) { adjustWarn('请先选择放入容器'); return; }
                                  if (from === to) { adjustWarn('容器间移动：取出容器与移入容器不能相同'); return; }
                                  try {
                                    const stacks = buildStacks(from);
                                    const rows: any[] = [];
                                    let total = 0;
                                    for (const [nc, lots] of Array.from(stacks.entries())) {
                                      let s = 0;
                                      const ids: number[] = [];
                                      for (const lot of (lots || [])) {
                                        const q = Math.floor(Number(lot?.qty || 0) || 0);
                                        if (!(q > 0)) continue;
                                        s += q;
                                        total += q;
                                        const id = Math.floor(Number(lot?.op_iu_id || 0) || 0);
                                        if (id > 0) ids.push(id);
                                      }
                                      if (s > 0) rows.push({ new_code: String(nc || '').trim(), qty: s, iuIds: Array.from(new Set(ids)) });
                                    }
                                    if (!rows.length) { message.info('该容器当前无可移动的产品'); return; }
                                    rows.sort((a, b) => (b.qty || 0) - (a.qty || 0));
                                    setAdjustMoveAll(true);
                                    setAdjustMoveAllPreviewRows(rows);
                                    setAdjustMoveAllPreviewTotal(total);
                                    setAdjustMoveAllPreviewOpen(true);
                                  } catch {
                                    adjustError('读取容器内容失败');
                                  }
                                }}
                              />
                            </>
                          ) : null}
                          <Button type="primary" loading={adjustLoading} disabled={!canOperateExec} onClick={execAdjustByMode}>
                            执行
                          </Button>
                        </div>

                        <div style={{ color:'#8c8c8c', lineHeight: 1.4 }}>
                          说明：内容调整只能“先取出到调整记录池，再从池放入其它容器或不放入（视为异常暂存）”。取出/放入会自动继承原记录的 IU/旧码/新码 信息，records 与最终内容口径一致。
                        </div>
                      </Space>
                    )
                  },
                  {
                    key: 'rescan',
                    label: '容器内容重新扫描',
                    children: (
                      <Space direction="vertical" style={{ width: '100%' }} size={8}>
                        <div style={{ display:'flex', alignItems:'center', gap: 8, flexWrap:'wrap' }}>
                          <span style={{ color:'#595959', fontWeight: 700 }}>操作容器：</span>
                          <Select
                            value={consoleBoxCode || undefined}
                            style={{ minWidth: 280 }}
                            placeholder="选择容器（可选已结束容器）"
                            options={(() => {
                              const set = new Map<string, { code: string; st?: string }>();
                              (boxes || []).forEach((b:any) => {
                                const cc = String(b?.code || '').trim();
                                if (!cc) return;
                                set.set(cc, { code: cc, st: String(b?.status || '') });
                              });
                              Object.keys(outputDraftByCode || {}).forEach((k) => {
                                const cc = String(k || '').trim();
                                if (!cc) return;
                                if (!set.has(cc)) set.set(cc, { code: cc, st: 'closed' });
                              });
                              return Array.from(set.values()).map((x) => {
                                const st = String(x.st || '');
                                const stTxt = st ? (st === 'closed' ? '已结束' : st === 'open' ? '进行中' : st === 'cancelled' ? '已取消' : st) : '';
                                return { value: x.code, label: `${x.code}${stTxt ? `（${stTxt}）` : ''}` };
                              });
                            })()}
                            showSearch
                            optionFilterProp="label"
                            onChange={(v) => setConsoleBoxCode(String(v || ''))}
                          />
                          <Button
                            loading={rescanLoading}
                            disabled={!canOperateExec || rescanStarted}
                            onClick={rescanMoveAllToPool}
                            style={(!canOperateExec || rescanStarted)
                              ? { background: '#f5f5f5', borderColor: '#d9d9d9', color: '#8c8c8c' }
                              : { background: '#ff7a45', borderColor: '#ff7a45', color: '#fff' }
                            }
                          >
                            开始重扫
                          </Button>
                          <Button
                            disabled={!canOperateExec || !rescanStarted}
                            onClick={() => setRescanCompareOpen(true)}
                            style={(!canOperateExec || !rescanStarted)
                              ? { background: '#f5f5f5', borderColor: '#d9d9d9', color: '#8c8c8c' }
                              : { background: '#13c2c2', borderColor: '#13c2c2', color: '#fff' }
                            }
                          >
                            完成与结果比对
                          </Button>
                        </div>

                        <div style={{ display:'flex', alignItems:'center', gap: 8, flexWrap:'wrap' }}>
                          <span style={{ color:'#595959' }}>数量</span>
                          <InputNumber min={1} precision={0} step={1} value={rescanQty as any} onChange={(v)=> setRescanQty(Math.max(1, Math.floor(Number(v||1))))} style={{ width: 90 }} />
                          <span style={{ color:'#595959' }}>新条码</span>
                          <Input
                            value={rescanNewCode}
                            onChange={(e)=> setRescanNewCode(String((e as any)?.target?.value ?? ''))}
                            onPressEnter={rescanAddFromPool}
                            placeholder="扫码/输入新条码"
                            style={{ width: 260, fontFamily:'monospace' }}
                            allowClear
                          />
                          <Button type="primary" loading={rescanLoading} disabled={!canOperateExec} onClick={rescanAddFromPool}>
                            加回原容器
                          </Button>
                          {/* “完成与结果比对”已上移至“开始重扫”右侧（并受 rescanStarted 控制） */}
                        </div>

                        <div style={{ color:'#8c8c8c', lineHeight: 1.4 }}>
                          说明：重扫等价于“全量取出到池→再按新条码从池中加回原容器”。完成后可查看重扫前后理论内容物差异；池子剩余会在最终提交时作为异常保存。
                        </div>
                      </Space>
                    )
                  }
                ]}
              />

              {opConsoleTab !== 'relabel_pack' ? (
                <>
                  <Divider style={{ margin: '10px 0' }} />
                  <Card
                    size="small"
                    title={(
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap: 12, flexWrap:'wrap' }}>
                        <span>调整池现状</span>
                        <span style={{ display:'inline-flex', alignItems:'center', gap: 8, flexWrap:'wrap' }}>
                          <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background:'#f6ffed', borderColor:'#b7eb8f', color:'#389e0d', fontWeight: 800 }}>
                            {`可用${poolSummary.totalPos}件`}
                          </Tag>
                          <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background:'#f9f0ff', borderColor:'#d3adf7', color:'#531dab', fontWeight: 800 }}>
                            {`新标签种类${poolSummary.newKinds}`}
                          </Tag>
                          <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background:'#e6f4ff', borderColor:'#91caff', color:'#0958d9', fontWeight: 800 }}>
                            {`匹配对象种类${poolSummary.iuKinds}`}
                          </Tag>
                        </span>
                      </div>
                    )}
                    headStyle={{ background:'#f6ffed' }}
                  >
                    <Tabs
                      size="small"
                      activeKey={poolTab}
                      onChange={(k) => setPoolTab(k as any)}
                      items={[
                        {
                          key: 'by_new',
                          label: '按新条码汇总',
                          children: (
                            <Table
                              size="small"
                              rowKey={(r:any)=> String(r?.new_code || '')}
                              pagination={{ pageSize: 8, showSizeChanger: true, pageSizeOptions: ['8','20','50'] as any }}
                              dataSource={poolSummary.rowsByNew}
                              columns={[
                                { title: '序号', width: 70, align:'center', render: (_:any, __:any, idx:number) => idx + 1 },
                                { title: '新条码', dataIndex: 'new_code', width: 220, render: (v:any)=> {
                                  const s = String(v || '').trim();
                                  if (!s) return '-';
                                  return <Typography.Text style={{ fontFamily:'monospace' }} copyable={{ text: s }}>{s}</Typography.Text>;
                                }},
                                { title: '可用数量', dataIndex: 'qty', width: 120, render: (v:any)=> <span style={{ fontWeight: 800, color:'#cf1322' }}>{Math.floor(Number(v||0)||0)}</span> },
                              ] as any}
                            />
                          )
                        },
                        {
                          key: 'by_iu',
                          label: '按匹配对象明细',
                          children: (
                            <Table
                              size="small"
                              rowKey={(r:any)=> String(r?.iu_id || '')}
                              pagination={{ pageSize: 8, showSizeChanger: true, pageSizeOptions: ['8','20','50'] as any }}
                              dataSource={poolSummary.rowsByIu}
                              columns={[
                                { title: '序号', width: 70, align:'center', render: (_:any, __:any, idx:number) => idx + 1 },
                                { title: '匹配对象', dataIndex: 'iu_sys', width: 220, render: (v:any)=> {
                                  const s = String(v || '').trim();
                                  return <Typography.Text style={{ fontFamily:'monospace' }} copyable={{ text: s }}>{s || '-'}</Typography.Text>;
                                }},
                                { title: '可用数量', dataIndex: 'total', width: 120, render: (v:any)=> <span style={{ fontWeight: 800, color:'#cf1322' }}>{Math.floor(Number(v||0)||0)}</span> },
                                { title: '涉及新条码', dataIndex: 'posItems', render: (v:any)=> {
                                  const list: any[] = Array.isArray(v) ? v : [];
                                  if (!list.length) return <span style={{ color:'#999' }}>-</span>;
                                  const show = list.slice(0, 6);
                                  return (
                                    <span style={{ display:'inline-flex', flexWrap:'wrap', gap: 6 }}>
                                      {show.map((x:any, i:number) => (
                                        <Tag key={`n_${i}`} style={{ marginInlineEnd: 0, borderRadius: 999 }}>
                                          <span style={{ fontFamily:'monospace' }}>{String(x?.new_code || '-')}</span>
                                          <span style={{ marginLeft: 6, fontWeight: 800, color:'#cf1322' }}>{String(Math.floor(Number(x?.qty||0)||0))}</span>
                                        </Tag>
                                      ))}
                                      {list.length > 6 ? <Tag style={{ marginInlineEnd: 0, borderRadius: 999 }}>{`+${list.length - 6}`}</Tag> : null}
                                    </span>
                                  );
                                }},
                              ] as any}
                            />
                          )
                        }
                      ]}
                    />
                  </Card>
                </>
              ) : null}
            </Card>
          </div>
          <div style={{ width: 1, background: '#d9d9d9', alignSelf:'stretch' }} />
          <div style={{ flex:1, background:'#ffffff', border:'1px solid #f0f0f0', borderRadius: 8, padding: 8 }}>
            <Card
              size="small"
              title="操作记录"
              headStyle={{ background: '#fff1f0' }}
              extra={(
                <div style={{ display:'inline-flex', alignItems:'center', gap: 6, fontSize: 12, lineHeight: '18px' }}>
                  <span style={{ display:'inline-block', padding:'1px 6px', border:'1px solid #91caff', background:'#e6f4ff', color:'#0958d9', borderRadius:4, fontWeight:700 }}>
                    记录数 {recordHeaderStats.recordCount}
                  </span>
                  <span style={{ display:'inline-block', padding:'1px 6px', border:'1px solid #87e8de', background:'#e6fffb', color:'#006d75', borderRadius:4, fontWeight:700 }}>
                    容器总数 {recordHeaderStats.boxCount}
                  </span>
                  <span style={{ display:'inline-block', padding:'1px 6px', border:'1px solid #d3adf7', background:'#f9f0ff', color:'#531dab', borderRadius:4, fontWeight:700 }}>
                    产品总数 {recordHeaderStats.productQty}
                  </span>
                  <Button
                    size="small"
                    type="primary"
                    ghost
                    disabled={(records || []).length === 0}
                    onClick={downloadAllRecordsExcel}
                  >
                    下载
                  </Button>
                </div>
              )}
            >
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap: 12, marginBottom: 10 }}>
                <Input.Search
                  allowClear
                  size="small"
                  value={recordSearch}
                  onChange={(e) => { setRecordSearch(e.target.value); setRecordPage(1); }}
                  placeholder="筛选：新条码 / 旧条码 / 容器编码 / 操作对象系统编码（模糊搜索，不区分大小写）"
                  style={{ width: 560 }}
                />
                <span style={{ color:'#8c8c8c' }}>
                  当前显示 {recordShownRows.length} 条
                </span>
              </div>
              <Table
                size="small"
                rowKey={(r:any)=> r.id}
                columns={buildRecordCols(recordPage, recordPageSize) as any}
                dataSource={recordShownRows}
                onRow={(row:any) => {
                  const ev = String(row?.event || '');
                  if (ev === '扫码失败') {
                    return { style: { background: '#fff1f0' } };
                  }
                  const bg = recordRowBgById.get(String(row?.id || ''));
                  return bg ? { style: { background: bg } } : {};
                }}
                pagination={{
                  current: recordPage,
                  pageSize: recordPageSize,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  pageSizeOptions: ['10','20','50','100'] as any,
                  showTotal: (total:number, range:[number, number]) => `共${total}条记录，显示${range[0]}-${range[1]}条`,
                  onChange: (p:number, ps:number) => { setRecordPage(p); setRecordPageSize(ps); },
                }}
              />
            </Card>
          </div>
        </div>

        {/* 草稿/提交 */}
        <Space>
          <Button onClick={doSaveDraft} loading={saving} disabled={!canOperateExec}>保存草稿</Button>
        </Space>
      </Space>

      {/* 基线文件预览/下载弹窗 */}
      <Modal
        title="基线文件 - 新旧条码对应表"
        open={blVisible}
        onCancel={() => setBlVisible(false)}
        footer={[<Button key="close" onClick={()=> setBlVisible(false)}>关闭</Button>]}
        width={720}
      >
        <Table
          size="small"
          rowKey={(r:any, i?:number)=> String(r?.id ?? i ?? 0)}
          loading={blLoading}
          dataSource={blFiles}
          pagination={{
            current: 1,
            pageSize: Math.max(1, blFiles.length || 10),
            total: blFiles.length,
            showSizeChanger: false,
            showQuickJumper: false,
          }}
          columns={[
            { title: '序号', dataIndex: '__idx', width: 80, align: 'center', render: (_:any, __:any, idx:number) => (idx + 1) },
            { title: '文件名', dataIndex: 'file_name', render: (v:any) => v || '-' },
            { title: '类型', dataIndex: 'mime_type', width: 160, render: (v:any) => v || '-' },
            { title: '大小', dataIndex: 'file_size', width: 120, render: (v:any) => (v != null ? `${v}B` : '-') },
            {
              title: '操作',
              key: 'ops',
              width: 140,
              render: (_:any, row:any) => {
                const url = resolveFileUrl(row);
                return (
                  <Space>
                    <Button size="small" type="link" onClick={() => previewFile(row)} disabled={!url}>预览</Button>
                    <Button size="small" onClick={() => {
                      if (!url) { message.warning('无可用链接'); return; }
                      const a = document.createElement('a');
                      a.href = url;
                      a.target = '_blank';
                      a.rel = 'noopener';
                      a.download = String(row?.file_name || 'mapping');
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                    }} disabled={!url}>下载</Button>
                  </Space>
                );
              }
            }
          ] as any}
        />
      </Modal>

      {/* 新增到已有容器：选择已有容器 */}
      <Modal
        title={(
          <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-start', gap: 12, flexWrap:'wrap', paddingRight: 48 }}>
            <span>选择已有容器</span>
            <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background:'#e6f4ff', borderColor:'#91caff', color:'#0958d9', fontWeight: 800 }}>
              {`已有容器 ${closedContainers.length}`}
            </Tag>
          </div>
        )}
        open={pickClosedOpen}
        onCancel={() => { setPickClosedOpen(false); setPickClosedCode(''); setPickClosedQuery(''); setPickClosedPage(1); setPickClosedPageSize(10); }}
        onOk={() => {
          const cc = String(pickClosedCode || '').trim();
          if (!cc) { message.warning('请先选择一个已结束容器'); return; }
          setAddToClosedEnabled(true);
          setAddToClosedCode(cc);
          setPickClosedOpen(false);
          try {
            const rec: OpRecordRow = { id: makeRecId(), ts: Date.now(), event: '新增到已有容器', container_code: cc, qty: 0, old_code: '', new_code: '' };
            setRecords(prev => [rec, ...prev]);
          } catch {}
        }}
        okText="开始新增"
        cancelText="取消"
        width={720}
        destroyOnClose
      >
        <div style={{ marginBottom: 10, display:'flex', gap: 8, alignItems:'center', flexWrap:'wrap' }}>
          <span style={{ color:'#595959', fontWeight: 700 }}>筛选：</span>
          <Input
            allowClear
            autoFocus
            placeholder="扫描/输入容器系统编码（模糊匹配）"
            value={pickClosedQuery}
            onChange={(e) => setPickClosedQuery(String((e as any)?.target?.value || ''))}
            onPressEnter={() => {}}
            style={{ width: 360, fontFamily:'monospace' }}
          />
          {pickClosedQuery ? (
            <span style={{ color:'#8c8c8c' }}>{`匹配 ${closedContainersFiltered.length}/${closedContainers.length}`}</span>
          ) : null}
        </div>
        <Table
          size="small"
          rowKey={(r:any)=> String(r?.code || '')}
          pagination={{
            current: pickClosedPage,
            pageSize: pickClosedPageSize,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10','20','50'] as any,
            onChange: (p:number, ps:number) => { setPickClosedPage(p); setPickClosedPageSize(ps); },
          }}
          dataSource={closedContainersFiltered}
          rowSelection={{
            type: 'radio',
            selectedRowKeys: pickClosedCode ? [pickClosedCode] : [],
            onChange: (keys: React.Key[]) => {
              const k = String(keys?.[0] || '').trim();
              setPickClosedCode(k);
            }
          }}
          columns={[
            { title: '容器系统编码', dataIndex: 'code', render: (v:any)=> {
              const s = String(v || '').trim();
              return <Typography.Text style={{ fontFamily:'monospace' }} copyable={{ text: s }}>{s || '-'}</Typography.Text>;
            }},
            { title: '容器类型', dataIndex: 'container_type', width: 140, render: (_:any, r:any)=> {
              const cc = String(r?.code || '').trim();
              const seg = cc.split('-');
              const mid = String(seg?.[1] || '').trim().toUpperCase();
              if (mid === 'B') return <Tag>纸箱</Tag>;
              if (mid === 'P') return <Tag>托盘</Tag>;
              if (mid === 'O') return <Tag>其他</Tag>;
              return <Tag>{mid || '-'}</Tag>;
            }},
          ] as any}
        />
      </Modal>

      {/* 进度明细弹窗：映射/箱内记录 */}
      <Modal
        title={(
          <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-start', gap: 10, width: '100%', flexWrap:'wrap' }}>
            <span>{mapDetailTitle || '记录明细'}</span>
            <span style={{ color:'#595959', fontWeight: 700 }}>操作记录</span>
            <span style={{ display:'inline-flex', alignItems:'center', gap: 8, flexWrap:'wrap' }}>
              {mapDetailFilter.kind === 'container' ? (
                <Tag
                  style={{ marginInlineEnd: 0, borderRadius: 999, background:'#fff7e6', borderColor:'#ffd591', color:'#ad4e00' }}
                >
                  <span style={{ display:'inline-flex', alignItems:'center', gap: 6 }}>
                    <TagsOutlined />
                    <span>涉及新标签{mapDetailNewLabelStats.newLabelCount}</span>
                  </span>
                </Tag>
              ) : (
                <Tag
                  style={{ marginInlineEnd: 0, borderRadius: 999, background:'#e6f4ff', borderColor:'#91caff', color:'#0958d9' }}
                >
                  <span style={{ display:'inline-flex', alignItems:'center', gap: 6 }}>
                    <InboxOutlined />
                    <span>涉及容器{mapDetailStats.boxCount}</span>
                  </span>
                </Tag>
              )}
              <Tag
                style={{ marginInlineEnd: 0, borderRadius: 999, background:'#f6ffed', borderColor:'#b7eb8f', color:'#389e0d' }}
              >
                <span style={{ display:'inline-flex', alignItems:'center', gap: 6 }}>
                  <ShoppingOutlined />
                  <span>{mapDetailFilter.kind === 'container' ? '总数量' : '操作总数量'}{mapDetailStats.totalQty}</span>
                </span>
              </Tag>
            </span>
          </div>
        )}
        open={mapDetailOpen}
        onCancel={() => { setMapDetailOpen(false); setMapDetailRows([]); setMapDetailFilter({ kind: 'none' }); setMapDetailHighlightKey(''); }}
        footer={[<Button key="close" onClick={() => { setMapDetailOpen(false); setMapDetailRows([]); setMapDetailFilter({ kind: 'none' }); setMapDetailHighlightKey(''); }}>关闭</Button>]}
        width={900}
        zIndex={mapBoxOpen ? 1200 : 1000}
      >
        <Table
          size="small"
          rowKey={(r:any, i?:number)=> String(r?.id ?? i ?? 0)}
          dataSource={mapDetailLiveRows}
          onRow={(row:any) => {
            const ev = String(row?.event || '');
            if (ev === '扫码失败') return { style: { background: '#fff1f0' } };
            const hk = String(mapDetailHighlightKey || '').trim();
            if (hk) {
              const k = `${String(row?.old_code || '').toUpperCase()}->${String(row?.new_code || '').toUpperCase()}`;
              if (k === hk) return { style: { background: '#f6ffed' } };
            }
            return {};
          }}
          pagination={{
            current: recordDetailPage,
            pageSize: recordDetailPageSize,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10','20','50','100'] as any,
            showTotal: (total:number, range:[number, number]) => `共${total}条记录，显示${range[0]}-${range[1]}条`,
            onChange: (p:number, ps:number) => { setRecordDetailPage(p); setRecordDetailPageSize(ps); },
          }}
          columns={buildRecordCols(recordDetailPage, recordDetailPageSize) as any}
        />
      </Modal>

      {/* 查看装箱弹窗：按映射行汇总各容器数量 */}
      <Modal
        title={(
          <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-start', gap: 10, width: '100%', flexWrap:'wrap' }}>
            <span>{mapBoxTitle || '装箱汇总'}</span>
            <span style={{ display:'inline-flex', alignItems:'center', gap: 8, flexWrap:'wrap' }}>
              <Tag
                style={{ marginInlineEnd: 0, borderRadius: 999, background:'#e6f4ff', borderColor:'#91caff', color:'#0958d9' }}
              >
                <span style={{ display:'inline-flex', alignItems:'center', gap: 6 }}>
                  <InboxOutlined />
                  <span>涉及容器{mapBoxStats.boxCount}</span>
                </span>
              </Tag>
              <Tag
                style={{ marginInlineEnd: 0, borderRadius: 999, background:'#f6ffed', borderColor:'#b7eb8f', color:'#389e0d' }}
              >
                <span style={{ display:'inline-flex', alignItems:'center', gap: 6 }}>
                  <ShoppingOutlined />
                  <span>操作总数量{mapBoxStats.totalQty}</span>
                </span>
              </Tag>
            </span>
          </div>
        )}
        open={mapBoxOpen}
        onCancel={() => { setMapBoxOpen(false); setMapBoxRows([]); setMapBoxKey(''); }}
        footer={[<Button key="close" onClick={() => { setMapBoxOpen(false); setMapBoxRows([]); setMapBoxKey(''); }}>关闭</Button>]}
        width={860}
        zIndex={1100}
      >
        <Table
          size="small"
          rowKey={(r:any, i?:number)=> String(r?.container_code ?? i ?? 0)}
          dataSource={mapBoxRows}
          onRow={(row:any) => {
            const isPool = !!row?.__isPool;
            return isPool ? { style: { background: '#fafafa' } } : {};
          }}
          pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10','20','50','100'] as any }}
          columns={[
            { title: '序号', width: 70, align: 'center', render: (_:any, __:any, idx:number) => idx + 1 },
            { title: '容器', dataIndex: 'container_code', width: 260, render: (v:any)=> {
              const s = String(v || '').trim();
              if (!s) return '-';
              if (s === String(poolCode || '').trim()) {
                return (
                  <span style={{ display:'inline-flex', alignItems:'center', gap: 8 }}>
                    <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background:'#fafafa', borderColor:'#d9d9d9', color:'#595959', fontWeight: 700 }}>
                      调整池
                    </Tag>
                  </span>
                );
              }
              return <Typography.Text style={{ fontFamily:'monospace' }} copyable={{ text: s }}>{s}</Typography.Text>;
            }},
            { title: '数量合计', dataIndex: 'qty', width: 120, render: (v:any)=> <span style={{ fontWeight: 700 }}>{Math.floor(Number(v||0)||0)}</span> },
            { title: '记录条数', dataIndex: 'cnt', width: 100, render: (v:any)=> <span style={{ color:'#595959' }}>{Math.floor(Number(v||0)||0)}</span> },
            { title: '涉及操作对象', dataIndex: 'iuIds', render: (v:any)=> {
              const ids: number[] = Array.isArray(v) ? v : [];
              if (!ids.length) return <span style={{ color:'#999' }}>-</span>;
              return (
                <span style={{ display:'inline-flex', flexWrap:'wrap', gap: 6 }}>
                  {ids.slice(0, 8).map((id:number) => {
                    const sys = iuIdToSys.get(id) || String((opIuSysById as any)?.[id] || '').trim() || `IU#${id}`;
                    return <Tag key={`iu_${id}`} style={{ marginInlineEnd: 0, borderRadius: 999 }}>{sys}</Tag>;
                  })}
                  {ids.length > 8 ? <Tag style={{ marginInlineEnd: 0, borderRadius: 999 }}>{`+${ids.length - 8}`}</Tag> : null}
                </span>
              );
            }},
            { title: '最近时间', dataIndex: 'lastTs', width: 170, render: (v:any)=> (v ? new Date(Number(v)).toLocaleString() : '-') },
            { title: '操作', key: 'ops', width: 120, render: (_:any, row:any) => {
              const cc = String(row?.container_code || '').trim();
              if (cc === String(poolCode || '').trim()) return <span style={{ color:'#999' }}>-</span>;
              return (
                <Button size="small" onClick={() => {
                  try {
                  const title = mapBoxTitle ? `${mapBoxTitle} · 容器 ${cc} · 全量记录` : `容器 ${cc} 全量记录`;
                    setMapDetailTitle(title);
                    setMapDetailRows([]);
                  // 复用“按容器统计-查看记录”口径：显示容器的全量记录（换标+调整+重扫+新增等）
                  setMapDetailFilter({ kind: 'container', container_code: cc, onlyRelabel: false });
                    setMapDetailHighlightKey(String(mapBoxKey || '').trim());
                    setMapDetailOpen(true);
                  } catch {}
                }}>查看明细</Button>
              );
            }},
          ] as any}
        />
      </Modal>

      {/* 容器间移动：全量移动预览/确认 */}
      <Modal
        title={(
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap: 10, width: '100%', paddingRight: 40 }}>
            <span>容器内所有产品（共{Math.floor(Number(adjustMoveAllPreviewTotal || 0) || 0)}件）</span>
            <span style={{ color:'#8c8c8c', fontSize: 12 }}>确认后将全部移入目标容器</span>
          </div>
        )}
        open={adjustMoveAllPreviewOpen}
        onCancel={() => {
          setAdjustMoveAllPreviewOpen(false);
          setAdjustMoveAll(false);
          setAdjustMoveAllPreviewRows([]);
          setAdjustMoveAllPreviewTotal(0);
        }}
        onOk={async () => {
          setAdjustMoveAllPreviewOpen(false);
          await execAdjustByMode();
          setAdjustMoveAllPreviewRows([]);
          setAdjustMoveAllPreviewTotal(0);
        }}
        okText="确认并执行移动"
        cancelText="取消"
        okButtonProps={{ danger: true }}
        width={860}
        zIndex={1150}
      >
        <div style={{ marginBottom: 8, color:'#595959' }}>
          <span style={{ fontWeight: 700 }}>取出容器：</span>
          <Typography.Text style={{ fontFamily:'monospace' }}>{String(consoleBoxCode || '').trim() || '-'}</Typography.Text>
          <span style={{ margin: '0 10px' }} />
          <span style={{ fontWeight: 700 }}>移入容器：</span>
          <Typography.Text style={{ fontFamily:'monospace' }}>{String(adjustToBoxCode || '').trim() || '-'}</Typography.Text>
        </div>
        <Table
          size="small"
          rowKey={(r:any, i?:number)=> String(r?.new_code ?? i ?? 0)}
          dataSource={adjustMoveAllPreviewRows}
          pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10','20','50','100'] as any }}
          columns={[
            { title: '序号', width: 70, align: 'center', render: (_:any, __:any, idx:number) => idx + 1 },
            { title: '新条码', dataIndex: 'new_code', width: 320, render: (v:any) => {
              const s = String(v || '').trim();
              return <Typography.Text style={{ fontFamily:'monospace' }} copyable={{ text: s }}>{s || '-'}</Typography.Text>;
            }},
            { title: '数量', dataIndex: 'qty', width: 120, render: (v:any) => <span style={{ fontWeight: 800 }}>{Math.floor(Number(v||0)||0)}</span> },
            { title: '涉及操作对象', dataIndex: 'iuIds', render: (v:any) => {
              const ids: number[] = Array.isArray(v) ? v : [];
              if (!ids.length) return <span style={{ color:'#999' }}>-</span>;
              return (
                <span style={{ display:'inline-flex', flexWrap:'wrap', gap: 6 }}>
                  {ids.slice(0, 8).map((id:number) => {
                    const sys = iuIdToSys.get(id) || String((opIuSysById as any)?.[id] || '').trim() || `IU#${id}`;
                    return <Tag key={`mv_iu_${id}`} style={{ marginInlineEnd: 0, borderRadius: 999 }}>{sys}</Tag>;
                  })}
                  {ids.length > 8 ? <Tag style={{ marginInlineEnd: 0, borderRadius: 999 }}>{`+${ids.length - 8}`}</Tag> : null}
                </span>
              );
            }},
          ] as any}
        />
      </Modal>

      {/* 重扫结果比对：重扫前理论内容物 vs 重扫后现状 */}
      <Modal
        title={(() => {
          const cc = String(rescanFromBoxCode || '').trim() || String(consoleBoxCode || '').trim();
          return (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap: 10, width: '100%', paddingRight: 40 }}>
              <span>{cc ? `重扫结果比对 · ${cc}` : '重扫结果比对'}</span>
              <span style={{ color:'#8c8c8c', fontSize: 12 }}>理论内容物：重扫开始时快照</span>
            </div>
          );
        })()}
        open={rescanCompareOpen}
        onCancel={() => setRescanCompareOpen(false)}
        footer={[
          <Button
            key="cleanup"
            danger
            disabled={!rescanStarted}
            onClick={() => {
              try {
                const cc = String(rescanFromBoxCode || consoleBoxCode || '').trim();
                if (!cc) { message.warning('请先开始重扫'); return; }
                Modal.confirm({
                  title: '确认一键清理误操作？',
                  centered: true,
                  okText: '确认清理并清空池余量',
                  cancelText: '取消',
                  okButtonProps: { danger: true },
                  content: (
                    <div style={{ color:'#595959', lineHeight: 1.6 }}>
                      <div>将把“本次重扫产生但未加回原容器”的调整池余量一键作废并清空。</div>
                      <div style={{ marginTop: 6, color:'#8c8c8c' }}>
                        实现方式为追加 <span style={{ fontFamily:'monospace' }}>重扫-作废</span> 负记录（不直接删除历史记录），以保持可审计。
                      </div>
                    </div>
                  ),
                  onOk: () => {
                    try {
                      const pool = String(poolCode || '').trim();
                      if (!pool) return;
                      // 计算当前池净额（iu+old+new -> qty）
                      const curNet: Record<string, number> = {};
                      (records || []).forEach((r:any) => {
                        if (String(r?.container_code || '').trim() !== pool) return;
                        const ev = String(r?.event || '').trim();
                        if (!(ev.startsWith('调整-') || ev.startsWith('重扫-'))) return;
                        const iu = Math.floor(Number(r?.op_iu_id || 0) || 0);
                        const oldc = String(r?.old_code || '').trim().toUpperCase() || '-';
                        const nc = String(r?.new_code || '').trim().toUpperCase() || '-';
                        const q = Math.trunc(Number(r?.qty || 0) || 0);
                        if (!(iu > 0) || !Number.isFinite(q) || q === 0 || !nc || nc === '-') return;
                        const k = `${iu}__${oldc}__${nc}`;
                        curNet[k] = Math.trunc(Number(curNet[k] || 0) || 0) + q;
                      });

                      const basePool = rescanPoolNetBaselineByKey || {};
                      const baseLots = rescanBaselineLotsNetByKey || {};
                      const recsToPush: OpRecordRow[] = [];
                      const now = Date.now();
                      let cleared = 0;
                      Object.keys(baseLots).forEach((k) => {
                        const lot = (baseLots as any)[k];
                        const baselinePoolQty = Math.trunc(Number((basePool as any)[k] || 0) || 0);
                        const currentPoolQty = Math.trunc(Number((curNet as any)[k] || 0) || 0);
                        const delta = currentPoolQty - baselinePoolQty; // 本次重扫期间该 key 在池里的净增
                        const canClear = Math.max(0, Math.min(Math.trunc(Number(lot?.qty || 0) || 0), delta));
                        if (canClear <= 0) return;
                        cleared += canClear;
                        recsToPush.push({
                          id: makeRecId(),
                          ts: now,
                          event: '重扫-作废',
                          container_code: pool,
                          old_code: String(lot?.old_code || '').trim(),
                          new_code: String(lot?.new_code || '').trim(),
                          qty: -Math.abs(canClear),
                          op_iu_id: Math.floor(Number(lot?.op_iu_id || 0) || 0),
                        });
                      });
                      if (!recsToPush.length || cleared <= 0) {
                        message.info('未发现可清理的本次重扫池余量');
                        return;
                      }
                      pushRecords(recsToPush);
                      message.success(`已清理本次重扫池余量 ${cleared} 件（已作废并清空）`);
                    } catch {
                      message.error('清理失败（请重试）');
                    }
                  }
                });
              } catch {}
            }}
          >
            一键清理误操作
          </Button>,
          <Button
            key="done"
            type="primary"
            onClick={() => {
              setRescanCompareOpen(false);
              // 结束重扫：清理上下文，避免影响下一次重扫
              setRescanStarted(false);
              setRescanFromBoxCode('');
              setRescanBaselineRows([]);
              setRescanBaselineLotsNetByKey({});
              setRescanPoolNetBaselineByKey({});
              setRescanNewCode('');
              setRescanQty(1);
            }}
          >
            结束重扫
          </Button>,
          <Button key="close" onClick={() => setRescanCompareOpen(false)}>关闭</Button>,
        ]}
        width={900}
        zIndex={1150}
      >
        {(() => {
          try {
            const cc = String(rescanFromBoxCode || consoleBoxCode || '').trim();
            if (!cc) return <div style={{ color:'#999' }}>请先开始重扫</div>;
            const beforeRows = Array.isArray(rescanBaselineRows) ? rescanBaselineRows : [];
            const afterStacks = buildStacks(cc);
            const afterMap = new Map<string, number>();
            for (const [newc, lots] of Array.from(afterStacks.entries())) {
              let s = 0;
              for (const lot of (lots || [])) {
                const q = Math.floor(Number(lot?.qty || 0) || 0);
                if (q > 0) s += q;
              }
              if (s > 0) afterMap.set(String(newc || '').trim(), s);
            }
            const beforeMap = new Map<string, number>();
            for (const r of beforeRows) {
              const k = String((r as any)?.new_code || '').trim();
              const q = Math.floor(Number((r as any)?.qty || 0) || 0);
              if (k && q > 0) beforeMap.set(k, q);
            }
            // 避免 TS2802：Map.keys() 在低 target 下不可直接用 spread 展开
            const keys = Array.from(new Set<string>([
              ...Array.from(beforeMap.keys()),
              ...Array.from(afterMap.keys()),
            ])).sort((a,b)=> a.localeCompare(b));
            const rows = keys.map((k) => {
              const b = beforeMap.get(k) || 0;
              const a = afterMap.get(k) || 0;
              return { new_code: k, before: b, after: a, delta: a - b };
            }).sort((x,y)=> (Math.abs(y.delta) - Math.abs(x.delta)) || (y.after - x.after) || x.new_code.localeCompare(y.new_code));
            const beforeTotal = Array.from(beforeMap.values()).reduce((s,n)=> s + (Number(n)||0), 0);
            const afterTotal = Array.from(afterMap.values()).reduce((s,n)=> s + (Number(n)||0), 0);
            const diffTotal = afterTotal - beforeTotal;

            return (
              <>
                <div style={{ marginBottom: 10, display:'flex', gap: 8, flexWrap:'wrap' }}>
                  <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background:'#fff7e6', borderColor:'#ffd591', color:'#ad4e00', fontWeight: 800 }}>
                    {`重扫前总数${beforeTotal}件`}
                  </Tag>
                  <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background:'#f6ffed', borderColor:'#b7eb8f', color:'#389e0d', fontWeight: 800 }}>
                    {`重扫后总数${afterTotal}件`}
                  </Tag>
                  <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background:'#e6f4ff', borderColor:'#91caff', color:'#0958d9', fontWeight: 800 }}>
                    {`差异${diffTotal >= 0 ? `+${diffTotal}` : String(diffTotal)}件`}
                  </Tag>
                </div>
                <Table
                  size="small"
                  rowKey={(r:any, i?:number)=> String(r?.new_code ?? i ?? 0)}
                  dataSource={rows}
                  pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10','20','50','100'] as any }}
                  columns={[
                    { title: '序号', width: 70, align: 'center', render: (_:any, __:any, idx:number) => idx + 1 },
                    { title: '新条码', dataIndex: 'new_code', width: 320, render: (v:any) => {
                      const s = String(v || '').trim();
                      return <Typography.Text style={{ fontFamily:'monospace' }} copyable={{ text: s }}>{s || '-'}</Typography.Text>;
                    }},
                    { title: '重扫前', dataIndex: 'before', width: 110, render: (v:any)=> <span style={{ fontWeight: 800 }}>{Math.floor(Number(v||0)||0)}</span> },
                    { title: '重扫后', dataIndex: 'after', width: 110, render: (v:any)=> <span style={{ fontWeight: 800 }}>{Math.floor(Number(v||0)||0)}</span> },
                    { title: '差异', dataIndex: 'delta', width: 110, render: (v:any)=> {
                      const n = Math.floor(Number(v||0)||0);
                      if (!n) return <span style={{ color:'#999' }}>0</span>;
                      return <span style={{ fontWeight: 900, color: n < 0 ? '#cf1322' : '#389e0d' }}>{n > 0 ? `+${n}` : String(n)}</span>;
                    }},
                  ] as any}
                />
                <div style={{ marginTop: 8, color:'#8c8c8c', lineHeight: 1.4 }}>
                  提示：若“重扫后总数”小于“重扫前总数”，通常意味着仍有部分产品留在调整池（可稍后继续加回；最终提交前不要求清零）。
                  若“重扫后总数”大于“重扫前总数”，多出的部分建议走“换标+装入容器 → 新增到已有容器”作为新增扫描处理。
                </div>
              </>
            );
          } catch {
            return <div style={{ color:'#999' }}>比对失败（请重试）</div>;
          }
        })()}
      </Modal>

      {/* 最终装箱数据（只读预览）：复用“结束容器确认”展示结构 */}
      <Modal
        title={(() => {
          if (!boxFinalData) return (boxFinalTitle || '最终装箱数据');
          const cc = String(boxFinalData.container_code || '').trim();
          const wg = Number((boxFinalDraft as any)?.weight_g ?? (boxFinalData.meta as any)?.weight_g);
          const actualKg = (Number.isFinite(wg) && wg > 0) ? Math.ceil(wg / 1000) : undefined;
          const limit = Number(limitWeightKg);
          const over = Number.isFinite(limit) && limit > 0 && Number.isFinite(actualKg as any) && (actualKg as any) > limit;
          return (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-start', gap: 10, width:'100%', flexWrap:'wrap' }}>
              <span style={{ display:'inline-flex', alignItems:'center', gap: 8, flexWrap:'wrap' }}>
                <span>最终装箱数据</span>
                {cc ? (
                  <>
                    <span style={{ color:'#d9d9d9' }}>·</span>
                    <span style={{ color:'#fa541c', fontWeight: 800 }}>{cc}</span>
                  </>
                ) : null}
                <span style={{ color:'#8c8c8c' }}>
                  指令:{' '}
                  <span style={{ color:'#1677ff', fontWeight: 800 }}>{String(instrCode || '-')}</span>
                </span>
              </span>
              <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background:'#f9f0ff', borderColor:'#d3adf7', color:'#531dab' }}>
                <span style={{ display:'inline-flex', alignItems:'center', gap: 6 }}>
                  <TagsOutlined />
                  <span>新条码种类{Number(boxFinalData.newKinds ?? 0) || 0}</span>
                </span>
              </Tag>
              <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background:'#f6ffed', borderColor:'#b7eb8f', color:'#389e0d' }}>
                <span style={{ display:'inline-flex', alignItems:'center', gap: 6 }}>
                  <ShoppingOutlined />
                  <span>产品总数{Number(boxFinalData.totalQty ?? 0) || 0}</span>
                </span>
              </Tag>
              <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background:'#fff7e6', borderColor:'#ffd591', color:'#ad4e00' }}>
                <span style={{ display:'inline-flex', alignItems:'center', gap: 6 }}>
                  <span style={{ fontWeight: 700 }}>限重</span>
                  <span>{Number.isFinite(limit) && limit > 0 ? `${Number(limit).toFixed(3)}kg` : '-'}</span>
                </span>
              </Tag>
              <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background:'#e6fffb', borderColor:'#87e8de', color:'#006d75' }}>
                <span style={{ display:'inline-flex', alignItems:'center', gap: 6 }}>
                  <span style={{ fontWeight: 700 }}>实重</span>
                  <span>{actualKg != null ? `${actualKg}kg` : '-'}</span>
                </span>
              </Tag>
              {over ? (
                <Tag style={{ background: '#fffb8f', borderColor: '#fffb8f', color: '#cf1322', fontWeight: 800, fontSize: 14, padding: '2px 10px', lineHeight: '20px', borderRadius: 999, marginInlineEnd: 0 }}>
                  超重
                </Tag>
              ) : null}
            </div>
          );
        })()}
        open={boxFinalOpen}
        onCancel={() => {
          try {
            const cc = String(boxFinalCode || '').trim();
            if (boxFinalDirty && cc && !boxFinalReadonly) {
              const l = (boxFinalDraft as any)?.length_cm;
              const w = (boxFinalDraft as any)?.width_cm;
              const h = (boxFinalDraft as any)?.height_cm;
              const wg2 = (boxFinalDraft as any)?.weight_g;
              // 先更新本地 boxMeta，再异步保存 Draft（仅物理数据，不覆盖 items）
              const lin = (boxFinalLenIn != null && Number.isFinite(Number(boxFinalLenIn)) && Number(boxFinalLenIn) > 0) ? Math.floor(Number(boxFinalLenIn)) : undefined;
              const win = (boxFinalWidIn != null && Number.isFinite(Number(boxFinalWidIn)) && Number(boxFinalWidIn) > 0) ? Math.floor(Number(boxFinalWidIn)) : undefined;
              const hin = (boxFinalHeiIn != null && Number.isFinite(Number(boxFinalHeiIn)) && Number(boxFinalHeiIn) > 0) ? Math.floor(Number(boxFinalHeiIn)) : undefined;
              const wlb = (boxFinalLb != null && Number.isFinite(Number(boxFinalLb)) && Number(boxFinalLb) > 0) ? Math.floor(Number(boxFinalLb)) : undefined;
              setBoxMeta(prev => ({
                ...(prev || {}),
                [cc]: {
                  ...(prev as any)?.[cc],
                  length_cm: l != null ? Number(l) : undefined,
                  width_cm: w != null ? Number(w) : undefined,
                  height_cm: h != null ? Number(h) : undefined,
                  weight_g: wg2 != null ? Number(wg2) : undefined,
                  weight_kg: (wg2 != null && Number.isFinite(Number(wg2)) && Number(wg2) > 0) ? Math.ceil(Number(wg2) / 1000) : undefined,
                  length_in: lin,
                  width_in: win,
                  height_in: hin,
                  weight_lb: wlb,
                }
              }));
              try {
                // 不阻塞关闭：失败仅提示
                request.post('/api/v1/storage/output-containers/drafts/upsert/', {
                  instruction_id: instrIdState,
                  code: cc,
                  status: 'closed_pending_final',
                  length_cm: l,
                  width_cm: w,
                  height_cm: h,
                  weight_g: wg2,
                  length_in: lin,
                  width_in: win,
                  height_in: hin,
                  weight_lb: wlb,
                  replace_items: false,
                }).catch((e:any) => {
                  message.error(e?.response?.data?.detail || e?.message || '保存容器草稿失败');
                });
              } catch {}
              // 修改物理数据后：标记需重打箱标（结束后允许调整/重扫/再打印）
              try { setLabelDirtyByCode(prev => ({ ...(prev || {}), [cc]: true })); } catch {}
              // 乐观更新：同步到 outputDraftByCode（便于箱标立即反映最新尺寸重量）
              try {
                setOutputDraftByCode(prev => ({
                  ...(prev || {}),
                  [cc]: {
                    ...((prev as any)?.[cc] || { code: cc }),
                    code: cc,
                    status: 'closed_pending_final',
                    length_cm: l != null ? Number(l) : undefined,
                    width_cm: w != null ? Number(w) : undefined,
                    height_cm: h != null ? Number(h) : undefined,
                    weight_g: wg2 != null ? Number(wg2) : undefined,
                    length_in: lin,
                    width_in: win,
                    height_in: hin,
                    weight_lb: wlb,
                    updated_at: new Date().toISOString(),
                  }
                }));
              } catch {}
            }
          } catch {}
          setBoxFinalOpen(false); setBoxFinalCode('');
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              try {
                const cc = String(boxFinalCode || '').trim();
                if (boxFinalDirty && cc && !boxFinalReadonly) {
                  const l = (boxFinalDraft as any)?.length_cm;
                  const w = (boxFinalDraft as any)?.width_cm;
                  const h = (boxFinalDraft as any)?.height_cm;
                  const wg2 = (boxFinalDraft as any)?.weight_g;
                  const lin = (boxFinalLenIn != null && Number.isFinite(Number(boxFinalLenIn)) && Number(boxFinalLenIn) > 0) ? Math.floor(Number(boxFinalLenIn)) : undefined;
                  const win = (boxFinalWidIn != null && Number.isFinite(Number(boxFinalWidIn)) && Number(boxFinalWidIn) > 0) ? Math.floor(Number(boxFinalWidIn)) : undefined;
                  const hin = (boxFinalHeiIn != null && Number.isFinite(Number(boxFinalHeiIn)) && Number(boxFinalHeiIn) > 0) ? Math.floor(Number(boxFinalHeiIn)) : undefined;
                  const wlb = (boxFinalLb != null && Number.isFinite(Number(boxFinalLb)) && Number(boxFinalLb) > 0) ? Math.floor(Number(boxFinalLb)) : undefined;
                  setBoxMeta(prev => ({
                    ...(prev || {}),
                    [cc]: {
                      ...(prev as any)?.[cc],
                      length_cm: l != null ? Number(l) : undefined,
                      width_cm: w != null ? Number(w) : undefined,
                      height_cm: h != null ? Number(h) : undefined,
                      weight_g: wg2 != null ? Number(wg2) : undefined,
                      weight_kg: (wg2 != null && Number.isFinite(Number(wg2)) && Number(wg2) > 0) ? Math.ceil(Number(wg2) / 1000) : undefined,
                      length_in: lin,
                      width_in: win,
                      height_in: hin,
                      weight_lb: wlb,
                    }
                  }));
                  try {
                    request.post('/api/v1/storage/output-containers/drafts/upsert/', {
                      instruction_id: instrIdState,
                      code: cc,
                      status: 'closed_pending_final',
                      length_cm: l,
                      width_cm: w,
                      height_cm: h,
                      weight_g: wg2,
                      length_in: lin,
                      width_in: win,
                      height_in: hin,
                      weight_lb: wlb,
                      replace_items: false,
                    }).catch((e:any) => {
                      message.error(e?.response?.data?.detail || e?.message || '保存容器草稿失败');
                    });
                  } catch {}
                }
              } catch {}
              setBoxFinalOpen(false); setBoxFinalCode('');
            }}
          >
            {(boxFinalDirty && !boxFinalReadonly) ? '保存并关闭' : '关闭'}
          </Button>
        ]}
        width={980}
        zIndex={1200}
        destroyOnClose
      >
        {boxFinalData ? (
          <div style={{ display:'flex', flexDirection:'column', gap: 12 }}>
            <Descriptions size="small" bordered column={2}>
              <Descriptions.Item label="容器系统编码">{boxFinalData.container_code}</Descriptions.Item>
              <Descriptions.Item label="容器类型">
                {(() => {
                  const cc = String(boxFinalData.container_code || '').trim();
                  const seg = cc.split('-');
                  const mid = String(seg?.[1] || '').trim().toUpperCase();
                  if (mid === 'B') return '纸箱';
                  if (mid === 'P') return '托盘';
                  if (mid === 'O') return '其他';
                  return mid || '-';
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="容器开始时间">{boxFinalData.startTs ? new Date(Number(boxFinalData.startTs)).toLocaleString() : '-'}</Descriptions.Item>
              <Descriptions.Item label="容器结束时间">{boxFinalData.endTs ? new Date(Number(boxFinalData.endTs)).toLocaleString() : '-'}</Descriptions.Item>
              <Descriptions.Item label="总操作时间">{boxFinalData.durHours != null ? `${Number(boxFinalData.durHours).toFixed(1)}小时` : '-'}</Descriptions.Item>
              <Descriptions.Item label="统计">{`新标签种类 ${boxFinalData.newKinds} | 产品总数 ${boxFinalData.totalQty}`}</Descriptions.Item>
            </Descriptions>

            <Divider style={{ margin:'8px 0' }} />

            <div style={{ display:'flex', alignItems:'flex-start', gap: 12, flexWrap:'wrap' }}>
              <div style={{ flex: 1, minWidth: 420 }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>容器内容物统计</div>
                <Table
                  size="small"
                  rowKey={(r:any)=> String(r?.key || '')}
                  dataSource={boxFinalData.rows || []}
                  pagination={{
                    current: boxFinalContentPage,
                    pageSize: boxFinalContentPageSize,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    pageSizeOptions: ['10','20','50'] as any,
                    onChange: (p:number, ps:number) => { setBoxFinalContentPage(p); setBoxFinalContentPageSize(ps); },
                  }}
                  columns={[
                    { title: '序号', width: 70, align: 'center', render: (_:any, __:any, idx:number) => ((Math.max(1, Number(boxFinalContentPage) || 1) - 1) * (Math.max(1, Number(boxFinalContentPageSize) || 10)) + idx + 1) },
                    { title: '新条码', dataIndex: 'new_code', width: 220, render: (v:any) => (String(v || '').trim() || '-') },
                    { title: '数量', dataIndex: 'qty', width: 120, render: (v:any)=> <span style={{ fontWeight: 700 }}>{Math.floor(Number(v||0)||0)}</span> },
                  ] as any}
                />
              </div>

              <div style={{ width: 420, flexShrink: 0 }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>{`最终尺寸与重量（${boxFinalReadonly ? '只读预览' : '可编辑'}）`}</div>
                {(() => {
                  const cmToIn = (cm: number) => cm / 2.54;
                  const inToCm = (inch: number) => inch * 2.54;
                  const kgToLb = (kg: number) => kg * 2.2046226218;
                  const lbToKg = (lb: number) => lb / 2.2046226218;
                  const cmInput = (val: any, onCmChange: (cm: number | undefined) => void) => (
                    <InputNumber
                      min={1}
                      precision={0}
                      disabled={boxFinalReadonly}
                      value={val as any}
                      onChange={(v) => {
                        const n = (v == null) ? undefined : Math.max(1, Math.floor(Number(v) || 0));
                        onCmChange(n);
                      }}
                      style={{ width: 86 }}
                    />
                  );
                  const inInput = (val: any, onInChange: (inch: number | undefined) => void) => (
                    <InputNumber
                      min={1}
                      precision={0}
                      disabled={boxFinalReadonly}
                      value={val as any}
                      onChange={(v) => {
                        const n = (v == null) ? undefined : Math.max(1, Math.floor(Number(v) || 0));
                        onInChange(n);
                      }}
                      style={{ width: 86 }}
                    />
                  );
                  const kgValue = (() => {
                    const g = Number((boxFinalDraft as any)?.weight_g);
                    if (!Number.isFinite(g) || g <= 0) return undefined;
                    return Math.ceil(g / 1000);
                  })();
                  const setWeightKg = (kg: number | undefined) => {
                    const g = (kg == null) ? undefined : Math.max(1000, Math.floor(Number(kg) || 0) * 1000);
                    setBoxFinalDraft(prev => ({ ...(prev || {}), weight_g: g }));
                  };
                  return (
                    <div style={{ display:'flex', flexDirection:'column', gap: 10 }}>
                      <div style={{ display:'flex', alignItems:'center', gap: 10, flexWrap:'wrap' }}>
                        <div style={{ color:'#595959', width: 70 }}>长度</div>
                        {cmInput((boxFinalDraft as any)?.length_cm, (cm) => {
                          setBoxFinalDraft(prev => ({ ...(prev || {}), length_cm: cm }));
                          const inch = (cm == null) ? undefined : Math.ceil(cmToIn(cm));
                          setBoxFinalLenIn(inch);
                          setBoxFinalDirty(true);
                        })}
                        <span style={{ color:'#8c8c8c' }}>厘米</span>
                        <span style={{ color:'#d9d9d9' }}>|</span>
                        {inInput(boxFinalLenIn, (inch) => {
                          setBoxFinalLenIn(inch);
                          const cm = (inch == null) ? undefined : Math.ceil(inToCm(inch));
                          setBoxFinalDraft(prev => ({ ...(prev || {}), length_cm: cm }));
                          setBoxFinalDirty(true);
                        })}
                        <span style={{ color:'#8c8c8c' }}>英寸</span>
                      </div>

                      <div style={{ display:'flex', alignItems:'center', gap: 10, flexWrap:'wrap' }}>
                        <div style={{ color:'#595959', width: 70 }}>宽度</div>
                        {cmInput((boxFinalDraft as any)?.width_cm, (cm) => {
                          setBoxFinalDraft(prev => ({ ...(prev || {}), width_cm: cm }));
                          const inch = (cm == null) ? undefined : Math.ceil(cmToIn(cm));
                          setBoxFinalWidIn(inch);
                          setBoxFinalDirty(true);
                        })}
                        <span style={{ color:'#8c8c8c' }}>厘米</span>
                        <span style={{ color:'#d9d9d9' }}>|</span>
                        {inInput(boxFinalWidIn, (inch) => {
                          setBoxFinalWidIn(inch);
                          const cm = (inch == null) ? undefined : Math.ceil(inToCm(inch));
                          setBoxFinalDraft(prev => ({ ...(prev || {}), width_cm: cm }));
                          setBoxFinalDirty(true);
                        })}
                        <span style={{ color:'#8c8c8c' }}>英寸</span>
                      </div>

                      <div style={{ display:'flex', alignItems:'center', gap: 10, flexWrap:'wrap' }}>
                        <div style={{ color:'#595959', width: 70 }}>高度</div>
                        <InputNumber
                          min={1}
                          precision={0}
                          disabled={boxFinalReadonly}
                          value={(boxFinalDraft as any)?.height_cm as any}
                          onChange={(v) => {
                            const cm = (v == null) ? undefined : Math.max(1, Math.floor(Number(v) || 0));
                            setBoxFinalDraft(prev => ({ ...(prev || {}), height_cm: cm }));
                            const inch = (cm == null) ? undefined : Math.ceil(cmToIn(cm));
                            setBoxFinalHeiIn(inch);
                            setBoxFinalDirty(true);
                          }}
                          style={{ width: 86 }}
                        />
                        <span style={{ color:'#8c8c8c' }}>厘米</span>
                        <span style={{ color:'#d9d9d9' }}>|</span>
                        {inInput(boxFinalHeiIn, (inch) => {
                          setBoxFinalHeiIn(inch);
                          const cm = (inch == null) ? undefined : Math.ceil(inToCm(inch));
                          setBoxFinalDraft(prev => ({ ...(prev || {}), height_cm: cm }));
                          setBoxFinalDirty(true);
                        })}
                        <span style={{ color:'#8c8c8c' }}>英寸</span>
                      </div>

                      <div style={{ display:'flex', alignItems:'center', gap: 10, flexWrap:'wrap' }}>
                        <div style={{ color:'#595959', width: 70 }}>容器实重</div>
                        <InputNumber
                          min={1}
                          precision={0}
                          disabled={boxFinalReadonly}
                          value={kgValue as any}
                          onChange={(v) => {
                            const kg = (v == null) ? undefined : Math.max(1, Math.floor(Number(v) || 0));
                            setWeightKg(kg);
                            const lb = (kg == null) ? undefined : Math.ceil(kgToLb(kg));
                            setBoxFinalLb(lb);
                            setBoxFinalDirty(true);
                          }}
                          style={{ width: 86 }}
                        />
                        <span style={{ color:'#8c8c8c' }}>公斤</span>
                        <span style={{ color:'#d9d9d9' }}>|</span>
                        <InputNumber
                          min={1}
                          precision={0}
                          disabled={boxFinalReadonly}
                          value={boxFinalLb as any}
                          onChange={(v) => {
                            const lb = (v == null) ? undefined : Math.max(1, Math.floor(Number(v) || 0));
                            setBoxFinalLb(lb);
                            const kg = (lb == null) ? undefined : Math.ceil(lbToKg(lb));
                            setWeightKg(kg);
                            setBoxFinalDirty(true);
                          }}
                          style={{ width: 86 }}
                        />
                        <span style={{ color:'#8c8c8c' }}>磅</span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ color:'#8c8c8c' }}>无可用数据</div>
        )}
      </Modal>

      {/* IOC 箱标预览（4x6 inch）：结束容器后自动弹出 */}
      <Modal
        title={(() => {
          const cc = String(boxLabelCode || '').trim();
          return (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap: 12, width:'100%', flexWrap:'wrap', paddingRight: 48 }}>
              <span>{cc ? `箱标预览 · ${cc}` : '箱标预览'}</span>
              <span style={{ display:'inline-flex', gap: 8, alignItems:'center', marginRight: 16 }}>
                <Button
                  size="small"
                  disabled={!boxLabelBlob}
                  onClick={() => {
                    try {
                      if (!boxLabelBlob) return;
                      const href = URL.createObjectURL(boxLabelBlob);
                      const a = document.createElement('a');
                      a.href = href;
                      a.download = `${cc || 'output_container'}.pdf`;
                      a.click();
                      setTimeout(() => { try { URL.revokeObjectURL(href); } catch {} }, 2000);
                    } catch {}
                  }}
                >
                  下载PDF
                </Button>
              </span>
            </div>
          );
        })()}
        open={boxLabelOpen}
        onCancel={() => {
          try { if (boxLabelUrl) URL.revokeObjectURL(boxLabelUrl); } catch {}
          setBoxLabelOpen(false);
          setBoxLabelUrl('');
          setBoxLabelBlob(null);
          setBoxLabelCode('');
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              try { if (boxLabelUrl) URL.revokeObjectURL(boxLabelUrl); } catch {}
              setBoxLabelOpen(false);
              setBoxLabelUrl('');
              setBoxLabelBlob(null);
              setBoxLabelCode('');
            }}
          >
            关闭
          </Button>
        ]}
        width={980}
        zIndex={1300}
        destroyOnClose
      >
        {boxLabelLoading ? (
          <div style={{ padding: 16, color:'#595959' }}>正在生成箱标预览…</div>
        ) : boxLabelUrl ? (
          <iframe
            title="output_container_label_preview"
            src={boxLabelUrl}
            style={{ width:'100%', height:'72vh', border:'1px solid #f0f0f0', borderRadius: 6 }}
          />
        ) : (
          <div style={{ padding: 16, color:'#999' }}>暂无预览</div>
        )}
      </Modal>

      {/* 文件预览弹窗（CSV/XLSX/PDF） */}
      <Modal
        title={(() => {
          const suffix = pvFileName ? ` · ${pvFileName}` : '';
          const left = (() => {
            if (pvKind === 'csv') return `文件预览（CSV）${suffix}`;
            if (pvKind === 'xlsx') return `文件预览（Excel${pvSheetName ? ' · ' + pvSheetName : ''}）${suffix}`;
            if (pvKind === 'pdf') return `文件预览（PDF）${suffix}`;
            if (pvKind === 'image') return `文件预览（图片）${suffix}`;
            return `文件预览${suffix}`;
          })();
          // CSV/XLSX 右侧增加搜索框（精确匹配，忽略大小写）
          if (pvKind === 'csv' || pvKind === 'xlsx') {
            return (
              <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-start', gap: 12, paddingRight: 120 }}>
                <span>{left}</span>
                <Input
                  size="small"
                  allowClear
                  value={pvSearch}
                  onChange={(e)=> setPvSearch(e.target.value)}
                  placeholder="搜索（模糊匹配，不区分大小写）"
                  style={{ width: 280 }}
                />
                <span style={{ flex: 1 }} />
              </div>
            );
          }
          return left;
        })()}
        open={pvVisible}
        onCancel={() => setPvVisible(false)}
        footer={[<Button key="close" onClick={()=> setPvVisible(false)}>关闭</Button>]}
        width={1280}
        zIndex={2000}
        maskClosable
        getContainer={() => document.body}
      >
        {pvLoading ? <div style={{ color:'#999' }}>加载中...</div> : (
          pvKind === 'pdf' ? (
            <div style={{ height: '70vh', border:'1px solid #d9d9d9' }}>
              <iframe src={pvUrl} style={{ width:'100%', height:'100%', border: 'none' }} title="PDF Preview" />
            </div>
          ) : pvKind === 'image' ? (
            <div style={{ display:'flex', justifyContent:'center', alignItems:'center', height: '70vh', border:'1px solid #d9d9d9' }}>
              <img
                alt="IMAGE PREVIEW"
                src={pvUrl}
                style={{ maxWidth:'100%', maxHeight: '100%', objectFit: 'contain' }}
                onLoad={(e:any) => { try { console.debug('[Baseline][Preview][IMG] onLoad', { width: e?.currentTarget?.naturalWidth, height: e?.currentTarget?.naturalHeight }); } catch {} }}
                onError={() => { try { console.debug('[Baseline][Preview][IMG] onError', { pvUrl }); } catch {} }}
              />
            </div>
          ) : (
            <Table
              size="small"
              rowKey={(r:any, i?:number)=> String(r?.__idx ?? i ?? 0)}
              dataSource={pvFilteredRows}
              columns={([
                {
                  title: '序号',
                  dataIndex: '__seq',
                  width: 80,
                  align: 'center',
                  render: (_:any, __:any, idx:number) => ((pvPage - 1) * pvPageSize + idx + 1),
                },
                ...(pvColumns as any[] || []),
              ]) as any}
              pagination={{
                current: pvPage,
                pageSize: pvPageSize,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: ['10','20','50','100'] as any,
                total: Array.isArray(pvFilteredRows) ? pvFilteredRows.length : 0,
                showTotal: (total:number, range:[number, number]) => `共${total}条记录，显示${range[0]}-${range[1]}条`,
                onChange: (p:number, ps:number) => { setPvPage(p); setPvPageSize(ps); },
              }}
              scroll={{ y: 400 }}
            />
          )
        )}
      </Modal>

      {/* 结束容器确认弹窗：展示统计 + 允许修正（尺寸/重量/内容数量） */}
      <Modal
        title={(
          <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-start', gap: 10, width:'100%', flexWrap:'wrap' }}>
            <span style={{ display:'inline-flex', alignItems:'center', gap: 8, flexWrap:'wrap' }}>
              <span>结束容器确认</span>
              {endBoxDraft?.code ? (
                <>
                  <span style={{ color:'#d9d9d9' }}>·</span>
                  <span style={{ color:'#fa541c', fontWeight: 800 }}>{String(endBoxDraft.code)}</span>
                </>
              ) : null}
              <span style={{ color:'#8c8c8c' }}>
                指令:{' '}
                <span style={{ color:'#1677ff', fontWeight: 800 }}>{String(instrCode || '-')}</span>
              </span>
            </span>
            <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background:'#f9f0ff', borderColor:'#d3adf7', color:'#531dab' }}>
              <span style={{ display:'inline-flex', alignItems:'center', gap: 6 }}>
                <TagsOutlined />
                <span>新条码种类{Number(endBoxStats?.newKinds ?? 0) || 0}</span>
              </span>
            </Tag>
            <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background:'#f6ffed', borderColor:'#b7eb8f', color:'#389e0d' }}>
              <span style={{ display:'inline-flex', alignItems:'center', gap: 6 }}>
                <ShoppingOutlined />
                <span>产品总数{Number(endBoxStats?.totalQty ?? 0) || 0}</span>
              </span>
            </Tag>
            <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background:'#fff7e6', borderColor:'#ffd591', color:'#ad4e00' }}>
              <span style={{ display:'inline-flex', alignItems:'center', gap: 6 }}>
                <span style={{ fontWeight: 700 }}>限重</span>
                <span>
                  {limitWeightKg != null ? `${Number(limitWeightKg).toFixed(3)}kg` : '-'}
                </span>
              </span>
            </Tag>
            <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background:'#e6fffb', borderColor:'#87e8de', color:'#006d75' }}>
              <span style={{ display:'inline-flex', alignItems:'center', gap: 6 }}>
                <span style={{ fontWeight: 700 }}>实重</span>
                <span>
                  {(() => {
                    const g = Number(endBoxDraft?.weight_g);
                    if (!Number.isFinite(g) || g <= 0) return '-';
                    return `${Math.ceil(g / 1000)}kg`;
                  })()}
                </span>
              </span>
            </Tag>
            {(() => {
              const limit = Number(limitWeightKg);
              const g = Number(endBoxDraft?.weight_g);
              const actualKg = (Number.isFinite(g) && g > 0) ? Math.ceil(g / 1000) : undefined;
              const over = Number.isFinite(limit) && limit > 0 && Number.isFinite(actualKg as any) && (actualKg as any) > limit;
              if (!over) return null;
              return (
                <Tag style={{ background: '#fffb8f', borderColor: '#fffb8f', color: '#cf1322', fontWeight: 800, fontSize: 14, padding: '2px 10px', lineHeight: '20px', borderRadius: 999, marginInlineEnd: 0 }}>
                  超重
                </Tag>
              );
            })()}
          </div>
        )}
        open={endBoxOpen}
        onCancel={() => { if (!endBoxSubmitting) setEndBoxOpen(false); }}
        onOk={submitEndCurrentBox}
        okText="提交并结束容器"
        cancelText="取消"
        confirmLoading={endBoxSubmitting}
        width={980}
        destroyOnClose
      >
        <div style={{ display:'flex', flexDirection:'column', gap: 12 }}>
          <Descriptions size="small" bordered column={2}>
            <Descriptions.Item label="容器系统编码">{endBoxDraft?.code || '-'}</Descriptions.Item>
            <Descriptions.Item label="包材类型">
              {(() => {
                const t = String(endBoxDraft?.container_type || '').toLowerCase();
                if (t === 'pallet') return '托盘';
                if (t === 'box' || t === 'carton') return '箱';
                if (t) return t;
                return '-';
              })()}
            </Descriptions.Item>
            <Descriptions.Item label="开始容器时间">{endBoxStats?.startText || '-'}</Descriptions.Item>
            <Descriptions.Item label="容器记录统计">
              {endBoxStats
                ? `换标记录 ${endBoxStats.swapCount} 条 | 扫码失败 ${endBoxStats.failCount} 条 | 产品总数 ${endBoxStats.totalQty} | 旧条码种类 ${endBoxStats.oldKinds} | 新条码种类 ${endBoxStats.newKinds}`
                : '-'}
            </Descriptions.Item>
          </Descriptions>

          <Divider style={{ margin:'8px 0' }} />

          <div style={{ display:'flex', alignItems:'flex-start', gap: 12, flexWrap:'wrap' }}>
            <div style={{ flex: 1, minWidth: 420 }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>容器内容物统计</div>
              <Table
                size="small"
                rowKey={(r:any)=> r.key}
                dataSource={endBoxContentRows}
                pagination={{
                  current: endBoxContentPage,
                  pageSize: endBoxContentPageSize,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  pageSizeOptions: ['10','20','50'] as any,
                  onChange: (p:number, ps:number) => { setEndBoxContentPage(p); setEndBoxContentPageSize(ps); },
                }}
                columns={[
                  { title: '序号', width: 70, align: 'center', render: (_:any, __:any, idx:number) => ((Math.max(1, Number(endBoxContentPage) || 1) - 1) * (Math.max(1, Number(endBoxContentPageSize) || 10)) + idx + 1) },
                  { title: '旧条码', dataIndex: 'old_code', width: 200, render: (v:any) => (String(v || '').trim() || '-') },
                  { title: '新条码', dataIndex: 'new_code', width: 220, render: (v:any) => (String(v || '').trim() || '-') },
                  {
                    title: '数量',
                    dataIndex: 'qty',
                    width: 120,
                    render: (_:any, row:any) => {
                      const q = Math.floor(Number(row?.qty || 0) || 0);
                      return <span style={{ fontWeight: 700 }}>{q}</span>;
                    }
                  },
                ] as any}
              />
            </div>

            <div style={{ width: 420, flexShrink: 0 }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>容器尺寸与重量（可修改）</div>
              {(() => {
                const cmToIn = (cm: number) => cm / 2.54;
                const inToCm = (inch: number) => inch * 2.54;
                const kgToLb = (kg: number) => kg * 2.2046226218;
                const lbToKg = (lb: number) => lb / 2.2046226218;
                const isPallet = String(endBoxDraft?.container_type || '').toLowerCase() === 'pallet';

                const cmInput = (val: any, onCmChange: (cm: number | undefined) => void) => (
                  <InputNumber
                    min={1}
                    precision={0}
                    value={val as any}
                    onChange={(v) => {
                      const n = (v == null) ? undefined : Math.max(1, Math.floor(Number(v) || 0));
                      onCmChange(n);
                    }}
                    style={{ width: 86 }}
                  />
                );

                const inInput = (val: any, onInChange: (inch: number | undefined) => void, disabled?: boolean) => (
                  <InputNumber
                    min={1}
                    precision={0}
                    disabled={!!disabled}
                    value={val as any}
                    onChange={(v) => {
                      const n = (v == null) ? undefined : Math.max(1, Math.floor(Number(v) || 0));
                      onInChange(n);
                    }}
                    style={{ width: 86 }}
                  />
                );

                const kgValue = (() => {
                  const g = Number(endBoxDraft?.weight_g);
                  if (!Number.isFinite(g) || g <= 0) return undefined;
                  return Math.ceil(g / 1000);
                })();

                const lbValue = (() => {
                  const kg = Number(kgValue);
                  if (!Number.isFinite(kg) || kg <= 0) return undefined;
                  return Math.ceil(kgToLb(kg));
                })();

                const setWeightKg = (kg: number | undefined) => {
                  const g = (kg == null) ? undefined : Math.max(1000, Math.floor(Number(kg) || 0) * 1000);
                  setEndBoxDraft(prev => ({ ...(prev || { code:'', container_type:'box' as any }), weight_g: g }));
                };

                return (
                  <div style={{ display:'flex', flexDirection:'column', gap: 10 }}>
                    <div style={{ display:'flex', alignItems:'center', gap: 10, flexWrap:'wrap' }}>
                      <div style={{ color:'#595959', width: 70 }}>长度</div>
                      {cmInput(endBoxDraft?.length_cm, (cm) => {
                        setEndBoxDraft(prev => ({ ...(prev || { code:'', container_type:'box' as any }), length_cm: cm }));
                        const inch = (cm == null) ? undefined : Math.ceil(cmToIn(cm));
                        setEndBoxLenIn(inch);
                      })}
                      <span style={{ color:'#8c8c8c' }}>厘米</span>
                      <span style={{ color:'#d9d9d9' }}>|</span>
                      {inInput(endBoxLenIn, (inch) => {
                        setEndBoxLenIn(inch);
                        const cm = (inch == null) ? undefined : Math.ceil(inToCm(inch));
                        setEndBoxDraft(prev => ({ ...(prev || { code:'', container_type:'box' as any }), length_cm: cm }));
                      })}
                      <span style={{ color:'#8c8c8c' }}>英寸</span>
                    </div>

                    <div style={{ display:'flex', alignItems:'center', gap: 10, flexWrap:'wrap' }}>
                      <div style={{ color:'#595959', width: 70 }}>宽度</div>
                      {cmInput(endBoxDraft?.width_cm, (cm) => {
                        setEndBoxDraft(prev => ({ ...(prev || { code:'', container_type:'box' as any }), width_cm: cm }));
                        const inch = (cm == null) ? undefined : Math.ceil(cmToIn(cm));
                        setEndBoxWidIn(inch);
                      })}
                      <span style={{ color:'#8c8c8c' }}>厘米</span>
                      <span style={{ color:'#d9d9d9' }}>|</span>
                      {inInput(endBoxWidIn, (inch) => {
                        setEndBoxWidIn(inch);
                        const cm = (inch == null) ? undefined : Math.ceil(inToCm(inch));
                        setEndBoxDraft(prev => ({ ...(prev || { code:'', container_type:'box' as any }), width_cm: cm }));
                      })}
                      <span style={{ color:'#8c8c8c' }}>英寸</span>
                    </div>

                    <div style={{ display:'flex', alignItems:'center', gap: 10, flexWrap:'wrap' }}>
                      <div style={{ color:'#595959', width: 70 }}>高度</div>
                      <InputNumber
                        min={1}
                        precision={0}
                        value={endBoxDraft?.height_cm as any}
                        onChange={(v) => {
                          const cm = (v == null) ? undefined : Math.max(1, Math.floor(Number(v) || 0));
                          setEndBoxDraft(prev => ({ ...(prev || { code:'', container_type:'box' as any }), height_cm: cm }));
                          const inch = (cm == null) ? undefined : Math.ceil(cmToIn(cm));
                          setEndBoxHeiIn(inch);
                        }}
                        style={{ width: 86 }}
                      />
                      <span style={{ color:'#8c8c8c' }}>厘米</span>
                      <span style={{ color:'#d9d9d9' }}>|</span>
                      {inInput(endBoxHeiIn, (inch) => {
                        setEndBoxHeiIn(inch);
                        const cm = (inch == null) ? undefined : Math.ceil(inToCm(inch));
                        setEndBoxDraft(prev => ({ ...(prev || { code:'', container_type:'box' as any }), height_cm: cm }));
                      })}
                      <span style={{ color:'#8c8c8c' }}>英寸</span>
                    </div>

                    <div style={{ display:'flex', alignItems:'center', gap: 10, flexWrap:'wrap' }}>
                      <div style={{ color:'#595959', width: 70 }}>容器实重</div>
                      <InputNumber
                        min={1}
                        precision={0}
                        value={kgValue as any}
                        onChange={(v) => {
                          const kg = (v == null) ? undefined : Math.max(1, Math.floor(Number(v) || 0));
                          setWeightKg(kg);
                          const lb = (kg == null) ? undefined : Math.ceil(kgToLb(kg));
                          setEndBoxLb(lb);
                        }}
                        style={{ width: 86 }}
                      />
                      <span style={{ color:'#8c8c8c' }}>公斤</span>
                      <span style={{ color:'#d9d9d9' }}>|</span>
                      <InputNumber
                        min={1}
                        precision={0}
                        value={endBoxLb as any}
                        onChange={(v) => {
                          const lb = (v == null) ? undefined : Math.max(1, Math.floor(Number(v) || 0));
                          setEndBoxLb(lb);
                          const kg = (lb == null) ? undefined : Math.ceil(lbToKg(lb));
                          setWeightKg(kg);
                        }}
                        style={{ width: 86 }}
                      />
                      <span style={{ color:'#8c8c8c' }}>磅</span>
                    </div>
                  </div>
                );
              })()}

              <div style={{ marginTop: 10 }}>
                <div style={{ color:'#595959' }}>理论总重量（与操作页口径一致，仅用于提示）</div>
                <div style={{ marginTop: 6 }}>
                  {currentBoxTheory.ok ? (
                    <Tag color="blue" style={{ fontWeight: 700 }}>{`理论总重 ${Number(currentBoxTheory.totalKg).toFixed(3)} kg`}</Tag>
                  ) : (
                    <Tag color="default">无法估算理论总重量</Tag>
                  )}
                </div>
              </div>

              {endBoxWarn ? (
                <div style={{ marginTop: 10, background:'#fffbe6', border:'1px solid #ffe58f', padding:'8px 10px', borderRadius: 6, color:'#614700' }}>
                  <strong>提示：</strong>{endBoxWarn}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </Modal>

      {/* 初次打开审核：BARCODE_MAPPING 每条映射必须明确到库存单元（操作目标） */}
      <Modal
        title="数据完整性审核：请为无法匹配的条码映射选择库存单元操作对象"
        open={mappingAuditOpen}
        closable={false}
        maskClosable={false}
        onCancel={() => { /* 强制审核：不允许直接关闭 */ }}
        footer={[
          <Button
            key="close"
            disabled={mappingAuditSaving || targetIuLoading || mappingAuditAutoMatching}
            onClick={() => {
              // 仅关闭弹窗，不写回、不放行扫码（允许用户先查看页面其它信息）
              try { setMappingAuditDismissed(true); } catch {}
              try { setMappingAuditOpen(false); } catch {}
            }}
          >
            关闭
          </Button>,
          <Button
            key="auto"
            loading={mappingAuditAutoMatching || targetIuLoading}
            disabled={mappingAuditSaving || targetIuLoading || mappingAuditAutoMatching || (mappingAuditRows || []).length === 0}
            onClick={async () => {
              try {
                const instrId = Number(instrIdState);
                if (!Number.isFinite(instrId)) return;
                const rows = Array.isArray(mappingAuditRows) ? mappingAuditRows : [];
                if (!rows.length) return;

                // 仅对“有旧条码”的行尝试自动匹配；缺旧条码只能人工选
                const norm = (v:any) => String(v ?? '').trim().toUpperCase();
                const needOlds = Array.from(new Set(
                  rows
                    .map((r:any) => norm(r?.old))
                    .filter((c:string) => !!c && c !== '-' && c !== 'NULL' && c !== 'UNDEFINED')
                ));
                if (needOlds.length === 0) {
                  message.info('没有可自动匹配的旧条码（仅剩缺旧条码的行需要手动选择）');
                  return;
                }

                setMappingAuditAutoMatching(true);

                // 分批调用后端解析（与后端接口限制保持一致）
                const chunkSize = 200;
                const resolved: Record<string, number> = {};
                for (let i = 0; i < needOlds.length; i += chunkSize) {
                  const chunk = needOlds.slice(i, i + chunkSize);
                  const resp = await request.post(`/api/v1/instruction/instructions/${instrId}/relabel-pack/resolve-old-barcodes/`, {
                    old_codes: chunk,
                  });
                  const items: any[] = Array.isArray(resp?.data?.items) ? resp.data.items : [];
                  items.forEach((it:any) => {
                    const oc = norm(it?.old_code);
                    const iu = Math.floor(Number(it?.inventory_unit_id || 0));
                    if (oc && iu > 0) resolved[oc] = iu;
                  });
                }

                const matchedKeys: string[] = [];
                // 本次计算出的全量 multi 覆盖（作为写回基线文件的权威输入，避免 setState 时序问题）
                const nextMultiLocal: Record<string, number[]> = { ...(mappingKeyIuOverrideMulti || {}) };
                // 写入 overrideMulti（供后续“确认并继续操作”写回基线文件）
                for (const r of rows) {
                  const key = String(r?.key || '');
                  if (!key) continue;
                  const already = Array.isArray(nextMultiLocal[key]) ? nextMultiLocal[key] : [];
                  if (already.length > 0) continue;
                  const oldc = norm(r?.old);
                  const iu = Math.floor(Number((resolved as any)?.[oldc] || 0));
                  if (iu > 0) {
                    nextMultiLocal[key] = [iu];
                    matchedKeys.push(key);
                  }
                }
                // 同步进 state（用于弹窗 UI 与后续确认按钮）
                try { setMappingKeyIuOverrideMulti(nextMultiLocal); } catch {}

                if (matchedKeys.length > 0) {
                  // 将自动匹配结果写回基线文件（让下次进入无需再匹配/审核）
                  try {
                    await persistBmMatchInfoToBaseline({ overrideMulti: nextMultiLocal });
                    message.success(`已执行匹配并写回基线文件：自动匹配 ${matchedKeys.length} 行`);
                  } catch (e:any) {
                    message.error(e?.message || '写回基线文件失败（已自动匹配的选择仍保留在弹窗中）');
                    // 写回失败时，不移除行，避免用户误以为已落库
                    return;
                  }
                }

                // 从弹窗列表中移除已自动匹配且已写入 overrideMulti 的行
                setMappingAuditRows((prevRows:any[]) => {
                  const arr = Array.isArray(prevRows) ? prevRows : [];
                  if (!matchedKeys.length) return arr;
                  const keySet = new Set(matchedKeys.map(String));
                  return arr.filter((r:any) => !keySet.has(String(r?.key || '')));
                });

                if (matchedKeys.length === 0) {
                  message.info('未能自动匹配任何行（可能旧条码不在指令目标范围内或不唯一）');
                }

                // 若已全部自动匹配完成（弹窗列表被清空），则直接放行（不强制再点一次确认按钮）
                try {
                  setTimeout(() => {
                    try {
                      setMappingAuditRows((cur:any[]) => {
                        const arr = Array.isArray(cur) ? cur : [];
                        if (arr.length === 0) {
                          try { setMappingAuditOk(true); } catch {}
                          try { setMappingAuditOpen(false); } catch {}
                        }
                        return arr;
                      });
                    } catch {}
                  }, 0);
                } catch {}
              } catch (e:any) {
                message.error(e?.response?.data?.detail || e?.message || '执行匹配失败');
              } finally {
                setMappingAuditAutoMatching(false);
              }
            }}
          >
            执行匹配
          </Button>,
          <Button
            key="ok"
            type="primary"
            loading={targetIuLoading || mappingAuditSaving}
            disabled={mappingAuditSaving || targetIuLoading}
            onClick={async () => {
              setMappingAuditSaving(true);
              try {
                await persistBmMatchInfoToBaseline();
                setMappingAuditOk(true);
                setMappingAuditOpen(false);
                message.success('已写回已选择的库存单元映射，并继续执行');
              } catch (e:any) {
                message.error(e?.message || '写回基线文件失败');
              } finally {
                setMappingAuditSaving(false);
              }
            }}
          >
            确认并继续操作
          </Button>
        ]}
        width={1280}
        bodyStyle={{ overflowX: 'hidden' }}
        destroyOnClose
      >
        <div style={{ marginBottom: 10, color:'#595959', lineHeight: 1.7 }}>
          <div>基线文件中存在未匹配操作对象的条码映射,请点击"执行匹配",系统将按“旧条码 → 产品分拣条码 → 库存单元内容项 → 本指令操作目标库存单元”进行自动匹配并从本列表移除.</div>
          <div>无法自动匹配的,需要你手动选择操作对象.</div>
          <div>操作对象可以留空,则此条码映射无法执行换标,常用于客户提交了多余的条码映射.</div>
          <div>关闭按钮将不执行任何操作,仅用于关闭此弹窗.在关闭后重新进入此操作阶段将重新显示此弹窗</div>
          <div>确认并执行操作后将同步写入 BARCODE_MAPPING 基线文件的“操作对象匹配信息”列；后续再次进入执行页将直接复用，无需重复审核.</div>
        </div>
        {/* 已移除“批量应用到全部行”，仅保留逐行指定 */}
        <Table
          size="small"
          rowKey={(r:any)=> String(r?.key || '')}
          dataSource={mappingAuditRows}
          pagination={false}
          scroll={{ y: 360 }}
          tableLayout="fixed"
          columns={[
            // 真正序号：按弹窗当前列表顺序从 1 开始（不再展示基线文件行号）
            { title: '序号', key: '__idx', width: 80, align:'center', render: (_:any, __:any, idx:number) => idx + 1 },
            { title: '旧条码', dataIndex: 'old', width: 220, render: (v:any)=> <Typography.Text style={{ fontFamily:'monospace' }}>{String(v||'-')}</Typography.Text> },
            { title: '新条码', dataIndex: 'newer', width: 220, render: (v:any)=> <Typography.Text style={{ fontFamily:'monospace' }}>{String(v||'-')}</Typography.Text> },
            {
              title: '选择操作对象（多选）',
              key: 'pick',
              render: (_:any, r:any) => {
                const k = String(r?.key || '');
                const val = (mappingKeyIuOverrideMulti as any)?.[k] || [];
                return (
                  <Select
                    mode="multiple"
                    showSearch
                    placeholder="请选择操作对象"
                    loading={targetIuLoading}
                    popupMatchSelectWidth={false as any}
                    dropdownStyle={{ minWidth: 900 }}
                    style={{ width: 520 }}
                    value={val as any}
                    optionFilterProp="label"
                    filterOption={(input, opt:any) => {
                      const s = String(opt?.label || '').toLowerCase();
                      return s.includes(String(input || '').toLowerCase());
                    }}
                    optionRender={(opt:any) => (
                      <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.25 }}>
                        {String(opt?.label || '')}
                      </div>
                    )}
                    options={(targetIuOptions || []).map((x:any) => {
                      const sys = String(x?.system_barcode || '').trim();
                      const iuId = Math.floor(Number(x?.id || 0));
                      const iu = (iuPreviewById as any)?.[iuId] || null;
                      const items: any[] = Array.isArray(iu?.content?.items) ? iu.content.items : [];
                      const brief = (() => {
                        try {
                          if (!items.length) return '';
                          const parts = items.slice(0, 3).map((it:any) => {
                            let bc = '';
                            try {
                              const scArr = Array.isArray(it?.sorting_codes) ? it.sorting_codes : [];
                              const codes = scArr.map((c:any) => String(c?.code || '')).filter(Boolean);
                              if (codes.length) bc = codes.join('/');
                            } catch {}
                            if (!bc) bc = String(it?.product_system_code || it?.system_code || it?.product_code || '').trim();
                            const q = Number(it?.quantity ?? it?.qty ?? it?.count ?? 0) || 0;
                            if (!bc) return '';
                            return `${bc} × ${q}`;
                          }).filter(Boolean);
                          const head = parts.join(' / ');
                          if (items.length > 3) return `${head} 等${items.length}项`;
                          return head;
                        } catch { return ''; }
                      })();
                      const label = sys ? `${sys} 内容项：${brief || '加载中...'}` : '-';
                      return { value: x.id, label };
                    })}
                    onChange={(vals:any) => {
                      const arr = Array.isArray(vals) ? vals : [];
                      const ids = Array.from(new Set(arr.map((v:any) => Math.floor(Number(v) || 0)).filter((n:number) => Number.isFinite(n) && n > 0)));
                      if (!k) return;
                      setMappingKeyIuOverrideMulti(prev => ({ ...(prev || {}), [k]: ids }));
                      // 同步第一个 IU 到单选 override（用于执行落库/展示）
                      const first = Number(ids?.[0] || 0);
                      setMappingKeyIuOverride(prev => ({ ...(prev || {}), [k]: (Number.isFinite(first) && first > 0) ? first : 0 }));
                      try { if (first > 0) ensureIuPreviews([first]); } catch {}
                    }}
                  />
                );
              }
            }
          ] as any}
        />
      </Modal>

      {/* 开始新容器弹窗（UI：与 InstructionOutputContainer 枚举一致：box/pallet/other；支持包材扫码自动回填） */}
      <Modal
        title="开始新容器"
        open={startContainerOpen}
        onCancel={() => { if (!startContainerLoading) setStartContainerOpen(false); }}
        onOk={createNewContainerLocal}
        okText="创建新容器"
        cancelText="取消"
        confirmLoading={startContainerLoading}
        width={720}
        destroyOnClose
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {(() => {
            // 从指令问卷配置中按“题目标题”找 key，再从 customer_answers 取值；并尝试映射 select 选项显示名称
            const findQ = (cands: string[]) => {
              const qs = Array.isArray(instrQuestionRows) ? instrQuestionRows : [];
              const norm = (s:any)=> String(s ?? '').trim().toLowerCase();
              const candNorm = cands.map(norm).filter(Boolean);
              return qs.find((q:any) => {
                const label = norm(q?.label);
                const key = norm(q?.key);
                if (!label && !key) return false;
                return candNorm.some(c => (label && label.includes(c)) || (key && key.includes(c)));
              }) || null;
            };
            const formatAns = (q:any): string => {
              try {
                if (!q) return '-';
                const k = String(q?.key || '').trim();
                if (!k) return '-';
                const raw = (instrAnswers || {})[k];
                if (raw === undefined || raw === null || raw === '') return '-';
                const t = String(q?.type || '');
                if (t === 'boolean') return raw ? '是' : '否';
                if (t === 'multiselect') {
                  const arr = Array.isArray(raw) ? raw : [];
                  const toLabel = (v:any) => {
                    const opts = Array.isArray(q?.options) ? q.options : [];
                    const hit = opts.find((o:any)=> String(o?.value) === String(v));
                    return String(hit?.label || v || '').trim();
                  };
                  const vals = arr.map(toLabel).filter(Boolean);
                  return vals.length ? vals.join('、') : '-';
                }
                if (t === 'select') {
                  const opts = Array.isArray(q?.options) ? q.options : [];
                  const hit = opts.find((o:any)=> String(o?.value) === String(raw));
                  return String(hit?.label || raw || '').trim() || '-';
                }
                if (t === 'number') {
                  const n = Number(raw);
                  const s = Number.isFinite(n) ? String(n) : String(raw);
                  const unit = String(q?.unit || '').trim();
                  return unit ? `${s}${unit}` : s;
                }
                // text / textarea
                return String(raw).trim() || '-';
              } catch { return '-'; }
            };
            const qPkg = findQ(['装箱包材选择', '物流包材选择', '包材选择']);
            const qMaxSide = findQ(['最长边', '最大边']);
            const qMaxWeight = findQ(['最大装箱重量', '装箱重量', '最大重量']);
            return (
              <div style={{ padding: '8px 10px', border: '1px solid #faad14', background: '#fff7e6', color: '#ad6800', borderRadius: 6 }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>指令装箱要求</div>
                <div>装箱包材选择：{formatAns(qPkg)}</div>
                <div>最长边：{formatAns(qMaxSide)}</div>
                <div>最大装箱重量：{formatAns(qMaxWeight)}</div>
              </div>
            );
          })()}
          <div style={{ padding: '8px 10px', border: '1px solid #91caff', background: '#e6f4ff', color: '#0958d9', borderRadius: 6 }}>
            可手动填写容器信息，或直接扫描发货包材系统编码自动填充。
          </div>

          <Card size="small" title="扫描发货包材（可选）" headStyle={{ background: '#fafafa' }}>
            <Space direction="vertical" style={{ width: '100%' }} size={8}>
              <Select
                ref={shippingPkgSelectRef}
                showSearch
                allowClear
                value={shippingPkgQuery || undefined}
                placeholder="扫描/输入包材系统编码或名称关键字（模糊匹配，忽略大小写）"
                filterOption={false}
                onDropdownVisibleChange={(open) => { if (open) loadDefaultShippingPkgOptions(); }}
                onSearch={(v) => { setShippingPkgQuery(String(v || '')); }}
                onChange={(v) => {
                  // 清空时
                  if (!v) {
                    setShippingPkgQuery('');
                    setShippingPkgErr('');
                    setShippingPkgHit(null);
                    setShippingPkgOptions([]);
                    lastShipQueryRef.current = '';
                    try { startForm.setFieldsValue({ condition: 'used' }); } catch {}
                    return;
                  }
                  // 选择某个 system_code 后：回填
                  const code = String(v || '').trim();
                  const hit = (shippingPkgOptions || []).find(it => String(it?.system_code || '').trim() === code);
                  if (hit) fillFromShippingPkg(hit);
                  setShippingPkgQuery(code);
                }}
                loading={shippingPkgLoading}
                notFoundContent={shippingPkgLoading ? '搜索中...' : '无匹配包材'}
                style={{ width: '100%' }}
                options={shippingPkgGroupedOptions as any}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {shippingPkgLoading ? <span style={{ color: '#8c8c8c' }}>识别中...</span> : null}
                {shippingPkgErr ? <span style={{ color: '#cf1322' }}>{shippingPkgErr}</span> : null}
                {shippingPkgHit ? (
                  <Tag style={{ background: '#f6ffed', borderColor: '#b7eb8f', color: '#389e0d', fontWeight: 600 }}>
                    已识别：{shippingPkgHit.name || '-'}（{shippingPkgHit.system_code}）
                  </Tag>
                ) : null}
                {shippingPkgHit && !['carton', 'pallet', 'other'].includes(String(shippingPkgHit.packaging_type || '')) ? (
                  <Tag color="gold">该包材类型将映射为“其他”容器</Tag>
                ) : null}
              </div>
            </Space>
          </Card>

          <Card size="small" title="容器信息" headStyle={{ background: '#fffbe6' }}>
            <Form
              form={startForm}
              layout="vertical"
              preserve={false}
            >
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item
                    label="容器类型"
                    name="container_type"
                    rules={[{ required: true, message: '请选择容器类型' }]}
                  >
                    <Select
                      options={[
                        { label: '纸箱', value: 'box' },
                        { label: '托盘', value: 'pallet' },
                        { label: '其他', value: 'other' },
                      ]}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Row gutter={12}>
                    <Col span={14}>
                      <Form.Item label="名称">
                        <Input
                          value={String(shippingPkgHit?.name || '-')}
                          readOnly
                          style={{ color: '#000', background: '#fff' }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={10}>
                      <Form.Item
                        label="新旧"
                        name="condition"
                      >
                        <Select
                          options={[
                            { label: '全新', value: 'new' },
                            { label: '旧', value: 'used' },
                          ]}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
              </Row>

              <Form.Item label="尺寸(cm)">
                <Row gutter={12}>
                  <Col span={8}>
                    <Form.Item
                      name="length_cm"
                    >
                      <InputNumber min={1} precision={0} step={1} style={{ width: '100%' }} placeholder="长" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="width_cm"
                    >
                      <InputNumber min={1} precision={0} step={1} style={{ width: '100%' }} placeholder="宽" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="height_cm"
                    >
                      <InputNumber
                        min={1}
                        precision={0}
                        step={1}
                        style={{ width: '100%' }}
                        placeholder="高"
                        disabled={String(startForm.getFieldValue('container_type') || '').trim() === 'pallet'}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form.Item>

              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item
                    label="净重(g)"
                    name="weight_g"
                  >
                    <InputNumber min={1} precision={0} step={1} style={{ width: '100%' }} placeholder="例如 850" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <div style={{ marginTop: 30, color: '#8c8c8c' }}>

                  </div>
                </Col>
              </Row>
            </Form>
          </Card>
        </div>
      </Modal>
    </div>
  );
};

export default CombinedRelabelBoxRenderer;
